import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import { useSeriesGlossary } from '../hooks/useSeriesGlossary';
import { EXPRESS_TERMINAL_STATUSES } from './expressStatus';
import { getMissingProofreadTranslationGuids, isContentBearingChapterRow } from './proofreadTranslationEligibility';
// BookDetailPage.tsx
// NOTE: Update frontend/src/__tests__/BookDetailPage.test.tsx and run
// `../scripts/test-frontend.sh BookDetailPage` when editing helper logic or behaviors covered there.
import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useProofreadPassSelection } from './hooks/useProofreadPassSelection';
import { resolveFrontendApiBase } from '../utils/apiBase';
import accountApi from '../utils/accountApi';
import { recordUxEvent } from '../utils/uxTelemetry';

import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Input,
  Button,
  Divider,
  Tabs,
  Spin,
  Modal,
  Drawer,
  Popover,
  Select,
  Radio,
  message,
  Checkbox,
  Tooltip,
  Tag,
  Space,
  Form,
  Alert,
  Dropdown,
  Menu,
  theme,
  Typography,
} from 'antd';
import type { TourProps } from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
  FileImageOutlined,
  TranslationOutlined,
  ExportOutlined,
  SaveOutlined,
  BulbOutlined,
  DownOutlined,
  FormOutlined,
  PlusOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownloadOutlined,
  CopyOutlined,
  LoadingOutlined,
  AudioOutlined,
  LeftOutlined,
  RightOutlined,
  BookOutlined,
} from '@ant-design/icons';
import '../BookDetailPage.css';
import { getAuth } from 'firebase/auth';
import { getAuthHeaders } from '../utils/authHeaders';
import { GlossaryScanEstimate, glossaryScanCost, launchRequestIdentity, LaunchRequestIdentity } from '../utils/glossaryScan';
import SagaRaven, { SAGA_SINGLETON_PRIORITIES } from '../SagaRaven';
import FeatherSpinner from '../components/FeatherSpinner';
import TranslationMilestoneTracker, { TranslationMilestoneTrackerProps } from '../components/TranslationMilestoneTracker';
import RelationshipAnalysisModal from '../components/RelationshipAnalysisModal';
import RelationshipMatrixEditorModal from '../components/RelationshipMatrixEditorModal';
import TranslationOptionsModal from '../TranslationOptionsModal';
import GenerateBlurbModal from '../GenerateBlurbModal';
import KeywordSuggestionModal from '../KeywordSuggestionModal';
import TranslationModal, { type TranslationBetaFeedbackPayload } from '../TranslationModal';
import ProofreadModal from '../ProofreadModal';
import { CustomPricing } from '../CustomPricing';
import AddLanguageModal from '../AddLanguageModal';
import NeverTranslateModal from '../NeverTranslateModal';
import AlwaysTranslateModal from '../AlwaysTranslateModal';
import TranslationStatusPanel, { resolveTranslationStatusState } from '../TranslationStatusPanel';
import ProofreadTabContent, { removeHyperlinks } from '../ProofreadTabContent';
import MetadataTranslationTabContent from '../MetadataTranslationTabContent';
import MetadataEditForm from '../MetadataEditForm';
import MetadataAutofillReviewModal, { type MetadataAutofillFieldKey } from '../metadata/MetadataAutofillReviewModal';
import ChapterList from '../ChapterList';
import ChapterContentPanel from '../ChapterContentPanel';
import ExportBookModal, { type ExportBookRequestPayload } from '../ExportBookModal';
import SimpleExportFormatModal, { type SimpleExportFormat } from './SimpleExportFormatModal';
import BookEditPanel from '../BookEditPanel';
import MetadataTranslationModal, { type MetadataTranslationBetaFeedbackPayload } from '../MetadataTranslationModal';
import ChapterUploadModal from '../ChapterUploadModal';
import { useTheme } from '../theme/ThemeProvider';
import SeriesNavigator from '../SeriesNavigator';
import CoverSelectionModal from '../CoverSelectionModal';
import TranslatedTabContent from '../TranslatedTabContent';
import CostSummary from '../components/CostSummary';
import ModalCostBreakdown, { type ModalCostBreakdownRow } from '../components/ModalCostBreakdown';
import BlurbWordCounter from '../components/BlurbWordCounter';
import { formatAxiosErrorDetail } from '../utils/apiError';
import { metadataTranslationFieldKey } from '../utils/metadataFieldKeys';
import InsufficientCreditsAlert from '../components/InsufficientCreditsAlert';
import { countWordsIncludingMarkup, WORD_COUNT_VERSION } from '../utils/wordcount';
import {
  formatBlurbWordLimitMessage,
  getBlurbWordCount,
  MAX_BLURB_TRANSLATION_WORDS,
  metadataFreeRunConsumed,
} from '../utils/metadataFreeRuns';
import { triggerDownloadUrl } from '../utils/download';
import {
  formatLanguagePairDisplayText,
  getTargetLanguageFromPair,
  isOriginalLanguageValue,
  splitLanguagePair,
  stripProofreadLabel,
} from '../utils/languagePairs';
import {
  resolveAccountTerms,
  resolveBookTerms,
  resolveSeriesTerms,
} from '../utils/glossaryKeys';
import { isMissingTranslatedMetadataValue } from '../utils/localizedMetadataDisplay';
import {
  canonicalizeLanguageKey,
  canonicalizeLanguageName,
  canonicalizeTranslationProgressMap,
} from '../utils/languageIdentity';
import { findTranslationPairForTargetLanguage } from '../utils/translationPairs';
import {
  hasCompletedProofreadProgressForRow,
  getSavedProofreadTextForRow,
  hasPendingProofreadReviewForRow as sharedHasPendingProofreadReviewForRow,
  hasSavedProofreadForRow,
  normalizeProofreadVersionValue,
  resolveProofreadWordCountForRow,
} from '../utils/proofreadState';
import {
  EDITING_NOTES_WORD_COST_MULTIPLIER,
  HINDI_WORD_COST_MULTIPLIER,
  PROOFREADING_WORD_COST_MULTIPLIER,
} from '../utils/proofreadingPricing';
import {
  Book,
  Chapter,
  TranslatedChapter,
  EditBook,
  BlurbTranslations,
  TranslationOption,
  TranslationOptions,
  CreditBalance,
  TranslationStatus,
  RecentExportEntry,
  CreateAudiobookParams,
  ChapterWordCounts,
  ChapterReorderPayload,
  SeriesSummary,
  FrenchNarrativeTone,
  TranslationProgressMap,
  TranslationProgressEntry,
  ProofreadChangeSet,
} from '../types';
import type { CreditTopUpContext } from '../types/creditTopUp';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import {
  useTranslationStatus,
} from '../TranslationStatusContext';
import { onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

import { useSelectedAIModel } from '../hooks/useSelectedAIModel';

import { LANGUAGE_CONFIG, getAllLanguages, isLanguageBeta, hasQuotationOptions, getQuotationStyle, getDefaultQuotationStyle } from '../LanguageConfig';
import { useOrderedModels } from '../AIModelConfig';
import { useTourFlags } from '../tours/useTourFlags';
import AppTour, { AppTourVideoLink, renderAppTourPanel } from '../tours/AppTour';
import { useGuidedTour } from '../tours/useGuidedTour';
import { tutorialVideoLinks } from '../tutorialVideoLinks';
import AskAIModal, { AskAIInvocationPayload } from '../AskAIModal';
import { useUsersSharedState } from '../UserContext';
import { RELATIONSHIP_UI_ENABLED } from '../featureFlags';
import { HUMAN_REVIEW_PROVIDERS_ROUTE } from '../serviceProviderDirectory';
import { isTutorialUiSuppressionRequested } from '../utils/tutorialMode';
import { resolveSeriesIdForName } from '../utils/seriesRecords';
import { useTutorialSandbox } from '../tutorials/TutorialSandboxProvider';
import BookDetailLeftRail from './components/BookDetailLeftRail';
import { createProofreadAdmissionQueue } from './proofreadAdmissionQueue';
import { submitDurableProofread, listPendingProofreadSubmissions, clearPendingProofreadSubmission } from './durableProofreadSubmission';
import {
  autoApplyConfidentTermScan,
  bookDetailApiDelete,
  bookDetailApiGet,
  fetchTranslatedChapterAvailability,
  bookDetailApiPost,
  bookDetailFetch,
  fetchBookDetail,
  fetchPreferredTranslationModel,
  fetchSeriesOptions,
  fetchTermScanPreflight as fetchTermScanPreflightRequest,
  fetchUserUiFlags,
  isBookDetailApiError,
  refreshBookWordCounts,
  requestExportDownloadUrl,
  setUserUiFlag,
  startTermScan as startTermScanRequest,
} from './BookDetailPage.api';
import {
  DEFAULT_RECOMMENDED_TRANSLATION_MODEL_ID,
  DEFAULT_RECOMMENDED_TRANSLATION_POLICY,
} from '../recommendedTranslationWorkflow';
import type { RecommendedTranslationWorkflowPolicy } from '../recommendedTranslationWorkflow';
import {
  isChapterTranslationActive,
  isTranslationForChapterPair,
  TranslatedContentHydrationCoordinator,
  type TranslatedContentHydrationReason,
} from './translatedContentHydration';
import {
  TERMINAL_JOB_STATUSES,
  buildOptimisticTranslationStatus,
  computeTranslationEstimatedMinutes,
  ensureMinimumLockEstimate,
  estimateExportMinutes,
  filterActiveChapterEditLocks,
  hasActiveProofreadForVersion,
  hasPendingProofreadReviewForRow,
  isRelationshipAnalysisAlreadyInProgressMessage,
  normalizeEditingNotesResponse,
  normalizeProofreadRequestStatus,
  reconcileProofreadOptimisticTranslation,
  resolveChapterEditLockState,
  resolveExportFilename,
  shouldOverwriteBulkProofreadForRow,
  shouldQueueBulkRelationshipAnalysis,
  shouldSubmitBulkProofreadForRow,
  shouldSuppressProofreadAggregateToast,
  summarizeBulkProofreadApplyResult,
  summarizeProofreadCompletionStatuses,
  translatedChapterNeedsProofreadHydration,
} from './utils/bookDetailProgressUtils';
import {
  BULK_TERM_SCAN_RUN_METADATA_RETRY_ATTEMPTS,
  BULK_TERM_SCAN_RUN_METADATA_RETRY_BASE_DELAY_MS,
  BULK_TRANSLATION_TARGET_CONCURRENCY,
  TERM_SCAN_FAILED_STATES,
  TERM_SCAN_IN_FLIGHT_STATES,
  TERM_SCAN_SUCCESS_STATES,
  TERM_SCAN_WARNING_POLL_INTERVAL_MS,
  buildBulkTranslationRequestPayload,
  buildTranslationRequestPayload,
  runBulkTermScanAutoRunPreflight,
} from './utils/bookDetailTermScanUtils';
import {
  FALLBACK_FRENCH_DIALOGUE_RULESET_OPTIONS,
  LEGACY_FRENCH_DIALOGUE_CLEANUP_RULESET_VERSION,
  TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION,
  buildFrenchDialogueRetranslationTopUpContext,
  deriveFrenchDialogueRetranslationUiState,
  normalizeFrenchDialogueRulesetVersion,
  normalizeFrenchNarrativeTone,
  normalizeQuotationStyleValue,
  resolveFrenchDialogueRulesetForRequest,
  type FrenchDialogueDiscountedRetranslationOffer,
  type FrenchDialogueLegacyEligibility,
} from './utils/bookDetailFrenchDialogueUtils';

const BLOCKING_JOB_TYPE_LABELS: Record<string, string> = {
  translation: 'translation jobs',
  proofread: 'proofread jobs',
  audiobookgeneration: 'audiobook generation',
  editingnotes: 'proofread notes',
  export: 'export jobs',
  frenchdialoguecorrection: 'French dialogue pass',
};
const BLOCKING_JOB_TYPES = new Set(Object.keys(BLOCKING_JOB_TYPE_LABELS));
const SUCCESSFUL_JOB_STATUSES = new Set(['completed', 'succeeded', 'finished']);
const PROOFREAD_REFRESH_JOB_TYPES = new Set(['proofread', 'editingnotes', 'proofread notes']);

type TermScanWarningState = 'missing' | 'running' | 'failed' | 'needs_review';
type LanguageDefaultMode = 'empty' | 'context' | 'specific';
type BookDetailExperienceMode = 'simple' | 'advanced';

interface InlineTopUpPlanDetails {
  planName?: string;
  monthlyCreditsAllocated?: number;
  monthlyCredits?: number;
  billingInterval?: string;
  intervalMonths?: number;
  creditsPerInterval?: number;
  price?: number;
  currency?: string;
  stripePriceID?: string;
  priceId?: string;
  isLegacy?: boolean;
  foreverCreditDiscountPercent?: number;
}

interface InlineTopUpSubscriptionSummary {
  trialEnd?: number | null;
  cancelAt?: number | null;
  nextPaymentDate?: number | null;
  currentPeriodEnd?: number | null;
  scheduledTargetPriceId?: string | null;
  scheduledTargetProductId?: string | null;
  scheduledEffectiveDate?: number | null;
  scheduledAction?: 'upgrade' | 'downgrade' | null;
  isPaused?: boolean;
}

const getTargetLanguageFromTab = (tabValue: string): string => {
  const trimmed = (tabValue || '').trim();
  if (!trimmed || trimmed === 'Original') {
    return '';
  }
  return getTargetLanguageFromPair(trimmed) || '';
};

const normalizeLanguageLabelKey = (value: string): string => canonicalizeLanguageKey(value);

const getLanguageFamilyKey = (value: unknown): string => {
  const key = canonicalizeLanguageKey(value);
  if (['english', 'british english', 'american english'].includes(key)) {
    return 'english';
  }
  return key;
};

const hasTranslatedChapterContentValue = (translatedChapter?: TranslatedChapter | null): boolean => {
  if (!translatedChapter) return false;

  const translatedWordCount = translatedChapter.translated_word_count || 0;
  const translatedText =
    typeof translatedChapter.translated_text === 'string'
      ? translatedChapter.translated_text.trim()
      : '';
  const proofreadText = getSavedProofreadTextForRow(translatedChapter);

  if (translatedWordCount > 0) {
    return true;
  }

  return translatedText.length > 0 || proofreadText.length > 0;
};

const SIMPLE_BLURB_QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const SIMPLE_BLURB_QUILL_FORMATS = [
  'bold',
  'italic',
  'underline',
  'list',
  'bullet',
  'link',
];

const getEncodedLanguagePairParts = (pair: string): { sourceLang: string; targetLang: string } => {
  const { sourceLang, targetLang } = splitLanguagePair(pair);
  return {
    sourceLang: sourceLang.replace(/ /g, '0'),
    targetLang: targetLang.replace(/ /g, '0'),
  };
};

const parseFilenameFromDisposition = (disposition?: string): string | null => {
  if (!disposition) {
    return null;
  }
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
  const raw = match?.[1] || match?.[2];
  if (!raw) {
    return null;
  }
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const sanitizeExportFilenamePart = (value: string): string => (
  value.replace(/\s+/g, '_').replace(/[^\w-]/g, '')
);

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const downloadTextFile = (content: string, filename: string): void => {
  downloadBlob(new Blob([content], { type: 'text/plain' }), filename);
};

export type ProofreadPassMode = 'missing' | 'retry_failed' | 'next_pass';

type ProofreadingPassSummary = {
  status?: string;
  latestPass?: {
    passId?: string | null;
    passNumber?: number;
    status?: string;
    mode?: ProofreadPassMode | string;
    targetChapterGuids?: string[];
    chapterResults?: Record<string, any>;
  } | null;
  effectivePass?: {
    passId?: string | null;
    passNumber?: number;
    status?: string;
    mode?: string | null;
    legacy?: boolean;
  } | null;
  durableSubmissionEnabled?: boolean;
  legacyInferred?: boolean;
  pendingReviewCount?: number;
  missingCount?: number;
  failedCount?: number;
  nextPassNumber?: number;
  allowedModes?: ProofreadPassMode[];
  targetChapterGuidsByMode?: Partial<Record<ProofreadPassMode, string[]>>;
  eligibleChapterGuids?: string[];
  blockingReason?: string;
  structuralChapterCount?: number;
  nextPassRequiresConfirmation?: boolean;
  repeatPassAvailable?: boolean;
  recommendedMode?: ProofreadPassMode | null;
};

const { Text } = Typography;

interface RelationshipChapterSummary {
  chapterGuid: string;
  chapterName: string;
  wordCount: number;
  chapterNumber: number;
  freeRunAvailable?: boolean;
}

interface RelationshipAnalysisSummary {
  missingChapters: number;
  wordCount: number;
  chargeableWordCount?: number;
  creditCost: number;
  chapters?: RelationshipChapterSummary[];
  chaptersToAnalyze?: number;
  mode?: 'missing' | 'rerun';
  freeRunAvailable?: boolean;
}

interface EnsureWordCountRequestItem {
  book_id: string;
  chapter_guid: string;
  scope: 'original' | 'translated' | 'proofread_original' | 'proofread_translated';
  source_lang?: string;
  target_lang?: string;
}

interface EnsureWordCountResultPayload {
  book_id: string;
  chapter_guid: string;
  scope: 'original' | 'translated' | 'proofread_original' | 'proofread_translated';
  word_count: number;
  version: number;
  updated: boolean;
  count_field: string;
  source_lang?: string | null;
  target_lang?: string | null;
  error?: string | null;
}

const { TabPane } = Tabs;
const { Option } = Select;

// Centralized API base for all requests in this file
const API_BASE = resolveFrontendApiBase();
const METADATA_OVERWRITE_REQUIRED = 'METADATA_OVERWRITE_REQUIRED';

type BookDetailTourId = 'main' | 'simpleMain' | 'chapter' | 'metadataLanguage';

interface PendingTour {
  id: BookDetailTourId;
  step: number;
  allowSeen: boolean;
}
interface BookDetailPageContentProps {
  bookId: string;
}

const BookDetailPageContent: React.FC<BookDetailPageContentProps> = ({ bookId }) => {
  const location = useLocation();
  const [activeMetadataTab, setActiveMetadataTab] = useState<string>(() =>
    new URLSearchParams(location.search).get('tab') === 'marketing' ? 'marketing' : 'metadata');
  const marketingWorkspaceActive = activeMetadataTab === 'marketing';
  const { token } = theme.useToken();
  const { isDark } = useTheme();
  const [book, setBook] = useState<Book>({ title: '', author: '', series: '', booknumber: 0, coverUrl: '', chapters: [], genres: [], seriesInheritance: {}, seriesDescription: '', seriesId: null });
const [editBook, setEditBook] = useState<EditBook>({
  title: '',
  author: '',
  series: '',
  booknumber: 0,
  genres: [],
  tropes: [],
  keywords: [],
  seriesId: null,
  seriesDescription: '',
  seriesInheritance: {},
});
  const [seriesOptions, setSeriesOptions] = useState<SeriesSummary[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null);
  const [seriesDescription, setSeriesDescription] = useState<string>('');
  const [seriesInheritance, setSeriesInheritance] = useState<Record<string, boolean>>({});
  const [isSeriesBusy, setIsSeriesBusy] = useState(false);
  const attemptedSeriesAttach = useRef<Set<string>>(new Set());
  const auth = getAuth();
  // Admin detection via UserContext (Option B)
  const { roles, userID: sharedUserId } = useUsersSharedState();
  const resolvedOwnerUserId = sharedUserId || auth.currentUser?.uid || '';
  const isAdmin = Array.isArray(roles) && roles.includes('admin');
  const showMetadataAdminTab = true;
  const loadSeriesOptions = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const options = await fetchSeriesOptions({ userId: auth.currentUser.uid });
      setSeriesOptions(options);
    } catch (error) {
      console.error('Failed to load series list', error);
    }
  }, [auth.currentUser]);

  const findSeriesIdByName = useCallback((name: string) => {
    return resolveSeriesIdForName(seriesOptions, name, selectedSeriesId);
  }, [selectedSeriesId, seriesOptions]);

  const filteredSeriesOptions = useMemo(() => {
    const currentAuthor = (editBook.author || book.author || '').trim().toLowerCase();
    return seriesOptions.filter(opt => {
      const optAuthor = (opt.author || '').trim().toLowerCase();
      if (!optAuthor) return true; // allow legacy/authorless series
      if (!currentAuthor) return false;
      return optAuthor === currentAuthor;
    });
  }, [book.author, editBook.author, seriesOptions]);

  useEffect(() => {
    if (marketingWorkspaceActive) return;
    loadSeriesOptions();
  }, [loadSeriesOptions, marketingWorkspaceActive]);

  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [isSavingChapterOrder, setIsSavingChapterOrder] = useState(false);
  const tutorialSandbox = useTutorialSandbox();
  const tutorialUiSuppressionRequested = useMemo(() => isTutorialUiSuppressionRequested(), []);
  const tutorialRequestedMetadataTab = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const requested = (params.get('tutorial_tab') || '').trim().toLowerCase();
    return ['dashboard', 'metadata', 'characters', 'audiobook'].includes(requested)
      ? requested
      : 'metadata';
  }, [location.search]);
  const tutorialRequestedExperienceMode = useMemo<BookDetailExperienceMode | null>(() => {
    const params = new URLSearchParams(location.search || '');
    const requested = (params.get('tutorial_mode') || params.get('tutorial_experience') || '').trim().toLowerCase();
    return requested === 'simple' || requested === 'advanced'
      ? requested
      : null;
  }, [location.search]);
  const isSandboxFixtureMode = tutorialSandbox.enabled && tutorialSandbox.mode === 'fixture';
  const sandboxFixturePayload = useMemo(() => (
    isSandboxFixtureMode && tutorialSandbox.fixturePayload && typeof tutorialSandbox.fixturePayload === 'object'
      ? tutorialSandbox.fixturePayload
      : null
  ), [isSandboxFixtureMode, tutorialSandbox.fixturePayload]);
  const sandboxFixtureBook = useMemo(() => {
    if (!isSandboxFixtureMode) return null;
    const payload = sandboxFixturePayload;
    if (!payload || typeof payload !== 'object') return null;
    const candidate = payload.book;
    if (!candidate || typeof candidate !== 'object') return null;
    return candidate as Record<string, any>;
  }, [isSandboxFixtureMode, sandboxFixturePayload]);
  // Randomize Saga spinner between drawing and coloring during cover generation
  const [coverSpinnerKind, setCoverSpinnerKind] = useState<'drawing'|'coloring'>('drawing');
  const [isEditing, setIsEditing] = useState<boolean>(() => tutorialUiSuppressionRequested);
  const [defaultBookTab, setDefaultBookTab] = useState<'dashboard' | 'metadata'>('dashboard');
  const [isDefaultBookTabLoaded, setIsDefaultBookTabLoaded] = useState(false);
  const defaultBookTabAppliedRef = useRef(false);
  const marketingTabRouteAppliedRef = useRef<string | null>(null);
  const marketingEditionRouteAppliedRef = useRef<string | null>(null);
  const [audiobookTourSignal, setAudiobookTourSignal] = useState(0);
  const [charactersTourSignal, setCharactersTourSignal] = useState(0);
  const [firstEnter, setFirstEnter] = useState(true);
  const [originalOrder, setOriginalOrder] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreditCheckPending, setIsCreditCheckPending] = useState(false);
  const [isFrenchDialogueModalOpening, setIsFrenchDialogueModalOpening] = useState(false);
  const [isFrenchDialogueRunStarting, setIsFrenchDialogueRunStarting] = useState(false);
  const [isRelationshipEditorVisible, setIsRelationshipEditorVisible] = useState(false);
  const [relationshipEditorChapterGuid, setRelationshipEditorChapterGuid] = useState<string | null>(null);
  const [isTitleValid, setIsTitleValid] = useState(true);
  const [isTranslateModalVisible, setIsTranslateModalVisible] = useState(false);
  const [isProofreadModalVisible, setIsProofreadModalVisible] = useState(false);
  const [isMetadataTranslateModalVisible, setIsMetadataIsTranslateModalVisible] = useState(false);
  const [isChapterUploadModalVisible, setIsChapterUploadModalVisible] = useState(false);
  const [isChapterUploadProcessing, setIsChapterUploadProcessing] = useState(false);
  const [isKeywordSuggestionModalVisible, setIsKeywordSuggestionModalVisible] = useState(false);
  const [sourceLanguage, setSourceLanguage] = useState('English');
  const [bookLanguage, setBookLanguage] = useState<string>('');
  const originalLanguageLabel = useMemo(
    () => (bookLanguage || 'Original').trim() || 'Original',
    [bookLanguage],
  );
  const [targetLanguage, setTargetLanguage] = useState('');
  const [translationTargetLanguages, setTranslationTargetLanguages] = useState<string[]>([]);
  const [targetMetadataLanguage, setTargetMetadataLanguage] = useState('');
  const [metadataTranslationOverwriteApproved, setMetadataTranslationOverwriteApproved] = useState(false);
const [translateVersion, setTranslateVersion] = useState('Original');
  const [termScanWarningState, setTermScanWarningState] = useState<TermScanWarningState | null>(null);
  const [termScanPendingReviewCount, setTermScanPendingReviewCount] = useState<number | null>(null);
  const [termScanWarningDetail, setTermScanWarningDetail] = useState<string | null>(null);
  const [termScanRunCompletedForPair, setTermScanRunCompletedForPair] = useState(false);
  const [, setTermScanWarningDismissed] = useState(false);
  const [isCheckingTermScanWarningStatus, setTermScanWarningLoading] = useState(false);
  const [, setTermScanWarningError] = useState<string | null>(null);
  const [termScanPreflightState, setTermScanPreflightState] = useState<any | null>(null);
const [termScanPreflightStateByLanguage, setTermScanPreflightStateByLanguage] = useState<Record<string, any>>({});
const [processingStatusOpenSignal, setProcessingStatusOpenSignal] = useState(0);
const termScanWarningFetchIdRef = useRef(0);
  const termScanPreflightFetchIdRef = useRef(0);
  const termScanPreflightByLanguageFetchIdRef = useRef(0);
  const [isOpeningGlossaryScan, setIsOpeningGlossaryScan] = useState(false);
  const [glossaryScanTrigger, setGlossaryScanTrigger] = useState<{ pair: string; nonce: number } | null>(null);
  const [isBetaUser, setIsBetaUser] = useState(false);
  const [isLangTesterUser, setIsLangTesterUser] = useState(false);
  const [showTranslatedFlags, setShowTranslatedFlags] = useState(true);
  const languages = getAllLanguages(isBetaUser);
  const betaLanguages = LANGUAGE_CONFIG.betaLanguages;
  const [creditCost, setCreditCost] = useState<number>(0);
  const [editingChapterGuid, setEditingChapterGuid] = useState<string | null>(null);
  const [editedChapterName, setEditedChapterName] = useState<string>('');
  const [blurbActiveTabKey, setBlurbActiveTabKey] = useState<string>('blurb');
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportVersion, setExportVersion] = useState('Original');
  const [proofreadVersion, setProofreadVersion] = useState('Original');
  const [bulkProofreadMode, setBulkProofreadMode] = useState<ProofreadPassMode>('missing');
  const [proofreadingPassSummary, setProofreadingPassSummary] = useState<ProofreadingPassSummary | null>(null);
  const [isLoadingProofreadingPassSummary, setIsLoadingProofreadingPassSummary] = useState(false);
  const [applyingPendingProofreadLanguage, setApplyingPendingProofreadLanguage] = useState<string | null>(null);
  const [pendingProofreadOverlayMessage, setPendingProofreadOverlayMessage] = useState<string | null>(null);
  const normalizeProofreadVersion = React.useCallback((value: unknown) => {
    return normalizeProofreadVersionValue(value);
  }, []);
  const setProofreadVersionSafe = React.useCallback((value: string) => {
    setProofreadVersion(normalizeProofreadVersion(value));
  }, [normalizeProofreadVersion]);
  const [exportFormat, setExportFormat] = useState('ePub');
const [includeImages, setIncludeImages] = useState(false);
const [includeWordImages, setIncludeWordImages] = useState(true);
const [exportOrientation, setExportOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
const [untranslatedChapters, setUntranslatedChapters] = useState<string[]>([]);
const getTargetLanguageFromVersion = useCallback((versionValue: string) => {
  if (!versionValue || versionValue === 'Original' || versionValue === 'Proofread') {
    return '';
  }
  return getTargetLanguageFromPair(versionValue) || '';
}, []);

const normalizeTranslationTargets = useCallback((values: string[]): string[] => {
  const sourceLanguage = bookLanguage || book.language || '';
  const sourceLangKey = canonicalizeLanguageKey(sourceLanguage);
  const sourceLangFamilyKey = getLanguageFamilyKey(sourceLanguage);
  const deduped: string[] = [];
  const seen = new Set<string>();
  (values || []).forEach((value) => {
    const cleanValue = (value || '').trim();
    if (!cleanValue) return;
    const cleanKey = canonicalizeLanguageKey(cleanValue);
    if (cleanKey === sourceLangKey) return;
    if (sourceLangFamilyKey && getLanguageFamilyKey(cleanValue) === sourceLangFamilyKey) return;
    if (seen.has(cleanKey)) return;
    seen.add(cleanKey);
    deduped.push(cleanValue);
  });
  return deduped;
}, [book.language, bookLanguage]);

const resolvedTranslationTargetLanguages = useMemo(() => {
  const normalized = normalizeTranslationTargets(translationTargetLanguages);
  if (normalized.length > 0) {
    return normalized;
  }
  const fallback = (targetLanguage || '').trim();
  if (!fallback) {
    return [];
  }
  if (getLanguageFamilyKey(bookLanguage || book.language || '') === getLanguageFamilyKey(fallback)) {
    return [];
  }
  return [fallback];
}, [translationTargetLanguages, targetLanguage, bookLanguage, book.language, normalizeTranslationTargets]);

useEffect(() => {
  const target = getTargetLanguageFromVersion(exportVersion).toLowerCase();
  const shouldForceHorizontal = target !== 'japanese' || exportFormat === 'ePub';
  if (shouldForceHorizontal && exportOrientation !== 'horizontal') {
    setExportOrientation('horizontal');
  }
}, [exportVersion, exportFormat, exportOrientation, getTargetLanguageFromVersion]);
const [unproofreadChapters, setUnproofreadChapters] = useState<string[]>([]);
  const [unproofreadNotesChapters, setUnproofreadNotesChapters] = useState<string[]>([]);
  const [showWarning, setShowWarning] = useState(false);
const [relationshipSummary, setRelationshipSummary] = useState<RelationshipAnalysisSummary | null>(null);
const [relationshipRerunSummary, setRelationshipRerunSummary] = useState<RelationshipAnalysisSummary | null>(null);
const [isRelationshipSummaryLoading, setIsRelationshipSummaryLoading] = useState(false);
const [isRelationshipRerunSummaryLoading, setIsRelationshipRerunSummaryLoading] = useState(false);
const [isRelationshipAnalysisRunning, setIsRelationshipAnalysisRunning] = useState(false);
const [isRelationshipModalVisible, setIsRelationshipModalVisible] = useState(false);
const [relationshipAnalysisMode, setRelationshipAnalysisMode] = useState<'missing' | 'rerun'>('missing');
const [relationshipSelectedChapterGuids, setRelationshipSelectedChapterGuids] = useState<string[]>([]);
const relationshipSelectionTouchedRef = useRef(false);
const [metadataTabSignal, setMetadataTabSignal] = useState<{ tab: string; ts: number } | null>(null);
const [metadataLanguageSignal, setMetadataLanguageSignal] = useState<{ language: string; ts: number } | null>(null);
const [termScanReviewTrigger, setTermScanReviewTrigger] = useState<{ pair: string; nonce: number } | null>(null);
const [registryRefreshSignal, setRegistryRefreshSignal] = useState<number | null>(null);
const [registryClearSignal, setRegistryClearSignal] = useState<number | null>(null);
const relationshipChapterOptions = useMemo(() => {
  if (!book?.chapters || book.chapters.length === 0) {
    return [];
  }
  return [...book.chapters]
    .filter((chapter) => chapter?.chapter_guid)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((chapter, index) => ({
      chapterGuid: chapter.chapter_guid,
      chapterName: chapter.chapter_name || `Chapter ${index + 1}`,
      chapterNumber: chapter.order ?? index + 1,
    }));
}, [book?.chapters]);
const hasRelationshipChapters = relationshipChapterOptions.length > 0;
const rerunRelationshipChapterGuids = useMemo(() => {
  if (!relationshipRerunSummary?.chapters || relationshipRerunSummary.chapters.length === 0) {
    return [];
  }
  const sorted = [...relationshipRerunSummary.chapters].sort((a, b) => {
    const numA = typeof a.chapterNumber === 'number' && a.chapterNumber > 0 ? a.chapterNumber : Number.POSITIVE_INFINITY;
    const numB = typeof b.chapterNumber === 'number' && b.chapterNumber > 0 ? b.chapterNumber : Number.POSITIVE_INFINITY;
    if (numA !== numB) return numA - numB;
    const nameA = (a.chapterName || '').toLowerCase();
    const nameB = (b.chapterName || '').toLowerCase();
    if (nameA && nameB && nameA !== nameB) return nameA.localeCompare(nameB);
    return (a.chapterGuid || '').localeCompare(b.chapterGuid || '');
  });
  const unique = new Set<string>();
  const ordered: string[] = [];
  sorted.forEach((chapter) => {
    if (!chapter.chapterGuid) return;
    if (unique.has(chapter.chapterGuid)) return;
    unique.add(chapter.chapterGuid);
    ordered.push(chapter.chapterGuid);
  });
  return ordered;
}, [relationshipRerunSummary?.chapters]);
  const isSelectedChapter = (chapterGuid: string): boolean => {
      if (!selectedChapter) return false;
      return chapterGuid === selectedChapter.chapter_guid;
  };
  const syncSelectedChapterRef = useCallback((chapters: Chapter[]) => {
    setSelectedChapter(prev => {
      if (!prev) {
        return prev;
      }
      const replacement = chapters.find(chapter => chapter.chapter_guid === prev.chapter_guid);
      return replacement || prev;
    });
  }, []);

  const [showDeleteTranslatedChaptersModal, setShowDeleteTranslatedChaptersModal] = useState(false);
  const [showDeleteProofreadChaptersModal, setShowDeleteProofreadChaptersModal] = useState(false);
  const [showDeleteSingleProofreadChapterModal, setShowDeleteSingleProofreadChapterModal] = useState(false);
  const [showDeleteSingleTranslatedChapterModal, setShowDeleteSingleTranslatedChapterModal] = useState(false);

  const [deleteChaptersSourceLang, setDeleteChaptersSourceLang] = useState<string>('');
  const [deleteChaptersTargetLang,setDeleteChaptersTargetLang] = useState<string>('');

  const [deleteSingleChapterSourceLang, setDeleteSingleChapterSourceLang] = useState<string>('');
  const [deleteSingleChapterTargetLang,setDeleteSingleChapterTargetLang] = useState<string>('');

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const [blurb, setBlurb] = useState<string>('');
  const [blurbTranslations, setBlurbTranslations] = useState<{ [language: string]: string }>({});
  const [tropes, setTropes] = useState<string[]>([]);
  const [keywords, setKeywords] = useState<string[]>([]);
const [intendedAudience, setIntendedAudience] = useState<string[]>([]);
const normalizeAudienceInput = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value
      .map(item => (typeof item === 'string' ? item.trim() : String(item ?? '')))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};
const normalizeMetadataDirtyText = (value: any): string => String(value ?? '').trim();
const normalizeMetadataDirtyHtml = (value: any): string => {
  const normalized = String(value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/&nbsp;/gi, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .trim();
  return /^<p><br><\/p>$/i.test(normalized) ? '' : normalized;
};
const normalizeMetadataDirtyList = (value: any): string[] => normalizeAudienceInput(value)
  .map(item => item.trim())
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b));
const metadataDirtyListsEqual = (left: any, right: any): boolean => {
  const leftList = normalizeMetadataDirtyList(left);
  const rightList = normalizeMetadataDirtyList(right);
  return leftList.length === rightList.length && leftList.every((value, index) => value === rightList[index]);
};
const normalizeMetadataDirtyObject = (value: any): string => {
  if (!value || typeof value !== 'object') {
    return '{}';
  }
  return JSON.stringify(
    Object.keys(value)
      .sort()
      .reduce<Record<string, any>>((acc, key) => {
        acc[key] = value[key];
        return acc;
      }, {})
  );
};
const [uploadType, setUploadType] = useState<string>('');
const normalizedUploadType = useMemo(() => (uploadType || '').trim().toLowerCase(), [uploadType]);
const isEpubUpload = normalizedUploadType === 'epub';
const [translationOptions, setTranslationOptions] = useState<TranslationOptions>({});
const [isTranslationOptionsModalVisible, setIsTranslationOptionsModalVisible] = useState(false);
const [canTranslateTitle, setCanTranslateTitle] = useState(false);
const [canTranslateSeries, setCanTranslateSeries] = useState(false);
const [canTranslateBlurb, setCanTranslateBlurb] = useState(false);
  const [titleTranslations, setTitleTranslations] = useState<{ [key: string]: string }>({});
  const [seriesTranslations, setSeriesTranslations] = useState<{ [key: string]: string }>({});
  const [seriesDescriptionTranslations, setSeriesDescriptionTranslations] = useState<{ [key: string]: string }>({});
const [keywordTranslations, setKeywordTranslations] = useState<{ [key: string]: string[] }>({});


const [isTranslatedTabActive, setIsTranslatedTabActive] = useState(false);
const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
const [isInlineTopUpModalVisible, setIsInlineTopUpModalVisible] = useState(false);
const [inlineTopUpContext, setInlineTopUpContext] = useState<CreditTopUpContext | null>(null);
const [inlineTopUpPlanDetails, setInlineTopUpPlanDetails] = useState<InlineTopUpPlanDetails>({});
const [inlineTopUpSubscriptionSummary, setInlineTopUpSubscriptionSummary] = useState<InlineTopUpSubscriptionSummary | null>(null);
const [isInlineTopUpLoading, setIsInlineTopUpLoading] = useState(false);
const [isInlineRenewing, setIsInlineRenewing] = useState(false);
const [inlineCanRenewEarly, setInlineCanRenewEarly] = useState<boolean | null>(null);
const [inlineRenewDisabledTooltip, setInlineRenewDisabledTooltip] = useState<string>('');
  const [userHasEnoughCredits, setUserHasEnoughCredits] = useState(true);
  const [userHasEnoughCreditsMetadata, setUserHasEnoughCreditsMetadata] = useState(true);

const hasUntranslatedChapters = untranslatedChapters.length > 0;

const availableCredits = useMemo(() => {
  if (!creditBalance) {
    return null;
  }
  const value = creditBalance.available_balance ?? creditBalance.credit_balance;
  return typeof value === 'number' ? value : null;
}, [creditBalance]);
const inlineHasCurrentPlan = Boolean(
  inlineTopUpPlanDetails.stripePriceID || inlineTopUpPlanDetails.priceId
);
const inlinePlanBillingInterval = (inlineTopUpPlanDetails.billingInterval || 'monthly')
  .toString()
  .toLowerCase();
const inlinePlanIntervalMonths =
  typeof inlineTopUpPlanDetails.intervalMonths === 'number' &&
  inlineTopUpPlanDetails.intervalMonths > 0
    ? inlineTopUpPlanDetails.intervalMonths
    : inlinePlanBillingInterval === 'yearly'
    ? 12
    : 1;
const inlinePlanMonthlyAllocation =
  inlineTopUpPlanDetails.monthlyCreditsAllocated ??
  inlineTopUpPlanDetails.monthlyCredits ??
  0;
const inlinePlanUpfrontCredits =
  inlinePlanBillingInterval === 'yearly'
    ? inlineTopUpPlanDetails.creditsPerInterval ?? inlinePlanMonthlyAllocation * inlinePlanIntervalMonths
    : undefined;
const inlineRenewDisabled =
  !!inlineTopUpSubscriptionSummary?.cancelAt ||
  !!inlineTopUpSubscriptionSummary?.isPaused ||
  inlineCanRenewEarly === false ||
  isInlineRenewing;
const inlineRenewDisabledMessage =
  inlineRenewDisabledTooltip ||
  (inlineTopUpSubscriptionSummary?.isPaused
    ? 'Resume your subscription before renewing.'
    : inlineTopUpSubscriptionSummary?.cancelAt
    ? 'Reactivate your subscription before renewing.'
    : inlineCanRenewEarly === false
    ? 'Early renewal is not available yet.'
    : 'This will charge immediately and reset your billing date to today.');

const RELATIONSHIP_WORD_RATE = 0.1;
const computeRelationshipCreditCost = React.useCallback(
  (chapters?: RelationshipChapterSummary[] | null, freeRunAvailable?: boolean | null) => {
    if (!chapters || chapters.length === 0) {
      return 0;
    }
    const resolvedFreeRunAvailable = !!freeRunAvailable;
    let chargeableWordCount = 0;
    chapters.forEach((chapter) => {
      const wordCount = typeof chapter.wordCount === 'number' && chapter.wordCount > 0 ? chapter.wordCount : 0;
      const isFreeRun = resolvedFreeRunAvailable && chapter.freeRunAvailable !== false;
      if (!isFreeRun) {
        chargeableWordCount += wordCount;
      }
    });
    return chargeableWordCount > 0 ? Math.ceil(chargeableWordCount * RELATIONSHIP_WORD_RATE) : 0;
  },
  []
);
const selectedRelationshipRerunChapters = useMemo(() => {
  if (!relationshipRerunSummary?.chapters || relationshipSelectedChapterGuids.length === 0) {
    return [];
  }
  const selectedSet = new Set(relationshipSelectedChapterGuids);
  return relationshipRerunSummary.chapters.filter(
    (chapter) => chapter.chapterGuid && selectedSet.has(chapter.chapterGuid)
  );
}, [relationshipRerunSummary?.chapters, relationshipSelectedChapterGuids]);
const isFullRelationshipRerunSelection = useMemo(() => {
  if (relationshipSelectedChapterGuids.length === 0) {
    return false;
  }
  if (rerunRelationshipChapterGuids.length === 0) {
    return false;
  }
  if (relationshipSelectedChapterGuids.length !== rerunRelationshipChapterGuids.length) {
    return false;
  }
  const selectedSet = new Set(relationshipSelectedChapterGuids);
  return rerunRelationshipChapterGuids.every((guid) => selectedSet.has(guid));
}, [relationshipSelectedChapterGuids, rerunRelationshipChapterGuids]);
const relationshipCreditCost = relationshipSummary?.creditCost ?? null;
const relationshipRerunCreditCost =
  relationshipAnalysisMode === 'rerun'
    ? computeRelationshipCreditCost(
        selectedRelationshipRerunChapters,
        relationshipRerunSummary?.freeRunAvailable
      )
    : relationshipRerunSummary?.creditCost ?? null;
const activeRelationshipSummary =
  relationshipAnalysisMode === 'rerun' ? relationshipRerunSummary : relationshipSummary;
const activeRelationshipSummaryLoading =
  relationshipAnalysisMode === 'rerun'
    ? isRelationshipRerunSummaryLoading
    : isRelationshipSummaryLoading;
const activeRelationshipCreditCost =
  relationshipAnalysisMode === 'rerun' ? relationshipRerunCreditCost : relationshipCreditCost;
const activeRelationshipCreditInsufficient =
  availableCredits != null &&
  activeRelationshipCreditCost != null &&
  availableCredits < activeRelationshipCreditCost;
const [hasUntranslatedChaptersForProofread, setHasUntranslatedChaptersForProofread] = useState(false);

const [hasProofreadChapters, setHasProofreadChapters] = useState(false);
const [hasProofreadNotesChapters, setHasProofreadNotesChapters] = useState(false);
const [hasUnproofreadChapters, setHasUnproofreadChapters] = useState(true);
const [hasUnproofreadNotesChapters, setHasUnproofreadNotesChapters] = useState(true);
const [hasOriginalProofreadComplete, setHasOriginalProofreadComplete] = useState(false);
const [viewMode, setViewMode] = useState('sideBySide');
const navigate = useNavigate();
const DEFAULT_PREFERRED_MODEL_ID = DEFAULT_RECOMMENDED_TRANSLATION_MODEL_ID;
const TRANSLATION_WORKFLOW_PREF_FLAG = 'translationWorkflowPreferAdvanced';
const TRANSLATION_AUTORUN_TERM_SCAN_FLAG = 'translationAutoRunTermScanBeforeBulk';
const TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG = 'translationRequireCharacterAnalysis';
const BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG = 'bulkTranslationTermScanPreflightEnabled';
const BOOK_DETAIL_EXPERIENCE_MODE_FLAG = 'bookDetailExperienceMode';
const BOOK_DETAIL_EXPERIENCE_PROMPT_SEEN_FLAG = 'bookDetailExperiencePromptSeen';
const BOOK_DETAIL_SIMPLE_ROLLOUT_AT = Date.parse('2026-05-06T00:00:00.000Z');
const [selectedAIModel, setSelectedAIModel] = useState<string>("");
const [selectedMetadataModel, setSelectedMetadataModel] = useState<string>("");
const [selectedProofreadModel, setSelectedProofreadModel] = useState<string>("");
const [preferredTranslationModelId, setPreferredTranslationModelId] = useState<string>(DEFAULT_PREFERRED_MODEL_ID);
const [recommendedWorkflowPolicy, setRecommendedWorkflowPolicy] = useState<RecommendedTranslationWorkflowPolicy>({
  ...DEFAULT_RECOMMENDED_TRANSLATION_POLICY,
});
const [translationWorkflowMode, setTranslationWorkflowMode] = useState<'easy' | 'advanced'>('easy');
const [autoProofreadEnabled, setAutoProofreadEnabled] = useState(false);
const [isTranslationAdvancedOpen, setIsTranslationAdvancedOpen] = useState(false);
const [translationAutoRunTermScanBeforeBulk, setTranslationAutoRunTermScanBeforeBulk] = useState(false);
const [requireCharacterAnalysis, setRequireCharacterAnalysis] = useState(true);
const [bulkTermScanPreflightEnabled, setBulkTermScanPreflightEnabled] = useState(true);
const workflowPreferenceLoadedRef = useRef(false);
const workflowPreferenceUserOverrideRef = useRef(false);
const [rejectedChapters, setRejectedChapters] = useState<string[]>([]);
const [betaLanguageWarning, setBetaLanguageWarning] = useState(false);

useEffect(() => {
  if (translationWorkflowMode !== 'easy') return;
  if (!preferredTranslationModelId) return;
  if (!isTranslateModalVisible) return;
  if (!targetLanguage) return;
  if (selectedAIModel !== preferredTranslationModelId) {
    setSelectedAIModel(preferredTranslationModelId);
  }
}, [translationWorkflowMode, selectedAIModel, preferredTranslationModelId, isTranslateModalVisible, targetLanguage]);

useEffect(() => {
  if (translationWorkflowMode === 'easy') {
    setAutoProofreadEnabled(false);
    return;
  }
  if (selectedAIModel && selectedAIModel === preferredTranslationModelId) {
    setAutoProofreadEnabled(false);
  }
}, [translationWorkflowMode, selectedAIModel, preferredTranslationModelId]);

const loadPreferredTranslationModel = useCallback(async () => {
  try {
    const policy = await fetchPreferredTranslationModel();
    if (policy.modelId) {
      setPreferredTranslationModelId(policy.modelId);
    }
    setRecommendedWorkflowPolicy(current => ({ ...current, ...policy }));
  } catch (_e) {
    // Non-blocking
  }
}, []);

useEffect(() => {
  if (marketingWorkspaceActive) return;
  loadPreferredTranslationModel();
}, [loadPreferredTranslationModel, marketingWorkspaceActive]);

useEffect(() => {
  if (isTranslateModalVisible) {
    loadPreferredTranslationModel();
  }
}, [isTranslateModalVisible, loadPreferredTranslationModel]);

const loadWorkflowPreference = useCallback(
  async (options: { force?: boolean } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    if (!options.force && workflowPreferenceLoadedRef.current) return;
    try {
      const data = await fetchUserUiFlags({ userId });
      const flags = data?.uiFlags || {};
      const systemFlags = data?.systemFlags || {};
      const preflightEnabled =
        typeof systemFlags[BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG] === 'boolean'
          ? Boolean(systemFlags[BULK_TERM_SCAN_PREFLIGHT_SYSTEM_FLAG])
          : true;
      const preferAdvanced = !!flags[TRANSLATION_WORKFLOW_PREF_FLAG];
      const autoRunTermScan =
        preflightEnabled &&
        typeof flags[TRANSLATION_AUTORUN_TERM_SCAN_FLAG] === 'boolean'
          ? flags[TRANSLATION_AUTORUN_TERM_SCAN_FLAG]
          : false;
      const requireAnalysis =
        typeof flags[TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG] === 'boolean'
          ? Boolean(flags[TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG])
          : true;
      if (!workflowPreferenceUserOverrideRef.current || options.force) {
        setTranslationWorkflowMode(preferAdvanced ? 'advanced' : 'easy');
      }
      setBulkTermScanPreflightEnabled(preflightEnabled);
      setTranslationAutoRunTermScanBeforeBulk(Boolean(autoRunTermScan));
      setRequireCharacterAnalysis(requireAnalysis);
    } catch (_e) {
      // Non-blocking
    } finally {
      workflowPreferenceLoadedRef.current = true;
    }
  },
  [auth.currentUser?.uid]
);

useEffect(() => {
  loadWorkflowPreference();
}, [loadWorkflowPreference]);

useEffect(() => {
  if (isTranslateModalVisible) {
    loadWorkflowPreference({ force: true });
  }
}, [isTranslateModalVisible, loadWorkflowPreference]);

const isUserCreatedAfterSimpleRollout = useCallback((): boolean => {
  const creationTime = auth.currentUser?.metadata?.creationTime;
  if (!creationTime) {
    return false;
  }
  const createdAt = Date.parse(creationTime);
  return Number.isFinite(createdAt) && createdAt >= BOOK_DETAIL_SIMPLE_ROLLOUT_AT;
}, [auth.currentUser?.metadata?.creationTime]);

const persistBookDetailExperienceMode = useCallback(
  async (mode: BookDetailExperienceMode, options: { promptSeen?: boolean } = {}) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    try {
      await setUserUiFlag({
        userId,
        flags: {
          [BOOK_DETAIL_EXPERIENCE_MODE_FLAG]: mode,
          ...(options.promptSeen ? { [BOOK_DETAIL_EXPERIENCE_PROMPT_SEEN_FLAG]: true } : {}),
        },
      });
    } catch (_e) {
      // Non-blocking preference persistence.
    }
  },
  [auth.currentUser?.uid],
);

const applyBookDetailExperienceMode = useCallback(
  (mode: BookDetailExperienceMode, options: { promptSeen?: boolean } = {}) => {
    setBookDetailExperienceMode(mode);
    setBookDetailExperiencePromptVisible(false);
    recordUxEvent('book_workspace_mode_changed', { mode });
    void persistBookDetailExperienceMode(mode, options);
  },
  [persistBookDetailExperienceMode],
);

useEffect(() => {
  // Marketing is available in the full workspace. Honor a direct link without
  // waiting for, or changing, the author's saved Guided Workflow preference.
  if (new URLSearchParams(location.search).get('tab') === 'marketing') {
    setBookDetailExperienceMode('advanced');
    setBookDetailExperienceLoaded(true);
    setBookDetailExperiencePromptVisible(false);
    return;
  }
  if (tutorialUiSuppressionRequested && tutorialRequestedExperienceMode) {
    setBookDetailExperienceMode(tutorialRequestedExperienceMode);
    setBookDetailExperienceLoaded(true);
    setBookDetailExperiencePromptVisible(false);
    return;
  }

  const userId = auth.currentUser?.uid;
  if (!userId) {
    setBookDetailExperienceMode('advanced');
    setBookDetailExperienceLoaded(true);
    setBookDetailExperiencePromptVisible(false);
    return;
  }

  let cancelled = false;
  const loadBookDetailExperience = async () => {
    setBookDetailExperienceLoaded(false);
    try {
      const response = await fetchUserUiFlags({ userId });
      if (cancelled) return;
      const flags = response?.uiFlags || {};
      const explicitMode = flags[BOOK_DETAIL_EXPERIENCE_MODE_FLAG];
      if (explicitMode === 'simple' || explicitMode === 'advanced') {
        setBookDetailExperienceMode(explicitMode);
        setBookDetailExperiencePromptVisible(false);
        return;
      }
      if (isUserCreatedAfterSimpleRollout()) {
        setBookDetailExperienceMode('simple');
        setBookDetailExperiencePromptVisible(false);
        void persistBookDetailExperienceMode('simple');
        return;
      }
      setBookDetailExperienceMode('advanced');
      setBookDetailExperiencePromptVisible(!flags[BOOK_DETAIL_EXPERIENCE_PROMPT_SEEN_FLAG]);
    } catch (_e) {
      if (!cancelled) {
        setBookDetailExperienceMode('advanced');
        setBookDetailExperiencePromptVisible(false);
      }
    } finally {
      if (!cancelled) {
        setBookDetailExperienceLoaded(true);
      }
    }
  };

  void loadBookDetailExperience();
  return () => {
    cancelled = true;
  };
}, [
  auth.currentUser?.uid,
  isUserCreatedAfterSimpleRollout,
  persistBookDetailExperienceMode,
  tutorialRequestedExperienceMode,
  tutorialUiSuppressionRequested,
  location.search,
]);

const resolveTermScanWarningState = useCallback(
  (run: any | null): TermScanWarningState | null => {
    if (!run) {
      return 'missing';
    }
    const status = (run?.status || '').toString().trim().toLowerCase();
    if (!status) {
      return 'missing';
    }
    if (TERM_SCAN_SUCCESS_STATES.has(status)) {
      const normalizeCount = (value: unknown): number | null => {
        if (typeof value === 'number' && Number.isFinite(value)) {
          return Math.max(0, Math.floor(value));
        }
        if (typeof value === 'string') {
          const parsed = Number(value);
          if (Number.isFinite(parsed)) {
            return Math.max(0, Math.floor(parsed));
          }
        }
        return null;
      };
      const explicitPending =
        normalizeCount(run?.pendingReviewCount) ??
        normalizeCount(run?.pending_review_count) ??
        normalizeCount(run?.pendingCount) ??
        normalizeCount(run?.pending_count);
      const suggestionCount =
        normalizeCount(run?.suggestionCount) ?? normalizeCount(run?.suggestion_count);
      const appliedCount = normalizeCount(run?.appliedCount) ?? normalizeCount(run?.applied_count) ?? 0;
      const ignoredCount = normalizeCount(run?.ignoredCount) ?? normalizeCount(run?.ignored_count) ?? 0;
      const computedPending = suggestionCount == null
        ? null
        : Math.max(0, suggestionCount - appliedCount - ignoredCount);
      const pendingCount = explicitPending ?? computedPending;
      if (pendingCount != null) {
        return pendingCount > 0 ? 'needs_review' : null;
      }
      const reviewed = Boolean(run?.reviewedAt || run?.reviewed_at || run?.reviewedBy);
      return reviewed ? null : 'needs_review';
    }
    if (TERM_SCAN_IN_FLIGHT_STATES.has(status)) {
      return 'running';
    }
    if (TERM_SCAN_FAILED_STATES.has(status)) {
      return 'failed';
    }
    return 'missing';
  },
  [],
);

const resolveTermScanPendingReviewCount = useCallback((run: any | null): number | null => {
  if (!run) {
    return null;
  }
  const normalizeCount = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(0, Math.floor(value));
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return Math.max(0, Math.floor(parsed));
      }
    }
    return null;
  };
  const explicitPending =
    normalizeCount(run?.pendingReviewCount) ??
    normalizeCount(run?.pending_review_count) ??
    normalizeCount(run?.pendingCount) ??
    normalizeCount(run?.pending_count);
  if (explicitPending != null) {
    return explicitPending;
  }
  const suggestionCount =
    normalizeCount(run?.suggestionCount) ?? normalizeCount(run?.suggestion_count);
  if (suggestionCount == null) {
    return null;
  }
  const appliedCount = normalizeCount(run?.appliedCount) ?? normalizeCount(run?.applied_count) ?? 0;
  const ignoredCount = normalizeCount(run?.ignoredCount) ?? normalizeCount(run?.ignored_count) ?? 0;
  return Math.max(0, suggestionCount - appliedCount - ignoredCount);
}, []);

const fetchTermScanWarningStatus = useCallback(async () => {
  const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
  const sourceLang = (bookLanguage || sourceLanguage || '').trim();
  const targetLang = (targetLanguage || '').trim();
  if (isSandboxFixtureMode) {
    const fixtureWarningState = typeof sandboxFixturePayload?.termScanWarningState === 'string'
      ? sandboxFixturePayload.termScanWarningState as TermScanWarningState
      : null;
    const fixturePendingReviewCount = typeof sandboxFixturePayload?.termScanPendingReviewCount === 'number'
      ? sandboxFixturePayload.termScanPendingReviewCount
      : null;
    const fixtureWarningDetail = typeof sandboxFixturePayload?.termScanWarningDetail === 'string'
      ? sandboxFixturePayload.termScanWarningDetail
      : null;
    setTermScanWarningState(fixtureWarningState);
    setTermScanPendingReviewCount(fixturePendingReviewCount);
    setTermScanWarningDetail(fixtureWarningDetail);
    setTermScanRunCompletedForPair(Boolean(sandboxFixturePayload?.termScanRunCompletedForPair));
    setTermScanWarningDismissed(Boolean(sandboxFixturePayload?.termScanWarningDismissed));
    setTermScanWarningLoading(false);
    setTermScanWarningError(null);
    return;
  }
  if (
    !isTranslateModalVisible ||
    !bookId ||
    !userId ||
    !sourceLang ||
    !targetLang ||
    targetLang === sourceLang
  ) {
    setTermScanWarningState(null);
    setTermScanPendingReviewCount(null);
    setTermScanWarningDetail(null);
    setTermScanRunCompletedForPair(false);
    setTermScanWarningDismissed(false);
    setTermScanWarningLoading(false);
    setTermScanWarningError(null);
    return;
  }
  const fetchId = ++termScanWarningFetchIdRef.current;
  setTermScanWarningLoading(true);
  setTermScanWarningError(null);
  try {
    const resp = await bookDetailApiPost(`${API_BASE}/books/${bookId}/term-scan/latest-matrix`, {
      userId,
      sourceLang,
      targetLangs: [targetLang],
    });
    if (fetchId !== termScanWarningFetchIdRef.current) {
      return;
    }
    const matrix = resp?.data?.matrix || {};
    const matrixEntry =
      matrix?.[targetLang] ||
      (Object.values(matrix || {})[0] as any) ||
      null;
    const run = matrixEntry?.run || null;
    const runFailureDetail = (run?.error || run?.termScanError || '').toString().trim();
    const runStatus = (run?.status || '').toString().trim().toLowerCase();
    setTermScanRunCompletedForPair(TERM_SCAN_SUCCESS_STATES.has(runStatus));
    const dismissed = Boolean(matrixEntry?.warningDismissed);
    setTermScanWarningDismissed(dismissed);
    setTermScanWarningDetail(runFailureDetail || null);
    const nextState = resolveTermScanWarningState(run);
    setTermScanPendingReviewCount(
      nextState === 'needs_review' ? resolveTermScanPendingReviewCount(run) : null,
    );
    if (nextState === 'running' || nextState === 'needs_review') {
      setTermScanWarningState(nextState);
    } else {
      setTermScanWarningState(dismissed ? null : nextState);
    }
  } catch (err: any) {
    if (fetchId !== termScanWarningFetchIdRef.current) {
      return;
    }
    console.error('Failed to load latest term scan warning status', err);
    setTermScanWarningState(null);
    setTermScanPendingReviewCount(null);
    setTermScanWarningDetail(null);
    setTermScanRunCompletedForPair(false);
    setTermScanWarningDismissed(false);
    setTermScanWarningError(err?.response?.data?.detail || err?.message || 'Unable to load scan status.');
  } finally {
    if (fetchId === termScanWarningFetchIdRef.current) {
      setTermScanWarningLoading(false);
    }
  }
}, [
  API_BASE,
  auth.currentUser?.uid,
  sharedUserId,
  bookId,
  bookLanguage,
  sourceLanguage,
  targetLanguage,
  isSandboxFixtureMode,
  sandboxFixturePayload,
  isTranslateModalVisible,
  resolveTermScanWarningState,
  resolveTermScanPendingReviewCount,
]);

useEffect(() => {
  if (!isTranslateModalVisible) {
    setTermScanWarningState(null);
    setTermScanPendingReviewCount(null);
    setTermScanWarningDetail(null);
    setTermScanRunCompletedForPair(false);
    setTermScanWarningDismissed(false);
    setTermScanWarningLoading(false);
    setTermScanWarningError(null);
    return;
  }
  void fetchTermScanWarningStatus();
}, [fetchTermScanWarningStatus, isTranslateModalVisible, targetLanguage]);

useEffect(() => {
  if (tutorialUiSuppressionRequested || !isTranslateModalVisible || termScanWarningState !== 'running') {
    return;
  }
  let cancelled = false;
  let inFlight = false;
  const poll = () => {
    if (cancelled || inFlight) {
      return;
    }
    inFlight = true;
    void fetchTermScanWarningStatus().finally(() => {
      inFlight = false;
    });
  };
  const intervalId = window.setInterval(poll, TERM_SCAN_WARNING_POLL_INTERVAL_MS);
  return () => {
    cancelled = true;
    window.clearInterval(intervalId);
  };
}, [fetchTermScanWarningStatus, isTranslateModalVisible, termScanWarningState, tutorialUiSuppressionRequested]);

const handleDismissTermScanWarning = useCallback(async (mode: 'permanent' | 'session' = 'permanent') => {
  if (mode === 'session') {
    setTermScanWarningState(null);
    setTermScanPendingReviewCount(null);
    setTermScanWarningDetail(null);
    return;
  }
  const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
  const sourceLang = (bookLanguage || sourceLanguage || '').trim();
  const targetLang = (targetLanguage || '').trim();
  if (!bookId || !userId || !sourceLang || !targetLang) {
    return;
  }
  try {
    await bookDetailApiPost(`${API_BASE}/books/${bookId}/term-scan/warning-dismiss`, {
      userId,
      sourceLang,
      targetLang,
    });
    setTermScanWarningDismissed(true);
    setTermScanWarningState(null);
    setTermScanPendingReviewCount(null);
    setTermScanWarningDetail(null);
  } catch (err) {
    console.error('Failed to dismiss term scan warning', err);
    message.error('Unable to dismiss the AI Glossary Scan warning.');
  }
}, [API_BASE, auth.currentUser?.uid, sharedUserId, bookId, bookLanguage, sourceLanguage, targetLanguage]);


const persistWorkflowPreference = async (mode: 'easy' | 'advanced') => {
  const userId = auth.currentUser?.uid;
  if (!userId) return;
  try {
    await bookDetailApiPost(`${API_BASE}/user/ui-flags/set`, {
      userId,
      flags: {
        [TRANSLATION_WORKFLOW_PREF_FLAG]: mode === 'advanced',
      },
    });
  } catch (_e) {
    // Non-blocking
  }
};

const persistTermScanAutoRunPreference = async (enabled: boolean) => {
  const userId = auth.currentUser?.uid;
  if (!userId) return;
  try {
    await bookDetailApiPost(`${API_BASE}/user/ui-flags/set`, {
      userId,
      flags: {
        [TRANSLATION_AUTORUN_TERM_SCAN_FLAG]: enabled,
      },
    });
  } catch (_e) {
    // Non-blocking
  }
};

const persistRequireCharacterAnalysisPreference = async (enabled: boolean) => {
  const userId = auth.currentUser?.uid;
  if (!userId) return;
  try {
    await bookDetailApiPost(`${API_BASE}/user/ui-flags/set`, {
      userId,
      flags: {
        [TRANSLATION_REQUIRE_CHARACTER_ANALYSIS_FLAG]: enabled,
      },
    });
  } catch (_e) {
    // Non-blocking
  }
};

const handleTranslationAutoRunTermScanBeforeBulkChange = (enabled: boolean) => {
  if (!bulkTermScanPreflightEnabled) {
    setTranslationAutoRunTermScanBeforeBulk(false);
    return;
  }
  setTranslationAutoRunTermScanBeforeBulk(Boolean(enabled));
  void persistTermScanAutoRunPreference(Boolean(enabled));
};

const handleRequireCharacterAnalysisChange = (enabled: boolean) => {
  setRequireCharacterAnalysis(Boolean(enabled));
  void persistRequireCharacterAnalysisPreference(Boolean(enabled));
};

const handleWorkflowModeChange = (mode: 'easy' | 'advanced') => {
  workflowPreferenceUserOverrideRef.current = true;
  setTranslationWorkflowMode(mode);
  persistWorkflowPreference(mode);
};

  const [isProofreadChecked, setIsProofreadChecked] = useState(false);
  const [autoAcceptAllProofreadChanges, setAutoAcceptAllProofreadChanges] = useState(false);
  const [isSavingAutoAcceptProofreadPreference, setIsSavingAutoAcceptProofreadPreference] = useState(false);
  const [proofreadExplanationLanguage, setProofreadExplanationLanguage] = useState<string>('English');
  const [isSavingProofreadExplanationLanguagePreference, setIsSavingProofreadExplanationLanguagePreference] = useState(false);
  const [isEditingNotesChecked, setIsEditingNotesChecked] = useState(false);
const [genres, setGenres] = useState<string[]>([]);
const commonGenres = ['Fantasy', 'Science Fiction', 'Mystery', 'Thriller', 'Romance', 'Young Adult', 'Children', 'Historical', 'Non-Fiction', 'Horror'];
  const [chapterTense, setChapterTense] = useState<string>("Past");

  const [quotationStyle, setQuotationStyle] = useState<string>("");
  const [enforceFrenchEmDash, setEnforceFrenchEmDash] = useState<boolean>(true);
  const [frenchDialogueRulesetVersion, setFrenchDialogueRulesetVersion] = useState<string>(
    TRADITIONAL_FRENCH_DIALOGUE_RULESET_VERSION,
  );
  const [proofreadAdditionalInstructions, setProofreadAdditionalInstructions] = useState<string>("");
const AUTO_FRENCH_TONE_VALUE = 'AI Detect';
const [frenchTone, setFrenchTone] = useState<string>('');
useEffect(() => {
  if (targetLanguage !== 'French') return;
  const savedTone = normalizeFrenchNarrativeTone(book.frenchNarrativeTone);
  if (savedTone) {
    if (!frenchTone || frenchTone === AUTO_FRENCH_TONE_VALUE) {
      setFrenchTone(savedTone);
    }
    return;
  }
  if (!frenchTone) {
    setFrenchTone(AUTO_FRENCH_TONE_VALUE);
  }
}, [targetLanguage, book.frenchNarrativeTone, frenchTone]);

useEffect(() => {
  let cancelled = false;
  const userId = auth.currentUser?.uid;
  if (!userId) {
    setAutoAcceptAllProofreadChanges(false);
    setProofreadExplanationLanguage('English');
    setIsSavingAutoAcceptProofreadPreference(false);
    setIsSavingProofreadExplanationLanguagePreference(false);
    return () => {
      cancelled = true;
    };
  }

  const loadProofreadPreference = async () => {
    try {
      const userRef = doc(db, 'users', userId);
      const snapshot = await getDoc(userRef);
      if (cancelled) {
        return;
      }
      const savedPreference = Boolean(
        snapshot.data()?.proofreadPreferences?.autoAcceptAllChanges,
      );
      const savedExplanationLanguage = String(
        snapshot.data()?.proofreadPreferences?.explanationLanguage || 'English',
      ).trim();
      setAutoAcceptAllProofreadChanges(savedPreference);
      setProofreadExplanationLanguage(
        LANGUAGE_CONFIG.baseLanguages.includes(savedExplanationLanguage)
          ? savedExplanationLanguage
          : 'English',
      );
    } catch (error) {
      console.error('Failed to load proofread preferences:', error);
    }
  };

  void loadProofreadPreference();
  return () => {
    cancelled = true;
  };
}, [auth.currentUser?.uid]);

const handleAutoAcceptAllProofreadChangesChange = useCallback(async (value: boolean) => {
  const userId = auth.currentUser?.uid;
  const previousValue = autoAcceptAllProofreadChanges;
  setAutoAcceptAllProofreadChanges(value);
  if (!userId) {
    return;
  }

  setIsSavingAutoAcceptProofreadPreference(true);
  try {
    await bookDetailApiPost(`${API_BASE}/account/preferences`, {
      proofreadPreferences: { autoAcceptAllChanges: value },
    });
  } catch (persistError) {
    console.error('Failed to save proofread preference:', persistError);
    setAutoAcceptAllProofreadChanges(previousValue);
    message.error('Unable to save your proofread auto-accept preference.', 8);
  } finally {
    setIsSavingAutoAcceptProofreadPreference(false);
  }
}, [auth.currentUser?.uid, autoAcceptAllProofreadChanges]);

const handleProofreadExplanationLanguageChange = useCallback(async (value: string) => {
  const normalizedValue = LANGUAGE_CONFIG.baseLanguages.includes(value) ? value : 'English';
  const userId = auth.currentUser?.uid;
  const previousValue = proofreadExplanationLanguage;
  setProofreadExplanationLanguage(normalizedValue);
  if (!userId) {
    return;
  }

  setIsSavingProofreadExplanationLanguagePreference(true);
  try {
    await bookDetailApiPost(`${API_BASE}/account/preferences`, {
      proofreadPreferences: { explanationLanguage: normalizedValue },
    });
  } catch (persistError) {
    console.error('Failed to save proofread preference:', persistError);
    setProofreadExplanationLanguage(previousValue || 'English');
    message.error('Unable to save your proofread explanation language preference.', 8);
  } finally {
    setIsSavingProofreadExplanationLanguagePreference(false);
  }
}, [auth.currentUser?.uid, proofreadExplanationLanguage]);

useEffect(() => {
  setFrenchDialogueRulesetVersion((prev) => normalizeFrenchDialogueRulesetVersion(prev));
}, []);

  const [isToggling, setIsToggling] = useState(false);

  const [canChapterBeProofread, setCanChapterBeProofread] = useState<boolean>(false);
  const [hasChapterBeenProofread, setHasChapterBeenProofread] = useState<boolean>(false);
  const [hasChapterBeenEditNotes, setHasChapterBeenEditNotes] = useState<boolean>(false);
  const [isHydratingProofreadTranslations, setIsHydratingProofreadTranslations] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const [isProofreadConfirmationModalVisible, setIsProofreadConfirmationModalVisible] = useState(false);
  const proofreadPassRequestIdRef = useRef<string | null>(null);
  const proofreadSubmittingRef = useRef(false);
  const proofreadAdmissionControllerRef = useRef<AbortController | null>(null);
  useEffect(() => () => {
    proofreadAdmissionControllerRef.current?.abort();
  }, [bookId, auth.currentUser?.uid]);

  const [isGenerateBlurbModalVisible, setIsGenerateBlurbModalVisible] = useState(false);

const [humanReviewModalVisible, setHumanReviewModalVisible] = useState(false);
const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
const generatedBlurbSaveApplyRef = useRef(false);
const [isAddLanguageModalVisible, setIsAddLanguageModalVisible] = useState(false);
const [alwaysTranslateWords, setAlwaysTranslateWords] = useState<{ [key: string]: string[] }>({});
const [neverTranslateWords, setNeverTranslateWords] = useState<{ [key: string]: string[] }>({});
const [accountAlwaysTranslateWords, setAccountAlwaysTranslateWords] = useState<{ [key: string]: string[] }>({});
const [accountNeverTranslateWords, setAccountNeverTranslateWords] = useState<{ [key: string]: string[] }>({});
const [isNeverTranslateModalVisible, setIsNeverTranslateModalVisible] = useState(false);
const [currentLanguagePair, setCurrentLanguagePair] = useState<string>('blurb');
const [isAlwaysTranslateModalVisible, setIsAlwaysTranslateModalVisible] = useState(false);
const { rules: seriesTranslateRules, status: seriesGlossaryStatus, refresh: refreshSeriesGlossary, setRules: setSeriesTranslateRules } = useSeriesGlossary(resolvedOwnerUserId, bookId || '', book.bookguid === bookId ? book.seriesId : null, isAlwaysTranslateModalVisible || isNeverTranslateModalVisible);
const [useBatch, setUseBatch] = useState<boolean>(false);
const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
const defaultLanguagePreferenceRef = useRef<{ language: string; mode: LanguageDefaultMode } | null>(null);
const [defaultLanguageMode, setDefaultLanguageMode] = useState<LanguageDefaultMode>('specific');
const creditBalanceFetchedAtRef = useRef<number>(0);
const initialCreditBalanceFetchUserRef = useRef<string | null>(null);
const translationStatsDebounceRef = useRef<number | null>(null);
const settledLockGuidsRef = useRef<Set<string>>(new Set());
const settledLocksInitializedRef = useRef(false);
const fetchBookDetailsRef = useRef<(options?: { silent?: boolean }) => void>(() => {});
const lastTranslatedVersionRef = useRef<number | null>(null);
const proofreadCompletionRefreshTimeoutRef = useRef<number | null>(null);
const proofreadTranslationHydrationCountRef = useRef(0);

useEffect(() => {
  settledLockGuidsRef.current.clear();
  settledLocksInitializedRef.current = false;
  lastTranslatedVersionRef.current = null;
  proofreadTranslationHydrationCountRef.current = 0;
  if (proofreadCompletionRefreshTimeoutRef.current && typeof window !== 'undefined') {
    window.clearTimeout(proofreadCompletionRefreshTimeoutRef.current);
    proofreadCompletionRefreshTimeoutRef.current = null;
  }
  setIsHydratingProofreadTranslations(false);
}, [bookId]);

useEffect(() => {
  defaultLanguagePreferenceRef.current = null;
  setDefaultLanguageMode('specific');
}, [auth.currentUser?.uid, sharedUserId]);

const [isAudiobookModalVisible, setIsAudiobookModalVisible] = useState<boolean>(false);
const [showRemoveCover, setShowRemoveCover] = useState(false);
const [selectedAudiobookVersion, setSelectedAudiobookVersion] = useState('Original');
const [audiobookVersionOptions, setAudiobookVersionOptions] = useState<Array<{value: string; label: string}>>([]);
const [audiobookProcessingError, setAudiobookProcessingError] = useState<string>("");
const [isCachePrewarming] = useState<boolean>(false);
const [recentExports, setRecentExports] = useState<RecentExportEntry[]>([]);
const [isRefreshingProgress, setIsRefreshingProgress] = useState(false);
const [expressTargetLanguage, setExpressTargetLanguage] = useState('');
const [expressOutputFormat, setExpressOutputFormat] = useState<'Word' | 'ePub'>('Word');
const [simpleExportFormatModalVisible, setSimpleExportFormatModalVisible] = useState(false);
const [simpleExportFormat, setSimpleExportFormat] = useState<SimpleExportFormat>('Word');
const [simpleExportSubmitting, setSimpleExportSubmitting] = useState(false);
const [expressGlossaryEnabled, setExpressGlossaryEnabled] = useState(false);
const [expressForceGlossaryRescan, setExpressForceGlossaryRescan] = useState(false);
const [expressGlossaryQuote, setExpressGlossaryQuote] = useState<{ context: string; estimate: GlossaryScanEstimate } | null>(null);
const [expressGlossaryQuoteError, setExpressGlossaryQuoteError] = useState('');
const [expressGlossaryQuoteRetry, setExpressGlossaryQuoteRetry] = useState(0);
const expressRequestIdentity = useRef<LaunchRequestIdentity | null>(null);
const expressSubmitPending = useRef(false);
const [expressBlurbEnabled, setExpressBlurbEnabled] = useState(false);
const [expressBlurbSourceText, setExpressBlurbSourceText] = useState('');
const [expressEmailEnabled, setExpressEmailEnabled] = useState(Boolean(auth.currentUser?.email));
const [expressSubmitting, setExpressSubmitting] = useState(false);
const [expressRetrying, setExpressRetrying] = useState(false);
const [expressEmailResending, setExpressEmailResending] = useState(false);
const [expressJob, setExpressJob] = useState<any>(null);
const [simpleMetadataOptions, setSimpleMetadataOptions] = useState<TranslationOptions>({});
const [simpleMetadataLoading, setSimpleMetadataLoading] = useState(false);
const [simpleMetadataError, setSimpleMetadataError] = useState('');
const [simpleMetadataRetryKey, setSimpleMetadataRetryKey] = useState(0);
const [simpleSelectedTitleOptionId, setSimpleSelectedTitleOptionId] = useState('__original__');
const [simpleSelectedSeriesOptionId, setSimpleSelectedSeriesOptionId] = useState('__original__');
const [simpleMetadataDetail, setSimpleMetadataDetail] = useState<{
  label: string;
  option: TranslationOption;
} | null>(null);
const [simpleBlurbDrawerOpen, setSimpleBlurbDrawerOpen] = useState(false);
const [simpleBlurbSaving, setSimpleBlurbSaving] = useState(false);
const [simpleBlurbTranslateModalOpen, setSimpleBlurbTranslateModalOpen] = useState(false);
const [simpleBlurbTranslating, setSimpleBlurbTranslating] = useState(false);
const [simpleTranslatedBlurbVisible, setSimpleTranslatedBlurbVisible] = useState(false);
const [simpleCostBreakdownOpen, setSimpleCostBreakdownOpen] = useState(false);
const [simpleEditDetailsVisible, setSimpleEditDetailsVisible] = useState(false);
const [simpleEditDetailsSaving, setSimpleEditDetailsSaving] = useState(false);
const [simpleEditDetailsDraft, setSimpleEditDetailsDraft] = useState({
  title: '',
  author: '',
  series: '',
  booknumber: '' as string | number,
});
const simpleCoverInputRef = useRef<HTMLInputElement | null>(null);
const [simpleMetadataAutofillReviewVisible, setSimpleMetadataAutofillReviewVisible] = useState(false);
const metadataAutofillInFlightRef = useRef(false);
const [bookDetailExperienceMode, setBookDetailExperienceMode] = useState<BookDetailExperienceMode>('advanced');
const [bookDetailExperienceLoaded, setBookDetailExperienceLoaded] = useState(false);
const [bookDetailExperiencePromptVisible, setBookDetailExperiencePromptVisible] = useState(false);
const selectedModelData = useSelectedAIModel(selectedAIModel);

useEffect(() => {
  if (expressJob?.id && isEpubUpload) {
    return;
  }
  setExpressOutputFormat(isEpubUpload ? 'ePub' : 'Word');
}, [expressJob?.id, isEpubUpload]);

useEffect(() => {
  if (expressJob?.id) {
    return;
  }
  setExpressEmailEnabled(Boolean(auth.currentUser?.email));
}, [auth.currentUser?.email, expressJob?.id]);

useEffect(() => {
  if (expressJob?.id) {
    return;
  }
  setExpressBlurbSourceText(String(book.blurb || ''));
}, [book.blurb, expressJob?.id]);

useEffect(() => {
  if (!isSandboxFixtureMode || !sandboxFixturePayload) {
    return;
  }
  const fixtureCreditBalance = sandboxFixturePayload.creditBalance;
  if (fixtureCreditBalance && typeof fixtureCreditBalance === 'object') {
    setCreditBalance(fixtureCreditBalance as CreditBalance);
    creditBalanceFetchedAtRef.current = Date.now();
  }

  const simpleWorkflow = sandboxFixturePayload.simpleWorkflow;
  if (simpleWorkflow && typeof simpleWorkflow === 'object') {
    const fixtureTarget = String(simpleWorkflow.targetLanguage || '').trim();
    if (fixtureTarget) {
      setExpressTargetLanguage(fixtureTarget);
      setTargetLanguage(fixtureTarget);
      setTranslationTargetLanguages(normalizeTranslationTargets([fixtureTarget]));
    }
    const fixtureOutputFormat = simpleWorkflow.outputFormat === 'ePub' ? 'ePub' : 'Word';
    setExpressOutputFormat(fixtureOutputFormat);
    setExpressGlossaryEnabled(Boolean(simpleWorkflow.glossaryEnabled));
    setExpressBlurbEnabled(Boolean(simpleWorkflow.blurbEnabled));
    setExpressEmailEnabled(Boolean(simpleWorkflow.emailEnabled));
  }

  if ('expressJob' in sandboxFixturePayload) {
    setExpressJob(sandboxFixturePayload.expressJob || null);
  }
  if (Array.isArray(sandboxFixturePayload.recentExports)) {
    setRecentExports(sandboxFixturePayload.recentExports as RecentExportEntry[]);
  } else {
    setRecentExports([]);
  }
}, [
  isSandboxFixtureMode,
  normalizeTranslationTargets,
  sandboxFixturePayload?.fingerprint,
  sandboxFixturePayload,
]);

const applyMetadataAutofillToLocalState = useCallback((metadataAutofill: any) => {
  if (!metadataAutofill || typeof metadataAutofill !== 'object') {
    return;
  }
  const suggestedFields = metadataAutofill.suggestedFields || {};
  const appliedFields = new Set(Array.isArray(metadataAutofill.appliedFields) ? metadataAutofill.appliedFields : []);
  const appliedValues = metadataAutofill.appliedValues || {};
  const getAppliedValues = (field: string, fallback: any): string[] => {
    const selected = appliedValues[field] || (field === 'genre' ? appliedValues.genres : undefined);
    if (Array.isArray(selected)) {
      return selected;
    }
    return Array.isArray(fallback) ? fallback : [];
  };
  const patch: Record<string, any> = { metadataAutofill };
  const shouldApplyBookContext = appliedFields.size > 0 || metadataAutofill.applyMode === 'empty_fields';
  if (shouldApplyBookContext && metadataAutofill.bookTranslationContext) {
    patch.bookTranslationContext = metadataAutofill.bookTranslationContext;
  }
  if (appliedFields.has('genre')) {
    const nextGenres = getAppliedValues('genre', suggestedFields.genre || suggestedFields.genres);
    setGenres(nextGenres);
    patch.genre = nextGenres;
    patch.genres = nextGenres;
  }
  if (appliedFields.has('tropes')) {
    const nextTropes = getAppliedValues('tropes', suggestedFields.tropes);
    setTropes(nextTropes);
    patch.tropes = nextTropes;
  }
  if (appliedFields.has('keywords')) {
    const nextKeywords = getAppliedValues('keywords', suggestedFields.keywords);
    setKeywords(nextKeywords);
    patch.keywords = nextKeywords;
  }
  if (appliedFields.has('intendedAudience')) {
    const nextAudience = normalizeAudienceInput(getAppliedValues('intendedAudience', suggestedFields.intendedAudience));
    setIntendedAudience(nextAudience);
    patch.intendedAudience = nextAudience;
  }
  setBook(prev => ({ ...prev, ...patch }));
}, []);

const runMetadataAutofill = useCallback(async (options?: {
  mode?: 'new_upload' | 'existing_suggestions';
  applyMode?: 'empty_fields' | 'suggestions_only';
  forceRegenerate?: boolean;
  applyFields?: string[];
  applyValues?: Record<string, string[]>;
}) => {
  if (!auth.currentUser?.uid || !bookId || metadataAutofillInFlightRef.current) {
    return null;
  }
  metadataAutofillInFlightRef.current = true;
  const startedPayload = {
    ...(book.metadataAutofill || {}),
    status: 'running',
    mode: options?.mode || book.metadataAutofill?.mode || 'existing_suggestions',
    applyMode: options?.applyMode || book.metadataAutofill?.applyMode || 'suggestions_only',
  };
  setBook(prev => ({ ...prev, metadataAutofill: startedPayload }));
  try {
    const response = await bookDetailApiPost(`${API_BASE}/books/${bookId}/metadata-autofill`, {
      userId: auth.currentUser.uid,
      mode: options?.mode || 'existing_suggestions',
      applyMode: options?.applyMode || 'suggestions_only',
      forceRegenerate: Boolean(options?.forceRegenerate),
      applyFields: options?.applyFields,
      applyValues: options?.applyValues,
    });
    const metadataAutofill = response.data?.metadataAutofill;
    if (metadataAutofill) {
      applyMetadataAutofillToLocalState(metadataAutofill);
    }
    if (response.data?.status === 'error') {
      message.warning(response.data?.message || 'AI metadata scan failed.');
    }
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.response?.data?.detail || error?.message || 'AI metadata scan failed.';
    const failedPayload = {
      ...(book.metadataAutofill || {}),
      status: 'failed',
      errorMessage,
    };
    setBook(prev => ({ ...prev, metadataAutofill: failedPayload }));
    message.error(errorMessage);
    return null;
  } finally {
    metadataAutofillInFlightRef.current = false;
  }
}, [API_BASE, applyMetadataAutofillToLocalState, auth.currentUser?.uid, book.metadataAutofill, bookId]);

useEffect(() => {
  const metadataAutofill = book.metadataAutofill;
  if (!metadataAutofill || typeof metadataAutofill !== 'object') {
    return;
  }
  if (metadataAutofill.mode !== 'new_upload' || metadataAutofill.status !== 'not_started') {
    return;
  }
  void runMetadataAutofill({ mode: 'new_upload', applyMode: 'empty_fields' });
}, [book.metadataAutofill, runMetadataAutofill]);

useEffect(() => {
  const currentExpressStatus = String(expressJob?.status || '').toLowerCase();
  const sourceLanguage = (bookLanguage || book.language || 'English').trim();
  const target = (expressTargetLanguage || targetLanguage || resolvedTranslationTargetLanguages[0] || '').trim();
  const currentExpressTarget = String(expressJob?.targetLanguage || '').trim();
  const currentExpressMatchesTarget = currentExpressTarget && target
    ? getLanguageFamilyKey(currentExpressTarget) === getLanguageFamilyKey(target)
    : Boolean(expressJob?.id);
  const currentExpressInProgress = Boolean(
    expressJob?.id
    && currentExpressMatchesTarget
    && !EXPRESS_TERMINAL_STATUSES.includes(currentExpressStatus)
  );
  if (bookDetailExperienceMode !== 'simple' || !resolvedOwnerUserId || currentExpressInProgress) {
    setSimpleMetadataLoading(false);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({});
    setSimpleSelectedTitleOptionId('__original__');
    setSimpleSelectedSeriesOptionId('__original__');
    return;
  }
  const targetProgress = canonicalizeTranslationProgressMap(book.translation_progress)?.[canonicalizeLanguageName(target)];
  const targetTotalChapters = targetProgress?.total_chapters || book.chapters?.length || 0;
  const targetTranslatedChaptersKey = `translated_chapters_${bookLanguage}_${target}`;
  const targetTranslatedChapters: TranslatedChapter[] = (book as any)[targetTranslatedChaptersKey] || [];
  const targetAlreadyCompleted = Boolean(
    targetProgress?.status === 'complete' ||
    (targetTotalChapters > 0 && (targetProgress?.translated_chapters || 0) >= targetTotalChapters) ||
    (
      book.chapters?.length &&
      book.chapters.every((chapter: any) => {
        const translatedChapter = targetTranslatedChapters.find(
          (candidate) => candidate?.chapter_guid === chapter?.chapter_guid
        );
        return hasTranslatedChapterContentValue(translatedChapter);
      })
    )
  );
  if (!sourceLanguage || !target || getLanguageFamilyKey(sourceLanguage) === getLanguageFamilyKey(target) || targetAlreadyCompleted) {
    setSimpleMetadataLoading(false);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({});
    setSimpleSelectedTitleOptionId('__original__');
    setSimpleSelectedSeriesOptionId('__original__');
    return;
  }
  const fields: Array<'title' | 'series'> = [];
  if ((book.title || '').trim()) fields.push('title');
  if ((book.series || '').trim()) fields.push('series');
  const newUploadAutofillStatus = String(book.metadataAutofill?.status || '').toLowerCase();
  const waitingForNewUploadAutofill = book.metadataAutofill?.mode === 'new_upload'
    && ['not_started', 'running'].includes(newUploadAutofillStatus);
  if (waitingForNewUploadAutofill) {
    setSimpleMetadataLoading(true);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({});
    setSimpleSelectedTitleOptionId('__original__');
    setSimpleSelectedSeriesOptionId('__original__');
    return;
  }
  if (fields.length === 0) {
    setSimpleMetadataLoading(false);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({});
    setSimpleSelectedTitleOptionId('__original__');
    setSimpleSelectedSeriesOptionId('__original__');
    return;
  }

  const fixtureOptions = isSandboxFixtureMode && sandboxFixturePayload?.simpleMetadataOptions;
  if (fixtureOptions && typeof fixtureOptions === 'object') {
    const titleOptions = Array.isArray((fixtureOptions as TranslationOptions).titleOptions)
      ? (fixtureOptions as TranslationOptions).titleOptions || []
      : [];
    const seriesOptions = Array.isArray((fixtureOptions as TranslationOptions).seriesOptions)
      ? (fixtureOptions as TranslationOptions).seriesOptions || []
      : [];
    setSimpleMetadataLoading(false);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({ titleOptions, seriesOptions });
    const recommendedTitle = titleOptions.find((option: TranslationOption) => option.recommended) || titleOptions[0];
    const recommendedSeries = seriesOptions.find((option: TranslationOption) => option.recommended) || seriesOptions[0];
    setSimpleSelectedTitleOptionId(recommendedTitle?.id || '__original__');
    setSimpleSelectedSeriesOptionId(recommendedSeries?.id || '__original__');
    return;
  }

  let cancelled = false;
  const loadOptions = async () => {
    setSimpleMetadataLoading(true);
    setSimpleMetadataError('');
    setSimpleMetadataOptions({});
    setSimpleSelectedTitleOptionId('__original__');
    setSimpleSelectedSeriesOptionId('__original__');
    try {
      const response = await bookDetailApiPost(`${API_BASE}/books/${bookId}/metadata-options`, {
        userId: resolvedOwnerUserId,
        sourceLanguage,
        targetLanguage: target,
        fields,
        forceRegenerate: false,
        intendedAudience,
        tone: target === 'French' ? 'AI Detect' : '',
      });
      if (cancelled) return;
      const titleOptions = Array.isArray(response.data?.titleOptions) ? response.data.titleOptions : [];
      const seriesOptions = Array.isArray(response.data?.seriesOptions) ? response.data.seriesOptions : [];
      setSimpleMetadataOptions({ titleOptions, seriesOptions });
      const recommendedTitle = titleOptions.find((option: TranslationOption) => option.recommended) || titleOptions[0];
      const recommendedSeries = seriesOptions.find((option: TranslationOption) => option.recommended) || seriesOptions[0];
      setSimpleSelectedTitleOptionId(recommendedTitle?.id || '__original__');
      setSimpleSelectedSeriesOptionId(recommendedSeries?.id || '__original__');
    } catch (error) {
      if (cancelled) return;
      console.error('Unable to load localized metadata options:', error);
      setSimpleMetadataOptions({});
      setSimpleSelectedTitleOptionId('__original__');
      setSimpleSelectedSeriesOptionId('__original__');
      setSimpleMetadataError('Localized title options are unavailable. Translate and export will unlock after title options are generated.');
    } finally {
      if (!cancelled) {
        setSimpleMetadataLoading(false);
      }
    }
  };
  void loadOptions();
  return () => {
    cancelled = true;
  };
}, [
  API_BASE,
  auth.currentUser?.uid,
  book.language,
  book.metadataAutofill?.mode,
  book.metadataAutofill?.status,
  book.metadataAutofill?.generatedAt,
  book.translation_progress,
  book.chapters,
  book.series,
  book.title,
  bookDetailExperienceMode,
  bookId,
  bookLanguage,
  expressJob?.id,
  expressJob?.status,
  expressJob?.targetLanguage,
  expressTargetLanguage,
  intendedAudience,
  isSandboxFixtureMode,
  resolvedTranslationTargetLanguages,
  resolvedOwnerUserId,
  sandboxFixturePayload,
  simpleMetadataRetryKey,
  targetLanguage,
]);

  const {
    translations: contextTranslations,
    refreshTranslations: debouncedFetchActiveTranslations,
    refreshTranslationsImmediate,
    addOptimisticTranslation,
    addClientIntent,
    clearClientIntent,
    removeOptimisticTranslation,
    updateOptimisticLockGuid,
    mergeTranslation,
    realtimeConnected: translationsRealtimeConnected,
    realtimeError: translationsRealtimeError,
  } = useTranslationStatus();

const pendingProofreadChecksRef = useRef<Record<string, number>>({});
useEffect(() => {
  const userId = auth.currentUser?.uid;
  if (!userId || !bookId) return;
  let disposed = false;
  listPendingProofreadSubmissions(userId, bookId).forEach(pending => {
    const last = pendingProofreadChecksRef.current[pending.requestId] || 0;
    if (Date.now() - last < 10000) return;
    pendingProofreadChecksRef.current[pending.requestId] = Date.now();
    const tempGuid = `Temp-ProofreadPass-${pending.requestId}`;
    void bookDetailApiGet(`${API_BASE}/proofreading-passes/submission`, {
      params: { userId, bookId, requestId: pending.requestId },
    }).then(response => {
      if (disposed || auth.currentUser?.uid !== userId) return;
      const accepted = response.data;
      clearPendingProofreadSubmission(pending);
      clearClientIntent(tempGuid);
      removeOptimisticTranslation(tempGuid);
      addOptimisticTranslation({
        chapterGuid: `proofreading-pass-${pending.requestId}`, chapterName: 'Whole-book proofreading',
        bookId, userId, sourceLang: bookLanguage, targetLang: pending.version,
        timestamp: new Date().toISOString(), type: 'ProofreadSubmission',
        status: accepted.processingStatus || 'pending', lockGuid: accepted.lockGuid, lockKey: accepted.lockKey,
        statusMessage: accepted.statusMessage || 'Preparing proofreading. You can leave this page.',
      });
    }).catch(() => { /* Keep the request identity until acceptance is resolved. */ });
  });
  return () => { disposed = true; };
}, [bookId, auth.currentUser?.uid, contextTranslations, bookLanguage, addOptimisticTranslation, clearClientIntent, removeOptimisticTranslation]);

useEffect(() => {
  if (bookDetailExperienceMode !== 'simple' || !bookId || !auth.currentUser?.uid) {
    if (isSandboxFixtureMode && bookDetailExperienceMode === 'simple' && sandboxFixturePayload && 'expressJob' in sandboxFixturePayload) {
      setExpressJob(sandboxFixturePayload.expressJob || null);
    }
    return;
  }
  const selectedTarget = (expressTargetLanguage || targetLanguage || resolvedTranslationTargetLanguages[0] || '').trim();
  if (!selectedTarget) {
    return;
  }
  if (isSandboxFixtureMode) {
    if ('expressJob' in (sandboxFixturePayload || {})) {
      setExpressJob(sandboxFixturePayload?.expressJob || null);
    }
    return;
  }
  const terminalStatuses = new Set(EXPRESS_TERMINAL_STATUSES);
  const currentStatus = String(expressJob?.status || '').toLowerCase();
  const currentTarget = String(expressJob?.targetLanguage || '').trim();
  const currentJobMatchesTarget = currentTarget
    ? getLanguageFamilyKey(currentTarget) === getLanguageFamilyKey(selectedTarget)
    : true;
  if (expressJob?.id && currentJobMatchesTarget && !terminalStatuses.has(currentStatus)) {
    return;
  }
  let cancelled = false;
  const hydrateActiveExpressJob = async () => {
    try {
      const response = await bookDetailApiGet(`${API_BASE}/books/${bookId}/express-translation/active`, {
        params: {
          user_id: auth.currentUser?.uid,
          target_language: selectedTarget,
          include_terminal: true,
        },
      });
      if (cancelled) return;
      const activeJob = response.data?.job;
      if (activeJob?.id) {
        setExpressJob(activeJob);
      } else if (expressJob?.id && !currentJobMatchesTarget) {
        setExpressJob(null);
      } else if (expressJob?.id && currentJobMatchesTarget && terminalStatuses.has(currentStatus)) {
        setExpressJob(null);
      }
    } catch (error) {
      if (!cancelled) {
        console.error('Error hydrating active Simple translation:', error);
      }
    }
  };
  void hydrateActiveExpressJob();
  return () => {
    cancelled = true;
  };
}, [
  API_BASE,
  auth.currentUser?.uid,
  bookDetailExperienceMode,
  bookId,
  expressJob?.id,
  expressJob?.status,
  expressJob?.targetLanguage,
  expressTargetLanguage,
  isSandboxFixtureMode,
  resolvedTranslationTargetLanguages,
  sandboxFixturePayload,
  targetLanguage,
]);

const relationshipAnalysisStatus = (book?.characterRelationshipsAnalysisStatus || '').toString().trim().toLowerCase();
const relationshipAnalysisJobActive = useMemo(() => {
  if (!bookId) {
    return false;
  }
  return (contextTranslations || []).some((entry: TranslationStatus) => {
    if (entry.bookId !== bookId) {
      return false;
    }
    const typeKey = (entry.type || '').toString().trim().toLowerCase();
    if (typeKey !== 'relationship analysis') {
      return false;
    }
    const statusKey = resolveTranslationStatusState(entry);
    return !TERMINAL_JOB_STATUSES.has(statusKey);
  });
}, [bookId, contextTranslations]);
const relationshipAnalysisInFlight =
  relationshipAnalysisJobActive || ['queued', 'running'].includes(relationshipAnalysisStatus);
const relationshipAnalysisLoading = relationshipAnalysisInFlight || isRelationshipAnalysisRunning;
const characterAnalysisAlreadyAvailable = relationshipAnalysisStatus === 'completed';
const effectiveRequireCharacterAnalysis = characterAnalysisAlreadyAvailable
  ? true
  : requireCharacterAnalysis;


  const activeBlockingJobs = useMemo(() => {
    return (contextTranslations || []).filter((job: TranslationStatus) => {
      const typeKey = (job?.type || '').toString().trim().toLowerCase();
      if (!BLOCKING_JOB_TYPES.has(typeKey)) {
        return false;
      }
      const statusKey = (job?.status || '').toString().trim().toLowerCase();
      if (!statusKey) {
        return true;
      }
      return !TERMINAL_JOB_STATUSES.has(statusKey);
    });
  }, [contextTranslations]);

  const activeEditLocks = useMemo(
    () => filterActiveChapterEditLocks(contextTranslations || [], selectedChapter?.chapter_guid),
    [contextTranslations, selectedChapter?.chapter_guid],
  );

  const { editLocked, reason: editLockReason } = useMemo(
    () => resolveChapterEditLockState(activeEditLocks),
    [activeEditLocks],
  );

  const isChapterUploadBlockedByJobs = activeBlockingJobs.length > 0;

  const chapterUploadDisabledReason = useMemo(() => {
    if (!isChapterUploadBlockedByJobs) {
      return undefined;
    }
    const labels = Array.from(
      new Set(
        activeBlockingJobs.map(job => {
          const typeKey = (job?.type || '').toString().trim().toLowerCase();
          return BLOCKING_JOB_TYPE_LABELS[typeKey] || 'jobs';
        }),
      ),
    );
    const jobSummary = labels.join(', ');
    return `Please wait for the pending ${jobSummary} to finish before uploading new chapters.`;
  }, [activeBlockingJobs, isChapterUploadBlockedByJobs]);

  const markChapterTranslatedLocally = useCallback(
    (chapterGuid: string, sourceLang?: string | null, targetLang?: string | null, chapterName?: string) => {
      if (!chapterGuid || !targetLang) {
        return;
      }
      const normalizedSource = sourceLang || bookLanguage;
      const key = `translated_chapters_${normalizedSource}_${targetLang}`;
      setBook(prev => {
        const existing: TranslatedChapter[] = (prev as any)[key] || [];
        if (existing.some(entry => entry.chapter_guid === chapterGuid)) {
          return prev;
        }
        const chapter = (prev.chapters || []).find(ch => ch.chapter_guid === chapterGuid);
        const stub: any = {
          chapter_guid: chapterGuid,
          chapter_name: chapter?.chapter_name || chapterName || 'Translated Chapter',
          translated_text: '',
          proofread_text: '',
          proofread_comments: '',
          translated_word_count: chapter?.word_count || 0,
          proofread_word_count: 0,
          source_language: normalizedSource,
          target_language: targetLang,
          api_model: selectedAIModel,
          translated_at: new Date().toISOString(),
          version: chapter?.version,
        };
        return {
          ...prev,
          [key]: [...existing, stub],
        };
      });
    },
    [bookLanguage, selectedAIModel, setBook],
  );

  const applyLocalTranslationDeletion = useCallback(
    (
      bookState: Book,
      sourceLang: string,
      targetLang: string,
      chapterGuid?: string | null,
    ): Book => {
      const normalizedSource = (sourceLang || '').trim();
      const normalizedTarget = (targetLang || '').trim();
      if (!normalizedSource || !normalizedTarget) {
        return bookState;
      }

      const key = `translated_chapters_${normalizedSource}_${normalizedTarget}`;
      const existing: TranslatedChapter[] = Array.isArray((bookState as any)[key])
        ? (bookState as any)[key]
        : [];
      const nextTranslatedChapters = chapterGuid
        ? existing.filter((entry) => entry.chapter_guid !== chapterGuid)
        : [];

      if (chapterGuid && nextTranslatedChapters.length === existing.length) {
        return bookState;
      }

      return {
        ...bookState,
        [key]: nextTranslatedChapters,
      };
    },
    [],
  );

  const getChapterVersionByGuid = useCallback(
    (chapterGuid?: string | null) => {
      if (!chapterGuid) {
        return undefined;
      }
      const match = (book.chapters || []).find(chapter => chapter.chapter_guid === chapterGuid);
      return match?.version;
    },
    [book.chapters],
  );

const [languageCovers, setLanguageCovers] = useState<{ [language: string]: string }>({});

const [isGeneratingCover, setIsGeneratingCover] = useState(false);
const [coverSelectionModalVisible, setCoverSelectionModalVisible] = useState(false);
const [generatedCoverImages, setGeneratedCoverImages] = useState<string[]>([]);
const [variationSeedImage, setVariationSeedImage] = useState<string | null>(null);
const [coverGenerationLanguage, setCoverGenerationLanguage] = useState('');
const [coverGenerationOptions, setCoverGenerationOptions] = useState<{
  mode: 'modify' | 'generate';
  replaceTitle?: boolean;
  replaceSeries?: boolean;
  addLanguageSticker?: boolean;
  authorMode?: 'include' | 'omit';
  // Partial generation metadata
  requestedCount?: number;
  generatedCount?: number;
  failureMessages?: string[];
  partialFailure?: boolean;
} | undefined>();
const [openCoverGenerationSignal, setOpenCoverGenerationSignal] = useState<{ language: string; ts: number } | null>(null);
const [openCoverHistorySignal, setOpenCoverHistorySignal] = useState<{ language: string; ts: number } | null>(null);
//--------------------------------------------------------------------------------------------------------
const translateModalLanguage = isTranslateModalVisible ? targetLanguage : undefined;
const { orderedModels: translationOrderedModels, loading: translationModelsLoading } = useOrderedModels(
  translateModalLanguage,
  isLangTesterUser,
  bookLanguage
);

const toggleSidebar = () => {
  setIsSidebarCollapsed(!isSidebarCollapsed);
};

  // Basic mobile detection for responsive layout tweaks
  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const [translateMetadata, setTranslateMetadata] = useState({
    title: false,
    series: false,
    blurb: false,
  });
  const [metadataAiInstructions, setMetadataAiInstructions] = useState<string>("");

  const [keywordSuggestionMetadata, setKeywordSuggestionMetadata] = useState({
    genre: false,
    tropes: false,
    keywords: false,
  });

  const [selectedTab, setSelectedTab] = useState<string>('Original');
const [metadataLanguage, setMetadataLanguage] = useState<string>('original');
const [metadataLanguageInitialized, setMetadataLanguageInitialized] = useState(false);
const [isMetadataTitleHovered, setIsMetadataTitleHovered] = useState(false);
  const [frenchDialogueLegacyEligibilityByPair, setFrenchDialogueLegacyEligibilityByPair] = useState<
    Record<string, FrenchDialogueLegacyEligibility>
  >({});
  const frenchDialogueLegacyEligibilityRef = useRef<Record<string, FrenchDialogueLegacyEligibility>>({});
  const frenchDialogueLegacyInflightRef = useRef<
    Partial<Record<string, Promise<FrenchDialogueLegacyEligibility | null>>>
  >({});
  const staleFrenchDialogueEligibilityRefreshRef = useRef<Set<string>>(new Set());



  useEffect(() => {
    frenchDialogueLegacyEligibilityRef.current = {
      ...frenchDialogueLegacyEligibilityRef.current,
      ...frenchDialogueLegacyEligibilityByPair,
    };
  }, [frenchDialogueLegacyEligibilityByPair]);

  useEffect(() => {
    setFrenchDialogueLegacyEligibilityByPair({});
    frenchDialogueLegacyEligibilityRef.current = {};
    frenchDialogueLegacyInflightRef.current = {};
    staleFrenchDialogueEligibilityRefreshRef.current = new Set();
  }, [bookId]);

  const invalidateFrenchDialogueEligibilityCacheForPair = useCallback((pair: string) => {
    const normalizedPair = String(pair || '').trim();
    if (!normalizedPair) {
      return;
    }

    setFrenchDialogueLegacyEligibilityByPair(prev => {
      if (!(normalizedPair in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[normalizedPair];
      return next;
    });

    const nextEligibilityCache = { ...frenchDialogueLegacyEligibilityRef.current };
    Object.keys(nextEligibilityCache).forEach((key) => {
      if (key === normalizedPair || key.startsWith(`${normalizedPair}::`)) {
        delete nextEligibilityCache[key];
      }
    });
    frenchDialogueLegacyEligibilityRef.current = nextEligibilityCache;

    const nextInflight = { ...frenchDialogueLegacyInflightRef.current };
    Object.keys(nextInflight).forEach((key) => {
      if (key === normalizedPair || key.startsWith(`${normalizedPair}::`)) {
        delete nextInflight[key];
      }
    });
    frenchDialogueLegacyInflightRef.current = nextInflight;
  }, []);

  useEffect(() => {
    if (!resolvedOwnerUserId) {
      setAccountAlwaysTranslateWords({});
      setAccountNeverTranslateWords({});
      return;
    }
    let cancelled = false;
    const fetchAccountRules = async () => {
      try {
        const [alwaysRes, neverRes] = await Promise.all([
          bookDetailApiGet(`${API_BASE}/get-account-always-translate`, {
            params: { userID: resolvedOwnerUserId },
          }),
          bookDetailApiGet(`${API_BASE}/get-account-never-translate`, {
            params: { userID: resolvedOwnerUserId },
          }),
        ]);
        if (cancelled) return;
        setAccountAlwaysTranslateWords(alwaysRes.data?.alwaysTranslateWords ?? {});
        setAccountNeverTranslateWords(neverRes.data?.neverTranslateWords ?? {});
      } catch (error) {
        if (cancelled) return;
        console.warn('Failed to load account translate rules', error);
        setAccountAlwaysTranslateWords({});
        setAccountNeverTranslateWords({});
      }
    };
    fetchAccountRules();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, resolvedOwnerUserId]);

  // Ask AI modal state
  const [askAIModalVisible, setAskAIModalVisible] = useState(false);
  const [askAIPayload, setAskAIPayload] = useState<AskAIInvocationPayload | null>(null);

  const handleOpenAskAI = useCallback((args: AskAIInvocationPayload) => {
    setAskAIPayload(args);
    setAskAIModalVisible(true);
  }, []);

  const hydrationBookRef = useRef(bookId);
  hydrationBookRef.current = bookId;
  const translatedContentHydrationCoordinatorRef = useRef(
    new TranslatedContentHydrationCoordinator(),
  );

  const translatedContentFetchKey = useCallback((
    chapterGuid: string,
    sourceLang: string,
    targetLang: string,
  ): string => {
    const chapterVersion = (book.chapters || []).find(
      chapter => chapter.chapter_guid === chapterGuid,
    )?.version;
    const translatedVersion = ((book as any)[`translated_chapters_${sourceLang}_${targetLang}`] || [])
      .find((chapter: any) => chapter.chapter_guid === chapterGuid)?.version;
    return `${bookId}:${chapterGuid}:${sourceLang}-${targetLang}:${String(chapterVersion ?? 'current')}:${String(translatedVersion ?? 'missing')}`;
  }, [book, bookId]);

  const hydrationKeyRef = useRef(translatedContentFetchKey);
  hydrationKeyRef.current = translatedContentFetchKey;

  const invalidateTranslatedContentRetry = useCallback((
    chapterGuid: string,
    sourceLang: string,
    targetLang: string,
  ): void => {
    translatedContentHydrationCoordinatorRef.current.invalidate(
      translatedContentFetchKey(chapterGuid, sourceLang, targetLang),
    );
  }, [translatedContentFetchKey]);

  const isTranslatedContentWorkActive = useCallback((
    chapterGuid: string,
    sourceLang: string,
    targetLang: string,
  ): boolean => isChapterTranslationActive(contextTranslations || [], {
    bookId: String(bookId || ''),
    chapterGuid,
    sourceLang,
    targetLang,
  }), [bookId, contextTranslations]);

  const hydrateTranslatedChapter = useCallback(async (
    chapterGuid: string,
    sourceLang: string,
    targetLang: string,
    options: {
      invalidateNegativeCache?: boolean;
      reason?: TranslatedContentHydrationReason;
    } = {},
  ): Promise<any | null> => {
    const fetchKey = translatedContentFetchKey(chapterGuid, sourceLang, targetLang);
    const reason = options.reason || 'background_hydration';
    const expectReady = reason === 'job_completion';
    const inventory = (book as any)[`translated_chapters_${sourceLang}_${targetLang}`];
    const knownMissing = book.translation_inventory_complete === true
      && (inventory === undefined || (Array.isArray(inventory) && !inventory.some((chapter: any) => chapter.chapter_guid === chapterGuid)))
      && !expectReady && reason !== 'manual_refresh';
    return translatedContentHydrationCoordinatorRef.current.request({
      key: fetchKey,
      isActive: knownMissing || (!expectReady && isTranslatedContentWorkActive(chapterGuid, sourceLang, targetLang)),
      expectReady,
      isCurrent: () => hydrationBookRef.current === bookId
        && hydrationKeyRef.current(chapterGuid, sourceLang, targetLang) === fetchKey,
      invalidateNegativeCache: options.invalidateNegativeCache,
      isMissingError: error => (
        isBookDetailApiError(error) && error.response?.status === 404
        && error.response?.data?.detail === 'Translated chapter not found'
      ),
      load: async () => {
        const availability = await fetchTranslatedChapterAvailability({
          bookId: String(bookId), chapterGuid, sourceLang, targetLang, reason,
        });
        if (!availability.ready) return null;
        if (hydrationBookRef.current !== bookId
          || hydrationKeyRef.current(chapterGuid, sourceLang, targetLang) !== fetchKey) return null;
        const data = availability.chapter;
        const bookKey = `translated_chapters_${sourceLang}_${targetLang}`;
        setBook((previous: any) => {
          const copy: any = { ...previous };
          const list: any[] = [...(copy[bookKey] || [])];
          const index = list.findIndex((chapter: any) => chapter.chapter_guid === chapterGuid);
          if (index >= 0) list[index] = { ...(list[index] || {}), ...data };
          else list.push(data);
          copy[bookKey] = list;
          return copy;
        });
        return data;
      },
    });
  }, [book, bookId, isTranslatedContentWorkActive, translatedContentFetchKey]);

  const tourFallbackRef = useRef<HTMLElement | null>(null);
  const userId = auth.currentUser?.uid ?? sharedUserId ?? null;
  const {
    flags: tourFlags,
    initialized: tourFlagsInitialized,
    markSeen: markTourSeen,
  } = useTourFlags(userId, API_BASE);
  const metadataAutofillReviewTour = useGuidedTour({
    userId,
    apiBase: API_BASE,
    flag: 'metadataAutofillReviewTourSeen',
    enabled: simpleMetadataAutofillReviewVisible,
    autoStart: true,
    ready: simpleMetadataAutofillReviewVisible,
  });
  const [activeTour, setActiveTour] = useState<BookDetailTourId | null>(null);
  const [pendingTour, setPendingTour] = useState<PendingTour | null>(null);
  const [mainTourStep, setMainTourStep] = useState<number>(0);
  const [simpleMainTourStep, setSimpleMainTourStep] = useState<number>(0);
  const [chapterTourStep, setChapterTourStep] = useState<number>(0);
  const [metadataLanguageTourStep, setMetadataLanguageTourStep] = useState<number>(0);
  const [glossaryScanModalReady, setGlossaryScanModalReady] = useState(false);
  const autoTourTriggeredRef = useRef(false);
  const metadataLanguageTourTriggeredRef = useRef(false);
  const [dashboardTourOpen, setDashboardTourOpen] = useState(false);
  const [dashboardTourStep, setDashboardTourStep] = useState(0);
  const dashboardTourTriggeredRef = useRef(false);

  const metadataSeen = !!tourFlags.bookDetailMetadataTourSeen;
  const overviewSeen = !!tourFlags.bookDetailOverviewTourSeen;
  const chapterSeen = !!tourFlags.bookDetailChapterTourSeen;
  const dashboardSeen = !!tourFlags.bookDetailDashboardTourSeen;
  const metadataLanguageSeen = !!tourFlags.bookDetailLanguageMetadataTourSeen;
  const simpleMainTourSeen = !!tourFlags.bookDetailSimpleModeTourSeen;
  const mainTourSeen = overviewSeen && metadataSeen;
  const isMetadataLanguageTranslated = metadataLanguage !== 'original';

  const getElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null, fallbackToBody: boolean = true): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && fallbackToBody && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined'
            ? document.createElement('div')
            : ({} as HTMLElement);
      }
      return tourFallbackRef.current;
    },
    []
  );

  const getPreferredTarget = useCallback(
    (ids: string[], fallbackToBody: boolean = true): HTMLElement => {
      for (const id of ids) {
        const el = getElement(id);
        if (el) return el;
      }
      return resolveTourTarget(null, fallbackToBody);
    },
    [getElement, resolveTourTarget]
  );

  const getGlossaryScanModalTarget = useCallback((): HTMLElement => {
    if (typeof document === 'undefined') return resolveTourTarget(null, true);
    const modalWrap = document.querySelector('.glossary-scan-modal') as HTMLElement | null;
    if (modalWrap && glossaryScanModalReady) {
      const modal = modalWrap.querySelector('.ant-modal') as HTMLElement | null;
      return modal ?? modalWrap;
    }
    return getPreferredTarget(['metadata-glossary-scan', 'metadata-glossary-section'], false);
  }, [getPreferredTarget, glossaryScanModalReady, resolveTourTarget]);

  const ensureGlossaryScanModalOpen = useCallback(() => {
    if (typeof document === 'undefined') return;
    const modalWrap = document.querySelector('.glossary-scan-modal');
    if (modalWrap) return;
    const button = getElement('metadata-glossary-scan');
    if (button) {
      button.click();
    }
  }, [getElement]);

  const closeGlossaryScanModal = useCallback(() => {
    if (typeof document === 'undefined') return;
    const modalWrap = document.querySelector('.glossary-scan-modal') as HTMLElement | null;
    if (!modalWrap) return;
    const closeButton = modalWrap.querySelector('.ant-modal-close') as HTMLButtonElement | null;
    if (closeButton) {
      closeButton.click();
      return;
    }
    const cancelButton = Array.from(modalWrap.querySelectorAll('button')).find(
      (button) => button.textContent?.trim() === 'Cancel'
    ) as HTMLButtonElement | undefined;
    if (cancelButton) {
      cancelButton.click();
    }
  }, []);

  const isGlossaryScanModalReady = useCallback(() => {
    if (typeof document === 'undefined') return false;
    const modalWrap = document.querySelector('.glossary-scan-modal');
    if (!modalWrap) return false;
    return Boolean(modalWrap.querySelector('[data-tour-ready="true"]'));
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );
  const metadataAutofillReviewTourSteps = useMemo<TourProps['steps']>(() => ([
    {
      title: 'Review the saved suggestions',
      description: renderTeacherPanel(
        <>
          This summary shows how many reviewed values are ready. Nothing changes on the book until you apply them.
        </>
      ),
      target: () => getPreferredTarget(['metadata-autofill-review-summary'], false),
      placement: 'bottom',
    },
    {
      title: 'Apply one group at a time',
      description: renderTeacherPanel(
        <>
          Each row is a metadata group, such as genres, tropes, keywords, or audience labels. Use the row button when you only want that group.
        </>
      ),
      target: () => getPreferredTarget(['metadata-autofill-review-first-row', 'metadata-autofill-review-list'], false),
      placement: 'bottom',
    },
    {
      title: 'Edit the values',
      description: renderTeacherPanel(
        <>
          Remove suggestions you do not want, or type your own genre, trope, keyword, or audience value and press Enter.
        </>
      ),
      target: () => getPreferredTarget(['metadata-autofill-review-first-values', 'metadata-autofill-review-first-row'], false),
      placement: 'top',
    },
    {
      title: 'Apply the reviewed metadata',
      description: renderTeacherPanel(
        <>
          This footer action saves all reviewed values across every group in the modal.
        </>
      ),
      target: () => getPreferredTarget(['metadata-autofill-review-apply-selected', 'metadata-autofill-review-summary'], false),
      placement: 'top',
    },
  ]), [getPreferredTarget, renderTeacherPanel]);

  const isTourSeen = useCallback(
    (id: BookDetailTourId) => {
      if (id === 'main') return mainTourSeen;
      if (id === 'simpleMain') return simpleMainTourSeen;
      if (id === 'chapter') return chapterSeen;
      if (id === 'metadataLanguage') return metadataLanguageSeen;
      return false;
    },
    [chapterSeen, mainTourSeen, metadataLanguageSeen, simpleMainTourSeen]
  );

  const applyStartTour = useCallback(
    (id: BookDetailTourId, step: number) => {
      if (id === 'main') {
        setMainTourStep(step);
      } else {
        setMainTourStep(0);
      }

      if (id === 'simpleMain') {
        setSimpleMainTourStep(step);
      } else {
        setSimpleMainTourStep(0);
      }

      if (id === 'chapter') {
        setChapterTourStep(step);
      } else {
        setChapterTourStep(0);
      }

      if (id === 'metadataLanguage') {
        setMetadataLanguageTourStep(step);
      } else {
        setMetadataLanguageTourStep(0);
      }

      setActiveTour(id);
    },
    []
  );

  const startTour = useCallback(
    (id: BookDetailTourId, options?: { step?: number; allowSeen?: boolean }) => {
      if (tutorialUiSuppressionRequested) {
        return;
      }
      const step = options?.step ?? 0;
      const allowSeen = options?.allowSeen ?? false;
      if (!allowSeen && isTourSeen(id)) {
        return;
      }
      if (activeTour) {
        setPendingTour({ id, step, allowSeen });
        return;
      }
      applyStartTour(id, step);
    },
    [activeTour, applyStartTour, isTourSeen, tutorialUiSuppressionRequested]
  );

  useEffect(() => {
    if (!activeTour && pendingTour) {
      const { id, step, allowSeen } = pendingTour;
      setPendingTour(null);
      if (allowSeen || !isTourSeen(id)) {
        applyStartTour(id, step);
      }
    }
  }, [activeTour, pendingTour, applyStartTour, isTourSeen]);

  const metadataTourSteps = useMemo<TourProps['steps']>(() => [
    {
      title: 'Start with the basics',
      description: renderTeacherPanel(
        <>
          Fix upload typos for Title, Author, and Series here. Gray, locked tags show series-level values for context—those can only be edited on the series page, but you can add per-book details right below.
          <AppTourVideoLink href={tutorialVideoLinks.originalDashboardMetadata}>
            Watch the metadata dashboard tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () => getPreferredTarget(['metadata-identity'], false),
      placement: 'bottom',
    },
    {
      title: 'Edit series defaults',
      description: renderTeacherPanel(
        <>
          Need to change the series itself? Click the notebook button to open the series manager. Updates there feed the locked values you see in these forms.
        </>
      ),
      target: () => getPreferredTarget(['metadata-edit-series'], false),
      placement: 'left',
    },
    {
      title: 'Switch languages',
      description: renderTeacherPanel(
        <>
          Jump between original and translated metadata, or add a new language when you’re ready to localize.
        </>
      ),
      target: () => getPreferredTarget(['metadata-language'], false),
      placement: 'bottomLeft',
    },
    {
      title: 'Use AI metadata help',
      description: renderTeacherPanel(
        <>
          Use AI fill to draft or review metadata suggestions, then translate metadata when you’re ready to localize.
        </>
      ),
      target: () => getPreferredTarget(['metadata-ai-fill', 'metadata-ai', 'metadata-actions'], false),
      placement: 'left',
    },
    {
      title: 'Copy metadata from another book',
      description: renderTeacherPanel(
        <>
          Use this button to pull genres, audiences, or other fields from a different manuscript instead of retyping everything.
        </>
      ),
      target: () => getPreferredTarget(['metadata-copy-metadata'], false),
      placement: 'left',
    },
    {
      title: 'Add or paste lists',
      description: renderTeacherPanel(
        <>
          Genres, Tropes, and Keywords all accept comma-separated lists, and any series defaults stay locked with a little padlock so you always know what’s inherited.
        </>
      ),
      target: () => getPreferredTarget(['metadata-lists'], false),
      placement: 'bottom',
    },
    {
      title: 'Polish your cover',
      description: renderTeacherPanel(
        <>
          Upload your cover art or generate a translated cover. Everything is organized by language, so you always know which cover belongs to which edition.
        </>
      ),
      target: () => getPreferredTarget(['metadata-cover'], false),
      placement: 'right',
    },
    {
      title: 'Review cover history',
      description: renderTeacherPanel(
        <>
          Open Cover History to compare older versions or switch the active cover for this language.
        </>
      ),
      target: () => getPreferredTarget(['metadata-cover-history', 'metadata-cover'], false),
      placement: 'right',
    },
    {
      title: 'Book-level actions',
      description: renderTeacherPanel(
        <>
          Use the Translate menu for either book or metadata translation. In dashboard view, this area also includes proofread and export actions.
        </>
      ),
      target: () => getPreferredTarget(['metadata-actions'], false),
      placement: 'top',
    },
    {
      title: 'Upload revised chapters',
      description: renderTeacherPanel(
        <>
          Need to append or replace manuscript content? The Dashboard Book tools include chapter
          upload when no blocking jobs are running. EPUB projects can overwrite existing chapters,
          while Word uploads can both overwrite and append brand-new ones.
        </>
      ),
      target: () => getPreferredTarget(['metadata-upload-chapters', 'metadata-actions'], false),
      placement: 'top',
    },
    {
      title: 'Audiobook workflow',
      description: renderTeacherPanel(
        <>
          Ready to create audio from your translations? Click here to start your audiobook workflow!
        </>
      ),
      target: () => getPreferredTarget(['metadata-audiobook-tab'], false),
      placement: 'bottom',
    },
  ], [getPreferredTarget, renderTeacherPanel]);

  const metadataLanguageTour = useMemo(() => {
    const steps: NonNullable<TourProps['steps']> = [];
    const pushStep = (step: NonNullable<TourProps['steps']>[number]) => {
      steps.push(step);
      return steps.length - 1;
    };

    pushStep({
      title: 'Language metadata',
      description: renderTeacherPanel(
        <>
          You're looking at metadata for a translated language. Use this selector to switch languages or add another one.
        </>
      ),
      target: () => getPreferredTarget(['metadata-language'], false),
      placement: 'bottomLeft',
    });

    pushStep({
      title: 'Translated metadata',
      description: renderTeacherPanel(
        <>
          Review the translated title, series name, and blurb for this language. These improve the
          storefront; translated chapters are the requirement for edition export.
          <AppTourVideoLink href={tutorialVideoLinks.translatedDashboard}>
            Watch the translated dashboard tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () => getPreferredTarget(['metadata-translation-section'], false),
      placement: 'right',
    });

    pushStep({
      title: 'Edit, copy, export',
      description: renderTeacherPanel(
        <>
          Use these controls to edit translations, copy them to your clipboard, or export the blurb.
        </>
      ),
      target: () =>
        getPreferredTarget(['metadata-translation-actions', 'metadata-translation-section'], false),
      placement: 'bottom',
    });

    pushStep({
      title: 'Glossary rules',
      description: renderTeacherPanel(
        <>
          Always/Never translate rules live here and steer how the AI handles key terms.
          <AppTourVideoLink href={tutorialVideoLinks.glossary}>
            Watch the glossary tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () => getPreferredTarget(['metadata-glossary-section'], false),
      placement: 'left',
    });

    pushStep({
      title: 'AI Glossary Scan',
      description: renderTeacherPanel(
        <>
          Run a scan to suggest glossary terms from your chapters. It is recommended before large
          translation runs, and the modal shows credit estimates before launch.
          <AppTourVideoLink href={tutorialVideoLinks.glossary}>
            Watch the glossary tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () =>
        getPreferredTarget(['metadata-glossary-scan', 'metadata-glossary-section'], false),
      placement: 'left',
    });

    const glossaryScanModalStepIndex = pushStep({
      title: 'Glossary scan modal',
      description: renderTeacherPanel(
        <>
          This modal estimates credits, explains what will be scanned, and lets you launch the run. You can review suggestions before applying them.
        </>
      ),
      target: () => getGlossaryScanModalTarget(),
      placement: 'top',
    });

    pushStep({
      title: 'Keyword translations',
      description: renderTeacherPanel(
        <>
          Review translated keywords and their source meanings here. Keywords are storefront polish,
          not an export blocker.
        </>
      ),
      target: () => getPreferredTarget(['metadata-keywords-section'], false),
      placement: 'left',
    });

    pushStep({
      title: 'Translate menu',
      description: renderTeacherPanel(
        <>
          Open this menu and choose Translate Metadata to localize the title, series name, and blurb. You can also start Translate Book from here.
        </>
      ),
      target: () => getPreferredTarget(['metadata-translate'], false),
      placement: 'left',
    });

    pushStep({
      title: 'Generate keywords',
      description: renderTeacherPanel(
        <>
          Ask AI to suggest keywords in the target language based on your genres and tropes.
        </>
      ),
      target: () => getPreferredTarget(['metadata-generate-keywords'], false),
      placement: 'left',
    });

    pushStep({
      title: 'Copy metadata',
      description: renderTeacherPanel(
        <>
          Copy genres, tropes, keywords, and more from another book to jump-start this language.
        </>
      ),
      target: () => getPreferredTarget(['metadata-copy-metadata'], false),
      placement: 'left',
    });

    pushStep({
      title: 'Delete translated/proofread chapters',
      description: renderTeacherPanel(
        <>
          Need a reset? Use this menu to clear translated or proofread chapters for this language.
        </>
      ),
      target: () => getPreferredTarget(['metadata-delete-chapters'], false),
      placement: 'left',
    });

    return {
      steps,
      glossaryScanModalStepIndex,
    };
  }, [getGlossaryScanModalTarget, getPreferredTarget, renderTeacherPanel]);

  const {
    steps: metadataLanguageTourSteps,
    glossaryScanModalStepIndex,
  } = metadataLanguageTour;

  const overviewTourSteps = useMemo<TourProps['steps']>(() => [
    {
      title: 'Book overview',
      description: renderTeacherPanel(
        <>
          The header shows your current title, opens metadata editing, and lets you navigate the series without leaving.
        </>
      ),
      target: () => getPreferredTarget(['book-header'], false),
      placement: 'bottom',
    },
    {
      title: 'Chapter navigator',
      description: renderTeacherPanel(
        <>
          Use the sidebar to jump between chapters, rename them, or keep the list tucked away on smaller screens.
        </>
      ),
      target: () => getPreferredTarget(['chapter-sidebar'], false),
      placement: 'right',
    },
    {
      title: 'Reorder chapters',
      description: renderTeacherPanel(
        <>
          {isEpubUpload ? (
            <>
              Drag handles live next to each chapter, but EPUB uploads keep the imported order so reordering stays disabled.
            </>
          ) : (
            <>
              Grab the handle to drag chapters into a new order and keep your manuscript organized. Only Word uploads can be rearranged here.
            </>
          )}
        </>
      ),
      target: () => getPreferredTarget(['chapter-reorder-handle', 'chapter-sidebar'], false),
      placement: 'right',
    },
    {
      title: 'Processing status',
      description: renderTeacherPanel(
        <>
          Monitor translations, proofreads, and audiobooks from here. Open the panel to manage or clear individual jobs.
        </>
      ),
      target: () => getPreferredTarget(['processing-status', 'processing-status-trigger'], false),
      placement: 'left',
    },
    {
      title: 'Workspace',
      description: renderTeacherPanel(
        <>
          This main area swaps between metadata editing and chapter content, depending on what you’re working on.
        </>
      ),
      target: () => getPreferredTarget(['workspace-container', 'book-main-card'], false),
      placement: 'left',
    },
  ], [getPreferredTarget, isEpubUpload, renderTeacherPanel]);

  const chapterTourSteps = useMemo<TourProps['steps']>(() => {
    const hasEditorControls = Boolean(getElement('editor-save') || getElement('editor-revert'));
    return [
      {
      title: 'Language controls',
      description: renderTeacherPanel(
        <>
          Click this drop down to switch between your original content and any translations.
        </>
      ),
      target: () => getPreferredTarget(['chapter-language-select', 'chapter-header'], false),
      placement: 'bottom',
      },
      {
      title: 'Chapter actions',
      description: renderTeacherPanel(
        <>
          Translate or proofread a single chapter, run book-wide actions, export files, or jump back to metadata.
          <AppTourVideoLink href={tutorialVideoLinks.chapterLevelControls}>
            Watch the chapter controls tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () => getPreferredTarget(['chapter-actions'], false),
      placement: 'left',
      },
      hasEditorControls
        ? {
            title: 'Save or revert edits',
            description: renderTeacherPanel(
              <>
                Use these controls to save manual edits or revert back to the last saved version.
                <AppTourVideoLink href={tutorialVideoLinks.chapterEditing}>
                  Watch the chapter editing tutorial
                </AppTourVideoLink>
              </>
            ),
            target: () => getPreferredTarget(['editor-save', 'editor-revert'], false),
            placement: 'left',
          }
        : null,
      {
      title: 'Tabs and versions',
      description: renderTeacherPanel(
        <>
          Choose a tab to move between original, translated, and proofread variants. Translated
          and proofread tabs appear only after that work exists.
        </>
      ),
      target: () => {
        const tabs = getElement('chapter-tabs');
        const nav = tabs?.querySelector('.ant-tabs-nav') as HTMLElement | null;
        const list = nav?.querySelector('.ant-tabs-nav-list') as HTMLElement | null;
        return list ?? nav ?? getPreferredTarget(['chapter-tabs'], false);
      },
      placement: 'bottom',
      },
      {
      title: 'Chapter content',
      description: renderTeacherPanel(
        <>
          Read the chapter text here with its formatting intact and ready for review.
        </>
      ),
      target: () => {
        if (typeof document !== 'undefined') {
          const candidates = Array.from(
            document.querySelectorAll(
              '.ant-tabs-tabpane-active [data-tour-id="chapter-content"]'
            )
          ).filter((el): el is HTMLElement => el instanceof HTMLElement);
          if (candidates.length > 0) {
            const sized = candidates
              .map((el) => {
                const rect = el.getBoundingClientRect();
                return { el, area: rect.width * rect.height, rect };
              })
              .filter(({ rect }) => rect.width > 0 && rect.height > 0);
            if (sized.length > 0) {
              sized.sort((a, b) => a.area - b.area);
              return sized[0].el;
            }
          }
          const activeEditor = document.querySelector(
            '.ant-tabs-tabpane-active [data-tour-id="editor-body"]'
          ) as HTMLElement | null;
          if (activeEditor) return activeEditor;
        }
        return getPreferredTarget(['chapter-content', 'editor-body'], false);
      },
      placement: 'left',
      },
    ].filter(Boolean) as NonNullable<TourProps['steps']>;
  }, [getElement, getPreferredTarget, renderTeacherPanel]);

  const dashboardTourSteps = useMemo<TourProps['steps']>(() => {
    const isOriginalDashboard = selectedTab === 'Original';
    return [
      {
        title: 'Dashboard overview',
        description: renderTeacherPanel(
          <>
            This dashboard summarizes the current language edition and highlights the most important next actions.
          </>
        ),
        target: () => getPreferredTarget(['dashboard-container'], false),
        placement: 'bottom',
      },
      {
        title: 'Progress at a glance',
        description: renderTeacherPanel(
          <>
            Track translation progress and jump to a language when you want to review or continue work.
          </>
        ),
        target: () => getPreferredTarget(['dashboard-progress'], false),
        placement: 'bottom',
      },
      {
        title: 'Quick actions',
        description: renderTeacherPanel(
          <>
            Expand Quick Links when you need shortcuts for translation, proofreading, uploads, exports, or other less common tools.
          </>
        ),
        target: () => getPreferredTarget(['dashboard-quick-links'], false),
        placement: 'right',
      },
      {
        title: 'Switch language editions',
        description: renderTeacherPanel(
          <>
            Change the language here to see that edition’s dashboard, progress, and next steps.
          </>
        ),
        target: () => getPreferredTarget(['metadata-language'], false),
        placement: 'bottomLeft',
      },
      {
        title: isOriginalDashboard ? 'Original vs translations' : 'Language-specific dashboard',
        description: renderTeacherPanel(
          <>
            {isOriginalDashboard
              ? 'The main dashboard includes exports and overall book actions. Language specific dashboards focus on that specific language edition.'
              : 'This dashboard is scoped to the selected language. Exports and actions apply to this edition, not the original.'}
            <AppTourVideoLink href={isOriginalDashboard ? tutorialVideoLinks.originalDashboardMetadata : tutorialVideoLinks.translatedDashboard}>
              {isOriginalDashboard ? 'Watch the original dashboard tutorial' : 'Watch the translated dashboard tutorial'}
            </AppTourVideoLink>
          </>
        ),
        target: () =>
          getPreferredTarget(
            isOriginalDashboard ? ['dashboard-exports', 'dashboard-next-steps'] : ['dashboard-next-steps', 'dashboard-quick-links'],
            false
          ),
        placement: 'right',
      },
      {
        title: 'Next steps for this language',
        description: renderTeacherPanel(
          <>
            Follow these suggestions to complete the current language edition—steps update as you make progress.
          </>
        ),
        target: () => getPreferredTarget(['dashboard-next-steps'], false),
        placement: 'right',
      },
    ];
  }, [getPreferredTarget, renderTeacherPanel, selectedTab]);

  const simpleMainTourSteps = useMemo<TourProps['steps']>(() => [
    {
      title: 'Guided Workflow',
      description: renderTeacherPanel(
        <>
          Guided Workflow keeps the experience focused on translating a book and exporting a finished edition from one guided screen.
          <AppTourVideoLink href={tutorialVideoLinks.simpleWorkflow}>
            Watch the simple workflow tutorial
          </AppTourVideoLink>
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-panel', 'workspace-container'], false),
      placement: 'left',
    },
    {
      title: 'Choose the destination',
      description: renderTeacherPanel(
        <>
          Pick the target language and output format first. Completed languages are marked so you do not start duplicate work by mistake.
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-basics'], false),
      placement: 'bottom',
    },
    {
      title: 'Review localized metadata',
      description: renderTeacherPanel(
        <>
          When options are available, choose the translated title and series name that should appear in the exported edition.
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-metadata', 'simple-translation-basics'], false),
      placement: 'bottom',
    },
    {
      title: 'Select add-ons',
      description: renderTeacherPanel(
        <>
          Add-ons like AI Glossary Scan, blurb translation, and email delivery are optional and shown separately from the required translation work.
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-addons'], false),
      placement: 'left',
    },
    {
      title: 'Check readiness and cost',
      description: renderTeacherPanel(
        <>
          The footer summarizes blockers, total credits, available balance, and the cost breakdown before you start.
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-footer'], false),
      placement: 'top',
    },
    {
      title: 'Start translation and export',
      description: renderTeacherPanel(
        <>
          Once the requirements are ready, this action queues the translation and creates the export for the selected language.
        </>
      ),
      target: () => getPreferredTarget(['simple-translation-primary-action', 'simple-translation-footer'], false),
      placement: 'top',
    },
  ], [getPreferredTarget, renderTeacherPanel]);

  const metadataStartIndex = useMemo(() => (overviewTourSteps ?? []).length, [overviewTourSteps?.length]);
  const mainTourSteps = useMemo<TourProps['steps']>(() => [...(overviewTourSteps ?? []), ...(metadataTourSteps ?? [])], [overviewTourSteps, metadataTourSteps]);

  const markTour = useCallback(
    (id: BookDetailTourId) => {
      if (id === 'main') {
        void markTourSeen('bookDetailOverviewTourSeen');
        void markTourSeen('bookDetailMetadataTourSeen');
      } else if (id === 'simpleMain') {
        void markTourSeen('bookDetailSimpleModeTourSeen');
      } else if (id === 'chapter') {
        void markTourSeen('bookDetailChapterTourSeen');
      } else if (id === 'metadataLanguage') {
        void markTourSeen('bookDetailLanguageMetadataTourSeen');
      }
    },
    [markTourSeen]
  );

  const handleDashboardTourClose = useCallback(() => {
    setDashboardTourOpen(false);
    setDashboardTourStep(0);
    void markTourSeen('bookDetailDashboardTourSeen');
  }, [markTourSeen]);

  const closeTour = useCallback(
    (id: BookDetailTourId, _options?: { triggeredByFinish?: boolean }) => {
      setActiveTour(prev => (prev === id ? null : prev));
      if (id === 'main') {
        setMainTourStep(0);
      } else if (id === 'simpleMain') {
        setSimpleMainTourStep(0);
      } else if (id === 'chapter') {
        setChapterTourStep(0);
      } else if (id === 'metadataLanguage') {
        setMetadataLanguageTourStep(0);
      }
      markTour(id);
    },
    [markTour]
  );

  const mainTourOpen = !tutorialUiSuppressionRequested && activeTour === 'main';
  const simpleMainTourOpen = !tutorialUiSuppressionRequested && activeTour === 'simpleMain';
  const chapterTourOpen = !tutorialUiSuppressionRequested && activeTour === 'chapter';
  const metadataLanguageTourOpen = !tutorialUiSuppressionRequested && activeTour === 'metadataLanguage';

  const handleMetadataTabChange = useCallback((tabKey: string) => {
    setActiveMetadataTab(tabKey);
  }, []);

  const handleMetadataLanguageChange = useCallback((language: string) => {
    setMetadataLanguage(language || 'original');
    setMetadataLanguageInitialized(true);
  }, []);

  const isCharactersTabActive = useCallback(() => {
    if (typeof document === 'undefined') return false;
    return Boolean(document.querySelector('[data-tour-id="characters-controls"]'));
  }, []);

  const triggerAudiobookTour = useCallback(() => {
    setAudiobookTourSignal(prev => prev + 1);
  }, []);

  const triggerCharactersTour = useCallback(() => {
    setCharactersTourSignal(prev => prev + 1);
  }, []);

  const handleStartMainTour = useCallback(() => {
    if (tutorialUiSuppressionRequested) {
      return;
    }
    if (process.env.NODE_ENV !== 'production') {
      console.info('[BookDetailPage] take tour', {
        bookDetailExperienceMode,
        isEditing,
        activeMetadataTab,
        hasSelectedChapter: Boolean(selectedChapter),
        charactersTabActive: isEditing ? isCharactersTabActive() : false,
      });
    }
    if (bookDetailExperienceMode === 'simple') {
      startTour('simpleMain', { allowSeen: true });
      return;
    }
    if (isEditing && isCharactersTabActive()) {
      triggerCharactersTour();
      return;
    }
    if (isEditing && activeMetadataTab === 'audiobook') {
      triggerAudiobookTour();
      return;
    }
    if (isEditing && activeMetadataTab === 'characters') {
      triggerCharactersTour();
      return;
    }
    if (isEditing && activeMetadataTab === 'dashboard') {
      setDashboardTourStep(0);
      setDashboardTourOpen(true);
      return;
    }
    if (isEditing && activeMetadataTab === 'metadata' && isMetadataLanguageTranslated) {
      startTour('metadataLanguage', { allowSeen: true });
      return;
    }
    if (!isEditing && selectedChapter) {
      startTour('chapter', { allowSeen: true });
      return;
    }
    startTour('main', { allowSeen: true });
  }, [
    activeMetadataTab,
    bookDetailExperienceMode,
    isEditing,
    isCharactersTabActive,
    isMetadataLanguageTranslated,
    selectedChapter,
    startTour,
    tutorialUiSuppressionRequested,
    triggerAudiobookTour,
    triggerCharactersTour,
  ]);

  const handleMainTourChange = useCallback((nextStep: number) => {
    if (nextStep >= metadataStartIndex && !isEditing) {
      setIsEditing(true);
    }
    setMainTourStep(nextStep);
  }, [metadataStartIndex, isEditing, setIsEditing]);

  const handleMetadataLanguageTourChange = useCallback(
    (nextStep: number) => {
      if (metadataLanguageTourStep === glossaryScanModalStepIndex && nextStep !== glossaryScanModalStepIndex) {
        closeGlossaryScanModal();
      }
      if (nextStep === glossaryScanModalStepIndex) {
        ensureGlossaryScanModalOpen();
      }
      setMetadataLanguageTourStep(nextStep);
    },
    [
      closeGlossaryScanModal,
      ensureGlossaryScanModalOpen,
      glossaryScanModalStepIndex,
      metadataLanguageTourStep,
    ]
  );

  useEffect(() => {
    if (!metadataLanguageTourOpen) return;
    if (metadataLanguageTourStep === glossaryScanModalStepIndex) {
      ensureGlossaryScanModalOpen();
    }
  }, [ensureGlossaryScanModalOpen, glossaryScanModalStepIndex, metadataLanguageTourOpen, metadataLanguageTourStep]);

  useEffect(() => {
    if (!metadataLanguageTourOpen || metadataLanguageTourStep !== glossaryScanModalStepIndex) {
      setGlossaryScanModalReady(false);
      return;
    }
    let cancelled = false;
    const interval = window.setInterval(() => {
      if (cancelled) return;
      const ready = isGlossaryScanModalReady();
      setGlossaryScanModalReady((prev) => (prev !== ready ? ready : prev));
    }, 100);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    glossaryScanModalStepIndex,
    isGlossaryScanModalReady,
    metadataLanguageTourOpen,
    metadataLanguageTourStep,
  ]);

  useEffect(() => {
    if (tutorialUiSuppressionRequested) return;
    if (autoTourTriggeredRef.current) return;
    if (!tourFlagsInitialized) return;
    if (!metadataLanguageInitialized) return;
    if (isLoading) return;
    if (bookDetailExperienceMode === 'simple') return;
    if (!isEditing) return;
    if (activeMetadataTab !== 'metadata') return;
    if (isMetadataLanguageTranslated) return;
    if (mainTourSeen) return;
    autoTourTriggeredRef.current = true;
    const timer = setTimeout(() => {
      startTour('main', { allowSeen: false });
    }, 200);
    return () => clearTimeout(timer);
  }, [
    tourFlagsInitialized,
    isLoading,
    isEditing,
    activeMetadataTab,
    bookDetailExperienceMode,
    metadataLanguageInitialized,
    isMetadataLanguageTranslated,
    mainTourSeen,
    startTour,
    tutorialUiSuppressionRequested,
  ]);

  useEffect(() => {
    if (tutorialUiSuppressionRequested) return;
    if (metadataLanguageTourTriggeredRef.current) return;
    if (!tourFlagsInitialized) return;
    if (!metadataLanguageInitialized) return;
    if (isLoading) return;
    if (!isEditing) return;
    if (activeMetadataTab !== 'metadata') return;
    if (!isMetadataLanguageTranslated) return;
    if (metadataLanguageSeen) return;
    metadataLanguageTourTriggeredRef.current = true;
    const timer = setTimeout(() => {
      startTour('metadataLanguage', { allowSeen: false });
    }, 200);
    return () => clearTimeout(timer);
  }, [
    tourFlagsInitialized,
    isLoading,
    isEditing,
    activeMetadataTab,
    metadataLanguageInitialized,
    isMetadataLanguageTranslated,
    metadataLanguageSeen,
    startTour,
    tutorialUiSuppressionRequested,
  ]);

  useEffect(() => {
    if (!mainTourOpen) return;
    if (mainTourStep < metadataStartIndex) return;
    if (isEditing) return;
    setIsEditing(true);
  }, [mainTourOpen, mainTourStep, metadataStartIndex, isEditing, setIsEditing]);

  useEffect(() => {
    if (tutorialUiSuppressionRequested) return;
    if (!tourFlagsInitialized) return;
    if (!selectedChapter) return;
    if (chapterSeen) return;
    if (!mainTourSeen) return;
    startTour('chapter');
  }, [tourFlagsInitialized, selectedChapter?.chapter_guid, startTour, mainTourSeen, chapterSeen, tutorialUiSuppressionRequested]);

  useEffect(() => {
    if (tutorialUiSuppressionRequested) return;
    if (dashboardTourTriggeredRef.current) return;
    if (!tourFlagsInitialized) return;
    if (isLoading) return;
    if (!isEditing) return;
    if (activeMetadataTab !== 'dashboard') return;
    if (dashboardSeen) return;
    dashboardTourTriggeredRef.current = true;
    const timer = setTimeout(() => {
      setDashboardTourStep(0);
      setDashboardTourOpen(true);
    }, 200);
    return () => clearTimeout(timer);
  }, [tourFlagsInitialized, isLoading, isEditing, activeMetadataTab, dashboardSeen, tutorialUiSuppressionRequested]);

  useEffect(() => {
    if (activeMetadataTab !== 'dashboard' && dashboardTourOpen) {
      setDashboardTourOpen(false);
      setDashboardTourStep(0);
    }
  }, [activeMetadataTab, dashboardTourOpen]);

  useEffect(() => {
    if (!tutorialUiSuppressionRequested) return;
    setActiveTour(null);
    setPendingTour(null);
    setMainTourStep(0);
    setSimpleMainTourStep(0);
    setChapterTourStep(0);
    setMetadataLanguageTourStep(0);
    setDashboardTourOpen(false);
    setDashboardTourStep(0);
  }, [tutorialUiSuppressionRequested]);

  // Auto-load translated chapter content immediately after background completion
  useEffect(() => {
    if (!selectedChapter) return;
    if (!selectedTab || selectedTab === 'Original') return;

    const { sourceLang: src, targetLang: tgt } = splitLanguagePair(selectedTab);
    if (!src || !tgt) return;

    const key = `translated_chapters_${src}_${tgt}`;
    const list: any[] = (book as any)[key] || [];
    const existing = list.find(
      (tc: any) => tc.chapter_guid === selectedChapter.chapter_guid
    );
    if (existing && !translatedChapterNeedsProofreadHydration(existing)) return; // already loaded

    hydrateTranslatedChapter(selectedChapter.chapter_guid, src, tgt, {
      reason: 'background_hydration',
    }).catch((error) => {
      console.error('Error hydrating translated chapter:', error);
    });
  }, [book, selectedTab, selectedChapter?.chapter_guid, hydrateTranslatedChapter]);

  const handleTabChange = async (value: string) => {
    setSelectedTab(value);

    if (!selectedChapter) return;

    try {
      setIsLoading(true);
      if (value === 'Original') {
        // Load original heavy content on demand if missing
        if (!selectedChapter.content || selectedChapter.content.length === 0) {
          const resp = await bookDetailApiGet(
            `${API_BASE}/uploadedbooks/book/${bookId}/chapters/${selectedChapter.chapter_guid}`
          );
          const data = resp.data || {};
          // Update book's chapter entry
          setBook((prev: any) => {
            const updated = { ...prev, chapters: [...prev.chapters] };
            const idx = updated.chapters.findIndex((ch: any) => ch.chapter_guid === selectedChapter.chapter_guid);
            if (idx >= 0) {
              updated.chapters[idx] = {
                ...updated.chapters[idx],
                content: data.content || '',
                raw_html_content: data.raw_html_content || '',
                proofread_chapter: data.proofread_chapter || '',
                proofread_comments: data.proofread_comments || '',
                word_count: data.word_count ?? updated.chapters[idx].word_count,
                proofread_word_count: data.proofread_word_count ?? updated.chapters[idx].proofread_word_count,
                proofread_generation: data.proofread_generation ?? updated.chapters[idx].proofread_generation,
                proofread_mode: data.proofread_mode ?? updated.chapters[idx].proofread_mode,
                proofread_change_set_id: data.proofread_change_set_id ?? updated.chapters[idx].proofread_change_set_id,
                proofread_change_set_status: data.proofread_change_set_status ?? updated.chapters[idx].proofread_change_set_status,
                proofread_change_set_updated_at: data.proofread_change_set_updated_at ?? updated.chapters[idx].proofread_change_set_updated_at,
                proofread_change_set_applied_at: data.proofread_change_set_applied_at ?? updated.chapters[idx].proofread_change_set_applied_at,
                proofread_change_set_summary: data.proofread_change_set_summary ?? updated.chapters[idx].proofread_change_set_summary,
              };
            }
            return updated;
          });
          // Update selectedChapter cache
          setSelectedChapter((prevSel: any) => prevSel ? {
            ...prevSel,
            content: data.content || '',
            raw_html_content: data.raw_html_content || '',
            proofread_chapter: data.proofread_chapter || '',
            proofread_comments: data.proofread_comments || '',
            word_count: data.word_count ?? prevSel.word_count,
            proofread_word_count: data.proofread_word_count ?? prevSel.proofread_word_count,
            proofread_generation: data.proofread_generation ?? prevSel.proofread_generation,
            proofread_mode: data.proofread_mode ?? prevSel.proofread_mode,
            proofread_change_set_id: data.proofread_change_set_id ?? prevSel.proofread_change_set_id,
            proofread_change_set_status: data.proofread_change_set_status ?? prevSel.proofread_change_set_status,
            proofread_change_set_updated_at: data.proofread_change_set_updated_at ?? prevSel.proofread_change_set_updated_at,
            proofread_change_set_applied_at: data.proofread_change_set_applied_at ?? prevSel.proofread_change_set_applied_at,
            proofread_change_set_summary: data.proofread_change_set_summary ?? prevSel.proofread_change_set_summary,
          } : prevSel);
        }
      } else {
        // Load translated content for this chapter and pair on demand if missing
        const { sourceLang, targetLang } = splitLanguagePair(value);
        if (!sourceLang || !targetLang) {
          return;
        }
        const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
        const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
        const existing = translatedChapters.find(
          (tc: any) => tc.chapter_guid === selectedChapter.chapter_guid
        );
        if (!existing || translatedChapterNeedsProofreadHydration(existing)) {
          await hydrateTranslatedChapter(
            selectedChapter.chapter_guid,
            sourceLang,
            targetLang,
            {
              reason: 'language_switch',
            },
          );
        }
      }
    } catch (e) {
      console.error('Error loading chapter content:', e);
    } finally {
      setIsLoading(false);
    }
  };

const toggleAlwaysTranslateModal = () => {
  setIsAlwaysTranslateModalVisible(!isAlwaysTranslateModalVisible);
};

const handleGlossaryRuleSwitch = (nextRuleType: 'always' | 'never') => {
  setIsAlwaysTranslateModalVisible(nextRuleType === 'always');
  setIsNeverTranslateModalVisible(nextRuleType === 'never');
};

useEffect(() => {
  const allChaptersProofread = book.chapters.every(chapter => hasSavedProofreadForRow(chapter));
  setHasOriginalProofreadComplete(allChaptersProofread);
  //console.log("hasOriginalProofreadComplete: "+hasOriginalProofreadComplete)
}, [book.chapters]);


const hasTranslatedContent = React.useCallback((translatedChapter?: TranslatedChapter | null): boolean => {
  return hasTranslatedChapterContentValue(translatedChapter);
}, []);

const getOriginalProofreadWordCount = React.useCallback((chapter?: Chapter | null): number => {
  return resolveProofreadWordCountForRow({
    row: chapter,
    fallbackWordCountField: 'word_count',
    fallbackTextField: 'content',
  })
}, [])

const getTranslatedProofreadWordCount = React.useCallback((chapter?: TranslatedChapter | null): number => {
  return resolveProofreadWordCountForRow({
    row: chapter,
    fallbackWordCountField: 'translated_word_count',
    fallbackTextField: 'translated_text',
  })
}, [])

const isChapterTranslatedForPair = React.useCallback((
  translatedChapters: TranslatedChapter[] | undefined,
  chapterGuid: string
): boolean => {
  if (!Array.isArray(translatedChapters) || translatedChapters.length === 0) {
    return false;
  }
  const translatedChapter = translatedChapters.find(
    (tc) => tc.chapter_guid === chapterGuid
  );
  return hasTranslatedContent(translatedChapter);
}, [hasTranslatedContent]);

const getChapterWordCounts = () => {
  const wordCounts: {
    [chapterGuid: string]: {
      original: number;
      originalProofread: number;
      translations: {
        [languagePair: string]: {
          translated: number;
          proofread: number;
        }
      }
    }
  } = {};

  // Initialize with original chapter word counts
  book.chapters.forEach(chapter => {
    wordCounts[chapter.chapter_guid] = {
      original: chapter.word_count,
      originalProofread: chapter.proofread_word_count || 0,
      translations: {}
    };
  });

  // Add translated word counts for each translation pair
  if (book.translation_pairs) {
    book.translation_pairs.forEach(pair => {
      const { sourceLang, targetLang } = splitLanguagePair(pair);
      if (!sourceLang || !targetLang) {
        return;
      }
      const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
      const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];

      translatedChapters.forEach(translatedChapter => {
        if (wordCounts[translatedChapter.chapter_guid]) {
          wordCounts[translatedChapter.chapter_guid].translations[pair] = {
            translated: translatedChapter.translated_word_count,
            proofread: translatedChapter.proofread_word_count || 0
          };
        }
      });
    });
  }

  return wordCounts;
};


  const ensureWordCounts = useCallback(async (requests: EnsureWordCountRequestItem[]): Promise<EnsureWordCountResultPayload[]> => {
    if (!auth.currentUser || requests.length === 0) return [];
    try {
      const response = await bookDetailApiPost(`${API_BASE}/wordcount/ensure`, {
        user_id: auth.currentUser.uid,
        requests,
      });
      return Array.isArray(response.data?.results) ? response.data.results : [];
    } catch (error) {
      console.error('Failed to ensure word counts', error);
      return [];
    }
  }, [auth.currentUser?.uid]);

  const ensureWordCountBatches = useCallback(async (requests: EnsureWordCountRequestItem[]): Promise<EnsureWordCountResultPayload[]> => {
    if (requests.length === 0) return [];
    const allResults: EnsureWordCountResultPayload[] = [];
    const batchSize = 20;

    for (let index = 0; index < requests.length; index += batchSize) {
      const batch = requests.slice(index, index + batchSize);
      const batchResults = await ensureWordCounts(batch);
      if (batchResults.length) {
        allResults.push(...batchResults);
      }
    }

    return allResults;
  }, [ensureWordCounts]);

  const applyEnsureResults = useCallback((results: EnsureWordCountResultPayload[]) => {
    if (!results.length) return;

    setBook(prev => {
      if (!prev) return prev;
      let next = prev;

      const originalMap = new Map<string, EnsureWordCountResultPayload>();
      const proofreadOriginalMap = new Map<string, EnsureWordCountResultPayload>();
      const translatedMap = new Map<string, Map<string, EnsureWordCountResultPayload>>();
      const proofreadTranslatedMap = new Map<string, Map<string, EnsureWordCountResultPayload>>();

      results.forEach(res => {
        if (res.error) return;
        switch (res.scope) {
          case 'original':
            originalMap.set(res.chapter_guid, res);
            break;
          case 'proofread_original':
            proofreadOriginalMap.set(res.chapter_guid, res);
            break;
          case 'translated': {
            const key = `${res.source_lang || ''}|${res.target_lang || ''}`;
            if (!translatedMap.has(key)) {
              translatedMap.set(key, new Map());
            }
            translatedMap.get(key)!.set(res.chapter_guid, res);
            break;
          }
          case 'proofread_translated': {
            const key = `${res.source_lang || ''}|${res.target_lang || ''}`;
            if (!proofreadTranslatedMap.has(key)) {
              proofreadTranslatedMap.set(key, new Map());
            }
            proofreadTranslatedMap.get(key)!.set(res.chapter_guid, res);
            break;
          }
        }
      });

      if (originalMap.size || proofreadOriginalMap.size) {
        let chaptersChanged = false;
        const updatedChapters = next.chapters.map(chapter => {
          const base = originalMap.get(chapter.chapter_guid);
          const proof = proofreadOriginalMap.get(chapter.chapter_guid);
          if (!base && !proof) return chapter;
          chaptersChanged = true;
          return {
            ...chapter,
            word_count: base ? base.word_count : chapter.word_count,
            word_count_version: base ? base.version : chapter.word_count_version,
            proofread_word_count: proof ? proof.word_count : chapter.proofread_word_count,
          };
        });
        if (chaptersChanged) {
          next = { ...next, chapters: updatedChapters };
        }
      }

      const updateTranslationArray = (
        mapSource: Map<string, Map<string, EnsureWordCountResultPayload>>,
        updateProofread: boolean,
      ) => {
        mapSource.forEach((chapterMap, key) => {
          const [src, tgt] = key.split('|');
          const propertyKey = `translated_chapters_${src}_${tgt}`;
          const existing = (next as any)[propertyKey];
          if (!Array.isArray(existing) || existing.length === 0) return;

          let arrayChanged = false;
          const updatedArray = existing.map((item: any) => {
            const res = chapterMap.get(item.chapter_guid);
            if (!res) return item;
            arrayChanged = true;
            const updatedItem: any = {
              ...item,
              word_count: res.word_count,
              word_count_version: res.version,
            };
            if (updateProofread) {
              updatedItem.proofread_word_count = res.word_count;
            } else {
              updatedItem.translated_word_count = res.word_count;
            }
            return updatedItem;
          });

          if (arrayChanged) {
            next = { ...next, [propertyKey]: updatedArray };
          }
        });
      };

      updateTranslationArray(translatedMap, false);
      updateTranslationArray(proofreadTranslatedMap, true);

      const bumpVersion = results.some(res => !res.error && res.version >= WORD_COUNT_VERSION);
      if (bumpVersion && ((next.word_count_version ?? 0) < WORD_COUNT_VERSION)) {
        next = { ...next, word_count_version: WORD_COUNT_VERSION };
      }

      return next;
    });

    setSelectedChapter(prev => {
      if (!prev) return prev;
      const relevant = results.find(res => {
        if (res.error || res.chapter_guid !== prev.chapter_guid) return false;
        return res.scope === 'original' || res.scope === 'proofread_original';
      });
      if (!relevant) return prev;
      if (relevant.scope === 'original') {
        return {
          ...prev,
          word_count: relevant.word_count,
          word_count_version: relevant.version,
        };
      }
      if (relevant.scope === 'proofread_original') {
        return {
          ...prev,
          proofread_word_count: relevant.word_count,
          word_count_version: relevant.version,
        };
      }
      return prev;
    });
  }, [setBook, setSelectedChapter]);




  useEffect(() => {
    if (!book || !auth.currentUser) return;

    const bookVersion = book.word_count_version ?? 0;
    const bookNeedsRefresh = bookVersion < WORD_COUNT_VERSION;
    const requests: EnsureWordCountRequestItem[] = [];

    (book.chapters || []).forEach(chapter => {
      const chapterVersion = chapter.word_count_version ?? 0;
      const needsChapterRefresh = bookNeedsRefresh || chapterVersion < WORD_COUNT_VERSION;

      if (needsChapterRefresh) {
        requests.push({
          book_id: bookId,
          chapter_guid: chapter.chapter_guid,
          scope: 'original',
        });
      }

      const proofreadOriginalText = typeof chapter.proofread_chapter === 'string'
        ? chapter.proofread_chapter.trim()
        : '';
      const hasProofreadContent = proofreadOriginalText.length > 0 || (chapter.proofread_word_count ?? 0) > 0;

      if (hasProofreadContent && (bookNeedsRefresh || chapterVersion < WORD_COUNT_VERSION)) {
        requests.push({
          book_id: bookId,
          chapter_guid: chapter.chapter_guid,
          scope: 'proofread_original',
        });
      }
    });

    (book.translation_pairs || []).forEach(pair => {
      const { sourceLang, targetLang } = splitLanguagePair(pair);
      if (!sourceLang || !targetLang) return;

      const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
      const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
      if (!Array.isArray(translatedChapters) || !translatedChapters.length) return;

      translatedChapters.forEach(chapter => {
        const entryVersion = chapter.word_count_version ?? 0;
        const needsEntryRefresh = bookNeedsRefresh || entryVersion < WORD_COUNT_VERSION;

        if (needsEntryRefresh) {
          requests.push({
            book_id: bookId,
            chapter_guid: chapter.chapter_guid,
            scope: 'translated',
            source_lang: sourceLang,
            target_lang: targetLang,
          });
        }

        const proofreadTranslatedText = getSavedProofreadTextForRow(chapter);
        const hasProofread = proofreadTranslatedText.length > 0 || (chapter.proofread_word_count ?? 0) > 0;

        if (hasProofread && (bookNeedsRefresh || entryVersion < WORD_COUNT_VERSION)) {
          requests.push({
            book_id: bookId,
            chapter_guid: chapter.chapter_guid,
            scope: 'proofread_translated',
            source_lang: sourceLang,
            target_lang: targetLang,
          });
        }
      });
    });

    if (!requests.length) return;

    let cancelled = false;
    (async () => {
      const results = await ensureWordCountBatches(requests);
      if (!cancelled && results.length) {
        applyEnsureResults(results);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [book, auth.currentUser?.uid, ensureWordCountBatches, applyEnsureResults, bookId]);

const checkAllChaptersProcessed = (version: string): { isProcessed: boolean, error: string } => {
  // For Original version, always valid
  if (version === 'Original') {
    return { isProcessed: true, error: "" };
  }

  // For Original-Proofread version
  if (version === 'Original-Proofread') {
    return {
      isProcessed: hasOriginalProofreadComplete,
      error: hasOriginalProofreadComplete ? "" : "Cannot create audiobook: Not all chapters have been proofread."
    };
  }

  // For translation versions
  const isProofread = version.endsWith('-Proofread');
  const cleanVersion = isProofread ? version.slice(0, -'-Proofread'.length) : version;
  const { sourceLang, targetLang } = splitLanguagePair(cleanVersion);
  if (sourceLang && targetLang) {

    // Get translated chapters
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];

    // Check if ALL chapters are translated
    const allChaptersTranslated = book.chapters.every((chapter) =>
      isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid)
    );

    if (!allChaptersTranslated) {
      return {
        isProcessed: false,
        error: `Cannot create audiobook: Not all chapters have been translated to ${targetLang}.`
      };
    }

    // If we need proofread translation, check that too
    if (isProofread) {
      const allChaptersProofread = book.chapters.every(chapter => {
        const translatedChapter = translatedChapters.find(tc => tc.chapter_guid === chapter.chapter_guid);
        return hasSavedProofreadForRow(translatedChapter);
      });

      if (!allChaptersProofread) {
        return {
          isProcessed: false,
          error: `Cannot create audiobook: Not all chapters have been proofread in ${targetLang}.`
        };
      }
    }

    return { isProcessed: true, error: "" };
  }

  // Default case
  return { isProcessed: true, error: "" };
};

const handleAudiobookVersionChange = (version: string) => {
  setSelectedAudiobookVersion(version);

  // Check if all chapters are processed for this version
  const { isProcessed, error } = checkAllChaptersProcessed(version);
  setAudiobookProcessingError(isProcessed ? "" : error);
};

const handleUpdateLanguageCovers = async (covers: { [language: string]: string | null }) => {
  // Optimistically merge into local map; allow '' to clear a language cover immediately in UI
  setLanguageCovers(prevCovers => {
    const next = { ...prevCovers };
    for (const [kRaw, v] of Object.entries(covers || {})) {
      const k = String(kRaw).toLowerCase();
      if (v === '') {
        next[k] = '';
      } else if (v == null) {
        delete next[k]; // null/undefined signals deletion
      } else {
        next[k] = v as string;
      }
    }
    return next;
  });

  // Refresh backend state to ensure consistency and rebuild covers from truth
  if (auth.currentUser) {
    try {
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);

      const rebuiltRaw: { [language: string]: string } = {};
      if ((updatedBook as any).coverUrl) {
        rebuiltRaw['original'] = (updatedBook as any).coverUrl;
      }
      if ((updatedBook as any).cover_urls) {
        Object.entries((updatedBook as any).cover_urls as Record<string, string>).forEach(([k, v]) => {
          if (v) rebuiltRaw[String(k).toLowerCase()] = v;
        });
      }
      Object.keys(updatedBook as any).forEach(key => {
        if (key.startsWith('coverUrl_') && (updatedBook as any)[key]) {
          const language = key.replace('coverUrl_', '').toLowerCase();
          rebuiltRaw[language] = (updatedBook as any)[key];
        }
      });

      const normalizedBook: Book = {
        ...updatedBook,
        coverUrl: rebuiltRaw.original || ''
      };
      setBook({ ...normalizedBook, bookguid: bookId });

      setLanguageCovers(prev => {
        const merged: { [language: string]: string } = { ...prev };
        for (const [k, v] of Object.entries(rebuiltRaw)) {
          const kk = String(k).toLowerCase();
          if (prev[kk] === '') continue; // preserve explicit removal sentinel for any language
          merged[kk] = v;
        }
        if (prev['original'] === '' && !rebuiltRaw.original) {
          merged['original'] = '';
        }
        return merged;
      });
    } catch (error) {
      console.error("Error updating book with new covers:", error);
    }
  }
};

const handleCoverGenerationStart = (language: string) => {
  setCoverGenerationLanguage(language);
  // Decide spinner variant before showing overlay to avoid mid-render switch
  setCoverSpinnerKind(Math.random() < 0.5 ? 'coloring' : 'drawing');
  setIsGeneratingCover(true);
};

const handleCoverGenerationComplete = (images: string[], options?: {
  mode: 'modify' | 'generate';
  replaceTitle?: boolean;
  replaceSeries?: boolean;
  addLanguageSticker?: boolean;
  authorMode?: 'include' | 'omit';
  // Partial generation metadata from backend
  requestedCount?: number;
  generatedCount?: number;
  failureMessages?: string[];
  partialFailure?: boolean;
}) => {
  setIsGeneratingCover(false);
  if (images.length > 0) {
    setGeneratedCoverImages(images);
    setCoverGenerationOptions(options);
    setVariationSeedImage(null); // not a variation flow
    setCoverSelectionModalVisible(true);
  }
};

const handleCoverVariationsStart = (language: string) => {
  setCoverGenerationLanguage(language);
  // Decide spinner variant before showing overlay to avoid mid-render switch
  setCoverSpinnerKind(Math.random() < 0.5 ? 'coloring' : 'drawing');
  setIsGeneratingCover(true);
};

const handleCoverVariationsComplete = (images: string[]) => {
  setIsGeneratingCover(false);
  if (images && images.length > 0) {
    setGeneratedCoverImages(images);
    setCoverGenerationOptions({ mode: 'modify' });
    // The variations modal returns [seed, ...generated]; mark the seed for labeling
    setVariationSeedImage(images[0] || null);
    setCoverSelectionModalVisible(true);
  }
};

const handleOpenCoverTranslationForLanguage = useCallback((language: string) => {
  setOpenCoverGenerationSignal({ language, ts: Date.now() });
}, []);

// Randomize spinner variant whenever cover generation starts
// Spinner kind is decided at start handlers; no re-randomization during loading

const handleChapterContentSave = async (chapterId: string, content: string, contentType: string, wordCount?: number): Promise<void> => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    throw new Error("No user logged in");
  }

  setIsLoading(true);

  try {
    // Parse the content type to determine what we're saving
    let endpoint = '';
    let payload: any = {
      user_id: auth.currentUser.uid,
      book_id: bookId,
      chapter_guid: chapterId,
      content: content
    };

    if (contentType === 'original') {
      // Saving original chapter content
      endpoint = `${API_BASE}/update-chapter-content/${bookId}/${chapterId}`;
      if (typeof wordCount === 'number') {
        payload.word_count = wordCount;
      }
    } else if (contentType === 'original_proofread') {
      // Saving proofread original chapter
      endpoint = `${API_BASE}/update-proofread-content/${bookId}/${chapterId}`;
      payload.source_lang = 'Original';
      payload.target_lang = 'Original';
      if (typeof wordCount === 'number') {
        payload.proofread_word_count = wordCount;
      }
    } else if (contentType.startsWith('translated_')) {
      // Saving translated chapter content
      const langPair = contentType.replace('translated_', '');
      const { sourceLang, targetLang } = splitLanguagePair(langPair);
      if (!sourceLang || !targetLang) {
        throw new Error(`Unsupported translated contentType: ${contentType}`);
      }
      endpoint = `${API_BASE}/update-translated-content/${bookId}/${chapterId}`;
      payload.source_lang = sourceLang;
      payload.target_lang = targetLang;
      if (typeof wordCount === 'number') {
        payload.word_count = wordCount;
      }
    } else if (contentType.startsWith('proofread_')) {
      // Saving proofread translated chapter
      const langPair = contentType.replace('proofread_', '');
      const { sourceLang, targetLang } = splitLanguagePair(langPair);
      if (!sourceLang || !targetLang) {
        throw new Error(`Unsupported proofread contentType: ${contentType}`);
      }
      endpoint = `${API_BASE}/update-proofread-translated-content/${bookId}/${chapterId}`;
      payload.source_lang = sourceLang;
      payload.target_lang = targetLang;
      if (typeof wordCount === 'number') {
        payload.proofread_word_count = wordCount;
      }
    }

    const response = await bookDetailApiPost(endpoint, payload);

    if (response.data.status === "error") {
      throw new Error(response.data.message || "Failed to save chapter content");
    }

    // Refresh the book data to reflect the changes
    const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
    let updatedBook: Book = updatedBookResponse.data;
    updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);

    // Update the book state
    setBook(updatedBook);

    // Update the selected chapter if it's the one we just saved
    if (selectedChapter && selectedChapter.chapter_guid === chapterId) {
      const updatedChapter = updatedBook.chapters.find(ch => ch.chapter_guid === chapterId);
      if (updatedChapter) {
        setSelectedChapter(updatedChapter);
      }
    }

    message.success('Chapter content saved successfully');
  } catch (error) {
    console.error("Error saving chapter content:", error);
    message.error('Failed to save chapter content');
    throw error;
  } finally {
    setIsLoading(false);
  }
};

const handleRevertChapterContent = async (chapterId: string, contentType: string): Promise<void> => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    throw new Error("No user logged in");
  }

  setIsLoading(true);

  try {
    let endpoint = '';
    // Always include required fields for backend request models
    let payload: any = {
      user_id: auth.currentUser.uid,
      book_id: bookId,
      chapter_guid: chapterId,
      content: '' // not used by revert endpoints but required by request model
    };

    if (contentType === 'original') {
      endpoint = `${API_BASE}/revert-chapter-content/${bookId}/${chapterId}`;
    } else if (contentType === 'original_proofread') {
      endpoint = `${API_BASE}/revert-proofread-content/${bookId}/${chapterId}`;
    } else if (contentType.startsWith('translated_')) {
      const langPair = contentType.replace('translated_', '');
      const { sourceLang, targetLang } = splitLanguagePair(langPair);
      if (!sourceLang || !targetLang) {
        throw new Error(`Unsupported translated contentType for revert: ${contentType}`);
      }
      endpoint = `${API_BASE}/revert-translated-content/${bookId}/${chapterId}`;
      payload.source_lang = sourceLang;
      payload.target_lang = targetLang;
    } else if (contentType.startsWith('proofread_')) {
      const langPair = contentType.replace('proofread_', '');
      const { sourceLang, targetLang } = splitLanguagePair(langPair);
      if (!sourceLang || !targetLang) {
        throw new Error(`Unsupported proofread contentType for revert: ${contentType}`);
      }
      endpoint = `${API_BASE}/revert-proofread-translated-content/${bookId}/${chapterId}`;
      payload.source_lang = sourceLang;
      payload.target_lang = targetLang;
    } else {
      throw new Error(`Unsupported contentType for revert: ${contentType}`);
    }

    const response = await bookDetailApiPost(endpoint, payload);

    if (response.data.status === "error") {
      throw new Error(response.data.message || "Failed to revert chapter content");
    }

    // Refresh the book data to reflect the changes
    const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
    let updatedBook: Book = updatedBookResponse.data;
    updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);

    // Update the book state
    setBook(updatedBook);

    // Update the selected chapter if it's the one we just reverted
    if (selectedChapter && selectedChapter.chapter_guid === chapterId) {
      const updatedChapter = updatedBook.chapters.find(ch => ch.chapter_guid === chapterId);
      if (updatedChapter) {
        setSelectedChapter(updatedChapter);
      }
    }

    message.success('Chapter content reverted to original');
  } catch (error) {
    console.error("Error reverting chapter content:", error);
    message.error('Failed to revert chapter content', 10);
    throw error;
  } finally {
    setIsLoading(false);
  }
};

const getAudiobookChapterWordCount = (chapter: Chapter): number => {
  if (selectedAudiobookVersion === 'Original') {
    return chapter.word_count;
  } else if (selectedAudiobookVersion === 'Original-Proofread') {
    return chapter.proofread_word_count || chapter.word_count;
  } else {
    const isProofread = selectedAudiobookVersion.endsWith('-Proofread');
    const cleanVersion = isProofread
      ? selectedAudiobookVersion.slice(0, -'-Proofread'.length)
      : selectedAudiobookVersion;
    const { sourceLang, targetLang } = splitLanguagePair(cleanVersion);
    if (sourceLang && targetLang) {

      const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
      const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
      const translatedChapter = translatedChapters.find(tc => tc.chapter_guid === chapter.chapter_guid);

      if (!translatedChapter) return 0;

      if (isProofread && translatedChapter.proofread_word_count) {
        return translatedChapter.proofread_word_count;
      } else {
        return translatedChapter.translated_word_count;
      }
    }
    return 0;
  }
};

const fetchCreditBalanceInternal = useCallback(async (userId: string): Promise<CreditBalance | null> => {
  if (isSandboxFixtureMode) {
    const fixtureBalance = sandboxFixturePayload?.creditBalance && typeof sandboxFixturePayload.creditBalance === 'object'
      ? sandboxFixturePayload.creditBalance as CreditBalance
      : {
          credit_balance: 42000,
          available_balance: 42000,
          temp_Credits: 0,
          forever_credits: 42000,
          planID: 'tutorial-plan',
        } as CreditBalance;
    setCreditBalance(fixtureBalance);
    creditBalanceFetchedAtRef.current = Date.now();
    return fixtureBalance;
  }
  try {
    const response = await bookDetailApiGet<CreditBalance>(`${API_BASE}/check_credit_balance/?userID=${userId}`);
    setCreditBalance(response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching credit balance', error);
    message.error('Failed to fetch credit balance', 10);
    return null;
  }
}, [isSandboxFixtureMode, sandboxFixturePayload]);

const fetchCreditBalance = useCallback(async (userId: string): Promise<void> => {
  await fetchCreditBalanceInternal(userId);
}, [fetchCreditBalanceInternal]);

const runWithCreditCheck = useCallback(async <T,>(task: () => Promise<T>): Promise<T> => {
  setIsCreditCheckPending(true);
  try {
    return await task();
  } finally {
    setIsCreditCheckPending(false);
  }
}, []);

const CREDIT_BALANCE_TTL = 60_000;

const fetchCreditBalanceCached = useCallback(
  async (userId: string, options?: { force?: boolean; silent?: boolean }) => {
    const now = Date.now();
    if (
      !options?.force &&
      creditBalance &&
      creditBalanceFetchedAtRef.current &&
      now - creditBalanceFetchedAtRef.current < CREDIT_BALANCE_TTL
    ) {
      return creditBalance;
    }

    const fetcher = async () => {
      const fetchedBalance = await fetchCreditBalanceInternal(userId);
      creditBalanceFetchedAtRef.current = Date.now();
      return fetchedBalance;
    };

    if (options?.silent) {
      return await fetcher();
    } else {
      return await runWithCreditCheck(fetcher);
    }
  },
  [creditBalance, fetchCreditBalanceInternal, runWithCreditCheck]
);

const normalizeTopUpContext = useCallback((context: CreditTopUpContext): CreditTopUpContext => {
  const toSafeWholeNumber = (value: unknown, mode: 'floor' | 'ceil'): number | null => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    if (mode === 'ceil') return Math.max(0, Math.ceil(numeric));
    return Math.max(0, Math.floor(numeric));
  };

  const requiredCredits = toSafeWholeNumber(context.requiredCredits, 'ceil') ?? 0;
  const availableCreditsValue = toSafeWholeNumber(context.availableCredits, 'floor') ?? 0;
  const providedShortfall = toSafeWholeNumber(context.shortfallCredits, 'ceil');
  const computedShortfall = Math.max(requiredCredits - availableCreditsValue, 0);
  return {
    source: context.source,
    requiredCredits,
    availableCredits: availableCreditsValue,
    shortfallCredits: providedShortfall != null ? providedShortfall : computedShortfall,
  };
}, []);

const fetchInlineTopUpBillingContext = useCallback(
  async (userId: string) => {
    if (!userId) return;
    setIsInlineTopUpLoading(true);
    try {
      const creditResponse = await bookDetailApiGet(`${API_BASE}/check_credit_balance/`, {
        params: { userID: userId },
      });
      const creditData = creditResponse.data || {};
      const planProductID =
        typeof creditData.planProductID === 'string' ? creditData.planProductID : undefined;
      const planPriceID =
        typeof creditData.planPriceID === 'string' ? creditData.planPriceID : undefined;
      const lookupField =
        typeof creditData.planLookupID === 'string' ? creditData.planLookupID : undefined;
      const planIDRaw: any = creditData.planID;
      const priceIDRaw: any = creditData.priceID;
      const fallbackPriceID =
        typeof priceIDRaw === 'string' && priceIDRaw ? priceIDRaw : undefined;
      const fallbackPlanID =
        typeof planIDRaw === 'string' && planIDRaw
          ? planIDRaw
          : (typeof planIDRaw === 'number' && planIDRaw !== 0 ? String(planIDRaw) : undefined);
      const lookupId =
        planPriceID || fallbackPriceID || lookupField || fallbackPlanID || planProductID;
      const resolvedPriceId = planPriceID || fallbackPriceID;

      let planData: any = {};
      if (lookupId) {
        try {
          const planResponse = await bookDetailApiGet(`${API_BASE}/get_plan_details/${lookupId}`, {
            params: resolvedPriceId ? { priceId: resolvedPriceId } : undefined,
          });
          planData = planResponse.data || {};
        } catch (planError) {
          console.warn('Failed to load inline top-up plan details', planError);
        }
      }

      const toOptionalNumber = (value: unknown): number | undefined => {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : undefined;
      };

      setInlineTopUpPlanDetails({
        planName:
          (typeof planData.planName === 'string' ? planData.planName : undefined) ||
          (typeof planData.plan_name === 'string' ? planData.plan_name : undefined),
        monthlyCreditsAllocated:
          toOptionalNumber(planData.monthlyCreditsAllocated) ??
          toOptionalNumber(planData.monthlyCredits) ??
          toOptionalNumber(planData.monthly_credits_allocated) ??
          toOptionalNumber(creditData.monthly_credits_allocated),
        monthlyCredits:
          toOptionalNumber(planData.monthlyCredits) ??
          toOptionalNumber(planData.monthly_credits_allocated),
        billingInterval:
          (typeof planData.billingInterval === 'string' ? planData.billingInterval : undefined) ||
          (typeof planData.billing_interval === 'string' ? planData.billing_interval : undefined),
        intervalMonths:
          toOptionalNumber(planData.intervalMonths) ??
          toOptionalNumber(planData.interval_months),
        creditsPerInterval:
          toOptionalNumber(planData.creditsPerInterval) ??
          toOptionalNumber(planData.credits_per_interval),
        price: toOptionalNumber(planData.price),
        currency: typeof planData.currency === 'string' ? planData.currency : undefined,
        stripePriceID:
          (typeof planData.stripePriceID === 'string' ? planData.stripePriceID : undefined) ||
          (typeof planData.priceId === 'string' ? planData.priceId : undefined) ||
          resolvedPriceId,
        priceId:
          (typeof planData.priceId === 'string' ? planData.priceId : undefined) ||
          resolvedPriceId,
        isLegacy: Boolean(planData.isLegacy),
        foreverCreditDiscountPercent:
          toOptionalNumber(planData.foreverCreditDiscountPercent) ??
          toOptionalNumber(planData.forever_credit_discount_percent),
      });

      try {
        const headers = await getAuthHeaders(auth.currentUser ?? undefined);
        const summaryResponse = await bookDetailApiGet(`${API_BASE}/get-subscription-details`, {
          params: { userID: userId },
          headers,
        });
        const summary = summaryResponse.data || {};
        setInlineTopUpSubscriptionSummary({
          trialEnd:
            typeof summary.trial_end === 'number'
              ? summary.trial_end
              : (typeof summary.trialEnd === 'number' ? summary.trialEnd : null),
          cancelAt:
            typeof summary.cancel_at === 'number'
              ? summary.cancel_at
              : (typeof summary.cancelAt === 'number' ? summary.cancelAt : null),
          nextPaymentDate:
            typeof summary.next_payment_date === 'number'
              ? summary.next_payment_date
              : (typeof summary.nextPaymentDate === 'number' ? summary.nextPaymentDate : null),
          currentPeriodEnd:
            typeof summary.current_period_end === 'number'
              ? summary.current_period_end
              : (typeof summary.currentPeriodEnd === 'number' ? summary.currentPeriodEnd : null),
          scheduledTargetPriceId:
            typeof summary.scheduled_target_price_id === 'string'
              ? summary.scheduled_target_price_id
              : (typeof summary.scheduledTargetPriceId === 'string'
                ? summary.scheduledTargetPriceId
                : null),
          scheduledTargetProductId:
            typeof summary.scheduled_target_product_id === 'string'
              ? summary.scheduled_target_product_id
              : (typeof summary.scheduledTargetProductId === 'string'
                ? summary.scheduledTargetProductId
                : null),
          scheduledEffectiveDate:
            typeof summary.scheduled_effective_date === 'number'
              ? summary.scheduled_effective_date
              : (typeof summary.scheduledEffectiveDate === 'number'
                ? summary.scheduledEffectiveDate
                : null),
          scheduledAction:
            summary.scheduled_action === 'upgrade' || summary.scheduled_action === 'downgrade'
              ? summary.scheduled_action
              : (summary.scheduledAction === 'upgrade' || summary.scheduledAction === 'downgrade'
                ? summary.scheduledAction
                : null),
          isPaused: Boolean(summary.is_paused ?? summary.isPaused),
        });
        setInlineCanRenewEarly(
          typeof summary.can_renew === 'boolean'
            ? summary.can_renew
            : (typeof summary.canRenew === 'boolean' ? summary.canRenew : null)
        );
        setInlineRenewDisabledTooltip(
          (typeof summary.renewal_wait_message === 'string' ? summary.renewal_wait_message : '') ||
          (typeof summary.message === 'string' ? summary.message : '')
        );
      } catch (summaryError) {
        setInlineTopUpSubscriptionSummary(null);
        setInlineCanRenewEarly(null);
        setInlineRenewDisabledTooltip('');
        console.warn('Failed to load inline top-up subscription details', summaryError);
      }
    } catch (error) {
      console.error('Failed to load inline top-up context', error);
      message.error('Unable to load top-up options right now. Please try again.', 8);
    } finally {
      setIsInlineTopUpLoading(false);
    }
  },
  [API_BASE, auth.currentUser, getAuthHeaders]
);

const handleInlineRenewNow = useCallback(async () => {
  const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
  if (!userId || isInlineRenewing) return;
  const toastKey = 'book-inline-renew';
  setIsInlineRenewing(true);
  message.loading({ content: 'Processing renewal...', key: toastKey, duration: 0 });
  try {
    const response = await accountApi.post(`${API_BASE}/renew-subscription`, { userID: userId });
    const data = response?.data || {};
    if (data.multiple_customers) {
      message.info({
        content: data.message || 'Multiple subscriptions found. Complete renewal from Billing.',
        key: toastKey,
      });
      const fallbackUrl = '/account/billing?openTopUp=1';
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const serverMessage =
      typeof data?.message === 'string'
        ? data.message
        : 'Your subscription has been renewed successfully.';
    const invoiceId: string | undefined = data?.invoice_id || data?.invoiceId;
    const invoiceStatus: string | undefined = data?.invoice_status || data?.invoiceStatus;
    const hostedInvoiceUrl: string | undefined = data?.hosted_invoice_url || data?.hostedInvoiceUrl;
    const invoicePdf: string | undefined = data?.invoice_pdf || data?.invoicePdf;

    if (invoiceId && invoiceStatus !== 'paid') {
      message.info({ content: serverMessage, key: toastKey });
      Modal.info({
        title: 'Manual Payment Required',
        content: (
          <Space direction="vertical">
            <span>{serverMessage}</span>
            {hostedInvoiceUrl ? (
              <Button type="primary" href={hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                View Invoice
              </Button>
            ) : null}
            {!hostedInvoiceUrl && invoicePdf ? (
              <Button type="primary" href={invoicePdf} target="_blank" rel="noopener noreferrer">
                Download Invoice PDF
              </Button>
            ) : null}
          </Space>
        ),
      });
    } else {
      message.success({ content: serverMessage, key: toastKey });
    }

    await Promise.allSettled([
      fetchCreditBalanceCached(userId, { force: true }),
      fetchInlineTopUpBillingContext(userId),
    ]);
    setIsInlineTopUpModalVisible(false);
  } catch (error: any) {
    const detail = error?.response?.data?.detail;
    const fallbackMessage =
      typeof detail === 'string'
        ? detail
        : (typeof error?.response?.data?.message === 'string'
          ? error.response.data.message
          : 'Unable to process renewal right now.');
    message.error({ content: fallbackMessage, key: toastKey });
  } finally {
    setIsInlineRenewing(false);
  }
}, [
  API_BASE,
  auth.currentUser?.uid,
  fetchCreditBalanceCached,
  fetchInlineTopUpBillingContext,
  isInlineRenewing,
  sharedUserId,
]);

const openTopUpFromBook = useCallback(
  (context: CreditTopUpContext) => {
    const resolvedUserId = auth.currentUser?.uid ?? sharedUserId ?? '';
    if (!resolvedUserId) {
      message.error('Please sign in to top up credits.');
      return;
    }
    setInlineTopUpContext(normalizeTopUpContext(context));
    setIsInlineTopUpModalVisible(true);
    void fetchInlineTopUpBillingContext(resolvedUserId);
  },
  [auth.currentUser?.uid, fetchInlineTopUpBillingContext, normalizeTopUpContext, sharedUserId]
);

const submitTranslationBetaFeedback = useCallback(
  async (payload: TranslationBetaFeedbackPayload) => {
    const userId = auth.currentUser?.uid ?? sharedUserId ?? null;
    if (!userId) {
      throw new Error('Please sign in to submit feedback.');
    }
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    await bookDetailApiPost(
      `${API_BASE}/ui/log-client-error`,
      {
        event: 'translation_beta_feedback',
        message: payload.feedback,
        details: `Issue: ${payload.issueType}`,
        userId,
        bookId,
        chapterGuid: selectedChapter?.chapter_guid ?? null,
        route: window.location.pathname,
        context: {
          source: payload.source,
          targetLanguage: payload.targetLanguage,
          contactEmail: payload.contactEmail || null,
          includeContext: payload.includeContext,
          translateVersion: payload.translateVersion,
          workflowMode: payload.workflowMode,
          selectedAIModel: payload.selectedAIModel,
          quotationStyle: payload.quotationStyle,
          autoProofreadEnabled: payload.autoProofreadEnabled,
          requireCharacterAnalysis: payload.requireCharacterAnalysis ?? null,
          termScanWarningState: payload.termScanWarningState || null,
        },
      },
      { headers }
    );
  },
  [API_BASE, auth.currentUser, bookId, getAuthHeaders, selectedChapter?.chapter_guid, sharedUserId]
);

const submitMetadataTranslationBetaFeedback = useCallback(
  async (payload: MetadataTranslationBetaFeedbackPayload) => {
    const userId = auth.currentUser?.uid ?? sharedUserId ?? null;
    if (!userId) {
      throw new Error('Please sign in to submit feedback.');
    }
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    await bookDetailApiPost(
      `${API_BASE}/ui/log-client-error`,
      {
        event: 'metadata_translation_beta_feedback',
        message: payload.feedback,
        details: `Issue: ${payload.issueType}`,
        userId,
        bookId,
        chapterGuid: selectedChapter?.chapter_guid ?? null,
        route: window.location.pathname,
        context: {
          source: payload.source,
          targetLanguage: payload.targetLanguage,
          contactEmail: payload.contactEmail || null,
          includeContext: payload.includeContext,
          selectedAIModel: payload.selectedAIModel,
          quotationStyle: payload.quotationStyle,
          translateMetadata: payload.translateMetadata,
          frenchTone: payload.frenchTone || null,
        },
      },
      { headers }
    );
  },
  [API_BASE, auth.currentUser, bookId, getAuthHeaders, selectedChapter?.chapter_guid, sharedUserId]
);

useEffect(() => {
  if (!resolvedOwnerUserId) {
    return;
  }

  const refreshCreditBalance = () => {
    fetchCreditBalanceCached(resolvedOwnerUserId, { force: true, silent: true }).catch(() => {});
  };

  const handleWindowFocus = () => {
    refreshCreditBalance();
  };
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      refreshCreditBalance();
    }
  };

  window.addEventListener('focus', handleWindowFocus);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    window.removeEventListener('focus', handleWindowFocus);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [fetchCreditBalanceCached, resolvedOwnerUserId]);

useEffect(() => {
  if (!resolvedOwnerUserId) {
    return;
  }
  if (initialCreditBalanceFetchUserRef.current === resolvedOwnerUserId) {
    return;
  }
  initialCreditBalanceFetchUserRef.current = resolvedOwnerUserId;
  fetchCreditBalanceInternal(resolvedOwnerUserId).catch(() => {});
}, [fetchCreditBalanceInternal, resolvedOwnerUserId]);

const fetchRelationshipSummary = useCallback(
  async (options?: { silent?: boolean }): Promise<RelationshipAnalysisSummary | null> => {
    if (!RELATIONSHIP_UI_ENABLED) {
      return null;
    }
    if (!bookId) {
      return null;
    }
    if (!resolvedOwnerUserId) {
      return null;
    }
    if (!options?.silent) {
      setIsRelationshipSummaryLoading(true);
    }
    try {
      const { data } = await bookDetailApiGet<RelationshipAnalysisSummary>(
        `${API_BASE}/uploadedbooks/book/${bookId}/relationships/analysis-summary`,
        { params: { userId: resolvedOwnerUserId } }
      );
      setRelationshipSummary(data);
      return data;
    } catch (error) {
      if (!options?.silent) {
        console.error('Failed to load character relationship summary', error);
      }
      return null;
    } finally {
      if (!options?.silent) {
        setIsRelationshipSummaryLoading(false);
      }
    }
  },
  [API_BASE, bookId, resolvedOwnerUserId]
);

const fetchRelationshipRerunSummary = useCallback(
  async (options?: { silent?: boolean }) => {
    if (!RELATIONSHIP_UI_ENABLED) {
      return;
    }
    if (!bookId) {
      return;
    }
    if (!resolvedOwnerUserId) {
      return;
    }
    if (!options?.silent) {
      setIsRelationshipRerunSummaryLoading(true);
    }
    try {
      const { data } = await bookDetailApiGet<RelationshipAnalysisSummary>(
        `${API_BASE}/uploadedbooks/book/${bookId}/relationships/analysis-summary`,
        { params: { includeAnalyzed: true, userId: resolvedOwnerUserId } }
      );
      setRelationshipRerunSummary(data);
    } catch (error) {
      if (!options?.silent) {
        console.error('Failed to load rerun relationship summary', error);
      }
    } finally {
      if (!options?.silent) {
        setIsRelationshipRerunSummaryLoading(false);
      }
    }
  },
  [API_BASE, bookId, resolvedOwnerUserId]
);

const bumpRegistryRefreshSignal = useCallback(() => {
  setRegistryRefreshSignal((prev) => (prev ?? 0) + 1);
}, []);

const bumpRegistryClearSignal = useCallback(() => {
  setRegistryClearSignal((prev) => (prev ?? 0) + 1);
}, []);

  const handleChapterRelationshipsChange = useCallback(
  (chapterGuid: string, updates: Partial<Chapter>) => {
    setBook((prev) => {
      if (!prev?.chapters) {
        return prev;
      }
      const updatedChapters = prev.chapters.map((chapter) =>
        chapter.chapter_guid === chapterGuid ? { ...chapter, ...updates } : chapter
      );
      return { ...prev, chapters: updatedChapters };
    });
    setSelectedChapter((prev) =>
      prev && prev.chapter_guid === chapterGuid ? ({ ...prev, ...updates } as Chapter) : prev
    );
    fetchRelationshipSummary({ silent: true });
    fetchRelationshipRerunSummary({ silent: true });
    bumpRegistryRefreshSignal();
  },
  [bumpRegistryRefreshSignal, fetchRelationshipRerunSummary, fetchRelationshipSummary]
);

const handleOpenRelationshipEditor = useCallback((chapterGuid?: string) => {
  setRelationshipEditorChapterGuid(chapterGuid ?? null);
  setIsRelationshipEditorVisible(true);
}, []);

const handleCloseRelationshipEditor = useCallback(() => {
  setIsRelationshipEditorVisible(false);
  setRelationshipEditorChapterGuid(null);
}, []);

  const handleRunRelationshipAnalysis = useCallback((mode?: 'missing' | 'rerun') => {
    if (!bookId || !bookLanguage) {
      message.error('Missing book language for analysis.');
      return;
    }
    const resolvedUserId = auth.currentUser?.uid ?? sharedUserId;
    if (!resolvedUserId) {
      message.error('Please sign in to run this analysis.');
      return;
    }
    const resolvedMode =
      mode ?? ((relationshipSummary?.missingChapters ?? 0) === 0 ? 'rerun' : 'missing');
    setRelationshipAnalysisMode(resolvedMode);
    fetchCreditBalanceCached(resolvedUserId, { silent: true }).catch(() => {});
    if (resolvedMode === 'rerun') {
      fetchRelationshipRerunSummary().catch(() => {});
    } else {
      fetchRelationshipSummary({ silent: true }).catch(() => {});
    }
    setIsRelationshipModalVisible(true);
  }, [
    auth.currentUser?.uid,
    bookId,
    bookLanguage,
    fetchCreditBalanceCached,
    fetchRelationshipRerunSummary,
    fetchRelationshipSummary,
    relationshipSummary?.missingChapters,
    sharedUserId,
  ]);

  const handleRelationshipAnalysisModeChange = useCallback(
    (includeAnalyzed: boolean) => {
      const nextMode = includeAnalyzed ? 'rerun' : 'missing';
      setRelationshipAnalysisMode(nextMode);
      if (includeAnalyzed) {
        fetchRelationshipRerunSummary().catch(() => {});
      } else {
        fetchRelationshipSummary({ silent: true }).catch(() => {});
      }
    },
    [fetchRelationshipRerunSummary, fetchRelationshipSummary]
  );

  useEffect(() => {
    if (!isRelationshipModalVisible) {
      relationshipSelectionTouchedRef.current = false;
      return;
    }
    if (relationshipAnalysisMode !== 'rerun') {
      return;
    }
    if (!relationshipSelectionTouchedRef.current) {
      setRelationshipSelectedChapterGuids(rerunRelationshipChapterGuids);
      return;
    }
    if (rerunRelationshipChapterGuids.length === 0) {
      setRelationshipSelectedChapterGuids([]);
      return;
    }
    setRelationshipSelectedChapterGuids((prev) => {
      const allowed = new Set(rerunRelationshipChapterGuids);
      return prev.filter((guid) => allowed.has(guid));
    });
  }, [
    isRelationshipModalVisible,
    relationshipAnalysisMode,
    rerunRelationshipChapterGuids,
  ]);

  const handleRelationshipSelectionChange = useCallback((chapterGuids: string[]) => {
    relationshipSelectionTouchedRef.current = true;
    setRelationshipSelectedChapterGuids(chapterGuids);
  }, []);

  const handleConfirmRelationshipAnalysis = useCallback(async () => {
    if (!bookId || !bookLanguage) {
      message.error('Missing book language for analysis.');
      setIsRelationshipModalVisible(false);
      return;
    }
    const resolvedUserId = auth.currentUser?.uid ?? sharedUserId;
    if (!resolvedUserId) {
      message.error('Please sign in to run this analysis.');
      setIsRelationshipModalVisible(false);
      return;
    }
    const includeAnalyzed = relationshipAnalysisMode === 'rerun';
    const summary = includeAnalyzed ? relationshipRerunSummary : relationshipSummary;
    const activeCreditCost = includeAnalyzed ? relationshipRerunCreditCost : summary?.creditCost ?? null;
    const shouldClearExisting = includeAnalyzed && isFullRelationshipRerunSelection;
    const shouldSendChapterGuids = includeAnalyzed && !isFullRelationshipRerunSelection;
    if (includeAnalyzed && relationshipSelectedChapterGuids.length === 0) {
      message.error('Select at least one chapter to re-run.');
      return;
    }
    if (
      activeCreditCost != null &&
      availableCredits != null &&
      availableCredits < activeCreditCost
    ) {
      message.error('Not enough credits to run this analysis.');
      setIsRelationshipModalVisible(false);
      return;
    }
    const resolvedTargetLang = bookLanguage;

    setIsRelationshipAnalysisRunning(true);
    try {
      const response = await bookDetailApiPost(`${API_BASE}/analyze-missing-relationships/`, {
        userId: resolvedUserId,
        bookId,
        sourceLang: bookLanguage,
        targetLang: resolvedTargetLang,
        includeAnalyzed,
        clearExisting: shouldClearExisting,
        chapterGuids: shouldSendChapterGuids ? relationshipSelectedChapterGuids : undefined,
      });
      const status = response?.data?.status;
      const statusKey = (status || '').toString().trim().toLowerCase();
      if (statusKey === 'queued' || statusKey === 'running') {
        setBook(prev => (prev ? { ...prev, characterRelationshipsAnalysisStatus: statusKey } : prev));
      }
      const canClearRegistry =
        shouldClearExisting &&
        statusKey !== '' &&
        !['insufficient_credits', 'noop', 'error'].includes(statusKey);
      const shouldRefreshRegistry = statusKey === 'success' || statusKey === 'completed';

      if (status === 'insufficient_credits') {
        message.error(response.data?.message || 'Not enough credits to run analysis.');
      } else if (status === 'noop') {
        message.info('All chapters already have character relationships.');
      } else if (status === 'error') {
        message.error(response.data?.message || 'Unable to start character relationship analysis.');
      } else {
        message.success('Character relationship analysis started.');
      }
      if (canClearRegistry) {
        bumpRegistryClearSignal();
      }
      fetchRelationshipSummary({ silent: true });
      fetchRelationshipRerunSummary({ silent: true });
      if (shouldRefreshRegistry) {
        bumpRegistryRefreshSignal();
      }
    } catch (error: any) {
      const detail = isBookDetailApiError(error) ? error.response?.data?.detail : null;
      message.error(detail || 'Failed to start character relationship analysis.');
    } finally {
      setIsRelationshipAnalysisRunning(false);
      setIsRelationshipModalVisible(false);
    }
  }, [
    API_BASE,
    auth.currentUser?.uid,
    availableCredits,
    bookId,
    bookLanguage,
    bumpRegistryClearSignal,
    bumpRegistryRefreshSignal,
    fetchRelationshipRerunSummary,
    fetchRelationshipSummary,
    isFullRelationshipRerunSelection,
    relationshipAnalysisMode,
    relationshipRerunCreditCost,
    relationshipSummary,
    relationshipRerunSummary,
    relationshipSelectedChapterGuids,
    selectedAIModel,
    sharedUserId,
  ]);


const updateUntranslatedChaptersForProofread = React.useCallback((newTargetLanguage?: string) => {
  //console.time('updateUntranslatedChaptersForProofread');
  if (!newTargetLanguage || newTargetLanguage === 'Original') {
    setHasUntranslatedChaptersForProofread(false);
		//console.timeEnd('updateUntranslatedChaptersForProofread');
    return;
  }

  const translatedChaptersKey = `translated_chapters_${bookLanguage}_${newTargetLanguage}`;
  const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
  const translatedByGuid = new Map<string, TranslatedChapter>();
  translatedChapters.forEach(chapter => {
    translatedByGuid.set(chapter.chapter_guid, chapter);
  });
  const untranslated = getMissingProofreadTranslationGuids(
    book.chapters,
    chapterGuid => hasTranslatedContent(translatedByGuid.get(chapterGuid)),
  );

  setHasUntranslatedChaptersForProofread(untranslated.length > 0);
  //console.timeEnd('updateUntranslatedChaptersForProofread');
}, [book, bookLanguage, hasTranslatedContent]);

useEffect(() => {
  //console.log("useEffect 2");
  //console.time('useEffect 2');
  const normalizedVersion = normalizeProofreadVersion(proofreadVersion);
  const { sourceLang, targetLang } = splitLanguagePair(normalizedVersion);

  if (!selectedChapter) {
    if (normalizedVersion === "Original") {
      updateUntranslatedChaptersForProofread("Original");
    } else {
      updateUntranslatedChaptersForProofread(targetLang);
    }
		//console.timeEnd('useEffect 2');
    return;
  }

  if (normalizedVersion === "Original") {
    updateUntranslatedChaptersForProofread("Original");
    setCanChapterBeProofread(true);
    setHasChapterBeenProofread(hasSavedProofreadForRow(selectedChapter));
    setHasChapterBeenEditNotes(!!selectedChapter.proofread_comments && selectedChapter.proofread_comments.length > 0);
  } else {
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
    const translatedChapter = translatedChapters.find(chap => chap.chapter_guid === selectedChapter.chapter_guid);

    if (translatedChapter) {
      const canProofreadTranslatedChapter = hasTranslatedContent(translatedChapter);
      setCanChapterBeProofread(canProofreadTranslatedChapter);
      setHasChapterBeenProofread(canProofreadTranslatedChapter && hasSavedProofreadForRow(translatedChapter));
      setHasChapterBeenEditNotes(
        canProofreadTranslatedChapter &&
          !!translatedChapter.proofread_comments &&
          translatedChapter.proofread_comments.length > 0
      );
    } else {
      setCanChapterBeProofread(false);
      setHasChapterBeenProofread(false);
      setHasChapterBeenEditNotes(false);
    }
  }
  //console.timeEnd('useEffect 2');
}, [
  selectedChapter,
  proofreadVersion,
  book,
  normalizeProofreadVersion,
  updateUntranslatedChaptersForProofread,
  hasTranslatedContent,
]);

useEffect(() => {
  fetchRelationshipSummary();
}, [fetchRelationshipSummary]);
const [isChapterTranslated, setIsChapterTranslated] = useState(false);

  useEffect(() => {
    if (selectedChapter) {
      const translatedChaptersKey = `translated_chapters_${bookLanguage}_${targetLanguage}`;
      const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];

      // Check if the current chapter is translated
      const isCurrentChapterTranslated = isChapterTranslatedForPair(
        translatedChapters,
        selectedChapter.chapter_guid
      );
      setIsChapterTranslated(isCurrentChapterTranslated);
    } else {
      setIsChapterTranslated(false);
    }
  }, [selectedChapter, book, bookLanguage, targetLanguage]);


  useEffect(() => {
	  //console.log("useEffect 3");
	  //console.time('useEffect 3');
  const handleLogout = () => {
    // Reset all states
    setBook({ title: '', author: '', series: '', booknumber: 0, coverUrl: '', chapters: [], genres: [] });
    setEditBook({
      title: '',
      author: '',
      series: '',
      booknumber: 0,
      genres: [],
      tropes: [],
      keywords: [],
      seriesId: null,
      seriesDescription: '',
      seriesInheritance: {},
    });
    setSelectedChapter(null);
	setIsProofreadChecked(false);
	setBetaLanguageWarning(false);
	setIsBetaUser(false);
	setIsEditingNotesChecked(false);
    setIsEditing(false);
    setOriginalOrder({});
    setIsLoading(false);
    setIsTitleValid(true);
    setIsTranslateModalVisible(false);
    setIsMetadataIsTranslateModalVisible(false);
	setIsKeywordSuggestionModalVisible(false);
    setSourceLanguage('English');
    setTargetLanguage('');
    setTranslationTargetLanguages([]);
	setTranslateVersion('Original');
    setEditingChapterGuid(null);
    setEditedChapterName('');
    setBlurbActiveTabKey('blurb');
    setIsExportModalVisible(false);
    setExportVersion('Original');
	setProofreadVersionSafe('Original');
    setExportFormat('ePub');
    setUntranslatedChapters([]);
    setShowWarning(false);
    setBookLanguage('');
	setGenres([]);
	setTropes([]);
	setKeywords([]);
	setQuotationStyle("");
	setProofreadAdditionalInstructions("");
    setBlurb('');
    setBlurbTranslations({});
    setUploadType('');
    setTranslationOptions({});
    setMetadataTranslationOverwriteApproved(false);
	setUnproofreadChapters([]);
	setUnproofreadNotesChapters([]);
	setIsProofreadModalVisible(false);
	setHasUnproofreadChapters(true);
	setHasUnproofreadNotesChapters(true);
	setHasProofreadChapters(false);
	setHasProofreadNotesChapters(false);
    setIsTranslationOptionsModalVisible(false);
    setCanTranslateTitle(false);
    setCanTranslateSeries(false);
    setCanTranslateBlurb(false);
    setTitleTranslations({});
    setSeriesTranslations({});
    setSeriesDescriptionTranslations({});
    setIsTranslatedTabActive(false);
    setCreditBalance(null);
    setSelectedProofreadModel('');
    setUserHasEnoughCredits(true);
	setUserHasEnoughCreditsMetadata(true);
	setCanChapterBeProofread(false);
    setTranslateMetadata({
      title: false,
      series: false,
      blurb: false,
    });
	setKeywordSuggestionMetadata({
      genre: false,
      tropes: false,
      keywords: false,
    });

      // Redirect to /manage-books
      navigate('/manage-books');
    };

    if (isSandboxFixtureMode || tutorialUiSuppressionRequested) {
      return;
    }

    const hasResolvedUser = Boolean(auth.currentUser?.uid || sharedUserId);
    if (hasResolvedUser) {
      return;
    }
    const logoutTimer = window.setTimeout(() => {
      const stillMissingUser = !auth.currentUser?.uid && !sharedUserId;
      if (stillMissingUser) {
        handleLogout();
      }
    }, 1500);
    return () => {
      window.clearTimeout(logoutTimer);
    };
	//console.timeEnd('useEffect 3');
	}, [auth.currentUser?.uid, isSandboxFixtureMode, navigate, sharedUserId, tutorialUiSuppressionRequested]);

  //Functions to Delete ALL translated chapters for selected language pair
  const handleDeleteTranslatedChapters = async (sourceLang: string, targetLang: string) => {
		setDeleteChaptersSourceLang(sourceLang);
		setDeleteChaptersTargetLang(targetLang);
		setShowDeleteTranslatedChaptersModal(true);
  };

  const handleDeleteTranslatedChaptersOK = () => {
		setShowDeleteTranslatedChaptersModal(false);
		deleteTranslatedChapters(deleteChaptersSourceLang, deleteChaptersTargetLang);
  };

  const handleDeleteTranslatedChaptersCancel = () => {
		setShowDeleteTranslatedChaptersModal(false);
  };

  //Functions to Delete ALL proofread chapters for selected language pair
  const handleDeleteProofreadChapters = async (sourceLang: string, targetLang: string) => {
		setDeleteChaptersSourceLang(sourceLang);
		setDeleteChaptersTargetLang(targetLang);
		setShowDeleteProofreadChaptersModal(true);
  };

  const handleDeleteProofreadChaptersOK = () => {
		setShowDeleteProofreadChaptersModal(false);
		deleteProofreadChapters(deleteChaptersSourceLang, deleteChaptersTargetLang);
  };

  const handleDeleteProofreadChaptersCancel = () => {
		setShowDeleteProofreadChaptersModal(false);
  };

  //Functions to Delete the selected chapter's Proofread for selected language pair
  const handleDeleteSingleProofreadChapter = async (sourceLang: string, targetLang: string) => {
		setDeleteSingleChapterSourceLang(sourceLang);
		setDeleteSingleChapterTargetLang(targetLang);
		setShowDeleteSingleProofreadChapterModal(true);
  };

  const handleDeleteSingleProofreadChapterOK = () => {
		setShowDeleteSingleProofreadChapterModal(false);
		if (selectedChapter) {
			deleteProofread(selectedChapter.chapter_guid, deleteSingleChapterSourceLang, deleteSingleChapterTargetLang);
		}
  };

  const handleDeleteSingleProofreadChapterCancel = () => {
		setShowDeleteSingleProofreadChapterModal(false);
  };

  //Functions to Delete the selected chapter's Translation for selected language pair
  const handleDeleteSingleTranslatedChapter = async (sourceLang: string, targetLang: string) => {
		setDeleteSingleChapterSourceLang(sourceLang);
		setDeleteSingleChapterTargetLang(targetLang);
		setShowDeleteSingleTranslatedChapterModal(true);
  };

  const handleDeleteSingleTranslatedChapterOK = () => {
		setShowDeleteSingleTranslatedChapterModal(false);
		if (selectedChapter) {
			deleteTranslation(selectedChapter.chapter_guid, deleteSingleChapterSourceLang, deleteSingleChapterTargetLang);
		}
  };

  const handleDeleteSingleTranslatedChapterCancel = () => {
		setShowDeleteSingleTranslatedChapterModal(false);
  };


  //API call to delete translated chapters
  const deleteTranslatedChapters = async (sourceLang: string, targetLang: string) => {
    if (!auth.currentUser) {
      console.error("No user logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await bookDetailApiPost(
        `${API_BASE}/delete-translated-chapters/${bookId}`,
        {
          user_id: auth.currentUser.uid,
          source_language: sourceLang,
          target_language: targetLang
        }
      );

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

      setBook(prev => applyLocalTranslationDeletion(prev, sourceLang, targetLang));

      // Refresh the book data to reflect the deletion
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
      setBook(applyLocalTranslationDeletion(updatedBook, sourceLang, targetLang));
      try {
        await Promise.all([
          refreshTranslationsImmediate(true),
          fetchRecentExports(),
        ]);
      } catch (refreshError) {
        console.error("Error refreshing export/status views after translation delete:", refreshError);
      }
	  let successMsg = `All Chapter Translations for ${targetLang} deleted successfully.`;
      message.success(successMsg);
    } catch (error) {
      console.error("Error deleting translation:", error);
      message.error("Error deleting translation",10);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProofreadChapters = async (sourceLang: string, targetLang: string) => {
    if (!auth.currentUser) {
      console.error("No user logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await bookDetailApiPost(
        `${API_BASE}/delete-proofread-chapters/${bookId}`,
        {
          user_id: auth.currentUser.uid,
          source_language: sourceLang,
          target_language: targetLang
        }
      );

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

      // Refresh the book data to reflect the deletion
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
      setBook(updatedBook);
      try {
        await Promise.all([
          refreshTranslationsImmediate(true),
          fetchRecentExports(),
        ]);
      } catch (refreshError) {
        console.error("Error refreshing export/status views after proofread delete:", refreshError);
      }
	  let successMsg = `All Chapter Proofreads for ${targetLang} deleted successfully.`;
      message.success(successMsg);
    } catch (error) {
      console.error("Error deleting proofreading:", error);
      message.error("Error deleting proofreading",10);
    } finally {
      setIsLoading(false);
    }
  };

const calculateWordCount = useCallback(
  (targetLang: string, aiModel: string, options?: { translatedChapterGuids?: Set<string> }): number => {
    if (!targetLang || targetLang === 'Original') return 0;

    if (selectedChapter) {
      let baseCount = selectedChapter.word_count || 0;
      if (aiModel === 'DeepSeek') {
        baseCount = baseCount * 0.5;
      }
      return Math.ceil(baseCount);
    }

    let translatedChapterGuids = options?.translatedChapterGuids;
    if (!translatedChapterGuids) {
      const translatedChaptersKey = `translated_chapters_${bookLanguage}_${targetLang}`;
      const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
      translatedChapterGuids = new Set(
        translatedChapters
          .filter((tChapter) => hasTranslatedContent(tChapter))
          .map((tChapter) => tChapter.chapter_guid)
      );
    }

    let totalCount = 0;
    for (const chapter of book.chapters) {
      if (translatedChapterGuids.has(chapter.chapter_guid)) {
        continue;
      }
      totalCount += chapter.word_count || 0;
    }

    if (aiModel === 'DeepSeek') {
      totalCount = totalCount * 0.5;
    }

    return Math.ceil(totalCount);
  },
  [selectedChapter, bookLanguage, book.chapters, book, hasTranslatedContent]
);

const calculateWordCountForTargets = useCallback(
  (targetLangs: string[], aiModel: string): number => {
    const normalizedTargets = normalizeTranslationTargets(targetLangs);
    if (!normalizedTargets.length) return 0;
    return normalizedTargets.reduce((total, lang) => total + calculateWordCount(lang, aiModel), 0);
  },
  [calculateWordCount, normalizeTranslationTargets],
);

useEffect(() => {
  //console.log("useEffect 1");
  //console.time('useEffect 1');
  const activeTargets =
    !selectedChapter && isTranslateModalVisible
      ? resolvedTranslationTargetLanguages
      : normalizeTranslationTargets([targetLanguage]);
  const wordCount = calculateWordCountForTargets(activeTargets, selectedAIModel);
  setUserHasEnoughCredits(creditBalance ? (creditBalance.available_balance ?? creditBalance.credit_balance) >= wordCount : false);
  //console.timeEnd('useEffect 1');
}, [
  creditBalance,
  selectedChapter,
  targetLanguage,
  isTranslateModalVisible,
  resolvedTranslationTargetLanguages,
  selectedAIModel,
  calculateWordCountForTargets,
  normalizeTranslationTargets,
]);

const handleGenerateBlurb = async () => {
  if (!auth.currentUser && !isSandboxFixtureMode) {
    console.error("No user logged in");
    return;
  }
  if(!isGenerateBlurbModalVisible) {
	  setIsGenerateBlurbModalVisible(true);
  }
  else {
	  setIsGenerateBlurbModalVisible(false);
  }
};


useEffect(() => {
  //console.log("useEffect 4");
  //console.time('useEffect 4');
    setCanTranslateTitle(!!book.title);
    setCanTranslateSeries(!!book.series);
    setCanTranslateBlurb(!!book.blurb);
	//console.timeEnd('useEffect 4');
  }, [book.title, book.series, book.blurb]);

const isAnyMetadataSelectedForTranslation = () => {
  return translateMetadata.title || translateMetadata.series || translateMetadata.blurb;
};

const exportTranslatedChaptersKey = React.useMemo(() => {
  const cleanVersion = stripProofreadLabel(exportVersion);
  if (!cleanVersion || cleanVersion === 'Original') return '';
  const { sourceLang, targetLang } = splitLanguagePair(cleanVersion);
  return sourceLang && targetLang ? `translated_chapters_${sourceLang}_${targetLang}` : '';
}, [exportVersion]);
const exportTranslatedChapters: TranslatedChapter[] = React.useMemo(
  () => exportTranslatedChaptersKey ? book[exportTranslatedChaptersKey] || [] : [],
  [book, exportTranslatedChaptersKey],
);

useEffect(() => {
	//console.log("useEffect 5");
	//console.time('useEffect 5');
  //console.log("exportVersion: "+exportVersion);
  if (exportVersion !== 'Original'&& !exportVersion.includes('Proofread')) {
    const { sourceLang, targetLang } = splitLanguagePair(exportVersion);
    if (!sourceLang || !targetLang) {
      setUntranslatedChapters([]);
      setUnproofreadChapters([]);
      setShowWarning(false);
      return;
    }
    const translatedChapters = exportTranslatedChapters;

    const untranslated = book.chapters
      .filter(isContentBearingChapterRow)
      .filter(
        (chapter) =>
          !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid)
      )
      .map((chapter) => chapter.chapter_name);

    setUntranslatedChapters(untranslated);
    setUnproofreadChapters([]);
    setShowWarning(untranslated.length > 0);
  }
  else if (exportVersion == 'Proofread') {
    const unproofreadChapters = book.chapters
      .filter(isContentBearingChapterRow)
      .filter(chapter => !hasSavedProofreadForRow(chapter))
      .map(chapter => chapter.chapter_name);

    setUnproofreadChapters(unproofreadChapters);
    setUntranslatedChapters([]);
    setShowWarning(unproofreadChapters.length > 0);
  }
  else if (exportVersion !== 'Original'&& exportVersion.includes('Proofread')) {
	  //console.log("Translated (Proofread)");
	const cleanExportVersion = stripProofreadLabel(exportVersion);
	const { sourceLang, targetLang } = splitLanguagePair(cleanExportVersion);
    if (!sourceLang || !targetLang) {
      setUntranslatedChapters([]);
      setUnproofreadChapters([]);
      setShowWarning(false);
      return;
    }
    const translatedChapters = exportTranslatedChapters;

    const untranslated = book.chapters
      .filter(isContentBearingChapterRow)
      .filter(
        (chapter) =>
          !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid)
      )
      .map((chapter) => chapter.chapter_name);

    const unproofreadChapters = book.chapters
      .filter(isContentBearingChapterRow)
      .filter((chapter) => {
        if (!isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid)) {
          return false;
        }
        const translatedChapter = translatedChapters.find(
          (tChapter) => tChapter.chapter_guid === chapter.chapter_guid
        );
        return !hasSavedProofreadForRow(translatedChapter);
      })
      .map((chapter) => chapter.chapter_name);
	//console.log("unproofreadChapters: "+unproofreadChapters);
    setUntranslatedChapters(untranslated);
    setUnproofreadChapters(unproofreadChapters);
    setShowWarning(unproofreadChapters.length > 0 || untranslated.length > 0);
  }
  else {
    setUntranslatedChapters([]);
    setUnproofreadChapters([]);
	setShowWarning(false);
  }
  //console.timeEnd('useEffect 5');
}, [exportVersion, book.chapters, book.translation_pairs, exportTranslatedChapters]);

useEffect(() => {
	//console.log("useEffect 6");
	//console.time('useEffect 6');
setProofreadVersionSafe(selectedTab);
//console.timeEnd('useEffect 6');
}, [selectedTab, setProofreadVersionSafe]);

const normalizedProofreadVersion = React.useMemo(
  () => normalizeProofreadVersion(proofreadVersion),
  [normalizeProofreadVersion, proofreadVersion],
);
const chapterListPendingReviewVersion = React.useMemo(() => {
  const currentPair = String(currentLanguagePair || '').trim();
  if (currentPair && currentPair.toLowerCase() !== 'blurb') {
    return normalizeProofreadVersion(currentPair.toLowerCase() === 'original' ? 'Original' : currentPair);
  }
  return normalizeProofreadVersion(selectedTab);
}, [currentLanguagePair, normalizeProofreadVersion, selectedTab]);
const proofreadLangParts = React.useMemo(() => {
  if (!normalizedProofreadVersion || normalizedProofreadVersion === 'Original') {
    return { sourceLang: '', targetLang: '' }
  }
  return splitLanguagePair(normalizedProofreadVersion)
}, [normalizedProofreadVersion])

const translatedChaptersByGuid = React.useMemo(() => {
  if (!proofreadLangParts.targetLang || !book) {
    return new Map<string, TranslatedChapter>()
  }
  const translatedChaptersKey = `translated_chapters_${proofreadLangParts.sourceLang}_${proofreadLangParts.targetLang}`
  const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || []
  const map = new Map<string, TranslatedChapter>()
  translatedChapters.forEach(chapter => {
    map.set(chapter.chapter_guid, chapter)
  })
  return map
}, [book, proofreadLangParts.sourceLang, proofreadLangParts.targetLang])

const fetchProofreadingPassSummary = React.useCallback(async (versionOverride?: string) => {
  const requestedVersion = normalizeProofreadVersion(versionOverride || proofreadVersion);
  if (isSandboxFixtureMode) {
    const fixtureSummary = sandboxFixturePayload?.proofreadingPassSummary || null;
    setProofreadingPassSummary(fixtureSummary as ProofreadingPassSummary | null);
    setIsLoadingProofreadingPassSummary(false);
    return fixtureSummary as ProofreadingPassSummary | null;
  }
  const summaryUserId = auth.currentUser?.uid || sharedUserId || '';
  if (!summaryUserId || !bookId || !bookLanguage || !requestedVersion) {
    setProofreadingPassSummary(null);
    return null;
  }
  setIsLoadingProofreadingPassSummary(true);
  try {
    const response = await bookDetailApiGet(`${API_BASE}/proofreading-passes/summary`, {
      params: {
        userId: summaryUserId,
        bookId,
        sourceLang: bookLanguage,
        proofreadVersion: requestedVersion,
      },
    });
    const summary = (response.data || null) as ProofreadingPassSummary | null;
    setProofreadingPassSummary(summary);
    return summary;
  } catch (error) {
    console.error('Error loading proofreading pass summary:', error);
    setProofreadingPassSummary(null);
    return null;
  } finally {
    setIsLoadingProofreadingPassSummary(false);
  }
}, [
  API_BASE,
  auth.currentUser?.uid,
  bookId,
  bookLanguage,
  isSandboxFixtureMode,
  normalizeProofreadVersion,
  proofreadVersion,
  sandboxFixturePayload,
  sharedUserId,
]);

const selectBulkProofreadModeFromSummary = React.useCallback((summary: ProofreadingPassSummary | null | undefined) => {
  const allowedModes = summary?.allowedModes || [];
  if (allowedModes.includes('retry_failed')) {
    setBulkProofreadMode('retry_failed');
  } else if (allowedModes.includes('missing')) {
    setBulkProofreadMode('missing');
  } else if (allowedModes.includes('next_pass')) {
    setBulkProofreadMode('next_pass');
  }
}, []);

const getBulkProofreadTargetGuidSet = React.useCallback((mode: ProofreadPassMode = bulkProofreadMode) => {
  const targetGuids = proofreadingPassSummary?.targetChapterGuidsByMode?.[mode];
  if (!Array.isArray(targetGuids)) {
    return null;
  }
  return new Set(targetGuids.map(guid => String(guid || '').trim()).filter(Boolean));
}, [bulkProofreadMode, proofreadingPassSummary]);

React.useEffect(() => {
  if (!isProofreadModalVisible || selectedChapter) {
    return;
  }
  void fetchProofreadingPassSummary(normalizedProofreadVersion);
}, [
  fetchProofreadingPassSummary,
  isProofreadModalVisible,
  normalizedProofreadVersion,
  selectedChapter,
]);

const invalidateProofreadConfirmation = React.useCallback(() => {
  proofreadPassRequestIdRef.current = null;
  setIsProofreadConfirmationModalVisible(false);
}, []);
useProofreadPassSelection({
  bookId, version: normalizedProofreadVersion, visible: isProofreadModalVisible,
  singleChapter: Boolean(selectedChapter), summary: proofreadingPassSummary,
  mode: bulkProofreadMode, setMode: setBulkProofreadMode,
  setChecked: setIsProofreadChecked, invalidate: invalidateProofreadConfirmation,
});

const pendingProofreadReviewChapterGuidsForSelectedTab = React.useMemo(() => {
  const pendingGuids = new Set<string>();
  if (chapterListPendingReviewVersion === 'Original' || !chapterListPendingReviewVersion) {
    (book.chapters || []).forEach((chapter) => {
      if (hasPendingProofreadReviewForRow(chapter)) {
        pendingGuids.add(String(chapter.chapter_guid || '').trim());
      }
    });
    return pendingGuids;
  }

  const { sourceLang, targetLang } = splitLanguagePair(chapterListPendingReviewVersion);
  if (!sourceLang || !targetLang) {
    return pendingGuids;
  }
  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  translatedChapters.forEach((chapter) => {
    if (hasPendingProofreadReviewForRow(chapter)) {
      pendingGuids.add(String(chapter.chapter_guid || '').trim());
    }
  });
  return pendingGuids;
}, [book, chapterListPendingReviewVersion, hasPendingProofreadReviewForRow]);

const bulkProofreadEligibility = React.useMemo(() => {
  const summaryTargetSet = getBulkProofreadTargetGuidSet();
  if (summaryTargetSet) {
    const pendingProofreadReviewCount = Number(proofreadingPassSummary?.pendingReviewCount || 0);
    return {
      eligibleProofreadChapterCount: summaryTargetSet.size,
      pendingProofreadReviewCount,
      hasEligibleProofreadChapters: summaryTargetSet.size > 0,
    };
  }

  const isOriginal = proofreadVersion === 'Original' || !proofreadVersion;
  let eligibleProofreadChapterCount = 0;
  let pendingProofreadReviewCount = 0;

  book.chapters.forEach((chapter) => {
    const translatedChapter = isOriginal
      ? null
      : translatedChaptersByGuid.get(chapter.chapter_guid);
    const hasTranslatedChapterContent = isOriginal ? true : hasTranslatedContent(translatedChapter);
    if (!hasTranslatedChapterContent) {
      return;
    }

    const proofreadRow = isOriginal ? chapter : translatedChapter;
    if (hasPendingProofreadReviewForRow(proofreadRow)) {
      pendingProofreadReviewCount += 1;
      return;
    }

    if (
      shouldSubmitBulkProofreadForRow(proofreadRow, {
        requiresTranslatedContent: !isOriginal,
        hasTranslatedContent: hasTranslatedChapterContent,
      })
    ) {
      eligibleProofreadChapterCount += 1;
    }
  });

  return {
    eligibleProofreadChapterCount,
    pendingProofreadReviewCount,
    hasEligibleProofreadChapters: eligibleProofreadChapterCount > 0,
  };
}, [
  book.chapters,
  getBulkProofreadTargetGuidSet,
  hasTranslatedContent,
  proofreadVersion,
  proofreadingPassSummary?.pendingReviewCount,
  translatedChaptersByGuid,
])

const getPendingProofreadReviewCountForVersion = React.useCallback((versionLabel: string) => {
  const normalizedVersion = normalizeProofreadVersion(versionLabel);
  if (!normalizedVersion || normalizedVersion === 'Original') {
    return (book.chapters || []).filter(chapter => hasPendingProofreadReviewForRow(chapter)).length;
  }
  const { sourceLang, targetLang } = splitLanguagePair(normalizedVersion);
  if (!sourceLang || !targetLang) {
    return 0;
  }
  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  return translatedChapters.filter(chapter => hasPendingProofreadReviewForRow(chapter)).length;
}, [book, normalizeProofreadVersion]);

const ensureProofreadTranslationAvailability = React.useCallback(async (
  versionLabel: string,
  chapterGuids?: string[],
): Promise<Map<string, TranslatedChapter>> => {
  const normalizedVersion = normalizeProofreadVersion(versionLabel);
  if (!normalizedVersion || normalizedVersion === 'Original') {
    return new Map<string, TranslatedChapter>();
  }

  const cleanVersion = normalizedVersion.replace('(Proofread)', '').trim();
  const { sourceLang, targetLang } = splitLanguagePair(cleanVersion);

  if (!sourceLang || !targetLang) {
    return new Map<string, TranslatedChapter>();
  }

  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const existingChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  const existingByGuid = new Map<string, TranslatedChapter>();
  existingChapters.forEach(chapter => {
    existingByGuid.set(chapter.chapter_guid, chapter);
  });

  const requestedChapters = chapterGuids?.length
    ? (book.chapters || []).filter(chapter => chapterGuids.includes(chapter.chapter_guid))
    : (book.chapters || []);
  const missingChapterGuids = getMissingProofreadTranslationGuids(
    requestedChapters,
    chapterGuid => hasTranslatedContent(existingByGuid.get(chapterGuid)),
  );

  if (!missingChapterGuids.length) {
    return existingByGuid;
  }

  proofreadTranslationHydrationCountRef.current += 1;
  setIsHydratingProofreadTranslations(true);

  try {
    const results = await Promise.allSettled(
      missingChapterGuids.map(chapterGuid => hydrateTranslatedChapter(
        chapterGuid,
        sourceLang,
        targetLang,
        { reason: 'proofread_preflight' },
      )),
    );

    const fetchedChapters: TranslatedChapter[] = [];
    results.forEach(result => {
      if (result.status !== 'fulfilled') {
        return;
      }
      const chapter = result.value;
      if (chapter?.chapter_guid && hasTranslatedContent(chapter)) {
        fetchedChapters.push(chapter);
      }
    });

    if (!fetchedChapters.length) {
      return existingByGuid;
    }

    const mergedByGuid = new Map(existingByGuid);
    fetchedChapters.forEach(chapter => {
      mergedByGuid.set(chapter.chapter_guid, {
        ...(mergedByGuid.get(chapter.chapter_guid) || {}),
        ...chapter,
      });
    });

    return mergedByGuid;
  } finally {
    proofreadTranslationHydrationCountRef.current = Math.max(
      0,
      proofreadTranslationHydrationCountRef.current - 1,
    );
    setIsHydratingProofreadTranslations(proofreadTranslationHydrationCountRef.current > 0);
  }
}, [book, book.chapters, hasTranslatedContent, hydrateTranslatedChapter, normalizeProofreadVersion]);

useEffect(() => {
  if (!isProofreadModalVisible) {
    return;
  }
  if (!normalizedProofreadVersion || normalizedProofreadVersion === 'Original') {
    return;
  }

  const chapterGuids = selectedChapter?.chapter_guid ? [selectedChapter.chapter_guid] : undefined;
  void ensureProofreadTranslationAvailability(normalizedProofreadVersion, chapterGuids);
}, [
  ensureProofreadTranslationAvailability,
  isProofreadModalVisible,
  normalizedProofreadVersion,
  selectedChapter?.chapter_guid,
]);

const checkProofreadChapters = React.useCallback(() => {
  const debugProofread = (() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true'
    } catch (_e) {
      return false
    }
  })()
  const debugStamp = (label: string) => {
    if (!debugProofread) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log(`[ProofreadModal] ${label} @ ${Math.round(now)}ms`)
  }
  debugStamp('checkProofreadChapters:start')
  const isOriginal = proofreadVersion === "Original" || !proofreadVersion;
  const translatedByGuid = isOriginal ? null : translatedChaptersByGuid;

  let proofreadChapters: string[] = [];
  let unproofreadChapters: string[] = [];
  let proofreadNotesChapters: string[] = [];
  let unproofreadNotesChapters: string[] = [];

  book.chapters.forEach(chapter => {
    const translatedChapter = translatedByGuid
      ? translatedByGuid.get(chapter.chapter_guid)
      : null;
    const hasTranslatedChapterContent = isOriginal ? true : hasTranslatedContent(translatedChapter);

    const isProofread = isOriginal
      ? hasSavedProofreadForRow(chapter)
      : hasTranslatedChapterContent && hasSavedProofreadForRow(translatedChapter);

    if (isProofread) {
      //console.log("chapter.chapter_name: ",chapter.chapter_name);
		  //console.log("translatedChapter.proofread_text: ",translatedChapter?.proofread_text);
		  proofreadChapters.push(chapter.chapter_name);
    } else {
      unproofreadChapters.push(chapter.chapter_name);
    }

    const hasEditingNotes = isOriginal
      ? Boolean(chapter.proofread_comments)
      : hasTranslatedChapterContent && Boolean(translatedChapter?.proofread_comments);
    if (hasEditingNotes) {
      proofreadNotesChapters.push(chapter.chapter_name);
    } else {
      unproofreadNotesChapters.push(chapter.chapter_name);
    }
  });

  setUnproofreadChapters(unproofreadChapters);
  setHasUnproofreadChapters(unproofreadChapters.length > 0);
  setHasProofreadChapters(proofreadChapters.length > 0);
  setUnproofreadNotesChapters(unproofreadNotesChapters);
  setHasUnproofreadNotesChapters(unproofreadNotesChapters.length > 0);
  setHasProofreadNotesChapters(proofreadNotesChapters.length > 0);

  debugStamp('checkProofreadChapters:end')
  //console.log("hasProofreadChapters:",hasProofreadChapters);
}, [book.chapters, proofreadVersion, translatedChaptersByGuid, hasTranslatedContent]);

useEffect(() => {
  //console.log("useEffect 7");
  //console.time('useEffect 7');
  checkProofreadChapters();
  //console.timeEnd('useEffect 7');
}, [checkProofreadChapters]);

const coerceLanguageValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const normalized = value.replace(/0/g, ' ').trim();
    return normalized || null;
  }
  if (value && typeof value === 'object') {
    const candidate =
      (typeof (value as any).value === 'string' && (value as any).value) ||
      (typeof (value as any).label === 'string' && (value as any).label) ||
      (typeof (value as any).language === 'string' && (value as any).language) ||
      (typeof (value as any).lang === 'string' && (value as any).lang);
    if (typeof candidate === 'string') {
      const normalized = candidate.replace(/0/g, ' ').trim();
      return normalized || null;
    }
  }
  return null;
};

const normalizeLanguageDefaultMode = (value: unknown): LanguageDefaultMode => {
  if (value === 'empty' || value === 'context' || value === 'specific') {
    return value;
  }
  return 'specific';
};

const resolveTranslationPairForLanguage = (language: string): string => {
  const cleanTarget = (language || '').trim();
  if (!cleanTarget) {
    return '';
  }
  return findTranslationPairForTargetLanguage(
    book.translation_pairs || [],
    (bookLanguage || book.language || '').trim(),
    cleanTarget,
  );
};

type ModalDefaultSelectionType = 'translation' | 'proofread' | 'export';

const resolveModalDefaultSelection = async (
  modalType: ModalDefaultSelectionType,
  options?: {
    preferredLanguage?: unknown;
    preferredVersion?: unknown;
  },
): Promise<string> => {
  const preferredLanguage = coerceLanguageValue(options?.preferredLanguage) || '';
  const preferredVersion =
    options?.preferredVersion == null ? '' : normalizeProofreadVersion(options.preferredVersion);
  const contextTargetLanguage = getTargetLanguageFromTab(selectedTab);
  const contextVersion = selectedTab && selectedTab !== 'Original' ? selectedTab : '';
  const sourceLanguage = (bookLanguage || book.language || '').trim();
  const isValidTarget = (candidate: string): boolean =>
    Boolean(candidate && candidate.trim() && candidate.trim() !== sourceLanguage);

  if (modalType === 'translation' && isValidTarget(preferredLanguage)) {
    return preferredLanguage.trim();
  }
  if (modalType === 'proofread' && preferredVersion) {
    return preferredVersion;
  }
  if (modalType === 'proofread') {
    if (selectedTab === 'Original') {
      return 'Original';
    }
    if (contextVersion) {
      return contextVersion;
    }
  }
  if (isSandboxFixtureMode && modalType === 'export') {
    if (selectedTab === 'Original') {
      return 'Original';
    }
    if (contextVersion) {
      return contextVersion;
    }
  }

  let preference = defaultLanguagePreferenceRef.current;
  if (!preference) {
    try {
      preference = await getLanguageDefaultPreference();
    } catch (_error) {
      preference = { language: '', mode: 'specific' };
    }
  }

  const mode = preference?.mode || defaultLanguageMode || 'specific';
  const specificLanguage = coerceLanguageValue(preference?.language) || '';

  if (mode === 'empty') {
    return '';
  }

  if (modalType === 'translation') {
    if (mode === 'context') {
      return isValidTarget(contextTargetLanguage) ? contextTargetLanguage : '';
    }
    return isValidTarget(specificLanguage) ? specificLanguage : '';
  }

  if (modalType === 'proofread') {
    if (mode === 'context') {
      return contextVersion || '';
    }
    if (!specificLanguage) {
      return '';
    }
    return resolveTranslationPairForLanguage(specificLanguage);
  }

  if (mode === 'context') {
    return contextVersion || '';
  }
  if (!specificLanguage) {
    return '';
  }
  return resolveTranslationPairForLanguage(specificLanguage);
};

const prepareTargetLanguageForModal = async (preferredTarget?: unknown) => {
  const resolved = await resolveModalDefaultSelection('translation', {
    preferredLanguage: preferredTarget,
  });
  let normalizedTargets = normalizeTranslationTargets(resolved ? [resolved] : []);
  setTranslationTargetLanguages(normalizedTargets);
  const primaryTarget = normalizedTargets[0] || '';
  setTargetLanguage(primaryTarget);
  setBetaLanguageWarning(normalizedTargets.some(lang => isLanguageBeta(lang)));
  if (!primaryTarget) {
    return '';
  }
  if (hasQuotationOptions(primaryTarget)) {
    const options = getQuotationStyle(primaryTarget)?.options || [];
    const hasValidStyle = options.some(opt => opt.value === quotationStyle);
    if (!hasValidStyle) {
      setQuotationStyle(getDefaultQuotationStyle(primaryTarget));
    }
  }
  scheduleTranslationStatsUpdate(primaryTarget, selectedAIModel);
  return primaryTarget;
};

const resolveMetadataTranslatePreferredTarget = React.useCallback(
  (preferredTarget?: unknown): string => {
    const preferred = coerceLanguageValue(preferredTarget) || '';
    if (preferred) {
      return preferred;
    }
    if (!metadataLanguage || metadataLanguage === 'original') {
      return '';
    }
    const { targetLang } = splitLanguagePair(metadataLanguage);
    const normalizedTarget = (targetLang || '').trim();
    const normalizedSource = (bookLanguage || book.language || '').trim();
    if (!normalizedTarget || normalizedTarget === normalizedSource) {
      return '';
    }
    return normalizedTarget;
  },
  [book.language, bookLanguage, metadataLanguage],
);

const toggleTranslateModal = async (preferredTargetLanguage?: string): Promise<void> => {
  if (isToggling) return;
  setIsToggling(true);

  if (isTranslateModalVisible) {
    // Simply close
    setFrenchTone('');
    setIsTranslateModalVisible(false);
    setIsToggling(false);
    return;
  }

  setFrenchTone('');

  const finalizeToggleReset = () => {
    setTimeout(() => {
      setIsToggling(false);
    }, 300);
  };

  const refreshCreditBalanceForTranslateModal = () => {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      return;
    }

    void fetchCreditBalanceCached(userId, { force: true, silent: true }).catch((error) => {
      console.error('Error refreshing credit balance:', error);
      message.error('Unable to refresh your credit balance. Displayed credits may be outdated.', 8);
    });
  };

  const formatTranslateModalError = (error: unknown): string => {
    if (isBookDetailApiError(error)) {
      const status = error.response?.status;
      const statusText = error.response?.statusText;
      const code = error.code;
      const parts = [status ? `status ${status}` : null, statusText, code].filter(Boolean);
      if (parts.length) {
        return parts.join(', ');
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'unknown error';
  };

  const logTranslateModalError = async (error: unknown) => {
    const userId = auth.currentUser?.uid ?? sharedUserId ?? null;
    if (!userId) {
      return;
    }

    try {
      const headers = await getAuthHeaders(auth.currentUser ?? undefined);
      const payload = {
        event: 'translate_modal_open_failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        details: formatTranslateModalError(error),
        stack: error instanceof Error ? error.stack : null,
        userId,
        bookId,
        chapterGuid: selectedChapter?.chapter_guid ?? null,
        route: window.location.pathname,
        context: {
          targetLanguage,
          translateVersion,
          hasOriginalProofreadComplete,
        },
      };
      await bookDetailApiPost(`${API_BASE}/ui/log-client-error`, payload, { headers });
    } catch (logError) {
      console.warn('Failed to log translate modal error:', logError);
    }
  };

  try {
    const targetPreparation = prepareTargetLanguageForModal(preferredTargetLanguage).catch((error) => {
      console.error('Unable to prepare translation target language:', error);
      void logTranslateModalError(error);
    });
    const nextTranslateVersion = selectedChapter
      ? (selectedChapter.proofread_chapter ? 'Proofread' : 'Original')
      : (hasOriginalProofreadComplete ? 'Proofread' : 'Original');
    setTranslateVersion(nextTranslateVersion);

    const upcomingCreditCost = getCreditCost();
    setCreditCost(upcomingCreditCost);
    setIsTranslateModalVisible(true);
    refreshCreditBalanceForTranslateModal();
    void targetPreparation;
  } catch (error) {
    console.error('Unable to open translate modal:', error);
    void logTranslateModalError(error);
    message.error('Something went wrong while preparing translation settings. Please try again.', 8);
  } finally {
    finalizeToggleReset();
  }
};

const handleOpenTranslateForLanguage = useCallback(
  (language: string) => {
    void toggleTranslateModal(language);
  },
  [toggleTranslateModal],
);

const flagLanguages = useMemo(
  () => languages.filter(lang => lang && canonicalizeLanguageKey(lang) !== canonicalizeLanguageKey(book.language)),
  [languages, book.language],
);

const normalizedTranslationProgress = useMemo(
  () => canonicalizeTranslationProgressMap(book.translation_progress),
  [book.translation_progress],
);

type TranslationTrackerData = TranslationMilestoneTrackerProps;

const filteredTranslationProgress = useMemo<TranslationProgressMap | undefined>(() => {
  if (!normalizedTranslationProgress) return undefined;
  if (!flagLanguages.length) return undefined;
  const allowed = new Set(flagLanguages.map(language => canonicalizeLanguageKey(language)));
  const entries: TranslationProgressMap = {};
  Object.entries(normalizedTranslationProgress).forEach(([language, progress]) => {
    if (allowed.has(canonicalizeLanguageKey(language))) {
      entries[language] = progress;
    }
  });
  return Object.keys(entries).length ? entries : undefined;
}, [normalizedTranslationProgress, flagLanguages]);

const hasLanguageFlagData = useMemo(() => {
  const translatedLanguages = Object.keys(filteredTranslationProgress || {});
  return flagLanguages.length > 0 || translatedLanguages.length > 0;
}, [flagLanguages, filteredTranslationProgress]);

const shouldDisplayLanguageFlags = showTranslatedFlags && hasLanguageFlagData;

const translationTrackerData = useMemo<TranslationTrackerData | null>(() => {
  if (!selectedTab || selectedTab === 'Original' || !selectedTab.includes('-')) {
    return null;
  }
  const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
  if (!sourceLang || !targetLang) {
    return null;
  }
  const trackerLanguage = canonicalizeLanguageName(targetLang) || targetLang;

  const translationEntry = normalizedTranslationProgress?.[trackerLanguage];
  const chapterCount = Array.isArray(book.chapters) ? book.chapters.length : 0;
  const totalChapters =
    translationEntry && translationEntry.total_chapters > 0
      ? translationEntry.total_chapters
      : chapterCount;

  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  const fallbackTranslated = translatedChapters.filter(
    chapter => (chapter.translated_word_count || 0) > 0 || Boolean(chapter.translated_text),
  ).length;

  const translatedCount = Math.min(
    Math.max(totalChapters, chapterCount),
    Math.max(translationEntry?.translated_chapters ?? 0, fallbackTranslated),
  );

  const proofreadCount = Math.min(
    Math.max(totalChapters, chapterCount),
    translatedChapters.filter(chapter => hasCompletedProofreadProgressForRow(chapter)).length,
  );
  const pendingProofreadReviewCount = translatedChapters.filter(
    chapter => hasPendingProofreadReviewForRow(chapter),
  ).length;
  const coverStatus = Boolean(
    languageCovers?.[targetLang] || languageCovers?.[targetLang.toLowerCase()],
  );
  const hasTranslatedMetadataValue = (value: unknown) => !isMissingTranslatedMetadataValue(value);
  const glossaryTermCount = [
    resolveBookTerms(neverTranslateWords, selectedTab),
    resolveBookTerms(alwaysTranslateWords, selectedTab),
    resolveSeriesTerms(seriesTranslateRules.neverTranslate, targetLang, selectedTab),
    resolveSeriesTerms(seriesTranslateRules.alwaysTranslate, targetLang, selectedTab),
    resolveAccountTerms(accountNeverTranslateWords, targetLang),
    resolveAccountTerms(accountAlwaysTranslateWords, targetLang),
  ].reduce((count, terms) => count + terms.length, 0);

  return {
    language: trackerLanguage,
    glossaryTermCount,
    totalChapters: Math.max(totalChapters, chapterCount),
    translatedCount,
    proofreadCount,
    pendingProofreadReviewCount,
    metadataStatus: {
      title: hasTranslatedMetadataValue(titleTranslations[selectedTab]),
      series: book.series ? hasTranslatedMetadataValue(seriesTranslations[selectedTab]) : true,
      blurb: hasTranslatedMetadataValue(blurbTranslations[selectedTab]),
    },
    keywordCount: keywordTranslations[selectedTab]?.length ?? 0,
    coverStatus,
    audiobookStatus: false,
  };
}, [
  selectedTab,
  book,
  normalizedTranslationProgress,
  blurbTranslations,
  keywordTranslations,
  seriesTranslations,
  titleTranslations,
  languageCovers,
  alwaysTranslateWords,
  neverTranslateWords,
  accountAlwaysTranslateWords,
  accountNeverTranslateWords,
  seriesTranslateRules.alwaysTranslate,
  seriesTranslateRules.neverTranslate,
]);


const navigateToAudiobookEdit = () => {
  navigate(`/audiobook/${bookId}`);
};

const toggleAudiobookModal = () => {
  if (isToggling) return;
  setIsToggling(true);

  if (isAudiobookModalVisible) {
    setIsAudiobookModalVisible(false);
    setIsToggling(false);
  } else {
    // Reset selected version and fetch credit balance
    setSelectedAudiobookVersion('Original');
    if (auth.currentUser) {
      fetchCreditBalance(auth.currentUser.uid);
    }

    // Calculate all available versions
    const versionOptions = [
      { value: 'Original', label: originalLanguageLabel }
    ];

    // Add original proofread if available
    if (hasOriginalProofreadComplete) {
      versionOptions.push({ value: 'Original-Proofread', label: `${originalLanguageLabel} (Proofread)` });
    }

    // Add translations and their proofread versions
    if (book.translation_pairs) {
      book.translation_pairs.forEach(pair => {
        const { sourceLang, targetLang } = splitLanguagePair(pair);
        if (!sourceLang || !targetLang) {
          return;
        }

        // Add regular translation
        versionOptions.push({
          value: `${sourceLang}-${targetLang}`,
          label: `${sourceLang} to ${targetLang}`
        });

        // Check if proofread translation is available
        const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
        const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
        const hasProofreadTranslation = translatedChapters.some(chapter => hasSavedProofreadForRow(chapter));

        // Add proofread translation if available
        if (hasProofreadTranslation) {
          versionOptions.push({
            value: `${sourceLang}-${targetLang}-Proofread`,
            label: `${sourceLang} to ${targetLang} (Proofread)`
          });
        }
      });
    }

    setAudiobookVersionOptions(versionOptions);
    setIsAudiobookModalVisible(true);

    setTimeout(() => {
      setIsToggling(false);
    }, 300);
  }
};

const toggleProofreadModal = async (
  nextVersion?: string,
  defaultActions?: { proofread?: boolean; editingNotes?: boolean },
) => {
  const debugProofread = (() => {
    try {
      return localStorage.getItem('debugProofreadModal') === 'true'
    } catch (_e) {
      return false
    }
  })()
  const debugStamp = (label: string) => {
    if (!debugProofread) return
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    console.log(`[ProofreadModal] ${label} @ ${Math.round(now)}ms`)
  }

  debugStamp('toggleProofreadModal:start')
  if (isToggling) return;

  setIsToggling(true);

  if (isProofreadModalVisible) {
    debugStamp('toggleProofreadModal:close')
    setFrenchTone('');
    setIsProofreadModalVisible(false);
    setTimeout(() => {
      setIsToggling(false);
    }, 300);
    return;
  }

  debugStamp('toggleProofreadModal:open:init')
  setFrenchTone('');
  let resolvedProofreadVersion = '';
  try {
    resolvedProofreadVersion = await resolveModalDefaultSelection('proofread', {
      preferredVersion: nextVersion,
    });
  } catch (error) {
    console.error('Error resolving proofread default selection:', error);
  }
  const shouldDefaultProofreadActionForTutorial = Boolean(
    isSandboxFixtureMode
    && [
      'translated-proofread-ready-book',
      'translated-proofread-review-book',
      'translated-proofread-second-pass-book',
    ].includes(tutorialSandbox.fixtureId)
  );
  const resolvedProofreadTargetLanguage = resolvedProofreadVersion === 'Original'
    ? bookLanguage
    : splitLanguagePair(resolvedProofreadVersion).targetLang || '';
  if (isSandboxFixtureMode) {
    const fixtureSummary = (sandboxFixturePayload?.proofreadingPassSummary || null) as ProofreadingPassSummary | null;
    setProofreadingPassSummary(fixtureSummary);
    setIsLoadingProofreadingPassSummary(false);
    selectBulkProofreadModeFromSummary(fixtureSummary);
  }
  setProofreadVersionSafe(resolvedProofreadVersion);
  setQuotationStyle(getDefaultQuotationStyle(resolvedProofreadTargetLanguage) || '');
  setSelectedProofreadModel('');
  setIsProofreadChecked(Boolean(defaultActions?.proofread ?? shouldDefaultProofreadActionForTutorial));
  setIsEditingNotesChecked(Boolean(defaultActions?.editingNotes));
  setProofreadAdditionalInstructions("");
    if (auth.currentUser && !isProofreadModalVisible) {
      debugStamp('toggleProofreadModal:fetchCreditBalance')
      fetchCreditBalance(auth.currentUser.uid);
    }
  debugStamp('toggleProofreadModal:show')
  setIsProofreadModalVisible(true);

  setTimeout(() => {
    setIsToggling(false); // Reset toggling state after a short delay
  }, 300);
};

const handleMetadataTranslation = async (
  overwriteConfirmed: boolean = false,
  preferredTargetLanguage?: string,
) => {
  const tutorialFlow = tutorialUiSuppressionRequested;
  if (!auth.currentUser && !tutorialFlow) {
    console.error("No user logged in");
    return;
  }

  let resolvedTargetLanguage = (preferredTargetLanguage || targetLanguage || '').trim();
  if (!resolvedTargetLanguage) {
    const metadataPreferredTarget = resolveMetadataTranslatePreferredTarget();
    resolvedTargetLanguage = await prepareTargetLanguageForModal(metadataPreferredTarget || undefined);
  }
  if (!resolvedTargetLanguage) {
    message.error('Please select a target language before translating metadata.', 8);
    return;
  }
  if (resolvedTargetLanguage !== targetLanguage) {
    setTargetLanguage(resolvedTargetLanguage);
  }

  const overwriteFields = getMetadataOverwriteFields(bookLanguage, resolvedTargetLanguage, translateMetadata);
  if (!overwriteConfirmed && overwriteFields.length > 0) {
    const fieldList = overwriteFields.join(', ');
    Modal.confirm({
      rootClassName: 'theme-aware-confirm',
      title: `Overwrite ${resolvedTargetLanguage} metadata?`,
      content: `The following translated fields already exist in ${resolvedTargetLanguage} and will be replaced: ${fieldList}.`,
      okText: 'Overwrite and Translate',
      cancelText: 'Cancel',
      onOk: () => {
        void handleMetadataTranslation(true, resolvedTargetLanguage);
      },
    });
    return;
  }

  setTargetMetadataLanguage(resolvedTargetLanguage);
  setMetadataTranslationOverwriteApproved(overwriteConfirmed);
  setIsLoading(true);
  void toggleMetadataTranslateModal();

  const languagePairKey = `${bookLanguage}-${resolvedTargetLanguage}`;

  if (tutorialFlow) {
    try {
      if (translateMetadata.blurb) {
        const sampleBlurbByLanguage: Record<string, string> = {
          german:
            'Als Archivarin Mara Vale einer verschwundenen Karte zum Glashafen folgt, entdeckt sie eine Stadt, in der jede Tür in eine andere Welt führt. Doch die Karte ist nur der Anfang: Jemand hat den Hafen absichtlich verborgen, und Mara muss entscheiden, welchen Geheimnissen sie trauen kann.',
          polish:
            'W wiktoriańskiej Anglii ekscentryczny naukowiec konstruuje niezwykły wehikuł czasu i wyrusza w odległą przyszłość, gdzie odkrywa pozornie łagodnych Elojów oraz mrocznych Morloków kryjących się pod ziemią. Gdy traci swoją maszynę, musi przetrwać w świecie pełnym zagrożeń i znaleźć drogę powrotną do własnej epoki.',
          french:
            'Dans l’Angleterre victorienne, un inventeur visionnaire met au point une machine capable de traverser les siècles. Son expédition vers un futur lointain révèle deux humanités opposées: les Eloïs, fragiles et insouciants, et les Morlocks, tapis dans l’ombre. Privé de son invention, il devra lutter pour survivre et revenir à son époque.',
          spanish:
            'En la Inglaterra victoriana, un inventor brillante crea una máquina capaz de viajar en el tiempo y se adentra en un futuro remoto. Allí descubre a los delicados Eloi y a los amenazantes Morlocks, que acechan bajo la superficie. Tras perder su máquina, deberá luchar por sobrevivir y encontrar el camino de regreso.',
        };
        const fallbackBlurb =
          sampleBlurbByLanguage[resolvedTargetLanguage.toLowerCase()] ||
          `${book.title || 'The Time Machine'} translation preview in ${resolvedTargetLanguage}.`;

        setBlurbTranslations(prevTranslations => ({
          ...prevTranslations,
          [languagePairKey]: fallbackBlurb,
        }));
        setBook(prevBook => ({
          ...prevBook,
          [`blurb_${bookLanguage.replace(/ /g, '0')}_${resolvedTargetLanguage.replace(/ /g, '0')}`]: fallbackBlurb,
        }));
      }

      const demoTranslationOptions: TranslationOptions = {};
      const titleSource = (book.title || 'The Time Machine').trim();
      const normalizedTargetLanguage = resolvedTargetLanguage.toLowerCase();
      if (translateMetadata.title) {
        const titleOptionsByLanguage: Record<string, TranslationOption[]> = {
          german: [
            {
              translatedText: 'Der Glashafen',
              sourceTrans: titleSource,
              desc: 'Direct and natural, preserving the title image while reading like a German fantasy title.',
            },
            {
              translatedText: 'Der Hafen aus Glas',
              sourceTrans: titleSource,
              desc: 'A slightly more literal option that emphasizes the physical image of the harbor.',
            },
            {
              translatedText: 'Hafen der Spiegel',
              sourceTrans: titleSource,
              desc: 'A more atmospheric option that leans into mystery over literal title continuity.',
            },
          ],
          polish: [
            {
              translatedText: 'Wehikuł czasu',
              sourceTrans: titleSource,
              desc: 'Faithful translation that preserves the classic, recognizable meaning.',
            },
            {
              translatedText: 'Podróż przez czas',
              sourceTrans: titleSource,
              desc: 'A more dramatic option emphasizing the adventure and momentum of the story.',
            },
            {
              translatedText: 'Maszyna czasu',
              sourceTrans: titleSource,
              desc: 'A concise option focused on the invention itself and its science-fiction tone.',
            },
          ],
        };
        demoTranslationOptions.titleOptions = titleOptionsByLanguage[normalizedTargetLanguage] || [
          {
            translatedText: titleSource,
            sourceTrans: titleSource,
            desc: `Sample option for ${resolvedTargetLanguage}.`,
          },
          {
            translatedText: `${titleSource} (${resolvedTargetLanguage})`,
            sourceTrans: titleSource,
            desc: 'Alternate sample option for tutorial preview.',
          },
          {
            translatedText: `Chronicles of Time`,
            sourceTrans: titleSource,
            desc: 'Stylized sample option for tutorial preview.',
          },
        ];
      }
      if (translateMetadata.series) {
        const seriesSource = (book.series || 'Chronicles of Time').trim();
        const seriesOptionsByLanguage: Record<string, TranslationOption[]> = {
          german: [
            {
              translatedText: 'Sternenlicht-Archiv',
              sourceTrans: seriesSource,
              desc: 'Keeps the series branding recognizable while localizing the compound phrase.',
            },
            {
              translatedText: 'Archiv des Sternenlichts',
              sourceTrans: seriesSource,
              desc: 'A smoother German phrase that sounds more literary but slightly changes the brand shape.',
            },
            {
              translatedText: 'Die Sternenlicht-Archive',
              sourceTrans: seriesSource,
              desc: 'A pluralized series-style option that can work well across multiple books.',
            },
          ],
          polish: [
            {
              translatedText: 'Kroniki czasu',
              sourceTrans: seriesSource,
              desc: 'Balanced and clear, preserving the original meaning.',
            },
            {
              translatedText: 'Opowieści z przyszłości',
              sourceTrans: seriesSource,
              desc: 'More evocative and literary while staying genre-appropriate.',
            },
            {
              translatedText: 'Saga podróży w czasie',
              sourceTrans: seriesSource,
              desc: 'Brand-friendly option suitable for multi-book discovery.',
            },
          ],
        };
        demoTranslationOptions.seriesOptions = seriesOptionsByLanguage[normalizedTargetLanguage] || [
          {
            translatedText: seriesSource,
            sourceTrans: seriesSource,
            desc: `Sample option for ${resolvedTargetLanguage}.`,
          },
          {
            translatedText: `${seriesSource} (${resolvedTargetLanguage})`,
            sourceTrans: seriesSource,
            desc: 'Alternate sample option for tutorial preview.',
          },
          {
            translatedText: 'Time Travel Saga',
            sourceTrans: seriesSource,
            desc: 'Brand-friendly sample option for tutorial preview.',
          },
        ];
      }

      setTranslationOptions(demoTranslationOptions);
      if (translateMetadata.title || translateMetadata.series) {
        setIsLoading(false);
        setIsTranslationOptionsModalVisible(true);
      } else {
        setMetadataTranslationOverwriteApproved(false);
        setIsLoading(false);
      }
    } catch (tutorialError) {
      console.error('Error generating tutorial metadata translation preview:', tutorialError);
      message.error('Unable to prepare metadata translation preview.', 8);
      setIsLoading(false);
    }
    return;
  }

  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.error("No user logged in");
    setIsLoading(false);
    return;
  }

  try {
    const translationPromises: Promise<any>[] = [];
    const translationTypes: string[] = [];

    // Translate Blurb
    if (translateMetadata.blurb) {
      translationPromises.push(bookDetailApiPost(`${API_BASE}/translate-blurb/`, {
        userId,
        bookId: bookId,
        sourceLang: bookLanguage,
        targetLang: resolvedTargetLanguage,
		model: selectedMetadataModel,
		genre: genres.join(', '),
		intendedAudience: intendedAudience,
		quotationStyle: quotationStyle,
        tone: resolvedTargetLanguage === 'French' ? frenchTone : '',
        overwriteFlag: overwriteConfirmed,
      }));
      translationTypes.push('blurb');
    }

    const metadataOptionFields: Array<'title' | 'series'> = [];
    if (translateMetadata.title) metadataOptionFields.push('title');
    if (translateMetadata.series) metadataOptionFields.push('series');
    if (metadataOptionFields.length > 0) {
      translationPromises.push(bookDetailApiPost(`${API_BASE}/books/${bookId}/metadata-options`, {
        userId,
        sourceLanguage: bookLanguage,
        targetLanguage: resolvedTargetLanguage,
        fields: metadataOptionFields,
        forceRegenerate: metadataOptionFields.some(field =>
          metadataOptionFreeRunConsumed(field, bookLanguage, resolvedTargetLanguage)
        ),
        model: selectedMetadataModel || undefined,
        intendedAudience,
        instructions: (metadataAiInstructions || '').slice(0, 300),
        tone: resolvedTargetLanguage === 'French' ? frenchTone : '',
      }));
      translationTypes.push('metadataOptions');
    }

    const results = await Promise.all(translationPromises);
    const newTranslationOptions: TranslationOptions = {};
    let blurbTranslationSucceeded = false;

    results.forEach((response, index) => {
      const translationType = translationTypes[index];
      switch (translationType) {
        case 'blurb':
          if (response.data.status === 'success') {
            blurbTranslationSucceeded = true;
            setBlurbTranslations(prevTranslations => ({
              ...prevTranslations,
              [languagePairKey]: response.data.translated_blurb,
            }));
            message.success('Blurb translated successfully');
          } else {
            message.error(`Blurb translation failed: ${response.data.message}`, 10);
          }
          break;
        case 'metadataOptions':
          newTranslationOptions.titleOptions = Array.isArray(response.data?.titleOptions)
            ? response.data.titleOptions
            : [];
          newTranslationOptions.seriesOptions = Array.isArray(response.data?.seriesOptions)
            ? response.data.seriesOptions
            : [];
          break;
        default:
          // Handle unknown type if necessary
          break;
      }
    });

    setTranslationOptions(newTranslationOptions);
    if (blurbTranslationSucceeded) {
      fetchBookDetails({ silent: Boolean(translateMetadata.title || translateMetadata.series) });
    }

	//message.success('Metadata translated successfully');

    if (translateMetadata.title || translateMetadata.series) {
      setIsLoading(false);
	  setIsTranslationOptionsModalVisible(true);
    }
	else {
      if (!blurbTranslationSucceeded) {
	    fetchBookDetails();
      }
      setMetadataTranslationOverwriteApproved(false);
	  setIsLoading(false);
	}

  } catch (error) {
    const detail = getMetadataOverwriteDetail(error);
    const detailMessage =
      typeof detail === 'string'
        ? detail
        : detail?.message || (error instanceof Error ? error.message : '');
    setMetadataTranslationOverwriteApproved(false);
    if (!overwriteConfirmed && isMetadataOverwriteRequiredError(error)) {
      setIsLoading(false);
      Modal.confirm({
        rootClassName: 'theme-aware-confirm',
        title: `Overwrite ${resolvedTargetLanguage} metadata?`,
        content: detailMessage || `Translated metadata already exists in ${resolvedTargetLanguage}.`,
        okText: 'Overwrite and Translate',
        cancelText: 'Cancel',
        onOk: () => {
          void handleMetadataTranslation(true, resolvedTargetLanguage);
        },
      });
      return;
    }
    console.error("Error translating metadata:", error);
    message.error(detailMessage || "Error translating metadata",10);
	setIsLoading(false);
  }
};


const parseTranslationOptions = (xmlString: string): TranslationOption[] => {
  const validXmlString = xmlString
    .replace(/&/g, '&amp;')  // Replace & with &amp;
    .replace(/'/g, '&apos;') // Replace ' with &apos;
    .trim();
  //console.log("validXmlString: "+validXmlString);
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(validXmlString, "text/xml");

  if (xmlDoc.getElementsByTagName("parsererror").length) {
    console.error("Error parsing XML string");
    return [];
  }

  const options: TranslationOption[] = [];
  const xmlOptions = xmlDoc.getElementsByTagName("option");

  for (let i = 0; i < xmlOptions.length; i++) {
    const translatedTitleNode = xmlOptions[i].getElementsByTagName("translatedtitle")[0];
    const sourceTransNode = xmlOptions[i].getElementsByTagName("sourceTrans")[0];
    const descNode = xmlOptions[i].getElementsByTagName("desc")[0];

    options.push({
      translatedText: translatedTitleNode?.textContent || '', // Fallback to empty string if null
      sourceTrans: sourceTransNode?.textContent || '', // Fallback to empty string if null
      desc: descNode?.textContent || '' // Fallback to empty string if null
    });
  }

  return options;
};

  // Function to toggle metadata translation modal
  const toggleMetadataTranslateModal = async (preferredTargetLanguage?: string) => {

    if (isToggling) return; // Prevent toggling if already in process

    setIsToggling(true);
    setSelectedMetadataModel(tutorialUiSuppressionRequested ? preferredTranslationModelId : "");

    // If closing, just close
    if (isMetadataTranslateModalVisible) {
      setFrenchTone('');
      setIsMetadataIsTranslateModalVisible(false);
      setTimeout(() => {
        setIsToggling(false);
      }, 300);
      return;
    }

    setFrenchTone('');
    setMetadataTranslationOverwriteApproved(false);
    const resolvedPreferredTarget = resolveMetadataTranslatePreferredTarget(preferredTargetLanguage);
    await prepareTargetLanguageForModal(resolvedPreferredTarget || undefined);

    // We're opening: enforce saved blurb if it has changed
    const savedBlurb = book.blurb || '';
    const currentBlurb = blurb || '';

    const finishToggleReset = () => {
      setTimeout(() => {
        setIsToggling(false); // Reset toggling state after a short delay
      }, 300);
    };

    const openModal = async () => {
      try {
        if (auth.currentUser && !tutorialUiSuppressionRequested) {
          await fetchCreditBalanceCached(auth.currentUser.uid, { force: true });
        }
        setTargetMetadataLanguage(resolvedPreferredTarget || '');
        setIsMetadataIsTranslateModalVisible(true);
        setTranslateMetadata(tutorialUiSuppressionRequested
          ? {
              title: Boolean(canTranslateTitle),
              series: Boolean(canTranslateSeries),
              blurb: Boolean(canTranslateBlurb),
            }
          : { title: false, series: false, blurb: false });
      } finally {
        finishToggleReset();
      }
    };

    if (currentBlurb !== savedBlurb) {
      if (tutorialUiSuppressionRequested) {
        setBook(prev => ({ ...prev, blurb: currentBlurb }));
        await openModal();
        return;
      }
      Modal.confirm({
        rootClassName: 'theme-aware-confirm',
        title: 'Save blurb before translating?',
        content: 'You have unsaved changes to your blurb. Please save your blurb before translating metadata to ensure the correct text is used.',
        okText: 'Save and Continue',
        cancelText: 'Cancel',
        okButtonProps: { className: 'app-primary-cta', 'data-testid': 'tutorial:translation:save-blurb-confirm-ok' } as any,
        cancelButtonProps: { 'data-testid': 'tutorial:translation:save-blurb-confirm-cancel' } as any,
        onOk: async () => {
          try {
            await handleBlurbSave();
            await openModal();
          } catch (e) {
            setIsToggling(false);
          }
        },
        onCancel: () => {
          setIsToggling(false);
        },
      });
      return;
    }

    // No unsaved blurb, open normally
    await openModal();
  };

  // Function to toggle metadata translation modal
const toggleKeywordSuggestionModal = async (preferredTargetLanguage?: string) => {
  if (!isKeywordSuggestionModalVisible) {
    const preferredTarget = String(preferredTargetLanguage || '').trim();
    const normalizedBookLanguage = String(bookLanguage || '').trim().toLowerCase();
    if (preferredTarget) {
      setTargetLanguage(preferredTarget);
      setTranslationTargetLanguages(
        preferredTarget.toLowerCase() === normalizedBookLanguage
          ? []
          : normalizeTranslationTargets([preferredTarget]),
      );
      setBetaLanguageWarning(
        preferredTarget.toLowerCase() !== normalizedBookLanguage && isLanguageBeta(preferredTarget),
      );
    } else {
      await prepareTargetLanguageForModal();
    }
  }
  if (auth.currentUser && !isKeywordSuggestionModalVisible) {
    fetchCreditBalance(auth.currentUser.uid);
  }

  setIsKeywordSuggestionModalVisible(!isKeywordSuggestionModalVisible);
  // Reset the translateMetadata state when opening the modal
  if (!isKeywordSuggestionModalVisible) {
    setKeywordSuggestionMetadata({ genre: false, tropes: false, keywords: false });
  }
};

const handleTutorialKeywordSuggestions = ({
  targetLanguage: generatedTargetLanguage,
  keywords: generatedKeywords,
}: {
  targetLanguage: string;
  keywords: string[];
}) => {
  const resolvedTargetLanguage = String(generatedTargetLanguage || targetLanguage || '').trim();
  if (!resolvedTargetLanguage) {
    return;
  }
  if (resolvedTargetLanguage.toLowerCase() === String(bookLanguage || '').trim().toLowerCase()) {
    const mergedKeywords: string[] = [];
    const seenKeywords = new Set<string>();
    [...keywords, ...(Array.isArray(generatedKeywords) ? generatedKeywords : [])].forEach((keyword) => {
      const cleanKeyword = String(keyword || '').trim();
      if (!cleanKeyword) return;
      const keywordKey = cleanKeyword.toLowerCase();
      if (seenKeywords.has(keywordKey)) return;
      seenKeywords.add(keywordKey);
      mergedKeywords.push(cleanKeyword);
    });
    setKeywords(mergedKeywords);
    return;
  }
  const pairKey = `${bookLanguage}-${resolvedTargetLanguage}`;
  setKeywordTranslations((prev) => ({
    ...prev,
    [pairKey]: Array.isArray(generatedKeywords) ? generatedKeywords : [],
  }));
};

const resolveProofreadChangeSetContext = useCallback(
  (sourceLang: string, targetLang: string) => {
    if (sourceLang === 'Original') {
      return {
        requestSourceLang: bookLanguage,
        proofreadVersion: 'Original',
      };
    }
    return {
      requestSourceLang: sourceLang,
      proofreadVersion: `${sourceLang}-${targetLang}`,
    };
  },
  [bookLanguage],
);

const resolveProofreadBulkContext = useCallback((versionLabel: string) => {
  const normalizedVersion = normalizeProofreadVersion(versionLabel);
  if (!normalizedVersion || normalizedVersion === 'Original') {
    return {
      requestSourceLang: bookLanguage,
      proofreadVersion: 'Original',
    };
  }
  const { sourceLang, targetLang } = splitLanguagePair(normalizedVersion);
  if (!sourceLang || !targetLang) {
    throw new Error('Please select a valid proofread version.');
  }
  return {
    requestSourceLang: sourceLang,
    proofreadVersion: `${sourceLang}-${targetLang}`,
  };
}, [bookLanguage, normalizeProofreadVersion]);

const fetchProofreadChangeSetForChapter = useCallback(
  async ({
    sourceLang,
    targetLang,
    chapterGuid,
  }: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
  }): Promise<ProofreadChangeSet | null> => {
    if (isSandboxFixtureMode) {
      const fixtureChangeSets = sandboxFixturePayload?.proofreadChangeSets;
      if (fixtureChangeSets && typeof fixtureChangeSets === 'object') {
        const normalizedSource = String(sourceLang || '').trim();
        const normalizedTarget = String(targetLang || '').trim();
        const candidateKeys = [
          `${normalizedSource}-${normalizedTarget}:${chapterGuid}`,
          `${normalizedSource}:${normalizedTarget}:${chapterGuid}`,
          chapterGuid,
        ];
        for (const key of candidateKeys) {
          const candidate = (fixtureChangeSets as Record<string, unknown>)[key];
          if (candidate && typeof candidate === 'object') {
            return candidate as ProofreadChangeSet;
          }
        }
      }
      return null;
    }
    const userId = auth.currentUser?.uid;
    if (!userId || !bookId || !chapterGuid) {
      return null;
    }
    const { requestSourceLang, proofreadVersion } = resolveProofreadChangeSetContext(
      sourceLang,
      targetLang,
    );
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    let response;
    try {
      response = await bookDetailApiPost(
        `${API_BASE}/proofread-change-sets/${bookId}/${chapterGuid}/latest`,
        {
          userId,
          sourceLang: requestSourceLang,
          proofreadVersion,
        },
        { headers },
      );
    } catch (error) {
      const detail = String((error as any)?.response?.data?.detail || '').trim();
      throw new Error(detail || 'Failed to load proofread change set.');
    }
    const payload = response?.data || {};
    if (payload.status === 'success') {
      return (payload.change_set || null) as ProofreadChangeSet | null;
    }
    if (payload.status === 'not_found') {
      return null;
    }
    throw new Error(payload.message || 'Failed to load proofread change set.');
  },
  [API_BASE, auth.currentUser?.uid, bookId, isSandboxFixtureMode, resolveProofreadChangeSetContext, sandboxFixturePayload],
);

const applyProofreadChangeSetForChapter = useCallback(
  async ({
    sourceLang,
    targetLang,
    chapterGuid,
    changeSetId,
    acceptedProposalIds,
  }: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
    changeSetId: string;
    acceptedProposalIds: string[];
  }) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !bookId || !chapterGuid || !changeSetId) {
      throw new Error('Missing required fields to apply proofread changes.');
    }
    const { requestSourceLang, proofreadVersion } = resolveProofreadChangeSetContext(
      sourceLang,
      targetLang,
    );
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    let response;
    try {
      response = await bookDetailApiPost(
        `${API_BASE}/proofread-change-sets/${bookId}/${chapterGuid}/apply`,
        {
          userId,
          sourceLang: requestSourceLang,
          proofreadVersion,
          changeSetId,
          acceptedProposalIds,
          model: selectedProofreadModel || undefined,
          tone: targetLang === 'French' ? frenchTone : undefined,
        },
        { headers },
      );
    } catch (error) {
      const status = Number((error as any)?.response?.status || 0);
      const detail = String((error as any)?.response?.data?.detail || '').trim();
      if (status === 409) {
        await refreshTranslationsImmediate(true);
        fetchBookDetailsRef.current?.();
        throw new Error(
          detail || 'A newer proofread run replaced this review. Refresh and review the latest proofread before saving.',
        );
      }
      throw new Error(detail || 'Failed to apply accepted proofread changes.');
    }
    const payload = response?.data || {};
    if (payload.status !== 'success') {
      throw new Error(payload.message || 'Failed to apply accepted proofread changes.');
    }
    await refreshTranslationsImmediate(true);
    fetchBookDetailsRef.current?.();
    return payload;
  },
  [
    API_BASE,
    auth.currentUser?.uid,
    bookId,
    frenchTone,
    refreshTranslationsImmediate,
    resolveProofreadChangeSetContext,
    selectedProofreadModel,
  ],
);

const discardProofreadChangeSetForChapter = useCallback(
  async ({
    sourceLang,
    targetLang,
    chapterGuid,
    changeSetId,
  }: {
    sourceLang: string;
    targetLang: string;
    chapterGuid: string;
    changeSetId: string;
  }) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !bookId || !chapterGuid || !changeSetId) {
      throw new Error('Missing required fields to discard a proofread review.');
    }
    const { requestSourceLang, proofreadVersion } = resolveProofreadChangeSetContext(
      sourceLang,
      targetLang,
    );
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    let response;
    try {
      response = await bookDetailApiPost(
        `${API_BASE}/proofread-change-sets/${bookId}/${chapterGuid}/discard`,
        {
          userId,
          sourceLang: requestSourceLang,
          proofreadVersion,
          changeSetId,
        },
        { headers },
      );
    } catch (error) {
      const status = Number((error as any)?.response?.status || 0);
      const detail = String((error as any)?.response?.data?.detail || '').trim();
      if (status === 409) {
        await refreshTranslationsImmediate(true);
        fetchBookDetailsRef.current?.();
        throw new Error(
          detail || 'A newer proofread run replaced this review. Refresh and review the latest proofread before discarding.',
        );
      }
      throw new Error(detail || 'Failed to discard proofread review.');
    }
    const payload = response?.data || {};
    if (payload.status !== 'success') {
      throw new Error(payload.message || 'Failed to discard proofread review.');
    }
    await refreshTranslationsImmediate(true);
    fetchBookDetailsRef.current?.();
    return payload;
  },
  [
    API_BASE,
    auth.currentUser?.uid,
    bookId,
    refreshTranslationsImmediate,
    resolveProofreadChangeSetContext,
  ],
);

const applyAllPendingProofreadChangeSetsForVersion = useCallback(
  async ({
    versionLabel,
    chapterGuids,
  }: {
    versionLabel: string;
    chapterGuids?: string[];
  }) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !bookId) {
      throw new Error('Missing required fields to apply pending proofread reviews.');
    }
    const { requestSourceLang, proofreadVersion } = resolveProofreadBulkContext(versionLabel);
    const headers = await getAuthHeaders(auth.currentUser ?? undefined);
    let response;
    try {
      response = await bookDetailApiPost(
        `${API_BASE}/proofread-change-sets/${bookId}/apply-all`,
        {
          userId,
          sourceLang: requestSourceLang,
          proofreadVersion,
          chapterGuids,
        },
        { headers },
      );
    } catch (error) {
      const detail = String((error as any)?.response?.data?.detail || '').trim();
      throw new Error(detail || 'Failed to apply pending proofread reviews.');
    }
    const payload = response?.data || {};
    if (payload.status !== 'success') {
      throw new Error(payload.message || 'Failed to apply pending proofread reviews.');
    }
    await refreshTranslationsImmediate(true);
    fetchBookDetailsRef.current?.();
    return payload as {
      status: 'success';
      appliedCount: number;
      skippedCount: number;
      removedCount: number;
      results: Array<{ chapterGuid: string; status: 'applied' | 'skipped' | 'error' | 'removed'; message?: string }>;
    };
  },
  [
    API_BASE,
    auth.currentUser?.uid,
    bookId,
    refreshTranslationsImmediate,
    resolveProofreadBulkContext,
  ],
);

const renderProofreadTabContent = (
  sourceLang: string,
  targetLang: string,
  tabActionsContainer?: HTMLElement | null,
  isActive?: boolean,
) => {
  return ProofreadTabContent({
    selectedChapter,
    book: { ...book, genres: book.genres || [] },
    sourceLang,
    targetLang,
    bookId: bookId || undefined,
    apiBase: API_BASE,
    isAdmin,
    viewMode,
    setViewMode,
    handleDeleteSingleProofreadChapter,
    deleteEditingNotes,
    fetchProofreadChangeSet: fetchProofreadChangeSetForChapter,
    applyProofreadChangeSet: applyProofreadChangeSetForChapter,
    discardProofreadChangeSet: discardProofreadChangeSetForChapter,
    onOpenProofreadModal: ({ sourceLang: modalSourceLang, targetLang: modalTargetLang, proofread, editingNotes }) => {
      const nextVersion = modalSourceLang === 'Original'
        ? 'Original'
        : `${modalSourceLang}-${modalTargetLang}`;
      void toggleProofreadModal(nextVersion, { proofread, editingNotes });
    },
    tabActionsContainer,
    isActive,
    includeEditingNotesInContent: false,
    onRefreshProofreadContent: async () => {
      await refreshTranslationsImmediate(true);
      fetchBookDetailsRef.current?.();
    },
  });
};

  const renderTranslatedTabContent = (
    sourceLang: string,
    targetLang: string,
    tabActionsContainer?: HTMLElement | null,
    isActive?: boolean,
  ) => {
    const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
    const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];

    if (translatedChapters && selectedChapter) {
      const translatedChapter = translatedChapters.find(
        (chap) => chap.chapter_guid === selectedChapter.chapter_guid
      );
      const isTranslated = !!(
        translatedChapter && (translatedChapter.translated_text || getSavedProofreadTextForRow(translatedChapter))
      );
      if (isTranslated) {
        return {
          content: (
            <TranslatedTabContent
              translatedChapter={translatedChapter}
              sourceLang={sourceLang}
              targetLang={targetLang}
              sourceChapterText={String(selectedChapter.content || selectedChapter.raw_html_content || '')}
              bookId={bookId || undefined}
              chapterGuid={selectedChapter.chapter_guid}
              isAdmin={isAdmin}
              apiBase={API_BASE}
              tabActionsContainer={tabActionsContainer}
              isActive={isActive}
              onDelete={(src, tgt) => handleDeleteSingleTranslatedChapter(src, tgt)}
            />
          ),
          isTranslated: true,
        };
      }
    }
    return {
      content: (
        <div data-tour-id="chapter-content">
          <span style={{ color: 'var(--text-warning)' }}>
            <ExclamationCircleOutlined /> This chapter has not yet been translated.
          </span>
        </div>
      ),
      isTranslated: false,
    };
  };

const fetchBookDetails = (options: { silent?: boolean } = {}) => {
  const silent = Boolean(options.silent);
  if (isSandboxFixtureMode && !sandboxFixtureBook) {
    if (!silent) {
      setIsLoading(Boolean(tutorialSandbox.loading));
    }
    return;
  }
  if (!silent) {
    setIsLoading(true);
  }
  let newRejectedChapters: string[] = [];

  const bookRequest = isSandboxFixtureMode
    ? Promise.resolve(sandboxFixtureBook)
    : fetchBookDetail({ bookId, slim: true });

  bookRequest
    .then(fetchedBook => {
      if (!fetchedBook) {
        throw new Error('Book detail response was empty');
      }
      setBookLanguage(fetchedBook.language || 'English');
	  setBlurb(fetchedBook.blurb);
	  setGenres(fetchedBook.genre || []);
	  setTropes(fetchedBook.tropes || []);
	  setKeywords(fetchedBook.keywords || []);
      const audienceSource = fetchedBook.intendedAudienceList ?? fetchedBook.intendedAudience;
      setIntendedAudience(normalizeAudienceInput(audienceSource));
	  setSourceLanguage(fetchedBook.language);
      const detectedUploadType = fetchedBook.uploadType || fetchedBook.fileType || '';
	  setUploadType(detectedUploadType);
	  setExportFormat(detectedUploadType || '');

	  const chapters = (fetchedBook.chapters || []).sort((a: Chapter, b: Chapter) => a.order - b.order);
	  const sortedChapters = (fetchedBook.chapters || []).sort((a: Chapter, b: Chapter) => a.order - b.order);

      const coversRaw: { [language: string]: string } = {};
      if (fetchedBook.coverUrl) {
        coversRaw.original = fetchedBook.coverUrl;
      }

      if (fetchedBook.cover_urls) {
        Object.entries(fetchedBook.cover_urls as Record<string, string>).forEach(([k, v]) => {
          if (v) coversRaw[String(k).toLowerCase()] = v;
        });
      }

      Object.keys(fetchedBook).forEach(key => {
        if (key.startsWith('coverUrl_') && fetchedBook[key]) {
          const language = key.replace('coverUrl_', '').toLowerCase();
          coversRaw[language] = fetchedBook[key];
        }
      });

      setLanguageCovers(prev => {
        const next: { [language: string]: string } = { ...coversRaw };
        if (prev['original'] === '' && !next.original) {
          next.original = '';
        }
        return next;
      });

      const normalizedFrenchTone = normalizeFrenchNarrativeTone(fetchedBook.frenchNarrativeTone);
      const normalizedBook = {
        ...fetchedBook,
        frenchNarrativeTone: normalizedFrenchTone,
        coverUrl: coversRaw.original || '',
        chapters,
      } as Book;

	  setBook({ ...normalizedBook, bookguid: bookId });
      // Resolve the link against the loaded book, not the initially empty book state.
      const entryParams = new URLSearchParams(location.search);
      const entryKey = `${bookId}:${location.key || location.search}`;
      if (entryParams.get('tab') === 'marketing' && marketingEditionRouteAppliedRef.current !== entryKey) {
        marketingEditionRouteAppliedRef.current = entryKey;
        const requestedEdition = entryParams.get('edition');
        const edition = requestedEdition && fetchedBook.translation_pairs?.includes(requestedEdition)
          ? requestedEdition : 'Original';
        setSelectedTab(edition);
        setCurrentLanguagePair(edition);
        setBlurbActiveTabKey(edition);
        setIsTranslatedTabActive(edition !== 'Original');
        setMetadataLanguageSignal({ language: edition, ts: Date.now() });
        setMetadataTabSignal({ tab: 'marketing', ts: Date.now() });
        defaultBookTabAppliedRef.current = true;
      }

      const initialInheritance = fetchedBook.seriesInheritance || {};
      setSeriesInheritance(initialInheritance);
      setSeriesDescription(fetchedBook.seriesDescription || '');
      setSelectedSeriesId(fetchedBook.seriesId || null);

	  setEditBook({
	    title: fetchedBook.title,
	    author: fetchedBook.author,
	    series: fetchedBook.series,
	    booknumber: fetchedBook.seriesBookNumber,
	    genres: fetchedBook.genres || [],
	    tropes: fetchedBook.tropes || [],
	    keywords: fetchedBook.keywords || [],
        seriesId: fetchedBook.seriesId || null,
        seriesDescription: fetchedBook.seriesDescription || '',
        seriesInheritance: initialInheritance,
        frenchNarrativeTone: normalizedFrenchTone,
	  });

	  //console.log("editBook.title: "+editBook.title);

	//const temp2 = "never_translate_English-Italian"
	//console.log("never translate English-Italian", fetchedBook[temp2]);

      // Fetch and set Always Translate and Never Translate words for each translation pair

	  const alwaysTranslateWords: { [key: string]: string[] } = {};
      const neverTranslateWords: { [key: string]: string[] } = {};

      fetchedBook.translation_pairs?.forEach((pair: string) => {
        const { sourceLang, targetLang } = getEncodedLanguagePairParts(pair);
        const alwaysTranslateKey = `always_translate_${sourceLang}_${targetLang}`;
        const neverTranslateKey  = `never_translate_${sourceLang}_${targetLang}`;
		//console.log("neverTranslateKey: ",neverTranslateKey)
        alwaysTranslateWords[pair] = fetchedBook[alwaysTranslateKey] || [];
        neverTranslateWords[pair] = fetchedBook[neverTranslateKey] || [];
		//console.log("Setting never translate words for pair:", pair, neverTranslateWords[pair]);
      });

      setAlwaysTranslateWords(alwaysTranslateWords);
      setNeverTranslateWords(neverTranslateWords);

      newRejectedChapters = sortedChapters.filter((chapter: Chapter) => {
		if (chapter.rejected_content) {
          //console.log(`Chapter "${chapter.chapter_name}" was rejected for content`);
          return true;
        }
		else {
                //console.log("RJ check: "+chapter.chapter_name+" "+chapter.rejected_content);
		return false;
		}
      }).map((chapter: Chapter) => chapter.chapter_name);

      setRejectedChapters(newRejectedChapters);

        // Extract translated book and shared-series metadata for each language pair.
        const extractedBlurbs: BlurbTranslations = {};
        const extractedTitles: { [key: string]: string } = {};
        const extractedSeries: { [key: string]: string } = {};
        const extractedSeriesDescriptions: { [key: string]: string } = {};
        const extractedKeywords: { [key: string]: string[] } = {};
        fetchedBook.translation_pairs?.forEach((pair: string) => {
          const { sourceLang, targetLang } = getEncodedLanguagePairParts(pair);
          const blurbKey = `blurb_${sourceLang}_${targetLang}`;
          const titleKey = `title_${sourceLang}_${targetLang}`;
          const seriesKey = `series_${sourceLang}_${targetLang}`;
          const seriesDescriptionKey = metadataTranslationFieldKey('seriesDescription', sourceLang, targetLang);
          const keywordsKey = `keywords_${sourceLang}_${targetLang}`;
          if (fetchedBook[blurbKey]) extractedBlurbs[pair] = fetchedBook[blurbKey];
          if (fetchedBook[titleKey]) extractedTitles[pair] = fetchedBook[titleKey];
          if (fetchedBook[seriesKey]) extractedSeries[pair] = fetchedBook[seriesKey];
          const canonicalSeriesDescriptions = fetchedBook.seriesMetadata?.translations?.seriesDescription || {};
          const canonicalSeriesDescription = canonicalSeriesDescriptions[pair]
            || canonicalSeriesDescriptions[`${sourceLang}_${targetLang}`]
            || canonicalSeriesDescriptions[seriesDescriptionKey];
          const translatedSeriesDescription = fetchedBook[seriesDescriptionKey] || canonicalSeriesDescription;
          if (translatedSeriesDescription) extractedSeriesDescriptions[pair] = translatedSeriesDescription;
          if (fetchedBook[keywordsKey]) extractedKeywords[pair] = fetchedBook[keywordsKey];
        });
        setBlurbTranslations(extractedBlurbs);
        setTitleTranslations(extractedTitles);
        setSeriesTranslations(extractedSeries);
        setSeriesDescriptionTranslations(extractedSeriesDescriptions);
        setKeywordTranslations(extractedKeywords);

	checkProofreadChapters()

      const chapterOrder: Record<string, number> = {};
      chapters.forEach((chapter: Chapter, index: number) => {
        chapterOrder[chapter.chapter_guid] = index + 1;
      });
      setOriginalOrder(chapterOrder);

    })
    .catch(error => {
      console.error("Error fetching book details:", error);
    })
    .finally(() => {
	  if(firstEnter) {
	  setIsEditing(true);
	  setFirstEnter(false);
	  }
      if (!silent) {
        setIsLoading(false);
      }
    });
}

fetchBookDetailsRef.current = fetchBookDetails;

  const ensureSeriesRecordAndAttach = useCallback(
    async (seriesName: string, authorOverride?: string, onlyIfUnassigned = false): Promise<string | null> => {
      if (!auth.currentUser) return null;
      const trimmed = (seriesName || '').trim();
      if (!trimmed) return null;

      const lower = trimmed.toLowerCase();
      if (attemptedSeriesAttach.current.has(lower)) {
        return findSeriesIdByName(trimmed);
      }

      let targetSeriesId = findSeriesIdByName(trimmed);

      try {
        setIsSeriesBusy(true);
        if (!targetSeriesId) {
          const authorName = (authorOverride || editBook.author || book.author || '').trim();
          const response = await bookDetailApiPost(`${API_BASE}/series`, {
            userId: auth.currentUser.uid,
            name: trimmed,
            creationKey: `implicit-name-v1:${trimmed.replace(/\s+/g, ' ').toLowerCase()}`,
            description: '',
            author: authorName,
            defaults: { author: authorName },
          });
          targetSeriesId = response.data.seriesId;
          await loadSeriesOptions();
        }

        if (targetSeriesId && selectedSeriesId !== targetSeriesId) {
          await bookDetailApiPost(`${API_BASE}/series/assign`, {
            userId: auth.currentUser.uid,
            seriesId: targetSeriesId,
            bookId,
            inheritance: seriesInheritance,
            onlyIfUnassigned,
          });
          setSelectedSeriesId(targetSeriesId);
          setBook(prev => ({ ...prev, seriesId: targetSeriesId }));
          setEditBook(prev => ({ ...prev, seriesId: targetSeriesId }));
        }

        attemptedSeriesAttach.current.add(lower);
        return targetSeriesId;
      } catch (error) {
        console.error('Failed to ensure series attachment', error);
        return null;
      } finally {
        setIsSeriesBusy(false);
      }
    },
    [API_BASE, auth.currentUser, bookId, findSeriesIdByName, loadSeriesOptions, selectedSeriesId, seriesInheritance]
  );

  useEffect(() => {
    if (marketingWorkspaceActive) return;
    if (book.series && !book.seriesId) {
      ensureSeriesRecordAndAttach(book.series, undefined, true);
    }
  }, [book.series, book.seriesId, ensureSeriesRecordAndAttach, marketingWorkspaceActive]);

  const blurbWordCount = React.useMemo(
    () => countWordsIncludingMarkup(book.blurb || ''),
    [book.blurb]
  );

  const getMetadataOptionRunKey = (field: 'title' | 'series', sourceLang: string, targetLang: string) =>
    `${field}_${sourceLang.replace(/ /g, '0')}_${targetLang.replace(/ /g, '0')}`;

  const metadataOptionFreeRunConsumed = (
    field: 'title' | 'series',
    sourceLang: string = bookLanguage,
    targetLang: string = targetLanguage,
  ): boolean => {
    const freeRuns = (book as any).metadataOptionFreeRuns || {};
    return Boolean(freeRuns[getMetadataOptionRunKey(field, sourceLang, targetLang)]);
  };

  const metadataBlurbFreeRunConsumed = (targetLang: string = targetLanguage): boolean => {
    return metadataFreeRunConsumed((book as any).blurbTranslationFreeRuns, targetLang);
  };

  const calculateMetadataTranslationCreditCost = (): number => {
    let metadataWordCount = 0;
    if (translateMetadata.title && metadataOptionFreeRunConsumed('title')) metadataWordCount += 50;
    if (translateMetadata.series && metadataOptionFreeRunConsumed('series')) metadataWordCount += 50;
    if (translateMetadata.blurb && metadataBlurbFreeRunConsumed()) metadataWordCount += blurbWordCount;
    return metadataWordCount; // Assuming 1 credit per word, adjust as needed
  };

const getLanguageDefaultPreference = useCallback(
  async (options?: { force?: boolean }) => {
    try {
      if (!options?.force && defaultLanguagePreferenceRef.current) {
        return defaultLanguagePreferenceRef.current;
      }

      const resolvedUserId = auth.currentUser?.uid ?? sharedUserId;
      if (!resolvedUserId) {
        console.warn('Unable to fetch language default preference because no user ID is available.');
        return { language: '', mode: 'specific' as LanguageDefaultMode };
      }

      const languageResponse = await bookDetailApiGet(`${API_BASE}/get-default-language`, {
        params: { userID: resolvedUserId },
      });

      const mode = normalizeLanguageDefaultMode(languageResponse.data?.defaultLanguageMode);
      const language = coerceLanguageValue(languageResponse.data?.defaultLanguage) || '';
      const preference = { language, mode };
      defaultLanguagePreferenceRef.current = preference;
      setDefaultLanguageMode(mode);
      return preference;
    } catch (error) {
      console.error('Error fetching language default preference:', error);
      throw error;
    }
  },
  [auth.currentUser?.uid, sharedUserId]
);

useEffect(() => {
  const resolvedUserId = auth.currentUser?.uid ?? sharedUserId;
  if (!resolvedUserId) {
    setIsDefaultBookTabLoaded(false);
    return;
  }

  let cancelled = false;

  const loadDefaultBookTab = async () => {
    if (new URLSearchParams(location.search).get('tab') === 'marketing') return;
    try {
      const response = await bookDetailApiGet(`${API_BASE}/get-default-book-tab`, {
        params: { userID: resolvedUserId },
      });
      if (cancelled) return;
      const nextTab = response.data?.defaultBookTab;
      setDefaultBookTab(nextTab === 'metadata' ? 'metadata' : 'dashboard');
    } catch (error) {
      console.error('Error fetching default book tab:', error);
      if (!cancelled) {
        setDefaultBookTab('dashboard');
      }
    } finally {
      if (!cancelled) {
        setIsDefaultBookTabLoaded(true);
      }
    }
  };

  loadDefaultBookTab();

  return () => {
    cancelled = true;
  };
}, [API_BASE, auth.currentUser?.uid, sharedUserId]);

useEffect(() => {
  if (new URLSearchParams(location.search).get('tab') === 'marketing') return;
  if (!isEditing) {
    defaultBookTabAppliedRef.current = false;
    return;
  }
  if (tutorialUiSuppressionRequested) {
    if (!defaultBookTabAppliedRef.current) {
      setMetadataTabSignal({ tab: tutorialRequestedMetadataTab, ts: Date.now() });
      defaultBookTabAppliedRef.current = true;
    }
    return;
  }
  if (!isDefaultBookTabLoaded) return;
  if (defaultBookTabAppliedRef.current) return;
  setMetadataTabSignal({ tab: defaultBookTab, ts: Date.now() });
  defaultBookTabAppliedRef.current = true;
}, [defaultBookTab, isDefaultBookTabLoaded, isEditing, tutorialRequestedMetadataTab, tutorialUiSuppressionRequested, location.search]);

useEffect(() => {
  const params = new URLSearchParams(location.search || '');
  const tab = (params.get('tab') || '').trim().toLowerCase();
  if (tab !== 'marketing') {
    return;
  }

  const routeSignal = `${bookId}:${location.key || location.search}`;
  if (marketingTabRouteAppliedRef.current === routeSignal) {
    return;
  }
  marketingTabRouteAppliedRef.current = routeSignal;

  defaultBookTabAppliedRef.current = true;
  setMetadataTabSignal({ tab: 'marketing', ts: Date.now() });
  if (!isEditing) {
    setIsEditing(true);
  }
}, [bookId, isEditing, location.key, location.search]);

  useEffect(() => {
	  //console.log("useEffect 9");
	  //console.time('useEffect 9');
    const cost = calculateMetadataTranslationCreditCost();
    const avail = creditBalance?.available_balance ?? creditBalance?.credit_balance
    setUserHasEnoughCreditsMetadata((avail ?? 0) >= cost);
	//console.timeEnd('useEffect 9');
  }, [translateMetadata, book, creditBalance]);

useEffect(() => {
  if (!bookId) {
    return;
  }
  if (isSandboxFixtureMode && !sandboxFixtureBook) {
    setIsLoading(Boolean(tutorialSandbox.loading));
    return;
  }

  let cancelled = false;

  const run = async () => {
    setIsLoading(true);
    const currentUserId = auth.currentUser?.uid;
    // Reports do not need a chapter-by-chapter word-count refresh before opening.
    const enteringMarketing = new URLSearchParams(location.search).get('tab') === 'marketing';
    if (!isSandboxFixtureMode && currentUserId && !enteringMarketing) {
      try {
        await refreshBookWordCounts({
          userId: currentUserId,
          bookId,
          force: false,
          currentUser: auth.currentUser,
        });
      } catch (error) {
        console.error('Failed to refresh book-level word counts', error);
      }
    }
    if (cancelled) {
      return;
    }
    fetchBookDetails();
    getLanguageDefaultPreference().catch(() => {});
  };

  run();

  return () => {
    cancelled = true;
  };
}, [bookId, auth, getLanguageDefaultPreference, isSandboxFixtureMode, sandboxFixtureBook, tutorialSandbox.loading]);

const handleEditToggle = () => {
  setIsEditing(!isEditing);
  setEditBook({
    title: book.title,
    author: book.author,
    series: book.series,
    booknumber: book.seriesBookNumber,
    genres: book.genres || [],
    tropes: book.tropes || [],
    keywords: book.keywords || [],
    seriesId: book.seriesId || null,
    seriesDescription: book.seriesDescription || '',
    seriesInheritance: book.seriesInheritance || {},
    ...(book.frenchNarrativeTone ? { frenchNarrativeTone: book.frenchNarrativeTone } : {}),
  });
  setSeriesDescription(book.seriesDescription || '');
  setSeriesInheritance(book.seriesInheritance || {});
  setSelectedSeriesId(book.seriesId || null);
  setSelectedChapter(null);
};

const handleEditMetadataClick = () => {
  defaultBookTabAppliedRef.current = true;
  setActiveMetadataTab('metadata');
  setMetadataTabSignal({ tab: 'metadata', ts: Date.now() });
  if (!isEditing) {
    handleEditToggle();
  }
};

const handleInputChange = (
  key: keyof typeof editBook,
  value: string | number | null | undefined,
  options?: { markDirty?: boolean }
) => {
  const normalizedValue =
    key === 'frenchNarrativeTone' && !value ? undefined : value
  setEditBook(prev => ({ ...prev, [key]: normalizedValue as any }));
  if (key === 'title' && typeof normalizedValue === 'string') {
    setIsTitleValid(!!normalizedValue.trim());
  }
  if (options?.markDirty === false) {
    return;
  }
  setHasUnsavedChanges(true);
};

const currentMetadataMatchesSavedBook = useCallback(() => {
  const savedBookNumber = (book as any).seriesBookNumber ?? (book as any).booknumber ?? '';
  const savedGenres = (book as any).genre ?? (book as any).genres ?? [];
  const savedAudience = (book as any).intendedAudienceList ?? (book as any).intendedAudience ?? [];
  const savedTone = normalizeFrenchNarrativeTone((book as any).frenchNarrativeTone);
  const currentTone = normalizeFrenchNarrativeTone((editBook as any).frenchNarrativeTone);

  return (
    normalizeMetadataDirtyText(editBook.title) === normalizeMetadataDirtyText(book.title) &&
    normalizeMetadataDirtyText(editBook.author) === normalizeMetadataDirtyText(book.author) &&
    normalizeMetadataDirtyText(editBook.series) === normalizeMetadataDirtyText(book.series) &&
    normalizeMetadataDirtyText(editBook.booknumber) === normalizeMetadataDirtyText(savedBookNumber) &&
    normalizeMetadataDirtyText(bookLanguage) === normalizeMetadataDirtyText((book as any).language || 'English') &&
    metadataDirtyListsEqual(genres, savedGenres) &&
    metadataDirtyListsEqual(tropes, (book as any).tropes ?? []) &&
    metadataDirtyListsEqual(keywords, (book as any).keywords ?? []) &&
    metadataDirtyListsEqual(intendedAudience, savedAudience) &&
    normalizeMetadataDirtyHtml(blurb) === normalizeMetadataDirtyHtml((book as any).blurb ?? '') &&
    normalizeMetadataDirtyText(seriesDescription) === normalizeMetadataDirtyText((book as any).seriesDescription ?? '') &&
    normalizeMetadataDirtyText(selectedSeriesId ?? '') === normalizeMetadataDirtyText((book as any).seriesId ?? '') &&
    normalizeMetadataDirtyObject(seriesInheritance) === normalizeMetadataDirtyObject((book as any).seriesInheritance ?? {}) &&
    normalizeMetadataDirtyText(currentTone ?? '') === normalizeMetadataDirtyText(savedTone ?? '')
  );
}, [
  blurb,
  book,
  bookLanguage,
  editBook,
  genres,
  intendedAudience,
  keywords,
  selectedSeriesId,
  seriesDescription,
  seriesInheritance,
  tropes,
]);

useEffect(() => {
  if (!hasUnsavedChanges) {
    return;
  }
  if (currentMetadataMatchesSavedBook()) {
    setHasUnsavedChanges(false);
  }
}, [currentMetadataMatchesSavedBook, hasUnsavedChanges]);

const shouldSuppressBlurbDirty = () => {
  if (!generatedBlurbSaveApplyRef.current) {
    return false;
  }
  generatedBlurbSaveApplyRef.current = false;
  return true;
};

const handleGeneratedBlurbSaved = (savedBlurb: string) => {
  generatedBlurbSaveApplyRef.current = true;
  setBook(prev => ({ ...prev, blurb: savedBlurb }));
  setBlurb(savedBlurb);
  setHasUnsavedChanges(false);
  window.setTimeout(() => {
    generatedBlurbSaveApplyRef.current = false;
    setHasUnsavedChanges(false);
  }, 0);
};

const handleFrenchNarrativeToneSaved = (tone?: FrenchNarrativeTone) => {
  setBook(prev => ({ ...prev, frenchNarrativeTone: tone }));
};

const handleSeriesDescriptionChange = (value: string) => {
  setSeriesDescription(value);
  setHasUnsavedChanges(true);
};

const handleSeriesInheritanceToggle = (field: string, inherit: boolean) => {
  setSeriesInheritance(prev => ({ ...prev, [field]: inherit }));
  setHasUnsavedChanges(true);
};

const handleSeriesSelection = async (seriesId: string | null) => {
  if (!auth.currentUser) return;
  setIsSeriesBusy(true);
  try {
    await bookDetailApiPost(`${API_BASE}/series/assign`, {
      userId: auth.currentUser.uid,
      seriesId,
      bookId,
      inheritance: seriesInheritance,
    });
    setSelectedSeriesId(seriesId);
    await loadSeriesOptions();
    await fetchBookDetails();
  } catch (error) {
    const messageText =
      (error as any)?.response?.data?.detail ||
      (error as any)?.response?.data?.message ||
      'Failed to assign series';
    console.error('Failed to assign series', error);
    message.error(messageText);
  } finally {
    setIsSeriesBusy(false);
  }
};

const handleCreateSeries = async (name: string, description: string) => {
  if (!auth.currentUser) return;
  setIsSeriesBusy(true);
  try {
    const response = await bookDetailApiPost(`${API_BASE}/series`, {
      userId: auth.currentUser.uid,
      name,
      description,
    });
    await loadSeriesOptions();
    await handleSeriesSelection(response.data.seriesId);
  } catch (error) {
    console.error('Failed to create series', error);
  } finally {
    setIsSeriesBusy(false);
  }
};

const handleCopyMetadata = async (sourceBookGuid: string, fields: string[], applyToSeries: boolean) => {
  if (!auth.currentUser) return;
  setIsSeriesBusy(true);
  try {
    await bookDetailApiPost(`${API_BASE}/metadata/copy`, {
      userId: auth.currentUser.uid,
      sourceBookId: sourceBookGuid,
      targetBookIds: [bookId],
      fields,
      targetSeriesId: applyToSeries && selectedSeriesId ? selectedSeriesId : undefined,
    });
    await fetchBookDetails();
  } catch (error) {
    console.error('Failed to copy metadata', error);
  } finally {
    setIsSeriesBusy(false);
  }
};

const handleSave = async () => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }

  if (!isTitleValid) {
    alert("The title is required."); // Provide feedback to the user
    return;
  }
  const currentBlurbWordCount = getBlurbWordCount(blurb);
  if (currentBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
    message.error(formatBlurbWordLimitMessage(currentBlurbWordCount), 8);
    return;
  }

  setIsLoading(true);

  try {
    let nextSeriesId = selectedSeriesId;
    if (editBook.series && editBook.series.trim()) {
      const ensuredId = await ensureSeriesRecordAndAttach(editBook.series.trim());
      if (ensuredId) {
        nextSeriesId = ensuredId;
      }
    } else {
      nextSeriesId = null;
    }

    await bookDetailApiPost(`${API_BASE}/update-book/`, {
		bookID: bookId,
		userID: auth.currentUser.uid,
		title: editBook.title,
		author: editBook.author,
		series: editBook.series,
		booknumber: editBook.booknumber,
		language: bookLanguage,
		genre: genres,
		tropes: tropes,
		keywords: keywords,
        intendedAudience: intendedAudience,
        seriesId: nextSeriesId,
        seriesInheritance,
        seriesDescription,
        frenchNarrativeTone: editBook.frenchNarrativeTone ?? null,
    });
    setBook({ ...book, ...editBook, genres: genres, seriesId: nextSeriesId ?? null, frenchNarrativeTone: editBook.frenchNarrativeTone });
    setSelectedSeriesId(nextSeriesId ?? null);
    //setIsEditing(false);

	//savetheblurb
	if (blurb) {
		await handleBlurbSave();
	}

	//refresh after update
    const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
    let updatedBook: Book = updatedBookResponse.data;
    updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
    setBook(updatedBook);
  } catch (error) {
    console.error("Error saving book details:", error);
  }
  setHasUnsavedChanges(false);
  setIsLoading(false);
};






  const deleteTranslation = async (chapterGuid: string, sourceLang: string, targetLang: string) => {
    if (!auth.currentUser) {
      console.error("No user logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await bookDetailApiPost(
        `${API_BASE}/delete-translated-chapter/${bookId}/${chapterGuid}`,
        {
          user_id: auth.currentUser.uid,
          source_language: sourceLang,
          target_language: targetLang
        }
      );

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

      setBook(prev => applyLocalTranslationDeletion(prev, sourceLang, targetLang, chapterGuid));

      // Refresh the book data to reflect the deletion
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
      setBook(applyLocalTranslationDeletion(updatedBook, sourceLang, targetLang, chapterGuid));
      message.success("Translation deleted successfully.");
    } catch (error) {
      console.error("Error deleting translation:", error);
      message.error("Error deleting translation",10);
    } finally {
      setIsLoading(false);
    }
  };


  const deleteProofread = async (chapterGuid: string, sourceLang: string, targetLang: string) => {
    if (!auth.currentUser) {
      console.error("No user logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await bookDetailApiPost(
        `${API_BASE}/delete-proofread-chapter/${bookId}/${chapterGuid}`,
        {
          user_id: auth.currentUser.uid,
		  sourceLang: sourceLang,
		  targetLang: targetLang
        }
      );

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

      // Refresh the book data to reflect the deletion
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
      setBook(updatedBook);
      message.success("Proofread Deleted Successfully.");
    } catch (error) {
      console.error("Error deleting translation:", error);
      message.error("Error deleting translation",10);
    } finally {
      setSelectedChapter(null);
	  setIsLoading(false);
    }
  };


  const deleteEditingNotes = async (chapterGuid: string, sourceLang: string, targetLang: string) => {
    if (!auth.currentUser) {
      console.error("No user logged in");
      return;
    }

    setIsLoading(true);

    try {
      const response = await bookDetailApiPost(
        `${API_BASE}/delete-editnotes-chapter/${bookId}/${chapterGuid}`,
        {
          user_id: auth.currentUser.uid,
		  sourceLang: sourceLang,
		  targetLang: targetLang
        }
      );

    if (response.data.status === "error") {
      throw new Error(response.data.message);
    }

      // Refresh the book data to reflect the deletion
      const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
      let updatedBook: Book = updatedBookResponse.data;
      updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
      setBook(updatedBook);
      message.success("Editing Notes Deleted Successfully.");
    } catch (error) {
      console.error("Error deleting translation:", error);
      message.error("Error deleting translation",10);
    } finally {
      setSelectedChapter(null);
	  setIsLoading(false);
    }
  };

  const selectChapter = async (chapter: Chapter) => {
    setSelectedChapter(chapter);
	getProofreadOptionProps();
	if (translateVersion == 'Proofread' && proofreadOptionProps.disabled) {
		setTranslateVersion('Original')
	}
    // Close the editing screen when a chapter is selected
    setIsEditing(false);

    // Lazy-load heavy original content if not already present
    try {
      if (!chapter.content || chapter.content.length === 0) {
        setIsLoading(true);
        const resp = await bookDetailApiGet(
          `${API_BASE}/uploadedbooks/book/${bookId}/chapters/${chapter.chapter_guid}`
        );
        const data = resp.data || {};
        // Update book state
        setBook((prev: any) => {
          const updated = { ...prev, chapters: [...prev.chapters] };
          const idx = updated.chapters.findIndex((ch: any) => ch.chapter_guid === chapter.chapter_guid);
          if (idx >= 0) {
            updated.chapters[idx] = {
              ...updated.chapters[idx],
              content: data.content || '',
              raw_html_content: data.raw_html_content || '',
              proofread_chapter: data.proofread_chapter || '',
              proofread_comments: data.proofread_comments || '',
              word_count: data.word_count ?? updated.chapters[idx].word_count,
              proofread_word_count: data.proofread_word_count ?? updated.chapters[idx].proofread_word_count,
              proofread_generation: data.proofread_generation ?? updated.chapters[idx].proofread_generation,
            };
          }
          return updated;
        });
        // Update selectedChapter cache
        setSelectedChapter((prevSel: any) => prevSel ? {
          ...prevSel,
          content: data.content || '',
          raw_html_content: data.raw_html_content || '',
          proofread_chapter: data.proofread_chapter || '',
          proofread_comments: data.proofread_comments || '',
          word_count: data.word_count ?? prevSel.word_count,
          proofread_word_count: data.proofread_word_count ?? prevSel.proofread_word_count,
          proofread_generation: data.proofread_generation ?? prevSel.proofread_generation,
        } : prevSel);
      }

      // Ensure translated content is loaded when viewing a translated tab
      if (selectedTab && selectedTab !== 'Original') {
        try {
          const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
          if (sourceLang && targetLang) {
            const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
            const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
            const existing = translatedChapters.find(
              (tc: any) => tc.chapter_guid === chapter.chapter_guid
            );
            if (!existing || translatedChapterNeedsProofreadHydration(existing)) {
              setIsLoading(true);
              await hydrateTranslatedChapter(
                chapter.chapter_guid,
                sourceLang,
                targetLang,
                {
                  reason: 'initial_view',
                },
              );
            }
          }
        } catch (e) {
          console.error('Error loading translated chapter on select:', e);
        }
      }
    } catch (e) {
      console.error('Error loading chapter content on select:', e);
    } finally {
      setIsLoading(false);
    }
  };

    const handleLanguageChange = (value: string, type: 'source' | 'target') => {
		if (type === 'source') {
            setSourceLanguage(value);
            if (value === targetLanguage) {
                message.error('Source and target languages must be different');
            }
        } else {
            setTargetLanguage(value);
			scheduleTranslationStatsUpdate(value, selectedAIModel);
            if (value === bookLanguage) {
                message.error('Source and target languages must be different');
            }
        }
    };

const handleAIModelChange = (value: string): void => {
    if (translationWorkflowMode === 'easy' && value && value !== preferredTranslationModelId) {
      setTranslationWorkflowMode('advanced');
    }
    setSelectedAIModel(value);
    scheduleTranslationStatsUpdate(targetLanguage, value);
  };

const updateUntranslatedChaptersAndWordCount = useCallback(
  (newTargetLanguage: string, aiModel: string) => {
    if (!newTargetLanguage || newTargetLanguage === 'Original') {
      setUntranslatedChapters([]);
      setShowWarning(false);
      setUserHasEnoughCredits(true);
      return;
    }

    const translatedChaptersKey = `translated_chapters_${bookLanguage}_${newTargetLanguage}`;
    const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
    const translatedChapterGuids = new Set(
      translatedChapters
        .filter((tChapter) => hasTranslatedContent(tChapter))
        .map((tChapter) => tChapter.chapter_guid)
    );

    const untranslated = book.chapters
      .filter((chapter) => !translatedChapterGuids.has(chapter.chapter_guid))
      .map((chapter) => chapter.chapter_name);

    setUntranslatedChapters(untranslated);
    setShowWarning(untranslated.length > 0);

    const newWordCount = calculateWordCount(newTargetLanguage, aiModel, { translatedChapterGuids });
    const availableCredits = creditBalance?.available_balance ?? creditBalance?.credit_balance ?? 0;
    setUserHasEnoughCredits(creditBalance != null && availableCredits >= newWordCount);
  },
  [book, book.chapters, bookLanguage, creditBalance, calculateWordCount, hasTranslatedContent]
);

const scheduleTranslationStatsUpdate = useCallback(
  (lang: string, aiModel: string) => {
    if (translationStatsDebounceRef.current && typeof window !== 'undefined') {
      window.clearTimeout(translationStatsDebounceRef.current);
    }
    translationStatsDebounceRef.current = null;
    if (typeof window === 'undefined') {
      updateUntranslatedChaptersAndWordCount(lang, aiModel);
      return;
    }
    translationStatsDebounceRef.current = window.setTimeout(() => {
      updateUntranslatedChaptersAndWordCount(lang, aiModel);
    }, 200);
  },
  [updateUntranslatedChaptersAndWordCount]
);

useEffect(() => {
  return () => {
    if (translationStatsDebounceRef.current && typeof window !== 'undefined') {
      window.clearTimeout(translationStatsDebounceRef.current);
    }
    translationStatsDebounceRef.current = null;
    if (proofreadCompletionRefreshTimeoutRef.current && typeof window !== 'undefined') {
      window.clearTimeout(proofreadCompletionRefreshTimeoutRef.current);
    }
    proofreadCompletionRefreshTimeoutRef.current = null;
  };
}, []);

const scheduleProofreadCompletionRefresh = useCallback(() => {
  if (typeof window === 'undefined') {
    fetchBookDetailsRef.current?.({ silent: true });
    return;
  }
  if (proofreadCompletionRefreshTimeoutRef.current) {
    window.clearTimeout(proofreadCompletionRefreshTimeoutRef.current);
  }
  proofreadCompletionRefreshTimeoutRef.current = window.setTimeout(() => {
    proofreadCompletionRefreshTimeoutRef.current = null;
    fetchBookDetailsRef.current?.({ silent: true });
  }, 600);
}, []);

useEffect(() => {
  if (!bookId) {
    return;
  }
  const bookDocRef = doc(db, 'uploadedBooks', bookId);
  const unsubscribe = onSnapshot(
    bookDocRef,
    snapshot => {
      if (!snapshot.exists()) {
        return;
      }
      const data = snapshot.data() || {};
      const translatedVersion = Number(data.translatedVersion ?? 0);
      const prevVersion = lastTranslatedVersionRef.current;
      lastTranslatedVersionRef.current = translatedVersion;
      /*
      // eslint-disable-next-line no-console
      console.info(
        '[translatedVersion watcher] fired:',
        typeof prevVersion === 'number' ? prevVersion : 'n/a',
        '->',
        translatedVersion
      );
      */
      if (typeof prevVersion === 'number' && translatedVersion !== prevVersion) {
        fetchBookDetailsRef.current?.({ silent: true });
        scheduleTranslationStatsUpdate(targetLanguage, selectedAIModel);
        refreshTranslationsImmediate(true).catch(err => {
          console.error('Failed to refresh translation statuses after watcher update', err);
        });
      }
    },
    error => {
      console.error('translatedVersion listener error', error);
    },
  );
  return () => {
    unsubscribe();
  };
}, [bookId, targetLanguage, selectedAIModel, scheduleTranslationStatsUpdate, refreshTranslationsImmediate]);

useEffect(() => {
	//console.log("useEffect 11");
	//console.time('useEffect 11');
  if (!selectedChapter) {
    updateUntranslatedChaptersAndWordCount(targetLanguage, selectedAIModel);
  }
  //console.timeEnd('useEffect 11');
}, [book.chapters, bookLanguage, targetLanguage, selectedAIModel, selectedChapter, updateUntranslatedChaptersAndWordCount]);

const fetchRecentExports = useCallback(async () => {
  if (isSandboxFixtureMode) {
    const fixtureRecentExports = sandboxFixturePayload?.recentExports;
    setRecentExports(Array.isArray(fixtureRecentExports)
      ? fixtureRecentExports as RecentExportEntry[]
      : []);
    return;
  }
  if (!auth.currentUser || !bookId) {
    return;
  }
  try {
    const response = await bookDetailApiGet(`${API_BASE}/export-history/${auth.currentUser.uid}`, {
      params: { bookId, limit: 25 },
    });
    const exports = (response?.data?.exports || []) as RecentExportEntry[];
    setRecentExports(exports);
  } catch (error) {
    console.error('Error fetching recent exports:', error);
  }
}, [API_BASE, auth.currentUser?.uid, bookId, isSandboxFixtureMode, sandboxFixturePayload]);

useEffect(() => {
  if (!bookId) {
    return;
  }
  const entries = contextTranslations || [];
  const seen = settledLockGuidsRef.current;

  if (!settledLocksInitializedRef.current) {
    entries.forEach(entry => {
      const guid = entry.lockGuid;
      if (!guid || entry.bookId !== bookId) {
        return;
      }
      const statusKey = resolveTranslationStatusState(entry);
      if (TERMINAL_JOB_STATUSES.has(statusKey)) {
        seen.add(guid);
      }
    });
    settledLocksInitializedRef.current = true;
    return;
  }

  entries.forEach(entry => {
    const guid = entry.lockGuid;
    if (!guid || entry.bookId !== bookId) {
      return;
    }
    const statusKey = resolveTranslationStatusState(entry);
    const isTerminal = TERMINAL_JOB_STATUSES.has(statusKey);
    if (!isTerminal) {
      if (seen.has(guid)) {
        seen.delete(guid);
      }
      return;
    }
    if (!seen.has(guid)) {
      seen.add(guid);
      const isSuccessful = SUCCESSFUL_JOB_STATUSES.has(statusKey);
      if (entry.type === 'Translation' && isSuccessful) {
        markChapterTranslatedLocally(entry.chapterGuid, entry.sourceLang, entry.targetLang, entry.chapterName);
        const { sourceLang, targetLang } = splitLanguagePair(selectedTab);
        const selectedIdentity = {
          bookId: String(bookId || ''),
          chapterGuid: selectedChapter?.chapter_guid || '',
          sourceLang,
          targetLang,
        };
        if (
          selectedIdentity.chapterGuid
          && sourceLang
          && targetLang
          && isTranslationForChapterPair(entry, selectedIdentity)
        ) {
          invalidateTranslatedContentRetry(entry.chapterGuid, sourceLang, targetLang);
          void hydrateTranslatedChapter(entry.chapterGuid, sourceLang, targetLang, {
            invalidateNegativeCache: true,
            reason: 'job_completion',
          }).catch(error => {
            console.error('Error hydrating translated chapter after completion:', error);
            message.error(error instanceof Error ? error.message : 'Unable to load the completed translation.');
          });
        }
      }
      if (entry.type === 'Export' && isSuccessful) {
        fetchRecentExports();
      }
      const typeKey = (entry.type || '').toString().trim().toLowerCase();
      if (isSuccessful && PROOFREAD_REFRESH_JOB_TYPES.has(typeKey)) {
        scheduleProofreadCompletionRefresh();
      }
      if (typeKey === 'relationship analysis') {
        const nextStatus = isSuccessful ? 'completed' : 'failed';
        setBook(prev => (prev ? { ...prev, characterRelationshipsAnalysisStatus: nextStatus } : prev));
        if (isSuccessful) {
          fetchRelationshipSummary({ silent: true });
          fetchRelationshipRerunSummary({ silent: true });
          bumpRegistryRefreshSignal();
        }
      }
    }
  });
}, [
  bumpRegistryRefreshSignal,
  contextTranslations,
  bookId,
  fetchRecentExports,
  fetchRelationshipRerunSummary,
  fetchRelationshipSummary,
  hydrateTranslatedChapter,
  invalidateTranslatedContentRetry,
  markChapterTranslatedLocally,
  scheduleProofreadCompletionRefresh,
  selectedChapter?.chapter_guid,
  selectedTab,
]);

const fetchBulkTermScanPreflight = async (targetLangValue: string, autoRunEnabled: boolean) => {
  const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
  const normalizedTargetLang = (targetLangValue || '').trim();
  if (!bookId || !userId || !bookLanguage || !normalizedTargetLang) {
    throw new Error('Missing context required for AI Glossary Scan preflight.');
  }
  return fetchTermScanPreflightRequest({
    bookId,
    userId,
    sourceLang: bookLanguage,
    targetLang: normalizedTargetLang,
    autoRunEnabled: Boolean(autoRunEnabled),
  });
};

useEffect(() => {
  termScanPreflightFetchIdRef.current += 1;
  termScanPreflightByLanguageFetchIdRef.current += 1;
  setTermScanPreflightState(null);
  setTermScanPreflightStateByLanguage({});
}, [bookId, bookLanguage, targetLanguage]);

useEffect(() => {
  if (
    !isTranslateModalVisible ||
    Boolean(selectedChapter) ||
    !bulkTermScanPreflightEnabled ||
    !translationAutoRunTermScanBeforeBulk
  ) {
    return;
  }
  const sourceLang = (bookLanguage || '').trim();
  const targetLang = (targetLanguage || '').trim();
  if (!sourceLang || !targetLang || sourceLang === targetLang) {
    return;
  }
  const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  const hasUntranslatedForTarget = book.chapters.some(
    (chapter) => !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid),
  );
  if (!hasUntranslatedForTarget) {
    setTermScanPreflightState(null);
    return;
  }
  const fetchId = ++termScanPreflightFetchIdRef.current;
  void (async () => {
    try {
      const preflight = await fetchBulkTermScanPreflight(targetLang, true);
      if (fetchId !== termScanPreflightFetchIdRef.current) {
        return;
      }
      setTermScanPreflightState(preflight || null);
    } catch (preflightError: any) {
      if (fetchId !== termScanPreflightFetchIdRef.current) {
        return;
      }
      console.error('Unable to load AI Glossary Scan preflight:', preflightError);
      setTermScanPreflightState(null);
    }
  })();
}, [
  isTranslateModalVisible,
  selectedChapter,
  bulkTermScanPreflightEnabled,
  translationAutoRunTermScanBeforeBulk,
  bookLanguage,
  targetLanguage,
  book,
  isChapterTranslatedForPair,
]);

useEffect(() => {
  if (
    !isTranslateModalVisible ||
    Boolean(selectedChapter) ||
    !bulkTermScanPreflightEnabled ||
    !translationAutoRunTermScanBeforeBulk ||
    resolvedTranslationTargetLanguages.length <= 1
  ) {
    termScanPreflightByLanguageFetchIdRef.current += 1;
    setTermScanPreflightStateByLanguage({});
    return;
  }

  const sourceLang = (bookLanguage || '').trim();
  const targets = resolvedTranslationTargetLanguages
    .map((lang) => (lang || '').trim())
    .filter((lang) => Boolean(lang) && lang.toLowerCase() !== sourceLang.toLowerCase())
    .filter((lang) => {
      const translatedChaptersKey = `translated_chapters_${sourceLang}_${lang}`;
      const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
      return book.chapters.some(
        (chapter) => !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid),
      );
    });

  if (!sourceLang || targets.length === 0) {
    termScanPreflightByLanguageFetchIdRef.current += 1;
    setTermScanPreflightStateByLanguage({});
    return;
  }

  const fetchId = ++termScanPreflightByLanguageFetchIdRef.current;
  void (async () => {
    const settled = await Promise.allSettled(
      targets.map(async (lang) => {
        const preflight = await fetchBulkTermScanPreflight(lang, true);
        return { lang, preflight: preflight || null };
      }),
    );

    if (fetchId !== termScanPreflightByLanguageFetchIdRef.current) {
      return;
    }

    const nextState: Record<string, any> = {};
    settled.forEach((result, index) => {
      const lang = targets[index];
      if (!lang) return;
      if (result.status === 'fulfilled') {
        nextState[lang] = result.value.preflight;
        return;
      }
      console.error(`Unable to load AI Glossary Scan preflight for ${lang}:`, result.reason);
      nextState[lang] = null;
    });
    setTermScanPreflightStateByLanguage(nextState);
  })();
}, [
  isTranslateModalVisible,
  selectedChapter,
  bulkTermScanPreflightEnabled,
  translationAutoRunTermScanBeforeBulk,
  resolvedTranslationTargetLanguages,
  bookLanguage,
  book,
  isChapterTranslatedForPair,
]);

const resolveTermScanPreflightExtraCreditCost = React.useCallback((preflightState: any | null): number => {
  const status = (preflightState?.status || '').toString().trim().toLowerCase();
  if (
    preflightState?.ready ||
    (status && status !== 'missing' && status !== 'failed' && status !== 'error')
  ) {
    return 0;
  }
  const rawCost = preflightState?.estimate?.creditCost;
  const parsed = typeof rawCost === 'number' ? rawCost : Number(rawCost);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 0;
  }
  return Math.ceil(parsed);
}, []);

const termScanPreflightExtraCreditCostByLanguage = useMemo(() => {
  if (selectedChapter || !bulkTermScanPreflightEnabled || !translationAutoRunTermScanBeforeBulk) {
    return {} as Record<string, number>;
  }

  const sourceLang = (bookLanguage || '').trim();
  const targetLanguagesWithPendingTranslations = resolvedTranslationTargetLanguages
    .map((lang) => (lang || '').trim())
    .filter((lang) => Boolean(lang) && lang.toLowerCase() !== sourceLang.toLowerCase())
    .filter((lang) => {
      const translatedChaptersKey = `translated_chapters_${sourceLang}_${lang}`;
      const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
      return book.chapters.some(
        (chapter) => !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid),
      );
    });

  if (targetLanguagesWithPendingTranslations.length === 0) {
    return {} as Record<string, number>;
  }

  return targetLanguagesWithPendingTranslations.reduce((acc, lang) => {
    const normalizedLang = (lang || '').trim();
    if (!normalizedLang) return acc;
    const preflightStateForLanguage =
      termScanPreflightStateByLanguage[normalizedLang] ??
      (normalizedLang === (targetLanguage || '').trim() ? termScanPreflightState : null);
    const extraCost = resolveTermScanPreflightExtraCreditCost(preflightStateForLanguage);
    if (extraCost > 0) {
      acc[normalizedLang] = extraCost;
    }
    return acc;
  }, {} as Record<string, number>);
}, [
  selectedChapter,
  bulkTermScanPreflightEnabled,
  translationAutoRunTermScanBeforeBulk,
  bookLanguage,
  book,
  isChapterTranslatedForPair,
  resolvedTranslationTargetLanguages,
  termScanPreflightStateByLanguage,
  targetLanguage,
  termScanPreflightState,
  resolveTermScanPreflightExtraCreditCost,
]);

const termScanPreflightExtraCreditCostTotal = useMemo(() => {
  return Object.values(termScanPreflightExtraCreditCostByLanguage).reduce((sum, cost) => {
    const parsed = Number(cost);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return sum;
    }
    return sum + Math.ceil(parsed);
  }, 0);
}, [termScanPreflightExtraCreditCostByLanguage]);

const runBulkTermScanAutoRun = async (targetLangValue: string, initialPreflight: any) => {
  const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
  const normalizedTargetLang = (targetLangValue || '').trim();
  if (!userId || !bookId) {
    return initialPreflight;
  }
  return runBulkTermScanAutoRunPreflight({
    initialPreflight,
    fetchPreflight: () => fetchBulkTermScanPreflight(normalizedTargetLang, true),
    startTermScan: () =>
      startTermScanRequest({
        bookId,
        userId,
        sourceLang: bookLanguage,
        targetLang: normalizedTargetLang,
      }),
    autoApplyConfident: (runId: string) =>
      autoApplyConfidentTermScan({
        bookId,
        runId,
        userId,
        sourceLang: bookLanguage,
        targetLang: normalizedTargetLang,
        confidenceThreshold: 0.85,
      })
        .then(() => undefined),
    metadataRetryAttempts: BULK_TERM_SCAN_RUN_METADATA_RETRY_ATTEMPTS,
    metadataRetryBaseDelayMs: BULK_TERM_SCAN_RUN_METADATA_RETRY_BASE_DELAY_MS,
    sleep: (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms)),
  });
};


const handleTranslation = async () => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.error("No user logged in");
    return;
  }

  if (!(selectedAIModel || '').trim()) {
    message.error('Please select an AI model before starting a translation.');
    return;
  }

  if (isTranslateModalVisible) {
    toggleTranslateModal();
  }
  setIsConfirmationModalVisible(false);

  let shouldFetchAfterTranslate = false;
  let shouldForceStatusRefresh = false;
  try {
    // If a specific chapter is selected, translate only that chapter
    if (selectedChapter) {
      const optimistic = buildOptimisticTranslationStatus({
        bookId,
        userId,
        sourceLanguage: bookLanguage,
        targetLanguage,
        chapter: {
          chapter_guid: selectedChapter.chapter_guid,
          chapter_name: selectedChapter.chapter_name,
          word_count: selectedChapter.word_count,
        },
        useBatch,
        batchInlineCount: 1,
      });
      addOptimisticTranslation(optimistic);

        const expectedChapterVersion = getChapterVersionByGuid(selectedChapter.chapter_guid) || selectedChapter.version;
        const resolvedFrenchDialogueRulesetVersion = resolveFrenchDialogueRulesetForRequest({
          workflowMode: translationWorkflowMode,
          targetLang: targetLanguage,
          quotationStyle,
          selectedRulesetVersion: frenchDialogueRulesetVersion,
        });
        const requestPayload = buildTranslationRequestPayload({
          userId,
          bookId,
          chapterGuid: selectedChapter.chapter_guid,
          sourceLang: bookLanguage,
          targetLang: targetLanguage,
          model: selectedAIModel,
          version: translateVersion,
          genres,
          intendedAudience: intendedAudience,
          quotationStyle: quotationStyle,
          frenchTone: targetLanguage === 'French' ? frenchTone : '',
          overwriteFlag: true,
          enforceFrenchEmDash: enforceFrenchEmDash,
          useBatch: useBatch,
          workflowMode: translationWorkflowMode,
          frenchDialogueRulesetVersion: resolvedFrenchDialogueRulesetVersion,
          enableSystemProofreader: autoProofreadEnabled,
          requireCharacterAnalysis: effectiveRequireCharacterAnalysis,
          expectedChapterVersion,
        });
        const response = await bookDetailApiPost(`${API_BASE}/translate-chapter/`, requestPayload);

      if (response.data.status === "error") {
        const backendMessage = response.data.message || '';
        message.error('Translation request failed. Check the Processing Status panel for updates.', 10);
        if (backendMessage.toLowerCase().includes("rejected content")) {
          setRejectedChapters([selectedChapter.chapter_name]);
        }
        setIsLoading(false);
        return;
      }
      await refreshTranslationsImmediate(true);
      const respStatus = response?.data?.status;
      if (respStatus === 'success') {
        message.success('Translation completed successfully.', 8);
        shouldFetchAfterTranslate = true;
        shouldForceStatusRefresh = true;
        debouncedFetchActiveTranslations(true);
      } else if (respStatus === 'pending' || respStatus === 'queued') {
        if (respStatus === 'queued') {
          message.info('Translation queued as a background job. Track progress in Processing Status.', 6);
        }
        // Pending translations surface progress via the status panel.
        await refreshTranslationsImmediate(true);
      } else {
        // Fallback
        message.info('Translation request submitted.', 6);
      }


    } else {
      const requestedTargetLanguages = resolvedTranslationTargetLanguages;
      if (!requestedTargetLanguages.length) {
        message.error('Please select at least one target language before translating.', 8);
        return;
      }

      type BulkTargetResult = {
        targetLang: string;
        status: 'queued' | 'partial' | 'failed' | 'skipped';
        failedChapters?: number;
      };

      const isMultiTargetRequest = requestedTargetLanguages.length > 1;
      const hasAnyBulkTranslationsPending = requestedTargetLanguages.some((lang) => {
        const translatedChaptersKey = `translated_chapters_${bookLanguage}_${lang}`;
        const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
        return book.chapters.some((chapter) =>
          !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid),
        );
      });
      let relationshipAnalysisIntentLockGuid: string | null = null;
      let relationshipAnalysisPreflightFailed = false;
      let relationshipAnalysisSummarySnapshot: RelationshipAnalysisSummary | null = relationshipSummary;
      const relationshipAnalysisIntentTargetLang =
        requestedTargetLanguages[0] || (targetLanguage || '').trim() || bookLanguage;

      const clearRelationshipAnalysisClientIntent = () => {
        if (!relationshipAnalysisIntentLockGuid) {
          return;
        }
        clearClientIntent(relationshipAnalysisIntentLockGuid);
        relationshipAnalysisIntentLockGuid = null;
      };

      const ensureRelationshipAnalysisClientIntent = (
        summary?: RelationshipAnalysisSummary | null,
      ) => {
        if (relationshipAnalysisIntentLockGuid) {
          return;
        }
        const estimatedChaptersToAnalyze =
          summary?.chaptersToAnalyze ?? summary?.missingChapters ?? 0;
        relationshipAnalysisIntentLockGuid =
          `client-intent-relationship-analysis-${bookId}-${relationshipAnalysisIntentTargetLang}-${Date.now()}`;
        addClientIntent({
          lockGuid: relationshipAnalysisIntentLockGuid,
          chapterGuid: 'book_analysis',
          chapterName: 'Character Relationship Analysis',
          sourceLang: bookLanguage,
          targetLang: relationshipAnalysisIntentTargetLang,
          timestamp: new Date().toISOString(),
          status: 'queued',
          processingState: 'queued',
          cleared: false,
          bookId,
          userId,
          word_count: summary?.wordCount,
          estimatedTimeMinutes: Math.max(1, Math.ceil(estimatedChaptersToAnalyze * 0.5)),
          error: '',
          type: 'Relationship Analysis',
          clientIntent: true,
        });
      };

      if (hasAnyBulkTranslationsPending && !relationshipAnalysisInFlight) {
        const latestSummary = await fetchRelationshipSummary({ silent: true });
        if (latestSummary) {
          relationshipAnalysisSummarySnapshot = latestSummary;
        }
      }

      const shouldQueueRelationshipAnalysis =
        hasAnyBulkTranslationsPending &&
        (shouldQueueBulkRelationshipAnalysis({
          missingChapters: relationshipAnalysisSummarySnapshot?.missingChapters,
          analysisInFlight: relationshipAnalysisInFlight,
        }) ||
          (!relationshipAnalysisInFlight &&
            isMultiTargetRequest &&
            !relationshipAnalysisSummarySnapshot));

      if (effectiveRequireCharacterAnalysis && shouldQueueRelationshipAnalysis) {
        ensureRelationshipAnalysisClientIntent(relationshipAnalysisSummarySnapshot);
        try {
          const analysisResponse = await bookDetailApiPost(`${API_BASE}/analyze-missing-relationships/`, {
            userId,
            bookId,
            sourceLang: bookLanguage,
            targetLang: relationshipAnalysisIntentTargetLang,
            includeAnalyzed: false,
            clearExisting: false,
          });
          const statusKey = (analysisResponse?.data?.status || '').toString().trim().toLowerCase();
          const detailMessage = (analysisResponse?.data?.message || '').toString().trim();
          const knownStatuses = new Set([
            'queued',
            'running',
            'noop',
            'success',
            'completed',
            'error',
            'insufficient_credits',
          ]);
          if (!knownStatuses.has(statusKey)) {
            clearRelationshipAnalysisClientIntent();
            relationshipAnalysisPreflightFailed = true;
            message.error(detailMessage || 'Unable to verify character relationship analysis state.', 8);
          }
          if (statusKey === 'queued' || statusKey === 'running') {
            setBook(prev => (prev ? { ...prev, characterRelationshipsAnalysisStatus: statusKey } : prev));
          }
          if (
            statusKey === 'error' &&
            !isRelationshipAnalysisAlreadyInProgressMessage(detailMessage)
          ) {
            clearRelationshipAnalysisClientIntent();
            relationshipAnalysisPreflightFailed = true;
            message.error(detailMessage || 'Unable to start character relationship analysis.', 8);
          }
          if (statusKey === 'insufficient_credits') {
            clearRelationshipAnalysisClientIntent();
            relationshipAnalysisPreflightFailed = true;
            message.error(detailMessage || 'Not enough credits to run character relationship analysis.', 8);
          }
          if (statusKey === 'noop' || statusKey === 'success' || statusKey === 'completed') {
            clearRelationshipAnalysisClientIntent();
          }
          if (
            statusKey === 'error' &&
            isRelationshipAnalysisAlreadyInProgressMessage(detailMessage)
          ) {
            setBook(prev => (prev ? { ...prev, characterRelationshipsAnalysisStatus: 'queued' } : prev));
          }
        } catch (analysisError: any) {
          clearRelationshipAnalysisClientIntent();
          relationshipAnalysisPreflightFailed = true;
          const detail = isBookDetailApiError(analysisError)
            ? analysisError.response?.data?.detail
            : null;
          message.error(detail || 'Failed to start character relationship analysis.', 8);
        }
      }

      if (relationshipAnalysisPreflightFailed) {
        return;
      }

      const queueBulkTranslationForTarget = async (targetLang: string): Promise<BulkTargetResult> => {
        const translatedChaptersKey = `translated_chapters_${bookLanguage}_${targetLang}`;
        const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
        const chaptersToTranslate = book.chapters
          .filter(isContentBearingChapterRow)
          .filter(chapter => !isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid));
        if (!chaptersToTranslate.length) {
          return { targetLang, status: 'skipped' };
        }

        const bulkClientIntentByChapterGuid: Record<string, string> = {};
        const bulkIntentTimestamp = new Date().toISOString();
        const bulkBatchJobId = useBatch ? `queued-${Date.now()}` : undefined;
        chaptersToTranslate.forEach(chapter => {
          const tempLockGuid = `client-intent-${bookId}-${targetLang}-${chapter.chapter_guid}-${Date.now()}`;
          bulkClientIntentByChapterGuid[chapter.chapter_guid] = tempLockGuid;
          const optimisticStatus: TranslationStatus = {
            lockGuid: tempLockGuid,
            chapterGuid: chapter.chapter_guid,
            chapterName: chapter.chapter_name,
            sourceLang: bookLanguage,
            targetLang,
            timestamp: bulkIntentTimestamp,
            status: 'queued',
            processingState: 'queued',
            cleared: false,
            bookId,
            userId,
            word_count: chapter.word_count,
            estimatedTimeMinutes: computeTranslationEstimatedMinutes(chapter.word_count),
            error: '',
            type: 'Translation',
            clientIntent: true,
          };
          if (useBatch && bulkBatchJobId) {
            (optimisticStatus as any).batchJobId = bulkBatchJobId;
            (optimisticStatus as any).batchProvider = 'gemini';
            (optimisticStatus as any).batchInlineCount = chaptersToTranslate.length;
          }
          addClientIntent(optimisticStatus);
        });

        const clearBulkClientIntents = (chapterGuids?: string[]) => {
          if (Array.isArray(chapterGuids) && chapterGuids.length > 0) {
            chapterGuids.forEach((chapterGuid) => {
              const lockGuid = bulkClientIntentByChapterGuid[chapterGuid];
              if (lockGuid) {
                clearClientIntent(lockGuid);
              }
            });
            return;
          }
          Object.values(bulkClientIntentByChapterGuid).forEach(clearClientIntent);
        };

        let glossaryScanIntentLockGuid: string | null = null;
        const ensureGlossaryScanClientIntent = () => {
          if (glossaryScanIntentLockGuid) {
            return;
          }
          glossaryScanIntentLockGuid = `client-intent-term-scan-${bookId}-${targetLang}-${Date.now()}`;
          addClientIntent({
            lockGuid: glossaryScanIntentLockGuid,
            chapterGuid: 'term_scan',
            chapterName: 'AI Glossary Scan',
            sourceLang: bookLanguage,
            targetLang,
            timestamp: new Date().toISOString(),
            status: 'queued',
            processingState: 'queued',
            cleared: false,
            bookId,
            userId,
            estimatedTimeMinutes: 10,
            error: '',
            type: 'Glossary Scan',
            clientIntent: true,
          });
        };
        const clearGlossaryScanClientIntent = () => {
          if (!glossaryScanIntentLockGuid) {
            return;
          }
          clearClientIntent(glossaryScanIntentLockGuid);
        };

        let preflightDecision: 'auto_run' | 'skip' = 'skip';
        let preflightState: any | null = null;
        if (!bulkTermScanPreflightEnabled) {
          preflightState = null;
          preflightDecision = 'skip';
        } else {
          try {
            preflightState = await fetchBulkTermScanPreflight(targetLang, translationAutoRunTermScanBeforeBulk);
            if (!preflightState?.ready && translationAutoRunTermScanBeforeBulk) {
              ensureGlossaryScanClientIntent();
              preflightState = await runBulkTermScanAutoRun(targetLang, preflightState);
            }
          } catch (preflightError: any) {
            console.error('Bulk term scan preflight error:', preflightError);
            preflightState = null;
            if (!isMultiTargetRequest || targetLang === targetLanguage) {
              setTermScanPreflightState(null);
            }
            if (translationAutoRunTermScanBeforeBulk) {
              clearBulkClientIntents();
              clearGlossaryScanClientIntent();
              return { targetLang, status: 'failed' };
            }
          }
          preflightDecision =
            preflightState?.enabled === false || preflightState?.status === 'disabled'
              ? 'skip'
              : translationAutoRunTermScanBeforeBulk
                ? 'auto_run'
                : 'skip';
        }
        if (!isMultiTargetRequest || targetLang === targetLanguage) {
          setTermScanPreflightState(preflightState || null);
        }
        if (
          preflightDecision === 'auto_run' &&
          !preflightState?.latestRun?.runId
        ) {
          ensureGlossaryScanClientIntent();
          try {
            preflightState = await runBulkTermScanAutoRun(targetLang, preflightState);
            if (!isMultiTargetRequest || targetLang === targetLanguage) {
              setTermScanPreflightState(preflightState || null);
            }
          } catch (runMetadataError: any) {
            console.error('Bulk term scan run metadata retry error:', runMetadataError);
          }
        }
        if (
          preflightDecision === 'auto_run' &&
          !preflightState?.latestRun?.runId
        ) {
          clearBulkClientIntents();
          return { targetLang, status: 'failed' };
        }

        const resolvedQuotationStyle = isMultiTargetRequest
          ? (getDefaultQuotationStyle(targetLang) || quotationStyle || '')
          : quotationStyle;
        const resolvedFrenchTone = targetLang === 'French'
          ? (isMultiTargetRequest ? (frenchTone || 'AI Detect') : frenchTone)
          : '';
        const resolvedFrenchDialogueRulesetVersion = resolveFrenchDialogueRulesetForRequest({
          workflowMode: translationWorkflowMode,
          targetLang,
          quotationStyle: resolvedQuotationStyle,
          selectedRulesetVersion: frenchDialogueRulesetVersion,
        });
        const bulkPayload = buildBulkTranslationRequestPayload({
          userId,
          bookId,
          sourceLang: bookLanguage,
          targetLang,
          model: selectedAIModel,
          version: translateVersion,
          genres,
          quotationStyle: resolvedQuotationStyle,
          enforceFrenchEmDash,
          intendedAudience,
          frenchTone: resolvedFrenchTone,
          useBatch,
          workflowMode: translationWorkflowMode,
          frenchDialogueRulesetVersion: resolvedFrenchDialogueRulesetVersion,
          enableSystemProofreader: autoProofreadEnabled,
          requireCharacterAnalysis: effectiveRequireCharacterAnalysis,
          preflightDecision,
          preflightState,
          chapters: chaptersToTranslate.map(chapter => ({
            chapterGuid: chapter.chapter_guid,
            expectedChapterVersion: getChapterVersionByGuid(chapter.chapter_guid) || chapter.version,
            overwriteFlag: false,
          })),
        });

        try {
          const response = await bookDetailApiPost(`${API_BASE}/translate-book/`, bulkPayload);
          const data = response?.data || {};
          if (data?.fallback) {
            clearBulkClientIntents();
            return { targetLang, status: 'failed' };
          }
          const failedChapters = (data?.chapters || []).filter((ch: any) => ch?.status === 'error');
          if (failedChapters.length) {
            const failedChapterGuids: string[] = Array.from(
              new Set(
                failedChapters
                  .map((ch: any): string => (ch?.chapterGuid || ch?.chapter_guid || '').toString().trim())
                  .filter((guid: string) => guid.length > 0),
              ),
            );
            if (failedChapterGuids.length > 0) {
              clearBulkClientIntents(failedChapterGuids);
            } else {
              clearBulkClientIntents();
            }
            return { targetLang, status: 'partial', failedChapters: failedChapters.length };
          }
          return { targetLang, status: 'queued' };
        } catch (bulkError) {
          console.error('Bulk translation error:', bulkError);
          clearBulkClientIntents();
          return { targetLang, status: 'failed' };
        }
      };

      const targetResults: BulkTargetResult[] = new Array(requestedTargetLanguages.length);
      const targetConcurrency = Math.max(
        1,
        Math.min(BULK_TRANSLATION_TARGET_CONCURRENCY, requestedTargetLanguages.length),
      );

      if (targetConcurrency === 1) {
        targetResults[0] = await queueBulkTranslationForTarget(requestedTargetLanguages[0]);
      } else {
        let nextTargetIndex = 0;
        const worker = async () => {
          while (true) {
            const currentIndex = nextTargetIndex;
            nextTargetIndex += 1;
            if (currentIndex >= requestedTargetLanguages.length) {
              return;
            }
            const targetLang = requestedTargetLanguages[currentIndex];
            targetResults[currentIndex] = await queueBulkTranslationForTarget(targetLang);
          }
        };
        await Promise.all(
          Array.from({ length: targetConcurrency }, () => worker()),
        );
      }

      const queuedTargets = targetResults.filter(result => result.status === 'queued');
      const partialTargets = targetResults.filter(result => result.status === 'partial');
      const skippedTargets = targetResults.filter(result => result.status === 'skipped');
      const failedTargets = targetResults.filter(result => result.status === 'failed');

      if ((queuedTargets.length > 0 || partialTargets.length > 0) && !useBatch) {
        shouldFetchAfterTranslate = true;
      }

      if (requestedTargetLanguages.length === 1) {
        if (queuedTargets.length > 0) {
          message.success('Book translation queued successfully.', 6);
        } else if (partialTargets.length > 0) {
          const failedCount = partialTargets[0]?.failedChapters || 0;
          message.warning(`Some chapters failed to queue (${failedCount}). Check Processing Status.`, 8);
        } else if (skippedTargets.length > 0) {
          message.info('All chapters are already translated for the selected target language.', 6);
        } else {
          message.error('Bulk translation request failed. Please try again.', 8);
        }
      } else {
        if (queuedTargets.length && !partialTargets.length && !failedTargets.length) {
          message.success(`Book translation queued for ${queuedTargets.length} language(s).`, 6);
        } else if (queuedTargets.length || partialTargets.length) {
          const queuedCount = queuedTargets.length + partialTargets.length;
          message.warning(
            `Queued ${queuedCount}/${requestedTargetLanguages.length} language(s). Failed: ${failedTargets.length}.`,
            8,
          );
        } else if (skippedTargets.length === requestedTargetLanguages.length) {
          message.info('All selected languages already have completed chapter translations.', 6);
        } else {
          message.error('No selected languages could be queued. Please try again.', 8);
        }
      }
    }

  } catch (error) {
    console.error('General translation error:', error);
    message.error('Error during translation', 10);
    debouncedFetchActiveTranslations();
  } finally {
    // Refresh the book data to reflect the new translations
    if (shouldFetchAfterTranslate) {
      if (shouldForceStatusRefresh) {
        debouncedFetchActiveTranslations(true);
      } else {
        debouncedFetchActiveTranslations();
      }
      fetchBookDetails();
    } else {
      debouncedFetchActiveTranslations();
    }
    setIsLoading(false);
  }
};


const handleProofread = async () => {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    console.error("No user logged in");
    return;
  }

  if (!(selectedProofreadModel || '').trim()) {
    message.error('Please select an AI model before starting a proofreading job.');
    return;
  }

  if (proofreadSubmittingRef.current) return;
  proofreadSubmittingRef.current = true;
  const admissionController = new AbortController();
  proofreadAdmissionControllerRef.current = admissionController;
  let quotaNoticeShown = false;
  const submitProofreadAdmission = createProofreadAdmissionQueue(admissionController.signal, () => {
    if (!quotaNoticeShown) {
      quotaNoticeShown = true;
      message.info('Proofreading submission is paused briefly and will resume automatically.', 8);
    }
  });
  // Capture the confirmed request before closing the modal invalidates UI intent.
  const passRequestId = proofreadPassRequestIdRef.current || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  proofreadPassRequestIdRef.current = passRequestId;
  const durableProofreadTempGuid = !selectedChapter && isProofreadChecked && proofreadingPassSummary?.durableSubmissionEnabled
    ? `Temp-ProofreadPass-${passRequestId}` : null;
  if (durableProofreadTempGuid) {
    const intent: TranslationStatus = {
      chapterGuid: `proofreading-pass-${passRequestId}`, chapterName: 'Whole-book proofreading',
      bookId, userId, sourceLang: bookLanguage, targetLang: proofreadVersion,
      timestamp: new Date().toISOString(), status: 'pending', type: 'ProofreadSubmission',
      lockGuid: durableProofreadTempGuid, clientIntent: true,
      statusMessage: 'Submitting proofreading request…',
    };
    addClientIntent(intent);
    addOptimisticTranslation(intent);
    setProcessingStatusOpenSignal(value => value + 1);
  }
  setIsProofreadModalVisible(false);
  setIsProofreadConfirmationModalVisible(false);

  const sanitizeInput = (input: string): string => {
    // Remove any HTML tags
    input = input.replace(/<[^>]*>/g, '');

    // Limit the length of the input
    const maxLength = 250 // Adjust this value as needed
    input = input.slice(0, maxLength);

    return input;
  };

  const reconcileOptimisticProofreadLaunch = ({
    tempLockGuid,
    status,
    lockGuid,
    lockKey,
  }: {
    tempLockGuid: string;
    status: unknown;
    lockGuid?: unknown;
    lockKey?: unknown;
  }) => (
    reconcileProofreadOptimisticTranslation({
      tempLockGuid,
      status,
      lockGuid,
      lockKey,
      updateOptimisticLockGuid,
      mergeTranslation,
      removeOptimisticTranslation,
    })
  );

  let failedProofreadChapters: string[] = [];
  let successfulProofreadChapters: string[] = [];
  let failedNotesChapters: string[] = [];
  let successfulNotesChapters: string[] = [];

  try {
    const proofreadStatus: Record<string, string> = {};
    const notesStatus: Record<string, string> = {};
    let anySingleProofreadPending = false;
    let didSubmitSingleProofread = false;
    let didSubmitSingleEditingNotes = false;
    let shouldFetchAfterProofread = false;
    let shouldForceStatusRefresh = false;
    const translatedProofreadChapterMap =
      proofreadVersion === 'Original' || durableProofreadTempGuid
        ? null
        : await ensureProofreadTranslationAvailability(
            proofreadVersion,
            selectedChapter?.chapter_guid ? [selectedChapter.chapter_guid] : undefined,
          );
    if (admissionController.signal.aborted) return;
    const getProofreadTranslatedChapter = (chapterGuid: string) =>
      translatedProofreadChapterMap?.get(chapterGuid);
    const getExpectedProofreadVersionForChapter = (chapterGuid: string) => {
      if (proofreadVersion === 'Original') {
        return getChapterVersionByGuid(chapterGuid);
      }
      return getProofreadTranslatedChapter(chapterGuid)?.version;
    };

    book.chapters.forEach(chapter => {
      proofreadStatus[chapter.chapter_name] = 'pending';
      notesStatus[chapter.chapter_name] = 'pending';
    });

    // If a specific chapter is selected, proofread only that chapter
  if (selectedChapter) {
      if (isProofreadChecked) {
        didSubmitSingleProofread = true;
        // Determine if this single chapter already has a proofread version to set overwrite
        let shouldOverwriteProofread = false;
        if (proofreadVersion === "Original") {
          const hasPendingReview =
            String(selectedChapter.proofread_mode || '').trim().toLowerCase() === 'proposal'
            && String(selectedChapter.proofread_change_set_id || '').trim() !== ''
            && !['', 'applied', 'superseded', 'discarded'].includes(
              String(selectedChapter.proofread_change_set_status || '').trim().toLowerCase(),
            );
          shouldOverwriteProofread = hasSavedProofreadForRow(selectedChapter) || hasPendingReview;
        } else {
          const translatedChapter = getProofreadTranslatedChapter(selectedChapter.chapter_guid);
          const hasPendingReview =
            String(translatedChapter?.proofread_mode || '').trim().toLowerCase() === 'proposal'
            && String(translatedChapter?.proofread_change_set_id || '').trim() !== ''
            && !['', 'applied', 'superseded', 'discarded'].includes(
              String(translatedChapter?.proofread_change_set_status || '').trim().toLowerCase(),
            );
          shouldOverwriteProofread = hasSavedProofreadForRow(translatedChapter) || hasPendingReview;
        }
        // Create temporary record for the proofread task
        const tempLockGuid = `Temp-${bookId}-${proofreadVersion}-${selectedChapter.chapter_guid}-${Date.now()}`;
        const optimistic: TranslationStatus = {
          lockGuid:   tempLockGuid,
          chapterGuid: selectedChapter.chapter_guid,
          chapterName: selectedChapter.chapter_name,
          sourceLang:  bookLanguage,
          targetLang:  proofreadVersion,
          timestamp:   new Date().toISOString(),
          status:      'queued',
          bookId,
          userId,
          word_count:           selectedChapter.word_count,
          estimatedTimeMinutes: ensureMinimumLockEstimate(Math.ceil(selectedChapter.word_count / 500)),
          error:      '',
          type:       'Proofread',
          clientIntent: true,
        };
        if (useBatch) {
          (optimistic as any).batchJobId = `queued-${Date.now()}`;
          (optimistic as any).batchProvider = 'gemini';
          (optimistic as any).batchInlineCount = 1;
        }

        addOptimisticTranslation(optimistic);

        const expectedChapterVersion = getExpectedProofreadVersionForChapter(selectedChapter.chapter_guid);
        let response;
        try {
          response = await submitProofreadAdmission(() => bookDetailApiPost(`${API_BASE}/proofread-chapter/`, {
            userId: userId,
            bookId: bookId,
            chapterGuid: selectedChapter.chapter_guid,
            sourceLang: bookLanguage,
            model: selectedProofreadModel,
            genre: genres.join(', '),
            tense: chapterTense,
            proofreadVersion: proofreadVersion,
            quotationStyle: quotationStyle,
            tone: getTargetLanguageFromVersion(proofreadVersion) === 'French' ? frenchTone : '',
            additionalInstructions: sanitizeInput(proofreadAdditionalInstructions),
            explanationLanguage: proofreadExplanationLanguage || 'English',
            overwriteFlag: shouldOverwriteProofread,
            autoAcceptAllChanges: autoAcceptAllProofreadChanges,
            useBatch: useBatch,
            expectedChapterVersion,
          }, { signal: admissionController.signal }));
        } catch (error) {
          removeOptimisticTranslation(tempLockGuid);
          throw error;
        }

        if (admissionController.signal.aborted) return;
        const proofreadLaunch = reconcileOptimisticProofreadLaunch({
          tempLockGuid,
          status: response?.data?.status,
          lockGuid: response?.data?.lockGuid,
          lockKey: response?.data?.lockKey,
        });

        if (response.data.status === "error") {
          const backendMessage = response.data.message || '';
          message.error('Proofreading request failed. Check the Processing Status panel for updates.', 10);
          if (backendMessage.toLowerCase().includes("rejected content")) {
            setRejectedChapters([selectedChapter.chapter_name]);
          }
          setIsLoading(false);
          return;
        }

        // Provide user feedback based on status
        try {
          if (proofreadLaunch.normalizedStatus === 'pending') {
            anySingleProofreadPending = true;
            message.info('Proofreading submitted. It will complete in the background.', 8);
          } else if (proofreadLaunch.normalizedStatus === 'proofread') {
            message.success('Proofreading completed successfully.', 8);
            shouldFetchAfterProofread = true;
            if (!shouldForceStatusRefresh) {
              shouldForceStatusRefresh = true;
              debouncedFetchActiveTranslations(true);
            }
          } else {
            message.info('Proofreading request submitted.', 6);
          }
        } catch {}
      }

      if (admissionController.signal.aborted) return;
      if (isEditingNotesChecked) {
        didSubmitSingleEditingNotes = true;
        // Determine if this single chapter already has editing notes to set overwrite
        let shouldOverwriteNotes = false;
        if (proofreadVersion === "Original") {
          shouldOverwriteNotes = !!selectedChapter.proofread_comments;
        } else {
          const translatedChapter = getProofreadTranslatedChapter(selectedChapter.chapter_guid);
          shouldOverwriteNotes = !!translatedChapter?.proofread_comments;
        }
        // Create temporary record for the editing notes task
        const tempLockGuid = `TempNotes-${bookId}-${proofreadVersion}-${selectedChapter.chapter_guid}-${Date.now()}`;
        const optimistic: TranslationStatus = {
          lockGuid:   tempLockGuid,
          chapterGuid: selectedChapter.chapter_guid,
          chapterName: selectedChapter.chapter_name,
          sourceLang:  bookLanguage,
          targetLang:  proofreadVersion,
          timestamp:   new Date().toISOString(),
          status:      'queued',
          bookId,
          userId,
          word_count:           selectedChapter.word_count,
          estimatedTimeMinutes: ensureMinimumLockEstimate(Math.ceil(selectedChapter.word_count / 500)),
          error:      '',
          type:       'EditingNotes',
          clientIntent: true,
        };

        addOptimisticTranslation(optimistic);

        let response;
        try {
          response = await bookDetailApiPost(`${API_BASE}/proofreadnotes-chapter/`, {
            userId: userId,
            bookId: bookId,
            chapterGuid: selectedChapter.chapter_guid,
            sourceLang: bookLanguage,
            model: selectedProofreadModel,
            genre: genres.join(', '),
            tense: chapterTense,
            proofreadVersion: proofreadVersion,
            overwriteFlag: shouldOverwriteNotes
          }, { signal: admissionController.signal });
        } catch (error) {
          removeOptimisticTranslation(tempLockGuid);
          throw error;
        }

        if (admissionController.signal.aborted) return;
        const notesResponse = normalizeEditingNotesResponse(response.data);
        const notesLaunch = reconcileOptimisticProofreadLaunch({
          tempLockGuid,
          status: notesResponse.status,
          lockGuid: notesResponse.lockGuid,
          lockKey: notesResponse.lockKey,
        });
        if (notesResponse.status === 'failed') {
          const backendMessage = notesResponse.message || '';
          message.error('Editing notes request failed. Check the Processing Status panel for updates.', 10);
          if (backendMessage.toLowerCase().includes("rejected content")) {
            setRejectedChapters([selectedChapter.chapter_name]);
          }
          setIsLoading(false);
          return;
        }
        notesStatus[selectedChapter.chapter_name] = notesResponse.status;
        if (notesLaunch.normalizedStatus === 'pending') {
          message.info('Editing notes submitted. They will complete in the background.', 8);
        } else {
          message.success('Editing notes generated successfully.', 8);
        }
        // Editing notes are applied immediately; refresh afterwards
        shouldFetchAfterProofread = true;
        if (!shouldForceStatusRefresh) {
          shouldForceStatusRefresh = true;
          debouncedFetchActiveTranslations(true);
        }
      }
    } else {
      if (isProofreadChecked) {
        if (proofreadingPassSummary?.durableSubmissionEnabled) {
          const tempGuid = durableProofreadTempGuid!;
          const accepted = await submitDurableProofread(
            { userId, bookId: bookId!, version: proofreadVersion, requestId: passRequestId },
            async requestId => (await bookDetailApiPost(`${API_BASE}/proofreading-passes/submit`, {
              userId, bookId, sourceLang: bookLanguage, proofreadVersion, requestId,
              mode: bulkProofreadMode, expectedLatestPassId: proofreadingPassSummary?.latestPass?.passId || '',
              confirmRepeat: bulkProofreadMode === 'next_pass', model: selectedProofreadModel,
              genre: genres.join(', '), tense: chapterTense, quotationStyle,
              additionalInstructions: sanitizeInput(proofreadAdditionalInstructions),
              explanationLanguage: proofreadExplanationLanguage || 'English',
              autoAcceptAllChanges: autoAcceptAllProofreadChanges,
              tone: getTargetLanguageFromVersion(proofreadVersion) === 'French' ? frenchTone : '', useBatch: false,
            })).data,
            async requestId => (await bookDetailApiGet(`${API_BASE}/proofreading-passes/submission`, {
              params: { userId, bookId, requestId },
            })).data,
            () => mergeTranslation(tempGuid, { statusMessage: 'Confirming your proofreading submission…' }),
          );
          if (admissionController.signal.aborted || auth.currentUser?.uid !== userId) return;
          clearClientIntent(tempGuid);
          removeOptimisticTranslation(tempGuid);
          addOptimisticTranslation({
            chapterGuid: `proofreading-pass-${accepted.requestId || passRequestId}`, chapterName: 'Whole-book proofreading',
            bookId, userId, sourceLang: bookLanguage, targetLang: proofreadVersion,
            timestamp: new Date().toISOString(), type: 'ProofreadSubmission',
            status: accepted.processingStatus || 'pending', lockGuid: accepted.lockGuid, lockKey: accepted.lockKey,
            statusMessage: accepted.statusMessage || `Preparing proofreading for ${accepted.targetCount} chapters. You can leave this page.`,
          });
          proofreadPassRequestIdRef.current = null;
          message.info('Proofreading accepted. You can leave this page while it continues.', 8);
          shouldForceStatusRefresh = true;
          void fetchProofreadingPassSummary(proofreadVersion);
        } else {
        const proofreadTargetLanguage = getTargetLanguageFromVersion(proofreadVersion);
        if (proofreadVersion !== 'Original' && !proofreadTargetLanguage) {
          message.error('Please select a valid proofread language before starting bulk proofreading.', 8);
          return;
        }
        const selectedBulkProofreadMode = bulkProofreadMode;
        const requestedTargetGuidSet = getBulkProofreadTargetGuidSet(selectedBulkProofreadMode);
        if (requestedTargetGuidSet && requestedTargetGuidSet.size === 0) {
          message.warning(
            proofreadingPassSummary?.blockingReason || 'No chapters are eligible for this proofreading pass.',
            8,
          );
          return;
        }
        await bookDetailApiPost(`${API_BASE}/analyze-missing-relationships/`, {
          userId: userId,
          bookId: bookId,
          sourceLang: bookLanguage,
          targetLang: proofreadTargetLanguage || targetLanguage,
        }, { signal: admissionController.signal });
        if (admissionController.signal.aborted) return;

        const passStartResponse = await bookDetailApiPost(`${API_BASE}/proofreading-passes/start`, {
          userId,
          bookId,
          sourceLang: bookLanguage,
          proofreadVersion,
          mode: selectedBulkProofreadMode,
          confirmRepeat: selectedBulkProofreadMode === 'next_pass',
          requestId: passRequestId,
          expectedLatestPassId: proofreadingPassSummary?.latestPass?.passId || '',
        }, { signal: admissionController.signal });
        if (admissionController.signal.aborted) return;
        const passData = passStartResponse.data || {};
        proofreadPassRequestIdRef.current = null;
        const passId = String(passData.proofreadPassId || passData.passId || '').trim();
        const passNumber = Number(passData.proofreadPassNumber || passData.passNumber || 0);
        const passMode = String(passData.proofreadPassMode || passData.mode || selectedBulkProofreadMode).trim() as ProofreadPassMode;
        const serverTargetGuidSet = new Set(
          (Array.isArray(passData.targetChapterGuids) ? passData.targetChapterGuids : [])
            .map((guid: unknown) => String(guid || '').trim())
            .filter(Boolean),
        );

        // Find chapters that need proofreading
        const chaptersToProofread = book.chapters.filter(chapter => {
          if (!serverTargetGuidSet.has(chapter.chapter_guid)) {
            return false;
          }
          if (proofreadVersion === "Original") {
            return true;
          } else {
            const translatedChapter = getProofreadTranslatedChapter(chapter.chapter_guid);
            return hasTranslatedContent(translatedChapter);
          }
        });

        if (chaptersToProofread.length === 0) {
          if (bulkProofreadEligibility.pendingProofreadReviewCount > 0) {
            message.warning(
              `No chapters are eligible for bulk proofreading. ${bulkProofreadEligibility.pendingProofreadReviewCount} chapter${bulkProofreadEligibility.pendingProofreadReviewCount === 1 ? '' : 's'} still ${bulkProofreadEligibility.pendingProofreadReviewCount === 1 ? 'has' : 'have'} pending proofread review.`,
              8,
            );
          }
          return;
        }

        // Create temporary records for each proofread task
        const proofreadIntentByChapterGuid: Record<string, string> = {};
        chaptersToProofread.forEach(chapter => {
          const tempLockGuid = `Temp-${bookId}-${proofreadVersion}-${chapter.chapter_guid}-${Date.now()}`;
          proofreadIntentByChapterGuid[chapter.chapter_guid] = tempLockGuid;
          const t: TranslationStatus = {
            lockGuid:   tempLockGuid,
            chapterGuid: chapter.chapter_guid,
            chapterName: chapter.chapter_name,
            sourceLang:  bookLanguage,
            targetLang:  proofreadVersion,
            timestamp:   new Date().toISOString(),
            status:      'queued',
            bookId,
            userId,
            word_count:           chapter.word_count,
            estimatedTimeMinutes: ensureMinimumLockEstimate(Math.ceil(chapter.word_count / 750)),
            error:      '',
            type:       'Proofread',
            clientIntent: true,
          };
          if (useBatch) {
            (t as any).batchJobId = `queued-${Date.now()}`;
            (t as any).batchProvider = 'gemini';
            (t as any).batchInlineCount = chaptersToProofread.length;
          }
          addOptimisticTranslation(t);
		});

        // Proofread all unproofread chapters
        const chapterPromises = chaptersToProofread.map(chapter => ({
          promise: submitProofreadAdmission(() => bookDetailApiPost(`${API_BASE}/proofread-chapter/`, {
            userId: userId,
            bookId: bookId,
            chapterGuid: chapter.chapter_guid,
            sourceLang: bookLanguage,
            model: selectedProofreadModel,
            genre: genres.join(', '),
            tense: chapterTense,
            proofreadVersion: proofreadVersion,
            quotationStyle: quotationStyle,
            tone: getTargetLanguageFromVersion(proofreadVersion) === 'French' ? frenchTone : '',
            additionalInstructions: sanitizeInput(proofreadAdditionalInstructions),
            explanationLanguage: proofreadExplanationLanguage || 'English',
            overwriteFlag: passMode === 'retry_failed' || passMode === 'next_pass',
            autoAcceptAllChanges: autoAcceptAllProofreadChanges,
            useBatch: useBatch,
            expectedChapterVersion: getExpectedProofreadVersionForChapter(chapter.chapter_guid),
            proofreadPassId: passId || undefined,
            proofreadPassNumber: passNumber > 0 ? passNumber : undefined,
            proofreadPassMode: passMode || undefined,
          }, { signal: admissionController.signal }))
          .then(response => {
            if (admissionController.signal.aborted) return;
            const tempLockGuid = proofreadIntentByChapterGuid[chapter.chapter_guid];
            const launchResult = reconcileOptimisticProofreadLaunch({
              tempLockGuid,
              status: response?.data?.status,
              lockGuid: response?.data?.lockGuid,
              lockKey: response?.data?.lockKey,
            });
            const localStatus = launchResult.normalizedStatus;
            proofreadStatus[chapter.chapter_name] = localStatus;
            if (localStatus === 'proofread') {
              shouldFetchAfterProofread = true;
              if (!shouldForceStatusRefresh) {
                shouldForceStatusRefresh = true;
                debouncedFetchActiveTranslations(true);
              }
            }
          })
          .catch(error => {
            removeOptimisticTranslation(proofreadIntentByChapterGuid[chapter.chapter_guid]);
            proofreadStatus[chapter.chapter_name] = 'failed';
          }),
          chapterName: chapter.chapter_name
        }));

        const promises = chapterPromises.map(chapter => chapter.promise.catch(() => {}));
        await Promise.allSettled(promises);
        if (admissionController.signal.aborted) return;
        void fetchProofreadingPassSummary(proofreadVersion);

        }
      }

      if (admissionController.signal.aborted) return;
      if (isEditingNotesChecked) {
        // Find chapters that need editing notes
        const chaptersToAddNotes = book.chapters.filter(chapter => {
          if (proofreadVersion === "Original") {
            return !chapter.proofread_comments;
          } else {
            const translatedChapter = getProofreadTranslatedChapter(chapter.chapter_guid);
            return !translatedChapter?.proofread_comments;
          }
        });

        // Create temporary records for each editing notes task
        const notesIntentByChapterGuid: Record<string, string> = {};
        chaptersToAddNotes.forEach(chapter => {
          const tempLockGuid = `TempNotes-${bookId}-${proofreadVersion}-${chapter.chapter_guid}-${Date.now()}`;
          notesIntentByChapterGuid[chapter.chapter_guid] = tempLockGuid;
          const t: TranslationStatus = {
            lockGuid:   tempLockGuid,
            chapterGuid: chapter.chapter_guid,
            chapterName: chapter.chapter_name,
            sourceLang:  bookLanguage,
            targetLang:  proofreadVersion,
            timestamp:   new Date().toISOString(),
            status:      'queued',
            bookId,
            userId,
            word_count:           chapter.word_count,
            estimatedTimeMinutes: ensureMinimumLockEstimate(Math.ceil(chapter.word_count / 750)),
            error:      '',
            type:       'EditingNotes',
            clientIntent: true,
          };
          addOptimisticTranslation(t);
		});

        // Generate editing notes for all chapters
        const chapterPromises = chaptersToAddNotes.map(chapter => ({
          promise: submitProofreadAdmission(() => bookDetailApiPost(`${API_BASE}/proofreadnotes-chapter/`, {
            userId: userId,
            bookId: bookId,
            chapterGuid: chapter.chapter_guid,
            sourceLang: bookLanguage,
            model: selectedProofreadModel,
            genre: genres.join(', '),
            tense: chapterTense,
            proofreadVersion: proofreadVersion,
            overwriteFlag: false
          }, { signal: admissionController.signal }))
          .then(response => {
            if (admissionController.signal.aborted) return;
            const normalizedResponse = normalizeEditingNotesResponse(response.data);
            const launchResult = reconcileOptimisticProofreadLaunch({
              tempLockGuid: notesIntentByChapterGuid[chapter.chapter_guid],
              status: normalizedResponse.status,
              lockGuid: normalizedResponse.lockGuid,
              lockKey: normalizedResponse.lockKey,
            });
            notesStatus[chapter.chapter_name] = launchResult.normalizedStatus;
            if (launchResult.normalizedStatus === 'proofread') {
              shouldFetchAfterProofread = true;
            }
            if (launchResult.normalizedStatus !== 'failed' && !shouldForceStatusRefresh) {
              shouldForceStatusRefresh = true;
              debouncedFetchActiveTranslations(true);
            }
          })
          .catch(error => {
            removeOptimisticTranslation(notesIntentByChapterGuid[chapter.chapter_guid]);
            notesStatus[chapter.chapter_name] = 'failed';
          }),
          chapterName: chapter.chapter_name
        }));

        const promises = chapterPromises.map(chapter => chapter.promise.catch(() => {}));
        await Promise.allSettled(promises);
        if (admissionController.signal.aborted) return;


      }
    }

    if (admissionController.signal.aborted) return;
    if (shouldForceStatusRefresh) {
      debouncedFetchActiveTranslations(true);
    } else {
      await debouncedFetchActiveTranslations();
    }

    if (admissionController.signal.aborted) return;
    // Refresh only when we expect immediate chapter mutations (e.g. editing notes or inline success).
    if (shouldFetchAfterProofread || isEditingNotesChecked) {
      fetchBookDetails();
    }

    // Determine overall status for toasts
    const isSingle = !!selectedChapter;
    const suppressFinalForSingleProofread = shouldSuppressProofreadAggregateToast({
      isSingle,
      didSubmitSingleProofread,
      didSubmitSingleEditingNotes,
    });
    const runSummary = summarizeProofreadCompletionStatuses(proofreadStatus, notesStatus);
    const hasPending = anySingleProofreadPending || runSummary === 'pending';
    if (!suppressFinalForSingleProofread && runSummary && !durableProofreadTempGuid) {
      if (runSummary === 'failed') {
        message.error('Some chapters could not be proofread.', 10);
      } else if (runSummary === 'skipped') {
        message.info('No eligible chapters were submitted. Selected chapters were already proofread or skipped.', 8);
      } else if (hasPending) {
        message.info('Proofreading submitted. It will complete in the background.', 8);
      } else {
        message.success('Proofreading completed successfully.', 8);
      }
    }

    //if(!selectedChapter) {
    //  setIsFailedProofreadModalVisible(true);
    //}

  } catch (error) {
    if (admissionController.signal.aborted) return;
    if (durableProofreadTempGuid) {
      const pending = listPendingProofreadSubmissions(userId, bookId!).length > 0;
      if (pending) {
        mergeTranslation(durableProofreadTempGuid, { statusMessage: 'Submission not yet confirmed. Refresh Processing Status before trying again.' });
      } else {
        clearClientIntent(durableProofreadTempGuid);
        removeOptimisticTranslation(durableProofreadTempGuid);
      }
    }
    console.error('General proofread error:', error);
    const detail = formatAxiosErrorDetail(error, 'Error during proofread');
    if (isBookDetailApiError(error) && error.response?.status === 409) {
      message.warning(detail, 10);
      void fetchProofreadingPassSummary(proofreadVersion);
    } else if (detail.includes('pending proofread review')) {
      message.warning(detail, 10);
    } else {
      message.error(detail || 'Error during proofread', 10);
    }
    debouncedFetchActiveTranslations();
  } finally {
    proofreadSubmittingRef.current = false;
    if (proofreadAdmissionControllerRef.current === admissionController) {
      proofreadAdmissionControllerRef.current = null;
    }
    if (!admissionController.signal.aborted) {
      setSelectedChapter(null);
      setIsLoading(false);
    }
  }
};

const handleCoverUpload = async (
  event: React.ChangeEvent<HTMLInputElement>,
  options: { forceOriginal?: boolean } = {},
) => {
  const file = event.target.files ? event.target.files[0] : null;
  if (!file || !auth.currentUser) return;

  if (file.size > MAX_FILE_SIZE) {
    message.error('File size should be less than 5MB.');
    return;
  }

  setIsLoading(true);

  const formData = new FormData();
  formData.append('file', file);

  try {
    const translatedTargetLanguage = (() => {
      if (options.forceOriginal) return '';
      if (!selectedTab || selectedTab === 'Original') return '';
      return getTargetLanguageFromTab(selectedTab);
    })();

    if (translatedTargetLanguage) {
      const imageUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to read image file'));
        reader.readAsDataURL(file);
      });
      const languageKey = translatedTargetLanguage.toLowerCase();
      const translatedUploadResponse = await bookDetailApiPost(`${API_BASE}/api/upload-cover`, {
        bookId,
        imageUrl,
        language: languageKey,
        userId: auth.currentUser.uid,
      });
      const payload = translatedUploadResponse.data || {};
      const uploadedCoverUrl = payload.coverUrl || '';
      if (payload.status !== 'success' || !uploadedCoverUrl) {
        throw new Error(payload.message || payload.detail || 'Failed to upload cover');
      }
      await handleUpdateLanguageCovers({ [languageKey]: uploadedCoverUrl });
      message.success(`Cover uploaded successfully for ${translatedTargetLanguage}`);
      return;
    }

    const response = await bookDetailApiPost(
      `${API_BASE}/update-book-cover/${bookId}?user_id=${auth.currentUser.uid}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    const uploadedCoverUrl = response.data.coverUrl || '';

    setBook(prevBook => ({ ...prevBook, coverUrl: uploadedCoverUrl }));
    setLanguageCovers(prev => {
      const next = { ...prev };
      if (uploadedCoverUrl) {
        next.original = uploadedCoverUrl;
      } else {
        delete next.original;
      }
      return next;
    });
    message.success('Cover uploaded successfully');
  } catch (error) {
    console.error("Error uploading cover: ", error);
    const detail = isBookDetailApiError(error)
      ? String(error.response?.data?.detail || error.response?.data?.message || '').trim()
      : '';
    message.error(detail ? `Error uploading cover: ${detail}` : 'Error uploading cover');
  } finally {
    if (event.target) {
      event.target.value = '';
    }
    setIsLoading(false);
  }
};

const handleRemoveCover = async () => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }

  setIsLoading(true);

  try {
    const url = `${API_BASE}/api/book/${bookId}/cover/original?user_id=${auth.currentUser.uid}`;
    await bookDetailApiDelete(url);
    setBook(prev => ({ ...prev, coverUrl: '' }));
    setLanguageCovers(prev => ({
      ...prev,
      original: ''
    }));
  } catch (error) {
    console.error("Error removing cover: ", error);
    message.error('Error removing cover');
  }

  setIsLoading(false);
};

const openSimpleEditDetails = () => {
  setSimpleEditDetailsDraft({
    title: String(book.title || ''),
    author: String(book.author || ''),
    series: String(book.series || ''),
    booknumber: (book as any).seriesBookNumber ?? (book as any).booknumber ?? '',
  });
  setSimpleEditDetailsVisible(true);
};

const saveSimpleEditDetails = async () => {
  const title = simpleEditDetailsDraft.title.trim();
  const author = simpleEditDetailsDraft.author.trim();
  const series = simpleEditDetailsDraft.series.trim();
  if (!title) {
    message.error('A book title is required.');
    return;
  }
  if (!auth.currentUser) {
    message.error('Please sign in to edit book details.');
    return;
  }

  setSimpleEditDetailsSaving(true);
  try {
    const currentSeries = String(book.series || '').trim();
    let nextSeriesId = series && series.toLocaleLowerCase() === currentSeries.toLocaleLowerCase()
      ? selectedSeriesId
      : null;
    if (series && !nextSeriesId) {
      nextSeriesId = await ensureSeriesRecordAndAttach(series, author);
    }

    const rawBookNumber = String(simpleEditDetailsDraft.booknumber ?? '').trim();
    const parsedBookNumber = rawBookNumber === '' ? 0 : Number(rawBookNumber);
    const safeBookNumber = Number.isFinite(parsedBookNumber) ? parsedBookNumber : 0;
    await bookDetailApiPost(`${API_BASE}/update-book/`, {
      bookID: bookId,
      userID: auth.currentUser.uid,
      title,
      author,
      series,
      booknumber: safeBookNumber,
      language: bookLanguage,
      genre: genres,
      tropes,
      keywords,
      intendedAudience,
      seriesId: nextSeriesId,
      seriesInheritance,
      seriesDescription,
      frenchNarrativeTone: editBook.frenchNarrativeTone ?? null,
    });

    const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
    const updatedBook: Book = updatedBookResponse.data;
    if (Array.isArray(updatedBook.chapters)) {
      updatedBook.chapters = [...updatedBook.chapters].sort((a: Chapter, b: Chapter) => a.order - b.order);
    }
    setBook(updatedBook);
    setEditBook(previous => ({
      ...previous,
      title,
      author,
      series,
      booknumber: safeBookNumber,
      seriesId: nextSeriesId,
    }));
    setSelectedSeriesId(nextSeriesId);
    setIsTitleValid(true);
    setSimpleEditDetailsVisible(false);
    message.success('Book details updated.');
  } catch (error) {
    console.error('Error saving Guided Workflow book details:', error);
    message.error(formatAxiosErrorDetail(error, 'Unable to update book details.'));
  } finally {
    setSimpleEditDetailsSaving(false);
  }
};

const confirmSimpleCoverRemoval = () => {
  Modal.confirm({
    rootClassName: 'theme-aware-confirm',
    title: 'Remove this cover?',
    content: 'The source book will show a placeholder until another cover is uploaded.',
    okText: 'Remove cover',
    okButtonProps: { danger: true },
    cancelText: 'Cancel',
    onOk: handleRemoveCover,
  });
};

const startEditingChapter = (chapter: Chapter) => {
  setEditingChapterGuid(chapter.chapter_guid);
  setEditedChapterName(chapter.chapter_name);
};

const saveChapterName = async (chapterGuid: string) => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }

  setIsLoading(true);

  try {
    const response = await bookDetailApiPost(
      `${API_BASE}/update-chapter-name/${bookId}/${chapterGuid}`,
      {
        user_id: auth.currentUser.uid,
        new_name: editedChapterName
      }
    );

    if (response.data.status === "success") {
      // Update local state
      const updatedChapters = book.chapters.map(chapter => {
        if (chapter.chapter_guid === chapterGuid) {
          return { ...chapter, chapter_name: editedChapterName };
        }
        return chapter;
      });
      setBook({ ...book, chapters: updatedChapters });
      message.success("Chapter name updated successfully");
    } else {
      message.error("Error updating chapter name",10);
    }
  } catch (error) {
    console.error("Error updating chapter name:", error);
    message.error("Error updating chapter name",10);
  }

  // Reset editing state
  setEditingChapterGuid(null);
  setEditedChapterName('');
  setIsLoading(false);
};


const cancelEdit = () => {
  setEditingChapterGuid(null);
  setEditedChapterName('');
};

const handleChapterReorder = useCallback(async ({ chapterGuid, sourceIndex, destinationIndex }: ChapterReorderPayload) => {
  if (isEpubUpload) {
    message.info('Chapter reordering is disabled for books uploaded from EPUB files.', 6);
    return;
  }

  if (isSavingChapterOrder) {
    return;
  }

  const userId = auth.currentUser?.uid;
  if (!userId) {
    message.error('You must be signed in to reorder chapters', 6);
    return;
  }

  const totalChapters = book.chapters.length;
  if (!totalChapters) {
    return;
  }

  const safeDestination = Math.min(Math.max(destinationIndex, 0), totalChapters - 1);

  let normalizedSourceIndex = sourceIndex;
  if (
    normalizedSourceIndex < 0 ||
    normalizedSourceIndex >= totalChapters ||
    book.chapters[normalizedSourceIndex]?.chapter_guid !== chapterGuid
  ) {
    normalizedSourceIndex = book.chapters.findIndex(chapter => chapter.chapter_guid === chapterGuid);
    if (normalizedSourceIndex === -1) {
      message.error('Unable to locate the selected chapter. Please refresh and try again.', 8);
      return;
    }
  }

  if (normalizedSourceIndex === safeDestination) {
    return;
  }

  const previousOrder = book.chapters.map(chapter => chapter.chapter_guid);
  const previousOrderSet = new Set(previousOrder);

  const reordered = Array.from(book.chapters);
  const [moved] = reordered.splice(normalizedSourceIndex, 1);
  if (!moved) {
    return;
  }
  reordered.splice(safeDestination, 0, moved);

  const normalized = reordered.map((chapter, index) => ({
    ...chapter,
    order: index + 1,
  }));

  const orderedGuids = normalized.map(chapter => chapter.chapter_guid);
  const hasChanges = orderedGuids.some((guid, index) => guid !== previousOrder[index]);
  if (!hasChanges) {
    return;
  }

  setBook(prev => ({ ...prev, chapters: normalized }));
  syncSelectedChapterRef(normalized);

  setIsSavingChapterOrder(true);
  try {
    await bookDetailApiPost(`${API_BASE}/update-chapter-order/${bookId}`, {
      user_id: userId,
      ordered_chapter_guids: orderedGuids,
    });
    fetchBookDetails();
  } catch (error: any) {
    console.error('Error updating chapter order:', error);
    const detail = error?.response?.data?.detail;
    let serverMessage: string | undefined;
    let supportCode: string | undefined;
    if (detail && typeof detail === 'object') {
      serverMessage = detail.message || detail.detail || undefined;
      supportCode = detail.support_code;
    } else if (typeof detail === 'string') {
      serverMessage = detail;
    } else if (error?.response?.data?.message) {
      serverMessage = error.response.data.message;
    }
    const fallback = serverMessage || 'Failed to update chapter order';
    const composed = supportCode ? `${fallback} (Support code: ${supportCode})` : fallback;
    message.error(composed, 10);
    let restoredChapters: Chapter[] = [];
    setBook(prev => {
      const chaptersByGuid = new Map(prev.chapters.map(chapter => [chapter.chapter_guid, chapter]));
      const rebuilt: Chapter[] = [];
      previousOrder.forEach((guid, index) => {
        const existing = chaptersByGuid.get(guid);
        if (existing) {
          rebuilt.push({ ...existing, order: index + 1 });
        }
      });
      const remaining = prev.chapters.filter(chapter => !previousOrderSet.has(chapter.chapter_guid));
      const remainingBaseOrder = rebuilt.length;
      remaining.forEach((chapter, offset) => {
        rebuilt.push({ ...chapter, order: remainingBaseOrder + offset + 1 });
      });
      restoredChapters = rebuilt;
      return { ...prev, chapters: rebuilt };
    });
    if (restoredChapters.length) {
      syncSelectedChapterRef(restoredChapters);
    }
  } finally {
    setIsSavingChapterOrder(false);
  }
}, [API_BASE, auth.currentUser?.uid, book.chapters, bookId, fetchBookDetails, isEpubUpload, isSavingChapterOrder, syncSelectedChapterRef]);

const toggleExportModal = async () => {
  if (isExportModalVisible) {
    setIsExportModalVisible(false);
    return;
  }

  setExportOrientation('horizontal');
  refreshTranslationsImmediate(true);
  fetchRecentExports();

  let resolvedExportVersion = '';
  try {
    resolvedExportVersion = await resolveModalDefaultSelection('export');
  } catch (error) {
    console.error('Error resolving export default selection:', error);
  }

  if (resolvedExportVersion === 'Proofread' && exportProofreadOptionProps.disabled) {
    resolvedExportVersion = '';
  }

  setExportVersion(resolvedExportVersion);
  setIsExportModalVisible(true);
};


const handleExport = async (requestPayload?: ExportBookRequestPayload) => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }
  const requestedVersion = (requestPayload?.version ?? exportVersion).trim();
  const requestedFormat = requestPayload?.format ?? exportFormat;
  const requestedIncludeImages = requestPayload?.includeImages ?? (uploadType === 'Word' ? includeWordImages : includeImages);
  const requestedOrientation = requestPayload?.writingOrientation ?? exportOrientation;
  if (!requestedVersion) {
    message.error('Please select an export version before exporting.');
    return;
  }

  setIsLoading(true);
  if (isExportModalVisible) {
    setIsExportModalVisible(false);
  }

  try {
    const response = await bookDetailApiPost(
      `${API_BASE}/export-uploaded-book/${bookId}`,
      {
        user_id: auth.currentUser.uid,
        version: requestedVersion,
        format: requestedFormat,
        title: book.title,
        include_images: requestedFormat === 'Word' ? requestedIncludeImages : true,
        writing_orientation: requestedOrientation,
      },
      {
        responseType: 'blob',
      },
    );
    const contentType = response?.headers?.['content-type'] as string | undefined;
    if (contentType && contentType.includes('application/json')) {
      const payload = JSON.parse(await (response.data as Blob).text());
      const lockGuid = payload?.lockGuid;
      if (lockGuid) {
        const queuedStatus: TranslationStatus = {
          lockGuid,
          chapterGuid: '',
          chapterName: `Export (${requestedFormat})`,
          sourceLang: '',
          targetLang: '',
          timestamp: new Date().toISOString(),
          status: 'queued',
          processingState: 'queued',
          cleared: false,
          bookId,
          userId: auth.currentUser.uid,
          estimatedTimeMinutes: estimateExportMinutes(book.chapters?.length || 0),
          error: '',
          type: 'Export',
          clientIntent: true,
          version: requestedVersion,
          format: requestedFormat,
        };
        addClientIntent(queuedStatus);
      }
      message.info(payload?.message || 'Export queued. Track progress in Processing Status.', 6);
      await refreshTranslationsImmediate(true);
    } else {
      const disposition = response?.headers?.['content-disposition'] as string | undefined;
      const headerFilename = parseFilenameFromDisposition(disposition);
      const fallbackFilename = resolveExportFilename(book.title, requestedVersion, requestedFormat);
      const filename = headerFilename && !headerFilename.toLowerCase().startsWith('book.') ? headerFilename : fallbackFilename;
      downloadBlob(response.data as Blob, filename);
      message.success('Export ready. Your download should begin shortly.', 6);
      await fetchRecentExports();
    }
  } catch (error) {
    console.error("Error exporting book:", error);
    const response = (error as any)?.response;
    if (response?.data) {
      try {
        const payloadText = response.data instanceof Blob ? await response.data.text() : JSON.stringify(response.data);
        const payload = JSON.parse(payloadText);
        if (payload?.status === 'queued') {
          const lockGuid = payload?.lockGuid;
          if (lockGuid) {
            const queuedStatus: TranslationStatus = {
              lockGuid,
              chapterGuid: '',
              chapterName: `Export (${requestedFormat})`,
              sourceLang: '',
              targetLang: '',
              timestamp: new Date().toISOString(),
              status: 'queued',
              processingState: 'queued',
              cleared: false,
              bookId,
              userId: auth.currentUser.uid,
              estimatedTimeMinutes: estimateExportMinutes(book.chapters?.length || 0),
              error: '',
              type: 'Export',
              clientIntent: true,
              version: requestedVersion,
              format: requestedFormat,
            };
            addClientIntent(queuedStatus);
          }
          message.info(payload?.message || 'Export queued. Track progress in Processing Status.', 6);
          await refreshTranslationsImmediate(true);
          return;
        }
        if (payload?.errorKind === 'UNRESOLVED_IMAGE_REFERENCE' && typeof payload.message === 'string') {
          message.error(payload.message, 10);
          return;
        }
      } catch {
        // Fall through to generic error message.
      }
    }
    message.error("Error exporting book",10);
  } finally {
    setIsLoading(false);
  }
};

const refreshExportDownloadLink = async (exportId: string): Promise<string | null> => {
  if (!auth.currentUser) {
    message.error('No user logged in');
    return null;
  }
  if (!exportId) {
    message.error('Missing export identifier');
    return null;
  }
  try {
    const freshUrl = await requestExportDownloadUrl({
      userId: auth.currentUser.uid,
      exportId,
      currentUser: auth.currentUser,
    });
    await refreshTranslationsImmediate(true);
    await fetchRecentExports();
    return freshUrl || null;
  } catch (error) {
    console.error('Error refreshing export download link:', error);
    message.error('Unable to refresh export link', 6);
    return null;
  }
};

const refreshTranslationProgress = async () => {
  if (!bookId || !auth.currentUser) {
    return;
  }
  const key = `refresh-progress-${bookId}`;
  setIsRefreshingProgress(true);
  message.loading({ content: 'Refreshing progress...', key });
  try {
    const response = await bookDetailApiPost<Record<string, TranslationProgressEntry>>(
      `${API_BASE}/uploadedbooks/${bookId}/refresh_progress`
    );
    const updatedProgress = response.data;
    setBook(prev => ({
      ...prev,
      translation_progress: updatedProgress,
    }));
    message.success({ content: 'Progress refreshed', key, duration: 2 });
  } catch (error) {
    console.error('Error refreshing progress:', error);
    message.error({ content: 'Failed to refresh progress', key, duration: 2 });
  } finally {
    setIsRefreshingProgress(false);
  }
};

const downloadRecentExport = async (entry: RecentExportEntry) => {
  if (!entry.exportId) {
    message.error('Missing export identifier');
    return;
  }
  try {
    let targetUrl = entry.downloadUrl || null;
    if (targetUrl) {
      try {
        const parsedUrl = new URL(targetUrl, window.location.origin);
        if (!parsedUrl.pathname.endsWith('/export-download-proxy')) {
          const headResponse = await fetch(targetUrl, { method: 'HEAD' });
          if (!headResponse.ok) {
            targetUrl = null;
          }
        }
      } catch {
        targetUrl = null;
      }
    }
    if (!targetUrl) {
      targetUrl = await refreshExportDownloadLink(entry.exportId);
    }
    if (targetUrl) {
      const parsedUrl = new URL(targetUrl, window.location.origin);
      if (parsedUrl.pathname.endsWith('/export-download-proxy')) {
        const response = await bookDetailFetch(targetUrl);
        if (!response.ok) {
          throw new Error(`Export proxy download failed with status ${response.status}`);
        }
        const disposition = response.headers.get('content-disposition') || undefined;
        const filename = parseFilenameFromDisposition(disposition)
          || entry.downloadFilename
          || 'export';
        downloadBlob(await response.blob(), filename);
      } else {
        triggerDownloadUrl(targetUrl);
      }
    } else {
      message.error('Unable to download export', 6);
    }
  } catch (error) {
    console.error('Error downloading export:', error);
    message.error('Unable to download export', 6);
  }
};

useEffect(() => {
  if (!bookId || (!auth.currentUser && !isSandboxFixtureMode)) {
    return;
  }
  fetchRecentExports();
}, [bookId, auth.currentUser?.uid, fetchRecentExports, isSandboxFixtureMode]);

useEffect(() => {
  if (isSandboxFixtureMode) {
    return;
  }
  if (!bookId || !auth.currentUser || !expressJob?.id) {
    return;
  }
  const terminalStatuses = new Set(EXPRESS_TERMINAL_STATUSES);
  if (terminalStatuses.has(String(expressJob.status || '').toLowerCase())) {
    return;
  }
  let cancelled = false;
  const poll = async () => {
    try {
      const response = await bookDetailApiGet(`${API_BASE}/books/${bookId}/express-translation/${expressJob.id}`, {
        params: { user_id: auth.currentUser?.uid },
      });
      if (cancelled) return;
      const nextJob = response.data;
      setExpressJob(nextJob);
      const status = String(nextJob?.status || '').toLowerCase();
      if (status === 'completed') {
        message.success('Translation export is ready.', 6);
        await fetchRecentExports();
        await refreshTranslationsImmediate(true);
      } else if (status === 'partial_success') {
        message.warning(
          String(nextJob?.message || 'Translation finished with an export or email issue.')
            .replace(/\bExpress translation\b/gi, 'Simple translation'),
          8,
        );
        await refreshTranslationsImmediate(true);
      } else if (status === 'failed') {
        message.error(
          String(nextJob?.message || 'Translation failed.').replace(/\bExpress translation\b/gi, 'Simple translation'),
          10,
        );
        await refreshTranslationsImmediate(true);
      } else if (status === 'cancelled' || status === 'canceled') {
        message.info(String(nextJob?.message || 'This translation run was cancelled.'), 10);
        await refreshTranslationsImmediate(true);
      }
    } catch (error) {
      console.error('Error polling Simple translation:', error);
    }
  };
  const interval = window.setInterval(poll, 5000);
  void poll();
  return () => {
    cancelled = true;
    window.clearInterval(interval);
  };
}, [API_BASE, bookId, auth.currentUser, auth.currentUser?.uid, expressJob?.id, expressJob?.status, fetchRecentExports, isSandboxFixtureMode, refreshTranslationsImmediate]);

const deleteChapter = async (chapterGuid: string) => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }

  if (selectedTab !== 'Original') {
    message.warning(
      'Source chapters can only be deleted from the Original workspace. Use More to manage translated chapters.',
      8,
    );
    return;
  }

  const userId = auth.currentUser.uid; // Assign the user ID to a variable

  const deleteWarning = exportFormat === 'ePub' ?
    (<>
      This permanently deletes the source chapter and every matching translation. The chapter will remain in an exported ePub in its original language.<br /><br />
      This action cannot be undone.
    </>) :
    (<>
      This permanently deletes the source chapter and every matching translation.<br /><br />
      This action cannot be undone.
    </>);

  Modal.confirm({
    title: 'Permanently delete this source chapter?',
    content: deleteWarning,
    onOk: async () => {
      setIsLoading(true);

      try {
		const response = await bookDetailApiDelete(`${API_BASE}/delete-chapter/${bookId}/${chapterGuid}?user_id=${userId}`);
        // Update the local state to reflect the deletion
        const updatedChapters = book.chapters.filter(chapter => chapter.chapter_guid !== chapterGuid);
        setBook({ ...book, chapters: updatedChapters });
        message.success("Chapter deleted successfully.");
      } catch (error) {
        console.error("Error deleting chapter:", error);
        message.error("Error deleting chapter",10);
      } finally {
        setIsLoading(false);
      }
    },
  });
};


const handleBlurbSave = async () => {
  if (!auth.currentUser) {
    console.error("No user logged in");
    return;
  }
  const currentBlurbWordCount = getBlurbWordCount(blurb);
  if (currentBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
    message.error(formatBlurbWordLimitMessage(currentBlurbWordCount), 8);
    throw new Error('Blurb exceeds word limit');
  }

  setIsLoading(true);

  try {
    await bookDetailApiPost(`${API_BASE}/update-book-blurb/`, {
		bookID: bookId,
		userID: auth.currentUser.uid,
		blurb: blurb
    });
    //setIsEditing(false);
	//refresh after update
    const updatedBookResponse = await bookDetailApiGet(`${API_BASE}/uploadedbooks/book/${bookId}`);
    let updatedBook: Book = updatedBookResponse.data;
    updatedBook.chapters = updatedBook.chapters.sort((a: Chapter, b: Chapter) => a.order - b.order);
    setBook(updatedBook);
  } catch (error) {
    console.error("Error saving book details:", error);
  }
  setIsLoading(false);
};


const exportBlurbToTxt = () => {
  // Determine which blurb to export based on the blurbActiveTabKey
  let currentBlurb = blurb;
  let fileSuffix = 'Original';
  if (blurbActiveTabKey !== 'original' && blurbActiveTabKey !== 'blurb') {
	const languagePair = blurbActiveTabKey;
    currentBlurb = blurbTranslations[languagePair] || 'No translation available';
    fileSuffix = languagePair.replace('_', ' to ');
  }

  // Sanitize the title for use in the filename
  const sanitizedTitle = book.title.replace(/\s+/g, '_').replace(/[^\w-]/g, '');
  downloadTextFile(currentBlurb, `${sanitizedTitle}_blurb_${fileSuffix}.txt`);
};

const getMetadataOverwriteDetail = (error: unknown): any => {
  const candidate = error as any;
  return candidate?.response?.data?.detail ?? (
    isBookDetailApiError(error) ? candidate.response?.data?.detail : null
  );
};

const isMetadataOverwriteRequiredError = (error: unknown): boolean => {
  const detail = getMetadataOverwriteDetail(error);
  return Boolean(detail && typeof detail === 'object' && detail.errorKind === METADATA_OVERWRITE_REQUIRED);
};

const metadataHasTranslation = (type: 'title' | 'series' | 'blurb', sourceLang: string, targetLang: string) => {
  const encodedKey = metadataTranslationFieldKey(type, sourceLang, targetLang);
  const rawKey = `${type}_${sourceLang}_${targetLang}`;
  const value = book[encodedKey] ?? book[rawKey];
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== undefined && value !== null;
};

const getMetadataOverwriteFields = (
  sourceLang: string,
  targetLang: string,
  selectedFields: { title: boolean; series: boolean; blurb: boolean },
): string[] => {
  const fields: string[] = [];
  if (selectedFields.title && metadataHasTranslation('title', sourceLang, targetLang)) {
    fields.push('Title');
  }
  if (selectedFields.series && metadataHasTranslation('series', sourceLang, targetLang)) {
    fields.push('Series Name');
  }
  if (selectedFields.blurb && metadataHasTranslation('blurb', sourceLang, targetLang)) {
    fields.push('Blurb');
  }
  return fields;
};

const handleTranslateBookClick = () => {
    proceedWithTranslateBookClick();
};

const proceedWithTranslateBookClick = () => {
  if (!hasUnproofreadChapters) {
    setTranslateVersion("Proofread");
    //console.log("test");
  } else {
    setTranslateVersion(prevVersion => {
      //console.log("Setting TranslateVersion to Original");
      return "Original";
    });
    //console.log("test2");
  }
  setSelectedAIModel("");
  setSelectedChapter(null);
  toggleTranslateModal();
};

const handleProofreadBookClick = () => {
if(!isEditing){
	handleEditToggle();
}
setSelectedChapter(null);
void toggleProofreadModal();
}

const handleExportBookClick = () => {
if(!isEditing){
handleEditToggle();
}
void toggleExportModal();
}

const startExpressTranslation = async () => {
  if (expressSubmitPending.current) return;
  if (!auth.currentUser) {
    message.error('No user logged in');
    return;
  }
  const requestUserId = resolvedOwnerUserId || auth.currentUser.uid;
  const targetCandidate = expressTargetLanguage || targetLanguage || resolvedTranslationTargetLanguages[0] || '';
  const resolvedTarget = simpleTargetLanguageOptions.includes(targetCandidate) ? targetCandidate : '';
  if (!resolvedTarget) {
    message.error('Choose a target language.');
    return;
  }
  if (isSimpleTargetLanguageCompleted(resolvedTarget)) {
    message.info(`${resolvedTarget} translation is already complete.`);
    return;
  }
  if (simpleTranslationInProgress) {
    message.warning(simpleTranslationInProgressMessage);
    return;
  }
  if (simpleMetadataLoading || !simpleMetadataOptionsReady) {
    message.error('Please wait for localized title options to finish.');
    return;
  }
  if (expressBlurbEnabled && !expressBlurbSourceText.trim()) {
    message.error('Enter the blurb text to translate.');
    return;
  }
  const currentBlurbWordCount = countWordsIncludingMarkup(expressBlurbSourceText || '');
  if (expressBlurbEnabled && currentBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
    message.error(`Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer.`, 8);
    return;
  }
  if (expressGlossaryBlocker) {
    message.error(expressGlossaryBlocker);
    return;
  }
  expressSubmitPending.current = true;
  setExpressSubmitting(true);
  try {
    const selectedTitleOption = (simpleMetadataOptions.titleOptions || []).find(option => option.id === simpleSelectedTitleOptionId);
    const selectedSeriesOption = (simpleMetadataOptions.seriesOptions || []).find(option => option.id === simpleSelectedSeriesOptionId);
    const payload = {
      userId: requestUserId,
      targetLanguage: resolvedTarget,
      outputFormat: isEpubUpload ? expressOutputFormat : 'Word',
      quotationStyle: getDefaultQuotationStyle(resolvedTarget),
      enableGlossaryScan: expressGlossaryEnabled,
      forceGlossaryRescan: expressGlossaryEnabled && expressForceGlossaryRescan,
      glossaryScanFingerprint: expressGlossaryEnabled ? expressGlossaryEstimate?.fingerprint : undefined,
      translateBlurb: expressBlurbEnabled,
      blurbSourceText: expressBlurbEnabled ? expressBlurbSourceText : undefined,
      localizedTitle: simpleSelectedTitleOptionId === '__original__' ? undefined : selectedTitleOption?.translatedText,
      localizedSeries: simpleSelectedSeriesOptionId === '__original__' ? undefined : selectedSeriesOption?.translatedText,
      localizedMetadataOptionIds: {
        title: simpleSelectedTitleOptionId,
        series: simpleSelectedSeriesOptionId,
      },
      emailDownloadLink: expressEmailEnabled,
      email: auth.currentUser.email || undefined,
      includeImages: true,
      writingOrientation: exportOrientation,
    };
    const requestContext = { bookId, sourceLanguage: bookLanguage || book.language, ...payload };
    const recoveringLaunch = expressRequestIdentity.current?.signature === JSON.stringify(requestContext);
    if (!recoveringLaunch) expressRequestIdentity.current = null;
    const requiredCredits = Math.max(
      0,
      Math.ceil(expressEstimatedTranslationCredits + expressEstimatedGlossaryCredits + expressEstimatedBlurbCredits),
    );
    const refreshedBalance = recoveringLaunch ? null : await fetchCreditBalanceCached(requestUserId, {
      force: true,
      silent: true,
    });
    const refreshedAvailableRaw =
      refreshedBalance?.available_balance ?? refreshedBalance?.credit_balance ?? availableCredits;
    const refreshedAvailable =
      typeof refreshedAvailableRaw === 'number' && Number.isFinite(refreshedAvailableRaw)
        ? Math.max(0, Math.floor(refreshedAvailableRaw))
        : null;
    if (!recoveringLaunch && requiredCredits > 0 && refreshedAvailable == null) {
      message.error('Unable to verify your credit balance. Please try again.', 8);
      return;
    }
    if (!recoveringLaunch && requiredCredits > 0 && refreshedAvailable != null && refreshedAvailable < requiredCredits) {
      openTopUpFromBook({
        source: 'simple_translation',
        requiredCredits,
        availableCredits: refreshedAvailable,
        shortfallCredits: Math.max(requiredCredits - refreshedAvailable, 0),
      });
      return;
    }
    expressRequestIdentity.current = launchRequestIdentity(expressRequestIdentity.current, requestContext);
    const response = await bookDetailApiPost(`${API_BASE}/books/${bookId}/express-translation`, {
      ...payload, requestId: expressRequestIdentity.current.requestId,
    });
    expressRequestIdentity.current = null;
    setExpressJob(response.data);
    message.success('Simple translation started.', 6);
    await refreshTranslationsImmediate(true);
  } catch (error: any) {
    console.error('Error starting Simple translation:', error);
    const errorDetail = String(error?.response?.data?.detail || 'Unable to start Simple translation')
      .replace(/\bExpress translation\b/gi, 'Simple translation');
    message.error(errorDetail, 8);
  } finally {
    expressSubmitPending.current = false;
    setExpressSubmitting(false);
  }
};

const retryFailedExpressTranslation = async () => {
  if (!auth.currentUser) {
    message.error('No user logged in');
    return;
  }
  if (!expressJob?.id) {
    message.error('No failed Simple translation job to retry.');
    return;
  }
  const requestUserId = resolvedOwnerUserId || auth.currentUser.uid;
  setExpressRetrying(true);
  try {
    const response = await bookDetailApiPost(
      `${API_BASE}/books/${bookId}/express-translation/${expressJob.id}/retry-failed`,
      { userId: requestUserId },
    );
    setExpressJob(response.data);
    message.success('Retrying failed chapters.', 6);
    await refreshTranslationsImmediate(true);
  } catch (error: any) {
    if (Number(error?.response?.status) === 409) {
      await refreshTranslationsImmediate(true);
      message.info(
        'Translation state was refreshed. Retry will be available after the current run finishes.',
        8,
      );
      return;
    }
    console.error('Error retrying Simple translation:', error);
    const rawDetail = error?.response?.data?.detail;
    const errorDetail = String(
      typeof rawDetail === 'string'
        ? rawDetail
        : rawDetail?.message || rawDetail?.detail || 'Unable to retry failed chapters',
    ).replace(/\bExpress translation\b/gi, 'Simple translation');
    message.error(errorDetail, 8);
  } finally {
    setExpressRetrying(false);
  }
};

const resendExpressExportEmail = async () => {
  if (!auth.currentUser) {
    message.error('No user logged in');
    return;
  }
  const resendEmail = auth.currentUser.email || expressJob?.email || '';
  if (!resendEmail) {
    message.error('No email address is available for this completed translation.');
    return;
  }
  if (!bookId || !expressJob?.id) {
    message.error('No completed Simple translation export to email.');
    return;
  }
  const requestUserId = resolvedOwnerUserId || auth.currentUser.uid;
  setExpressEmailResending(true);
  try {
    const response = await bookDetailApiPost(
      `${API_BASE}/books/${bookId}/express-translation/${expressJob.id}/resend-email`,
      {
        userId: requestUserId,
        email: resendEmail,
      },
    );
    setExpressJob(response.data);
    const sentTo = String(response.data?.emailSentTo || resendEmail);
    message.success(`Export email sent to ${sentTo}.`, 6);
  } catch (error: any) {
    console.error('Error resending Simple export email:', error);
    const rawDetail = error?.response?.data?.detail;
    const errorDetail = String(
      typeof rawDetail === 'string'
        ? rawDetail
        : rawDetail?.message || rawDetail?.detail || 'Unable to send export email',
    ).replace(/\bExpress translation\b/gi, 'Simple translation');
    message.error(errorDetail, 8);
  } finally {
    setExpressEmailResending(false);
  }
};

const calculateAdjustedCost = React.useCallback(() => {
  if (isProofreadModalVisible) {
    const { targetLang } = proofreadLangParts
    const isHindi = targetLang === 'Hindi';
    const proofreadMultiplier = isHindi
      ? HINDI_WORD_COST_MULTIPLIER
      : PROOFREADING_WORD_COST_MULTIPLIER;
    const editingNotesMultiplier = isHindi
      ? HINDI_WORD_COST_MULTIPLIER
      : EDITING_NOTES_WORD_COST_MULTIPLIER;

    if (selectedChapter) {
      const chapterWordCount = proofreadVersion === "Original"
        ? getOriginalProofreadWordCount(selectedChapter)
        : getTranslatedProofreadWordCount(translatedChaptersByGuid.get(selectedChapter.chapter_guid));
      if (chapterWordCount === 0) {
        return 0;
      } else {
        if (isProofreadChecked && isEditingNotesChecked) {
          return (
            Math.ceil(chapterWordCount * proofreadMultiplier)
            + Math.ceil(chapterWordCount * editingNotesMultiplier)
          );
        } else if (isProofreadChecked || isEditingNotesChecked) {
          return Math.ceil(
            chapterWordCount
            * (isProofreadChecked ? proofreadMultiplier : editingNotesMultiplier)
          );
        } else {
          return 0;
        }
      }
    } else {
      let cost = 0;
      const passTargetGuidSet = getBulkProofreadTargetGuidSet();

      book.chapters.forEach(chapter => {
        const translatedChapter = translatedChaptersByGuid.get(chapter.chapter_guid);
        let needsProofread = false;
        let needsEditingNotes = false;
        const proofreadRow = proofreadVersion === "Original" ? chapter : translatedChapter;
        const chapterWordCount = proofreadVersion === "Original"
          ? getOriginalProofreadWordCount(chapter)
          : getTranslatedProofreadWordCount(translatedChapter);

        const hasProofreadBaseContent = proofreadVersion === "Original"
          ? true
          : hasTranslatedContent(translatedChapter);

        if (proofreadVersion === "Original") {
          needsProofread = (
            isProofreadChecked
            && (passTargetGuidSet
              ? passTargetGuidSet.has(chapter.chapter_guid)
              : shouldSubmitBulkProofreadForRow(proofreadRow))
          );
          needsEditingNotes = isEditingNotesChecked && (!chapter.proofread_comments);
        } else {
          needsProofread = (
            isProofreadChecked
            && (passTargetGuidSet
              ? passTargetGuidSet.has(chapter.chapter_guid)
              : shouldSubmitBulkProofreadForRow(proofreadRow, {
                requiresTranslatedContent: true,
                hasTranslatedContent: hasProofreadBaseContent,
              }))
          );
          needsEditingNotes = isEditingNotesChecked && (!translatedChapter?.proofread_comments);
        }

        if (needsProofread || needsEditingNotes) {
          if (needsProofread) {
            cost += Math.ceil(chapterWordCount * proofreadMultiplier);
          }
          if (needsEditingNotes) {
            cost += Math.ceil(chapterWordCount * editingNotesMultiplier);
          }
        }
      });

      return Math.ceil(cost);
    }
  }
  return 0;
}, [
  isProofreadModalVisible,
  proofreadVersion,
  proofreadLangParts,
  selectedChapter,
  book,
  isProofreadChecked,
  isEditingNotesChecked,
  translatedChaptersByGuid,
  getOriginalProofreadWordCount,
  getTranslatedProofreadWordCount,
  getBulkProofreadTargetGuidSet,
  hasTranslatedContent,
  hasPendingProofreadReviewForRow,
]);

  useEffect(() => {
	  //console.log("useEffect 12");
	  //console.time('useEffect 12');
    // Check if the user is part of the beta program
    const userId = auth.currentUser?.uid;
	const checkBetaStatus = async () => {
      if (!userId) {
        return;
      }
      try {
        const response = await bookDetailApiGet(`${API_BASE}/check-beta-status/${userId}`);
        setIsBetaUser(response.data.isBetaUser);
        setIsLangTesterUser(response.data.isLangTesterUser);
        setShowTranslatedFlags(response.data.showTranslatedFlags ?? true);
		  //console.log('response.data.isLangTesterUser '+response.data.isLangTesterUser);
      } catch (error) {
        console.error('Error checking beta status:', error);
      }
    };

    void checkBetaStatus();
	//console.timeEnd('useEffect 12');
  }, [API_BASE, auth.currentUser?.uid]);

const getCreditCost = () => {
    //console.log("getCreditCost");
    const activeTargets =
      !selectedChapter && isTranslateModalVisible
        ? resolvedTranslationTargetLanguages
        : normalizeTranslationTargets([targetLanguage]);
    const wordCount = calculateWordCountForTargets(activeTargets, selectedAIModel);
    let totalCost;
	totalCost=wordCount;
    //if (selectedAIModel === 'DeepSeek') {
    //    totalCost = Math.ceil(wordCount * 0.5);
    //} else {
    //    totalCost = wordCount;
    //}

	return Math.ceil(totalCost);
};

const getCreditCostByLanguage = React.useCallback((language: string): number => {
  return calculateWordCountForTargets([language], selectedAIModel);
}, [calculateWordCountForTargets, selectedAIModel]);
const isLanguageTranslationComplete = React.useCallback((
  language: string,
  options?: { mode?: 'book' | 'chapter'; chapterGuid?: string }
): boolean => {
  const normalizedLanguage = (language || '').trim();
  const normalizedSource = (bookLanguage || '').trim();
  if (!normalizedLanguage || !normalizedSource) {
    return false;
  }
  if (normalizedLanguage.toLowerCase() === normalizedSource.toLowerCase()) {
    return false;
  }
  const translatedChaptersKey = `translated_chapters_${normalizedSource}_${normalizedLanguage}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  const requestedMode = options?.mode;
  const resolvedMode = requestedMode || (selectedChapter?.chapter_guid ? 'chapter' : 'book');
  const chapterGuidForCompletion = (options?.chapterGuid || selectedChapter?.chapter_guid || '').trim();

  if (resolvedMode === 'chapter') {
    if (!chapterGuidForCompletion) {
      return false;
    }
    return isChapterTranslatedForPair(translatedChapters, chapterGuidForCompletion);
  }
  if (!Array.isArray(book.chapters) || book.chapters.length === 0) {
    return false;
  }
  return book.chapters.every((chapter) => isChapterTranslatedForPair(translatedChapters, chapter.chapter_guid));
}, [bookLanguage, book, selectedChapter, isChapterTranslatedForPair]);

const getCoverCreditCost = ({ mode, imageCount }: { mode: 'modify' | 'generate'; imageCount: number }) => {
  const costPerImage = mode === 'generate' ? 1000 : 500;
  return costPerImage * imageCount;
};

  const getProofreadOptionProps = () => {
    if (selectedChapter && !hasSavedProofreadForRow(selectedChapter)) {
      return { value: 'Proofread', disabled: true, text: `${originalLanguageLabel} (Proofread) (Unavailable - Chapter has not been Proofread)` };
    }
	else if (!selectedChapter && !hasOriginalProofreadComplete) {
      return { value: 'Proofread', disabled: true, text: `${originalLanguageLabel} (Proofread) (Unavailable - Not all Chapters have been proofread)` };
    }
	else {
      return { value: 'Proofread', disabled: false, text: `${originalLanguageLabel} (Proofread)` };
    }
  };

  const proofreadOptionProps = getProofreadOptionProps();

  const getExportProofreadOptionProps = () => {
    const hasAnyOriginalProofread = book.chapters.some(
      chapter => hasSavedProofreadForRow(chapter),
    );
    if (!hasAnyOriginalProofread) {
      return {
        value: 'Proofread',
        disabled: true,
        text: `${originalLanguageLabel} (Proofread where available) (Unavailable - no chapters have been proofread)`,
      };
    }
    return { value: 'Proofread', disabled: false, text: `${originalLanguageLabel} (Proofread where available)` };
  };

  const exportProofreadOptionProps = getExportProofreadOptionProps();


  const formatLangDisplayText = useCallback((pair: string) => {
    return formatLanguagePairDisplayText(pair, { originalLabel: originalLanguageLabel });
  }, [originalLanguageLabel]);


  const getProofreadOptions = () => {
    const proofreadOptions: string[] = [];
    if (book.translation_pairs) {
      book.translation_pairs.forEach(pair => {
        const { sourceLang, targetLang } = splitLanguagePair(pair);
        if (!sourceLang || !targetLang) {
          return;
        }
        const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
        const translatedChapters: TranslatedChapter[] = book[translatedChaptersKey] || [];
        const hasProofread = translatedChapters.some(chapter => hasSavedProofreadForRow(chapter));
        if (hasProofread) {
          proofreadOptions.push(`${sourceLang}-${targetLang} (Proofread)`);
        }
      });
    }
	return proofreadOptions;
  };

  const proofreadOptions = getProofreadOptions();

  const handleAddLanguage = () => {
    setIsAddLanguageModalVisible(true);
  };

  const handleLanguageAdded = () => {
    // Refresh the book details to update the language list
    fetchBookDetails();
  };

  const ensureMetadataLanguage = useCallback(
    async (targetLanguage: string) => {
      const cleanTarget = (targetLanguage || '').trim();
      if (!cleanTarget) {
        return false;
      }
      const sourceLanguage = (bookLanguage || book.language || '').trim();
      if (!sourceLanguage || cleanTarget === sourceLanguage) {
        return true;
      }
      const normalizedTarget = normalizeLanguageLabelKey(cleanTarget);
      const existingTargets = new Set(
        (book.translation_pairs || [])
          .map((pair) => normalizeLanguageLabelKey(splitLanguagePair(pair || '').targetLang))
          .filter(Boolean),
      );
      if (existingTargets.has(normalizedTarget)) {
        return true;
      }
      if (!bookId) {
        message.error('Missing book ID for language update.');
        return false;
      }
      try {
        await bookDetailApiPost(
          `${API_BASE}/add-language/${bookId}`,
          JSON.stringify({ language: cleanTarget }),
          { headers: { 'Content-Type': 'application/json' } },
        );
        await fetchBookDetails();
        return true;
      } catch (error) {
        console.error('Error adding language:', error);
        message.error('Failed to add language');
        return false;
      }
    },
    [API_BASE, book.translation_pairs, book.language, bookId, bookLanguage, fetchBookDetails],
  );

  const fetchFrenchDialogueLegacyEligibility = useCallback(
    async ({
      pair,
      targetLanguage,
      chapterGuid,
      rulesetVersion,
      force = false,
    }: {
      pair: string;
      targetLanguage: string;
      chapterGuid?: string;
      rulesetVersion?: string;
      force?: boolean;
    }): Promise<FrenchDialogueLegacyEligibility | null> => {
      const normalizedPair = (pair || '').trim();
      const normalizedChapterGuid = String(chapterGuid || '').trim().toLowerCase();
      const normalizedRulesetVersion = (rulesetVersion || '').trim();
      if (!normalizedPair || normalizedPair.toLowerCase() === 'original') {
        console.info('[french-dialogue-legacy][ui] skipped eligibility fetch (invalid pair)', {
          pair,
          normalizedPair,
          chapterGuid: normalizedChapterGuid || undefined,
          rulesetVersion: normalizedRulesetVersion || '(auto)',
          bookId,
        });
        return null;
      }
      const { targetLang } = splitLanguagePair(normalizedPair);
      const resolvedTarget = (targetLanguage || targetLang || '').trim();
      if (!resolvedTarget || normalizeLanguageLabelKey(resolvedTarget) !== 'french') {
        console.info('[french-dialogue-legacy][ui] skipped eligibility fetch (non-french target)', {
          pair: normalizedPair,
          targetLanguage,
          resolvedTarget,
          chapterGuid: normalizedChapterGuid || undefined,
          rulesetVersion: normalizedRulesetVersion || '(auto)',
          bookId,
        });
        return null;
      }

      const baseCacheKey = normalizedRulesetVersion
        ? `${normalizedPair}::${normalizedRulesetVersion}`
        : normalizedPair;
      const cacheKey = normalizedChapterGuid
        ? `${baseCacheKey}::chapter:${normalizedChapterGuid}`
        : baseCacheKey;
      const cache = frenchDialogueLegacyEligibilityRef.current[cacheKey];
      if (!force && cache) {
        console.info('[french-dialogue-legacy][ui] eligibility cache hit', {
          pair: normalizedPair,
          chapterGuid: normalizedChapterGuid || undefined,
          rulesetVersion: normalizedRulesetVersion || '(auto)',
          eligibility: cache,
          bookId,
        });
        return cache;
      }
      const inflightEligibility = frenchDialogueLegacyInflightRef.current[cacheKey];
      if (!force && inflightEligibility) {
        console.info('[french-dialogue-legacy][ui] eligibility request already in flight', {
          pair: normalizedPair,
          chapterGuid: normalizedChapterGuid || undefined,
          rulesetVersion: normalizedRulesetVersion || '(auto)',
          bookId,
        });
        return inflightEligibility;
      }

      const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
      if (!userId || !bookId) {
        console.info('[french-dialogue-legacy][ui] skipped eligibility fetch (missing ids)', {
          pair: normalizedPair,
          userIdPresent: Boolean(userId),
          bookIdPresent: Boolean(bookId),
        });
        return null;
      }

      const requestPromise = (async () => {
        try {
          const headers = await getAuthHeaders(auth.currentUser ?? undefined);
          const response = await bookDetailApiGet<FrenchDialogueLegacyEligibility>(
            `${API_BASE}/books/${bookId}/french-dialogue-legacy/eligibility`,
            {
              params: {
                userId,
                targetLang: resolvedTarget || 'French',
                pair: normalizedPair,
                chapterGuid: normalizedChapterGuid || undefined,
                rulesetVersion: normalizedRulesetVersion || undefined,
              },
              headers,
            },
          );
          const payload = response.data || null;
          console.info('[french-dialogue-legacy][ui] eligibility fetched', {
            pair: normalizedPair,
            chapterGuid: normalizedChapterGuid || undefined,
            rulesetVersion: normalizedRulesetVersion || '(auto)',
            eligibility: payload,
            force,
            bookId,
          });
          if (payload) {
            if (!normalizedRulesetVersion && !normalizedChapterGuid) {
              setFrenchDialogueLegacyEligibilityByPair(prev => ({
                ...prev,
                [normalizedPair]: payload,
              }));
            }
            frenchDialogueLegacyEligibilityRef.current[cacheKey] = payload;
            return payload;
          }
          return null;
        } catch (error) {
          console.error('Failed to fetch French dialogue legacy eligibility:', error);
          return null;
        } finally {
          delete frenchDialogueLegacyInflightRef.current[cacheKey];
        }
      })();

      frenchDialogueLegacyInflightRef.current[cacheKey] = requestPromise;
      return requestPromise;
    },
    [API_BASE, auth.currentUser, bookId, sharedUserId],
  );

  const activeFrenchDialoguePairs = useMemo(() => {
    const pairs = new Set<string>();
    (contextTranslations || []).forEach((entry: TranslationStatus) => {
      if (entry.bookId !== bookId) {
        return;
      }
      const typeKey = (entry.type || '').toString().trim().toLowerCase();
      if (typeKey !== 'frenchdialoguecorrection') {
        return;
      }
      const statusKey = resolveTranslationStatusState(entry);
      if (TERMINAL_JOB_STATUSES.has(statusKey)) {
        return;
      }
      const sourceLang = String(entry.sourceLang || '').trim();
      const targetLang = String(entry.targetLang || '').trim();
      if (!sourceLang || !targetLang) {
        return;
      }
      pairs.add(`${sourceLang}-${targetLang}`);
    });
    return Array.from(pairs).sort();
  }, [bookId, contextTranslations]);

  useEffect(() => {
    const activePairs = new Set(activeFrenchDialoguePairs);
    const stalePairsToRefresh: string[] = [];

    Object.entries(frenchDialogueLegacyEligibilityByPair).forEach(([pair, eligibility]) => {
      const reasonCode = String(eligibility?.reasonCode || '').trim().toLowerCase();
      const hasActiveRun = activePairs.has(pair);
      if (reasonCode === 'run_in_progress' && !hasActiveRun) {
        if (!staleFrenchDialogueEligibilityRefreshRef.current.has(pair)) {
          stalePairsToRefresh.push(pair);
        }
        return;
      }
      staleFrenchDialogueEligibilityRefreshRef.current.delete(pair);
    });

    stalePairsToRefresh.forEach((pair) => {
      staleFrenchDialogueEligibilityRefreshRef.current.add(pair);
      invalidateFrenchDialogueEligibilityCacheForPair(pair);
      const { targetLang } = splitLanguagePair(pair);
      void fetchFrenchDialogueLegacyEligibility({
        pair,
        targetLanguage: targetLang || 'French',
        force: true,
      }).finally(() => {
        const latestReasonCode = String(
          frenchDialogueLegacyEligibilityRef.current[pair]?.reasonCode || '',
        ).trim().toLowerCase();
        if (latestReasonCode !== 'run_in_progress') {
          staleFrenchDialogueEligibilityRefreshRef.current.delete(pair);
        }
      });
    });
  }, [
    activeFrenchDialoguePairs,
    fetchFrenchDialogueLegacyEligibility,
    frenchDialogueLegacyEligibilityByPair,
    invalidateFrenchDialogueEligibilityCacheForPair,
  ]);

  useEffect(() => {
    const frenchPairs = (book.translation_pairs || []).filter((pairValue: string) => {
      const { targetLang } = splitLanguagePair(pairValue || '');
      return normalizeLanguageLabelKey(targetLang) === 'french';
    });
    if (!frenchPairs.length) {
      return;
    }
    frenchPairs.forEach((pairValue: string) => {
      const { targetLang } = splitLanguagePair(pairValue || '');
      void fetchFrenchDialogueLegacyEligibility({
        pair: pairValue,
        targetLanguage: targetLang || 'French',
        force: false,
      });
    });
  }, [book.translation_pairs, fetchFrenchDialogueLegacyEligibility]);

  useEffect(() => {
    if (!metadataLanguage || metadataLanguage === 'original') {
      return;
    }
    const { targetLang } = splitLanguagePair(metadataLanguage);
    if (normalizeLanguageLabelKey(targetLang) !== 'french') {
      return;
    }
    void fetchFrenchDialogueLegacyEligibility({
      pair: metadataLanguage,
      targetLanguage: targetLang || 'French',
      force: false,
    });
  }, [metadataLanguage, fetchFrenchDialogueLegacyEligibility]);

  const handleRunFrenchDialogueLegacyCorrection = useCallback(
    ({
      pair,
      targetLanguage,
      chapterGuid,
      chapterName,
    }: {
      pair: string;
      targetLanguage: string;
      chapterGuid?: string;
      chapterName?: string;
    }) => {
      const normalizedPair = (pair || '').trim();
      const normalizedChapterGuid = String(chapterGuid || '').trim().toLowerCase();
      const providedChapterName = String(chapterName || '').trim();
      const isChapterScopedRun = Boolean(normalizedChapterGuid);
      const resolvedChapterName = isChapterScopedRun
        ? (
            providedChapterName
            || (book.chapters || []).find(
              (chapter) =>
                String(chapter?.chapter_guid || '').trim().toLowerCase() === normalizedChapterGuid,
            )?.chapter_name
            || ''
          ).trim()
        : '';
      if (!normalizedPair) {
        return;
      }
      const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
      if (!userId) {
        message.error('You must be signed in to run the French dialogue pass.');
        return;
      }

      const getCleanupEligibility = (
        eligibility: FrenchDialogueLegacyEligibility | null | undefined,
      ): FrenchDialogueLegacyEligibility | null => {
        if (!eligibility) {
          return null;
        }
        const fromMap =
          eligibility.rulesetEligibility?.[LEGACY_FRENCH_DIALOGUE_CLEANUP_RULESET_VERSION];
        if (fromMap) {
          return fromMap;
        }
        return eligibility;
      };

      const resolveCleanupIneligibilityReason = (
        eligibility: FrenchDialogueLegacyEligibility | null | undefined,
        offer?: FrenchDialogueDiscountedRetranslationOffer | null,
      ): string => {
        if (!eligibility) {
          return 'Eligibility unavailable for cleanup.';
        }
        const discountedReasonCode = String(offer?.reasonCode || '').trim().toLowerCase();
        if (discountedReasonCode === 'discount_offer_consumed') {
          return 'The free cleanup pass and discounted retranslation have already been used for this book.';
        }
        if (discountedReasonCode === 'run_in_progress') {
          return 'A discounted retranslation is already in progress for this book.';
        }
        if (eligibility.alreadyRan) {
          return 'The free cleanup pass has already been used.';
        }
        const reasonCode = String(eligibility.reasonCode || '').trim().toLowerCase();
        if (reasonCode === 'already_ran') {
          return 'The free cleanup pass has already been used.';
        }
        if (
          !isChapterScopedRun
          && Boolean(offer?.eligible)
          && (reasonCode === 'guillemet_style' || reasonCode === 'chapter_already_completed')
        ) {
          return 'The free cleanup pass has already been used.';
        }
        if (reasonCode === 'run_in_progress') {
          return 'A run is already in progress.';
        }
        if (reasonCode === 'chapter_already_completed') {
          return isChapterScopedRun
            ? 'French dialogue cleanup already completed for this chapter.'
            : 'Already completed for this book.';
        }
        if (reasonCode === 'no_translated_chapters') {
          return 'No translated chapters found.';
        }
        if (reasonCode === 'after_cutoff') {
          return 'Not eligible due to translation date cutoff.';
        }
        if (reasonCode === 'guillemet_style') {
          return 'Translation already appears to use guillemet style.';
        }
        if (reasonCode === 'chapter_not_translated') {
          return isChapterScopedRun
            ? 'This chapter is not translated yet.'
            : 'Requested chapter is not translated.';
        }
        if (reasonCode === 'cleanup_consumed_by_retranslation') {
          return 'The free cleanup pass was already consumed by a successful discounted retranslation.';
        }
        const detail = String(eligibility.reasonDetail || '').trim();
        if (detail) {
          return detail;
        }
        if (reasonCode) {
          return `Not eligible (${reasonCode.replace(/_/g, ' ')}).`;
        }
        return 'Cleanup is not eligible for this book.';
      };

      const resolveRetranslationIneligibilityReason = (
        offer: FrenchDialogueDiscountedRetranslationOffer | null | undefined,
      ): string => {
        if (!offer) {
          return 'Offer availability unavailable.';
        }
        const reasonCode = String(offer.reasonCode || '').trim().toLowerCase();
        if (reasonCode === 'discount_offer_consumed') {
          return 'The one-time 50% discounted retranslation has already been used.';
        }
        if (reasonCode === 'run_in_progress') {
          return 'A discounted retranslation is already in progress for this book.';
        }
        if (reasonCode === 'book_only') {
          return 'The discounted retranslation is available only for full-book runs.';
        }
        if (reasonCode === 'no_translated_chapters') {
          return 'No translated French chapters are available to retranslate.';
        }
        if (reasonCode === 'missing_source_chapter') {
          return 'One or more translated chapters no longer have matching source chapters.';
        }
        const detail = String(offer.reasonDetail || '').trim();
        if (detail) {
          return detail;
        }
        if (reasonCode) {
          return `Offer unavailable (${reasonCode.replace(/_/g, ' ')}).`;
        }
        return 'The discounted retranslation offer is not available.';
      };

      const resolvePendingChapterCount = (
        eligibility: FrenchDialogueLegacyEligibility | null | undefined,
      ): number => {
        const cleanupEligibility = getCleanupEligibility(eligibility) || eligibility;
        const reasonCode = String(cleanupEligibility?.reasonCode || '').trim().toLowerCase();

        if (isChapterScopedRun) {
          if (reasonCode === 'chapter_not_translated' || reasonCode === 'chapter_already_completed') {
            return 0;
          }
          const requestedCompleted = cleanupEligibility?.requestedChapterCompleted;
          if (typeof requestedCompleted === 'boolean') {
            return requestedCompleted ? 0 : 1;
          }
        }

        const pendingFromEligibility = Number(cleanupEligibility?.chaptersPending);
        if (!isChapterScopedRun && Number.isFinite(pendingFromEligibility) && pendingFromEligibility >= 0) {
          return pendingFromEligibility;
        }

        const { sourceLang, targetLang } = splitLanguagePair(normalizedPair);
        const translatedChaptersKey = `translated_chapters_${sourceLang}_${targetLang}`;
        const translatedChapters: any[] = (book as any)[translatedChaptersKey] || [];
        const expectedRulesetVersion = String(
          cleanupEligibility?.rulesetVersion || LEGACY_FRENCH_DIALOGUE_CLEANUP_RULESET_VERSION,
        ).trim();
        const completedStatuses = new Set(['applied', 'no_changes', 'skipped']);

        if (isChapterScopedRun) {
          const translatedChapter = translatedChapters.find((chapter) => {
            const chapterId = String((chapter as any)?.chapter_guid || '').trim().toLowerCase();
            return chapterId === normalizedChapterGuid;
          });
          if (!translatedChapter || !String((translatedChapter as any)?.translated_text || '').trim()) {
            return 0;
          }
          const correction = (translatedChapter as any)?.legacy_dialogue_correction;
          if (!correction || typeof correction !== 'object') {
            return 1;
          }
          const status = String((correction as any)?.status || '').trim().toLowerCase();
          const rulesetVersion = String((correction as any)?.rulesetVersion || '').trim();
          const matchesRuleset = !expectedRulesetVersion || rulesetVersion === expectedRulesetVersion;
          return matchesRuleset && completedStatuses.has(status) ? 0 : 1;
        }

        const translatedCount = translatedChapters.filter((chapter) =>
          Boolean(String((chapter as any)?.translated_text || '').trim()),
        ).length;
        const completedCount = translatedChapters.filter((chapter) => {
          if (!String((chapter as any)?.translated_text || '').trim()) {
            return false;
          }
          const correction = (chapter as any)?.legacy_dialogue_correction;
          if (!correction || typeof correction !== 'object') {
            return false;
          }
          const status = String((correction as any)?.status || '').trim().toLowerCase();
          const rulesetVersion = String((correction as any)?.rulesetVersion || '').trim();
          if (expectedRulesetVersion && rulesetVersion !== expectedRulesetVersion) {
            return false;
          }
          return completedStatuses.has(status);
        }).length;
        return Math.max(0, translatedCount - completedCount);
      };

      type LegacyRunType = 'cleanup' | 'retranslation';
      const refreshEligibility = async (force: boolean): Promise<void> => {
        const requests: Array<Promise<FrenchDialogueLegacyEligibility | null>> = [
          fetchFrenchDialogueLegacyEligibility({
            pair: normalizedPair,
            targetLanguage: targetLanguage || 'French',
            chapterGuid: normalizedChapterGuid || undefined,
            force,
          }),
        ];
        if (isChapterScopedRun) {
          requests.push(
            fetchFrenchDialogueLegacyEligibility({
              pair: normalizedPair,
              targetLanguage: targetLanguage || 'French',
              force,
            }),
          );
        }
        await Promise.all(requests);
      };

      const openConfirmationModal = (
        eligibility: FrenchDialogueLegacyEligibility | null | undefined,
        modalCreditBalance: CreditBalance | null | undefined,
      ) => {
        let selectedRunType: LegacyRunType = 'cleanup';
        let latestCanStart = false;
        let confirmModalRef: { update: (config: Record<string, unknown>) => void; destroy: () => void } | null = null;
        const modalAvailableCreditsRaw =
          modalCreditBalance?.available_balance ?? modalCreditBalance?.credit_balance;
        const modalAvailableCredits =
          typeof modalAvailableCreditsRaw === 'number' && Number.isFinite(modalAvailableCreditsRaw)
            ? modalAvailableCreditsRaw
            : typeof modalAvailableCreditsRaw === 'string' && Number.isFinite(Number(modalAvailableCreditsRaw))
              ? Number(modalAvailableCreditsRaw)
              : null;
        const modalPlanId = (modalCreditBalance as any)?.planID ?? (creditBalance as any)?.planID;
        const updateConfirmReadyState = (canStart: boolean) => {
          if (latestCanStart === canStart) {
            return;
          }
          latestCanStart = canStart;
          if (confirmModalRef) {
            confirmModalRef.update({
              okButtonProps: {
                className: 'app-primary-cta',
                disabled: !canStart,
              },
            });
          }
        };

        const LegacyRunSelectionContent: React.FC = () => {
          const cleanupEligibility = getCleanupEligibility(eligibility) || eligibility;
          const retranslationOffer = eligibility?.discountedRetranslation;
          const isFailedOnlyRecovery = retranslationOffer?.mode === 'retry_failed_only';
          const cleanupEligible = Boolean(cleanupEligibility?.eligible);
          const retranslationUiState = deriveFrenchDialogueRetranslationUiState({
            isChapterScopedRun,
            offer: retranslationOffer,
            availableCredits: modalAvailableCredits,
          });
          const initialRunType: LegacyRunType = (
            cleanupEligible
              ? 'cleanup'
              : (retranslationUiState.showOption ? 'retranslation' : 'cleanup')
          );
          const [runType, setRunType] = useState<LegacyRunType>(initialRunType);
          selectedRunType = runType;
          const selectRunType = (nextRunType: LegacyRunType) => {
            if (nextRunType === 'cleanup' && !cleanupEligible) {
              return;
            }
            if (nextRunType === 'retranslation' && !retranslationUiState.showOption) {
              return;
            }
            setRunType(nextRunType);
          };
          const cleanupDisabledReason = resolveCleanupIneligibilityReason(
            cleanupEligibility,
            retranslationOffer,
          );
          const retranslationDisabledReason = resolveRetranslationIneligibilityReason(retranslationOffer);
          const cleanupChaptersToRun = resolvePendingChapterCount(eligibility);
          const retranslationChaptersToRunRaw =
            Number(retranslationOffer?.chaptersRemaining)
            || Number(retranslationOffer?.chaptersFailedRemaining)
            || ((retranslationOffer?.chapterGuidsToQueue || []).length > 0
              ? (retranslationOffer?.chapterGuidsToQueue || []).length
              : 0);
          const retranslationChaptersToRun =
            Number.isFinite(retranslationChaptersToRunRaw) && retranslationChaptersToRunRaw > 0
              ? Math.floor(retranslationChaptersToRunRaw)
              : cleanupChaptersToRun;
          const chaptersToRun = runType === 'retranslation'
            ? retranslationChaptersToRun
            : cleanupChaptersToRun;
          const chapterCountLabel = chaptersToRun === 1 ? '1 chapter' : `${chaptersToRun} chapters`;
          const selectedCardBorder = '1px solid var(--ant-primary-color, #1677ff)';
          const unselectedCardBorder = '1px solid var(--border-subtle, #ddd)';
          const selectedCreditCost =
            runType === 'retranslation' ? retranslationUiState.discountedCredits : 0;
          const selectedBaseCreditCost =
            runType === 'retranslation' ? retranslationUiState.requiredCredits : 0;
          const selectedShortfallCredits =
            runType === 'retranslation' ? retranslationUiState.shortfallCredits : 0;
          const selectedHasEnoughCredits =
            runType === 'retranslation'
              ? (retranslationUiState.hasEnoughCredits ?? null)
              : true;
          const creditsResolved = modalAvailableCredits != null;
          const selectedRunCanStart =
            creditsResolved
            && (
              runType === 'retranslation'
                ? (
                    retranslationUiState.showOption
                    && Boolean(retranslationOffer?.eligible)
                    && retranslationUiState.hasEnoughCredits !== false
                  )
                : cleanupEligible
            );

          useEffect(() => {
            updateConfirmReadyState(selectedRunCanStart);
          }, [selectedRunCanStart]);

          useEffect(() => {
            if (runType === 'cleanup' && !cleanupEligible && retranslationUiState.showOption) {
              setRunType('retranslation');
            }
          }, [cleanupEligible, retranslationUiState.showOption, runType]);

          return (
            <div>
              <Typography.Paragraph>
                Choose how you want to update the French output.
              </Typography.Paragraph>
              {isChapterScopedRun && (
                <Typography.Paragraph>
                  <strong>Selected chapter:</strong> {resolvedChapterName || normalizedChapterGuid}
                </Typography.Paragraph>
              )}
              <Typography.Paragraph>
                <strong>Chapters to update:</strong> {chapterCountLabel}
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                <strong>Run type:</strong>
              </Typography.Paragraph>
              <Radio.Group
                value={runType}
                onChange={(event) => setRunType(String(event?.target?.value || '').trim() as LegacyRunType)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Tooltip title={!cleanupEligible ? cleanupDisabledReason : undefined}>
                    <div
                      onClick={() => selectRunType('cleanup')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectRunType('cleanup');
                        }
                      }}
                      role="button"
                      tabIndex={cleanupEligible ? 0 : -1}
                      aria-disabled={!cleanupEligible}
                      style={{
                        border: runType === 'cleanup' ? selectedCardBorder : unselectedCardBorder,
                        borderRadius: 8,
                        padding: '10px 12px',
                        cursor: cleanupEligible ? 'pointer' : 'not-allowed',
                        opacity: cleanupEligible ? 1 : 0.65,
                      }}
                    >
                      <Radio value="cleanup" disabled={!cleanupEligible}>
                        <strong>Post-translation cleanup (Free, one-time)</strong>
                      </Radio>
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                        Applies French dialog cleanup to the existing translated text without retranslation.
                      </Typography.Text>
                      <Typography.Text style={{ display: 'block', marginTop: 6 }}>
                        <strong>Recommended for:</strong> More recent translations that mainly need
                        dialogue cleanup.
                      </Typography.Text>
                      {cleanupEligible && (
                        <Typography.Text
                          type="success"
                          style={{ display: 'block', marginTop: 6 }}
                        >
                          One free cleanup pass is available for this book.
                        </Typography.Text>
                      )}
                    </div>
                  </Tooltip>
                  {retranslationUiState.showOption && (
                    <div
                      onClick={() => selectRunType('retranslation')}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          selectRunType('retranslation');
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      style={{
                        border: runType === 'retranslation' ? selectedCardBorder : unselectedCardBorder,
                        borderRadius: 8,
                        padding: '10px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <Radio value="retranslation">
                        <strong>
                          {isFailedOnlyRecovery
                            ? 'Retranslation recovery (50% off, failed chapters only)'
                            : 'Full retranslation (50% off, one-time)'}
                        </strong>
                      </Radio>
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                        {isFailedOnlyRecovery
                          ? 'Reruns only the chapters that failed in the previous discounted retranslation attempt.'
                          : 'Retranslates this full book using the fixed easy-mode French dialog preset.'}
                      </Typography.Text>
                      <Typography.Text style={{ display: 'block', marginTop: 6 }}>
                        <strong>Recommended for:</strong> Older translations that would benefit from
                        improved dialogue structure and stronger overall quality.
                      </Typography.Text>
                      {retranslationUiState.eligible ? (
                        <Typography.Text
                          type={
                            retranslationUiState.hasEnoughCredits === false ? 'warning' : 'success'
                          }
                          style={{ display: 'block', marginTop: 6 }}
                        >
                          {retranslationUiState.hasEnoughCredits === false
                            ? `You need ${retranslationUiState.shortfallCredits} more credits to start this discounted retranslation.`
                            : (
                                isFailedOnlyRecovery
                                  ? 'Only the remaining failed discounted retranslation chapters will be rerun.'
                                  : 'This one-time 50% discounted retranslation is available for this book.'
                              )}
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                          Offer unavailable: {retranslationDisabledReason}
                        </Typography.Text>
                      )}
                    </div>
                  )}
                </Space>
              </Radio.Group>
              {runType === 'retranslation' && retranslationUiState.showOption && !retranslationUiState.eligible && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={`Discounted retranslation cannot run for this book: ${retranslationDisabledReason}`}
                />
              )}
              {runType === 'retranslation' && retranslationUiState.showOption && retranslationUiState.eligible && retranslationUiState.hasEnoughCredits === false && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={`You need ${retranslationUiState.shortfallCredits} more credits to start the discounted retranslation.`}
                />
              )}
              <Typography.Paragraph style={{ marginTop: 12, marginBottom: 8 }}>
                <strong>Offer rules:</strong>
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                Free cleanup pass: one-time and keeps existing translated text.
              </Typography.Paragraph>
              <Typography.Paragraph style={{ marginBottom: 8 }}>
                Discounted full retranslation: one-time at 50%. If it completes successfully, it also
                consumes the free cleanup pass. Using the free cleanup pass first does not consume this
                discount offer.
              </Typography.Paragraph>
              <CostSummary
                creditBalance={modalAvailableCredits}
                baseCost={selectedBaseCreditCost}
                discountedCost={
                  runType === 'retranslation' && selectedBaseCreditCost !== selectedCreditCost
                    ? selectedCreditCost
                    : undefined
                }
                planId={modalPlanId}
                tooltipText="Credits are charged from your available balance when the selected option starts."
                containerStyle={{ marginBottom: 12 }}
              />
              {modalAvailableCredits == null && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Loading credit balance before you can start this run."
                />
              )}
              {runType === 'retranslation' && selectedHasEnoughCredits === false && (
                <InsufficientCreditsAlert
                  message="You do not have enough credits for this discounted retranslation."
                  shortfallCredits={selectedShortfallCredits}
                  onTopUpCredits={
                    modalAvailableCredits != null
                      ? () =>
                          openTopUpFromBook(
                            buildFrenchDialogueRetranslationTopUpContext({
                              discountedCredits: selectedCreditCost,
                              availableCredits: modalAvailableCredits,
                            }),
                          )
                      : undefined
                  }
                  style={{ marginBottom: 12 }}
                />
              )}
            </div>
          );
        };

        const queueCleanupRun = async () => {
          const headers = await getAuthHeaders(auth.currentUser ?? undefined);
          const response = await bookDetailApiPost(
            `${API_BASE}/books/${bookId}/french-dialogue-legacy/run`,
            {
              userId,
              pair: normalizedPair,
              chapterGuid: normalizedChapterGuid || undefined,
              rulesetVersion: LEGACY_FRENCH_DIALOGUE_CLEANUP_RULESET_VERSION,
            },
            { headers },
          );
          const payload = response?.data || {};
          const lockGuid = payload?.lockGuid;
          const chapterLocks = Array.isArray(payload?.chapterLocks) ? payload.chapterLocks : [];
          const { sourceLang, targetLang } = splitLanguagePair(normalizedPair);
          if (chapterLocks.length > 0) {
            chapterLocks.forEach((entry: any) => {
              const chapterLockGuid = String(entry?.lockGuid || '').trim();
              if (!chapterLockGuid) {
                return;
              }
              const entryChapterGuid = String(entry?.chapterGuid || '').trim();
              const entryChapterName = String(entry?.chapterName || '').trim();
              addClientIntent({
                lockGuid: chapterLockGuid,
                chapterGuid: entryChapterGuid,
                chapterName: entryChapterName || 'French Dialogue Correction',
                sourceLang: String(entry?.sourceLang || sourceLang || bookLanguage || '').trim(),
                targetLang: String(entry?.targetLang || targetLang || targetLanguage || 'French').trim(),
                timestamp: new Date().toISOString(),
                status: 'queued',
                processingState: 'queued',
                type: 'FrenchDialogueCorrection',
                bookId,
                userId,
              });
            });
          } else if (lockGuid) {
            addClientIntent({
              lockGuid,
              chapterGuid: normalizedChapterGuid || '',
              chapterName: resolvedChapterName || 'French Dialogue Correction',
              sourceLang: sourceLang || bookLanguage,
              targetLang: targetLang || targetLanguage || 'French',
              timestamp: new Date().toISOString(),
              status: 'queued',
              processingState: 'queued',
              type: 'FrenchDialogueCorrection',
              bookId,
              userId,
            });
          }
          const successMessage = isChapterScopedRun
            ? resolvedChapterName
              ? `French dialogue pass queued for ${resolvedChapterName}.`
              : 'French dialogue pass queued for this chapter.'
            : 'French dialogue pass queued.';
          message.success(successMessage, 8);
          await refreshEligibility(true);
          await refreshTranslationsImmediate(true);
        };

        const queueDiscountedRetranslationRun = async () => {
          const offer = eligibility?.discountedRetranslation;
          const retranslationUiState = deriveFrenchDialogueRetranslationUiState({
            isChapterScopedRun,
            offer,
            availableCredits: modalAvailableCredits,
          });
          try {
            const refreshedBalance = await fetchCreditBalanceCached(userId, { force: true });
            const refreshedAvailableRaw =
              refreshedBalance?.available_balance ?? refreshedBalance?.credit_balance ?? null;
            const refreshedAvailable =
              typeof refreshedAvailableRaw === 'number' && Number.isFinite(refreshedAvailableRaw)
                ? refreshedAvailableRaw
                : typeof refreshedAvailableRaw === 'string' && Number.isFinite(Number(refreshedAvailableRaw))
                  ? Number(refreshedAvailableRaw)
                  : null;
            if (
              typeof refreshedAvailable === 'number' &&
              refreshedAvailable < retranslationUiState.discountedCredits
            ) {
              openTopUpFromBook(
                buildFrenchDialogueRetranslationTopUpContext({
                  discountedCredits: retranslationUiState.discountedCredits,
                  availableCredits: refreshedAvailable,
                }),
              );
              return;
            }
          } catch (balanceError) {
            console.warn('Failed to refresh credit balance before discounted retranslation', balanceError);
          }

          const headers = await getAuthHeaders(auth.currentUser ?? undefined);
          const response = await bookDetailApiPost(
            `${API_BASE}/books/${bookId}/french-dialogue-legacy/retranslation`,
            {
              userId,
              pair: normalizedPair,
            },
            { headers },
          );
          const payload = response?.data || {};
          const chapterLocks = Array.isArray(payload?.chapterLocks) ? payload.chapterLocks : [];
          const recoveryMode = String(payload?.recoveryMode || offer?.mode || '').trim().toLowerCase();
          const { sourceLang, targetLang } = splitLanguagePair(normalizedPair);
          chapterLocks.forEach((entry: any) => {
            const chapterLockGuid = String(entry?.lockGuid || '').trim();
            if (!chapterLockGuid) {
              return;
            }
            const entryChapterGuid = String(entry?.chapterGuid || '').trim();
            const entryChapterName = String(entry?.chapterName || '').trim();
            addOptimisticTranslation(
              buildOptimisticTranslationStatus({
                bookId,
                userId,
                sourceLanguage: String(entry?.sourceLang || sourceLang || bookLanguage || '').trim(),
                targetLanguage: String(entry?.targetLang || targetLang || targetLanguage || 'French').trim(),
                chapter: {
                  chapter_guid: entryChapterGuid,
                  chapter_name: entryChapterName || 'French Dialogue Retranslation',
                  word_count: undefined,
                },
                useBatch: false,
              }),
            );
          });
          message.success(
            recoveryMode === 'failed_only' || recoveryMode === 'retry_failed_only'
              ? 'Failed discounted retranslation chapters queued.'
              : 'Discounted French dialogue retranslation queued.',
            8,
          );
          await refreshEligibility(true);
          await refreshTranslationsImmediate(true);
          await fetchCreditBalanceCached(userId, { force: true });
        };

        confirmModalRef = Modal.confirm({
          className: isDark ? 'dark-aware-info-modal' : undefined,
          title: 'Apply Updated French Dialog Rules',
          width: 760,
          content: <LegacyRunSelectionContent />,
          okText: 'Start selected option',
          cancelText: 'Cancel',
          okButtonProps: {
            className: 'app-primary-cta',
            disabled: true,
          },
          onOk() {
            if (!latestCanStart) {
              if (modalAvailableCredits == null) {
                message.info('Credit balance is still loading. Try again in a moment.');
              }
              return Promise.reject(new Error('French dialogue run is not ready to start.'));
            }

            if (selectedRunType === 'retranslation') {
              const offer = eligibility?.discountedRetranslation;
              const retranslationUiState = deriveFrenchDialogueRetranslationUiState({
                isChapterScopedRun,
                offer,
                availableCredits: modalAvailableCredits,
              });
              if (!retranslationUiState.showOption) {
                message.warning('The discounted retranslation offer is only available for whole-book runs.');
                return Promise.reject(new Error('Retranslation is not available.'));
              }
              if (!offer?.eligible) {
                message.warning(resolveRetranslationIneligibilityReason(offer));
                return Promise.reject(new Error('Retranslation is not eligible.'));
              }
              if (retranslationUiState.hasEnoughCredits === false && modalAvailableCredits != null) {
                openTopUpFromBook(
                  buildFrenchDialogueRetranslationTopUpContext({
                    discountedCredits: retranslationUiState.discountedCredits,
                    availableCredits: modalAvailableCredits,
                  }),
                );
                return Promise.reject(new Error('Insufficient credits for discounted retranslation.'));
              }

              confirmModalRef?.destroy();
              setIsFrenchDialogueRunStarting(true);
              void (async () => {
                try {
                  await queueDiscountedRetranslationRun();
                } catch (error) {
                  if (isBookDetailApiError(error)) {
                    const reasonCode = (
                      (error.response?.data as any)?.detail?.reasonCode
                      || (error.response?.data as any)?.reasonCode
                      || ''
                    ).toString();
                    if (reasonCode === 'insufficient_credits') {
                      const shortfallAvailable = modalAvailableCredits ?? 0;
                      openTopUpFromBook(
                        buildFrenchDialogueRetranslationTopUpContext({
                          discountedCredits: retranslationUiState.discountedCredits,
                          availableCredits: shortfallAvailable,
                        }),
                      );
                      await refreshEligibility(true);
                      return;
                    }
                    if (reasonCode === 'run_in_progress') {
                      message.info('A discounted French dialogue retranslation is already in progress.');
                      await refreshEligibility(true);
                      await refreshTranslationsImmediate(true);
                      return;
                    }
                    if (reasonCode === 'discount_offer_consumed') {
                      message.info('The one-time discounted French dialogue retranslation has already been used.');
                      await refreshEligibility(true);
                      return;
                    }
                  }
                  console.error('Failed to queue discounted French dialogue retranslation:', error);
                  message.error('Failed to queue discounted French dialogue retranslation.');
                } finally {
                  setIsFrenchDialogueRunStarting(false);
                }
              })();
              return;
            }

            const selectedEligibility = getCleanupEligibility(eligibility) || eligibility;
            if (!selectedEligibility?.eligible) {
              message.warning(resolveCleanupIneligibilityReason(selectedEligibility));
              return Promise.reject(new Error('Cleanup is not eligible.'));
            }

            confirmModalRef?.destroy();
            setIsFrenchDialogueRunStarting(true);
            void (async () => {
              try {
                await queueCleanupRun();
              } catch (error) {
                if (isBookDetailApiError(error) && error.response?.status === 409) {
                  const reasonCode = (
                    (error.response?.data as any)?.detail?.reasonCode
                    || (error.response?.data as any)?.reasonCode
                    || ''
                  ).toString();
                  if (reasonCode === 'run_in_progress') {
                    message.info('A French dialogue pass run is already in progress.');
                  } else if (reasonCode === 'chapter_already_completed') {
                    message.info(
                      isChapterScopedRun
                        ? 'French dialogue pass already completed for this chapter.'
                        : 'French dialogue pass already completed for this book.',
                    );
                  } else {
                    message.info(
                      isChapterScopedRun
                        ? 'French dialogue pass is already complete for this chapter.'
                        : 'French dialogue pass is already complete for this book.',
                    );
                  }
                  await refreshEligibility(true);
                  await refreshTranslationsImmediate(true);
                  return;
                }
                console.error('Failed to queue French dialogue pass:', error);
                message.error('Failed to queue French dialogue pass.');
              } finally {
                setIsFrenchDialogueRunStarting(false);
              }
            })();
            return;
          },
        });
      };

      const cachedEligibilityKey = normalizedChapterGuid
        ? `${normalizedPair}::chapter:${normalizedChapterGuid}`
        : normalizedPair;
      const cachedEligibility =
        frenchDialogueLegacyEligibilityRef.current[cachedEligibilityKey]
        || (!normalizedChapterGuid ? frenchDialogueLegacyEligibilityByPair[normalizedPair] : null)
        || null;

      setIsFrenchDialogueModalOpening(true);
      void (async () => {
        const latestEligibility = await fetchFrenchDialogueLegacyEligibility({
          pair: normalizedPair,
          targetLanguage: targetLanguage || 'French',
          chapterGuid: normalizedChapterGuid || undefined,
          force: true,
        });
        let latestCreditBalance: CreditBalance | null | undefined = creditBalance;
        try {
          latestCreditBalance = await fetchCreditBalanceCached(userId, { force: false, silent: false });
        } catch (creditError) {
          console.warn('Failed to fetch credit balance before opening French dialogue modal', creditError);
        } finally {
          setIsFrenchDialogueModalOpening(false);
        }
        openConfirmationModal(latestEligibility || cachedEligibility, latestCreditBalance || creditBalance);
      })();

    },
    [
      API_BASE,
      addClientIntent,
      addOptimisticTranslation,
      availableCredits,
      auth.currentUser,
      book,
      bookId,
      bookLanguage,
      creditBalance,
      fetchCreditBalanceCached,
      fetchFrenchDialogueLegacyEligibility,
      frenchDialogueLegacyEligibilityByPair,
      isDark,
      openTopUpFromBook,
      refreshTranslationsImmediate,
      sharedUserId,
    ],
  );

  const handleOpenGlossaryScanFromTranslateModal = useCallback(async () => {
    const targetLang = (targetLanguage || '').trim();
    if (!targetLang) {
      message.error('Select a target language to run an AI glossary scan.');
      return;
    }
    if (!isEditing) {
      handleEditMetadataClick();
    }
    const sourceLang = (bookLanguage || book.language || '').trim();
    if (!sourceLang || sourceLang === targetLang) {
      message.error('Source and target languages must be different.');
      return;
    }
    setIsOpeningGlossaryScan(true);
    try {
      const ready = await ensureMetadataLanguage(targetLang);
      if (!ready) {
        return;
      }
      const existingPair = findTranslationPairForTargetLanguage(
        book.translation_pairs || [],
        sourceLang,
        targetLang,
      );
      const pair = existingPair || `${sourceLang}-${targetLang}`;
      setMetadataTabSignal({ tab: 'metadata', ts: Date.now() });
      setMetadataLanguageSignal({ language: pair, ts: Date.now() });
      setActiveMetadataTab('metadata');
      setSelectedTab(pair);
      setCurrentLanguagePair(pair);
      setBlurbActiveTabKey(pair);
      setIsTranslatedTabActive(true);
      void handleTabChange(pair);
      setGlossaryScanTrigger(prev => ({ pair, nonce: (prev?.nonce ?? 0) + 1 }));
      setFrenchTone('');
      setIsTranslateModalVisible(false);
    } finally {
      setIsOpeningGlossaryScan(false);
    }
  }, [
    book.language,
    bookLanguage,
    book.translation_pairs,
    ensureMetadataLanguage,
    handleEditMetadataClick,
    handleTabChange,
    isEditing,
    targetLanguage,
  ]);

  const handleReviewGlossaryScanFromTranslateModal = useCallback(async () => {
    const targetLang = (targetLanguage || '').trim();
    if (!targetLang) {
      message.error('Select a target language to review the AI glossary scan.');
      return;
    }
    if (!isEditing) {
      handleEditMetadataClick();
    }
    const sourceLang = (bookLanguage || book.language || '').trim();
    if (!sourceLang || sourceLang === targetLang) {
      message.error('Source and target languages must be different.');
      return;
    }
    setIsOpeningGlossaryScan(true);
    try {
      const ready = await ensureMetadataLanguage(targetLang);
      if (!ready) {
        return;
      }
      const existingPair = findTranslationPairForTargetLanguage(
        book.translation_pairs || [],
        sourceLang,
        targetLang,
      );
      const pair = existingPair || `${sourceLang}-${targetLang}`;
      setMetadataTabSignal({ tab: 'metadata', ts: Date.now() });
      setMetadataLanguageSignal({ language: pair, ts: Date.now() });
      setActiveMetadataTab('metadata');
      setSelectedTab(pair);
      setCurrentLanguagePair(pair);
      setBlurbActiveTabKey(pair);
      setIsTranslatedTabActive(true);
      void handleTabChange(pair);
      setTermScanReviewTrigger(prev => ({ pair, nonce: (prev?.nonce ?? 0) + 1 }));
      setFrenchTone('');
      setIsTranslateModalVisible(false);
    } finally {
      setIsOpeningGlossaryScan(false);
    }
  }, [
    book.language,
    bookLanguage,
    book.translation_pairs,
    ensureMetadataLanguage,
    handleEditMetadataClick,
    handleTabChange,
    isEditing,
    targetLanguage,
  ]);

  const resolveTranslationPair = useCallback(
    (targetLanguage: string) => {
      const cleanTarget = (targetLanguage || '').trim();
      if (!cleanTarget || isOriginalLanguageValue(cleanTarget)) {
        return 'Original';
      }
      const pair = findTranslationPairForTargetLanguage(
        book.translation_pairs || [],
        (bookLanguage || book.language || '').trim(),
        cleanTarget,
      );
      if (pair) {
        return pair;
      }
      const sourceLanguage = (bookLanguage || book.language || '').trim();
      if (!sourceLanguage || cleanTarget === sourceLanguage) {
        return 'Original';
      }
      return `${sourceLanguage}-${cleanTarget}`;
    },
    [book.translation_pairs, book.language, bookLanguage],
  );

  const handleApplyPendingProofreadReviewsForVersion = useCallback(
    (versionLabel: string) => {
      if (applyingPendingProofreadLanguage) {
        message.info('Pending proofreading changes are already being applied.', 6);
        return;
      }
      const pendingCount = getPendingProofreadReviewCountForVersion(versionLabel);
      if (pendingCount <= 0) {
        message.info('There are no pending proofread reviews to apply for this version.', 6);
        return;
      }

      const normalizedVersion = normalizeProofreadVersion(versionLabel);
      const isOriginalVersion = isOriginalLanguageValue(normalizedVersion);
      const effectiveVersion = isOriginalVersion ? 'Original' : normalizedVersion;
      const versionDisplay = isOriginalVersion ? originalLanguageLabel : formatLangDisplayText(effectiveVersion);
      const applyingLanguageKey =
        isOriginalVersion
          ? 'Original'
          : getTargetLanguageFromPair(effectiveVersion) || versionDisplay;
      const applyingLanguageDisplay = isOriginalVersion ? originalLanguageLabel : applyingLanguageKey;
      const runApplyPendingProofreadReviews = async () => {
        setApplyingPendingProofreadLanguage(applyingLanguageKey);
        setPendingProofreadOverlayMessage(
          `Applying pending proofreading changes for ${applyingLanguageDisplay}.`,
        );
        try {
          const result = await applyAllPendingProofreadChangeSetsForVersion({
            versionLabel: effectiveVersion,
          });
          const toastSummary = summarizeBulkProofreadApplyResult({
            appliedCount: Number(result.appliedCount || 0),
            skippedCount: Number(result.skippedCount || 0),
            removedCount: Number(result.removedCount || 0),
            results: result.results || [],
          });
          message[toastSummary.level](toastSummary.message, 10);
        } catch (error) {
          const detail = String((error as any)?.message || '').trim();
          message.error(detail || 'Failed to apply pending proofread reviews.', 10);
        } finally {
          setApplyingPendingProofreadLanguage(current =>
            current === applyingLanguageKey ? null : current,
          );
          setPendingProofreadOverlayMessage(current =>
            current === `Applying pending proofreading changes for ${applyingLanguageDisplay}.`
              ? null
              : current,
          );
        }
      };
      Modal.confirm({
        rootClassName: 'theme-aware-confirm',
        title: `Apply ${pendingCount} pending proofread review${pendingCount === 1 ? '' : 's'}?`,
        content: `This will accept and save all pending proofread changes for ${versionDisplay}. If you want to manually review the changes, go to the proofread tab for each chapter.`,
        okText: 'Apply All',
        cancelText: 'Cancel',
        okButtonProps: { className: 'app-primary-cta' },
        onOk: () => {
          void runApplyPendingProofreadReviews();
        },
      });
    },
    [
      applyAllPendingProofreadChangeSetsForVersion,
      applyingPendingProofreadLanguage,
      formatLangDisplayText,
      getPendingProofreadReviewCountForVersion,
      normalizeProofreadVersion,
      originalLanguageLabel,
    ],
  );

  const handleOpenProofreadForLanguage = useCallback(
    (language: string) => {
      const pair = resolveTranslationPair(language);
      void toggleProofreadModal(pair);
    },
    [resolveTranslationPair, toggleProofreadModal],
  );

  const handleApplyPendingProofreadReviewsForLanguage = useCallback(
    (language: string) => {
      const pair = resolveTranslationPair(language);
      handleApplyPendingProofreadReviewsForVersion(pair);
    },
    [handleApplyPendingProofreadReviewsForVersion, resolveTranslationPair],
  );

  const handlePrepareMetadataTranslateLanguage = useCallback(
    (language: string) => {
      setTargetMetadataLanguage(language);
    },
    [],
  );

  const handlePrepareKeywordSuggestionLanguage = useCallback(
    (language: string) => {
      setTargetLanguage(language);
      const cleanLanguage = String(language || '').trim();
      if (cleanLanguage.toLowerCase() === String(bookLanguage || '').trim().toLowerCase()) {
        setTranslationTargetLanguages([]);
      } else {
        setTranslationTargetLanguages(normalizeTranslationTargets([cleanLanguage]));
      }
    },
    [bookLanguage, normalizeTranslationTargets],
  );

const handleTranslateAudiobookCreditsText = async (text: string, targetLang: string): Promise<string> => {
  try {
    if (!auth.currentUser) {
      throw new Error('No user logged in');
    }

	//console.log("handleTranslateAudiobookCreditsText text "+ text)

    const response = await bookDetailApiPost(`${API_BASE}/translate-audio-credits/`, {
      userId: auth.currentUser.uid,
      bookId: bookId,
      sourceLang: bookLanguage,
      targetLang,
      model: selectedAIModel,
      creditText: text,
      genre: genres.join(', '),
      quotationStyle: quotationStyle
    });

    if (response.data.status === 'success' && response.data.translated_blurb) {
      return response.data.translated_blurb;
    }
    throw new Error(response.data.message || 'Translation failed');
  } catch (error) {
    console.error('Error translating text:', error);
    message.error('Failed to translate text');
    throw error;
  }

  };

  const renderRelationshipSummaryCard = (
    options?: { style?: React.CSSProperties; hideTitle?: boolean }
  ) => {
    const { style, hideTitle } = options || {};
    const missingChapterCount = relationshipSummary?.missingChapters;
    const hasMissingChapters = typeof missingChapterCount === 'number' && missingChapterCount > 0;
    if (!isRelationshipSummaryLoading && relationshipSummary && !hasMissingChapters) {
      return null;
    }
    const summaryLabel = relationshipSummary
      ? hasMissingChapters
        ? `${missingChapterCount} chapter${missingChapterCount === 1 ? '' : 's'} need analysis`
        : null
      : 'Summary unavailable.';
    const freeRunAvailable = relationshipSummary?.freeRunAvailable;
    const freeRunLabel = freeRunAvailable && hasMissingChapters ? 'First run free' : null;

    return (
      <Card size="small" style={{ marginBottom: 12, width: '100%', ...style }}>
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          <div
            style={{
              color: token.colorTextSecondary,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {!hideTitle && (
              <>
                <span style={{ fontWeight: 600, marginRight: 8, color: token.colorText }}>
                  Character Relationships:
                </span>
                <Tooltip title="We analyze each chapter to determine which characters interact and whether they use formal or informal pronouns. Translators use this context to pick the correct tone.">
                  <QuestionCircleOutlined style={{ color: token.colorTextSecondary }} />
                </Tooltip>
              </>
            )}
            <span style={{ marginLeft: hideTitle ? 0 : 8 }}>
              {isRelationshipSummaryLoading ? 'Loading summary…' : summaryLabel}
            </span>
            {freeRunLabel && (
              <Tag
                color="green"
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                {freeRunLabel}
              </Tag>
            )}
          </div>
        </Space>
      </Card>
    );
  };

const getTranslatedTitle = () => {
  if (selectedTab === 'Original') return book.title;

  // Handle different translation key formats
  const translationKey = selectedTab.replace('-', '_');
  const alternativeKey = selectedTab; // Try the original format too

  // Check both possible key formats
  const translation = titleTranslations[translationKey] ||
                     titleTranslations[alternativeKey] ||
                     titleTranslations[`${bookLanguage}_${getTargetLanguageFromTab(selectedTab)}`];

  // If no translation exists, indicate it clearly
  if (!translation) {
    return `[${book.title}]`; // Indicate untranslated with brackets
  }

  return translation;
};

const getTranslatedSeriesName = () => {
  if (!book.series || selectedTab === 'Original') return book.series || '';

  // Handle different translation key formats
  const translationKey = selectedTab.replace('-', '_');
  const alternativeKey = selectedTab;

  // Check both possible key formats
  const translation = seriesTranslations[translationKey] ||
                     seriesTranslations[alternativeKey] ||
                     seriesTranslations[`${bookLanguage}_${getTargetLanguageFromTab(selectedTab)}`];

  if (!translation) {
    return `[${book.series}]`; // Indicate untranslated with brackets
  }

  return translation;
};

const isSimpleBookDetailMode = bookDetailExperienceMode === 'simple';
const headerEditLabel = bookDetailExperienceLoaded && !isSimpleBookDetailMode
  ? 'Edit metadata'
  : 'Edit book info';
const simpleSourceLanguageFamilyKey = getLanguageFamilyKey(bookLanguage || book.language || '');
const simpleCandidateTargetLanguageOptions = normalizeTranslationTargets([
  ...resolvedTranslationTargetLanguages,
  ...languages,
]).filter(language => getLanguageFamilyKey(language) !== simpleSourceLanguageFamilyKey);
const simpleActiveStatusStates = new Set(['initializing', 'pending', 'queued', 'running', 'retrying']);
const simpleBlockingStatusTypes = new Set(['translation', 'glossaryscan', 'relationshipanalysis', 'export']);
const simpleCoverInitials = (book.title || 'Book')
  .split(/\s+/)
  .map(part => part.trim()[0])
  .filter(Boolean)
  .slice(0, 2)
  .join('')
  .toUpperCase();
const hasSimpleActiveWorkForTarget = (language: string): boolean => {
  const targetFamilyKey = getLanguageFamilyKey(language);
  if (!targetFamilyKey) {
    return false;
  }
  const currentExpressTarget = String(expressJob?.targetLanguage || '').trim();
  const currentExpressStatus = String(expressJob?.status || '').toLowerCase();
  if (
    expressJob?.id
    && currentExpressTarget
    && getLanguageFamilyKey(currentExpressTarget) === targetFamilyKey
    && !EXPRESS_TERMINAL_STATUSES.includes(currentExpressStatus)
  ) {
    return true;
  }
  return (contextTranslations || []).some((entry: TranslationStatus) => {
    const entryBookId = String((entry as any).bookId || '').trim();
    if (entryBookId !== bookId) return false;
    const entryTargetLanguage = String((entry as any).targetLang || (entry as any).targetLanguage || '').trim();
    if (!entryTargetLanguage || getLanguageFamilyKey(entryTargetLanguage) !== targetFamilyKey) {
      return false;
    }
    const statusKey = resolveTranslationStatusState(entry);
    if (!simpleActiveStatusStates.has(statusKey)) return false;
    const typeKey = String((entry as any).type || '')
      .replace(/[\s_-]+/g, '')
      .toLowerCase();
    return simpleBlockingStatusTypes.has(typeKey);
  });
};
const isSimpleTargetLanguageCompleted = (language: string): boolean => {
  if (hasSimpleActiveWorkForTarget(language)) {
    return false;
  }
  const canonicalLanguage = canonicalizeLanguageName(language);
  const progress = normalizedTranslationProgress?.[canonicalLanguage];
  const totalChapters = progress?.total_chapters || book.chapters?.length || 0;
  if (progress?.status === 'complete') {
    return true;
  }
  if (totalChapters > 0 && (progress?.translated_chapters || 0) >= totalChapters) {
    return true;
  }
  const translatedChaptersKey = `translated_chapters_${bookLanguage}_${language}`;
  const translatedChapters: TranslatedChapter[] = (book as any)[translatedChaptersKey] || [];
  return Boolean(
    book.chapters?.length &&
    book.chapters.every((chapter: any) => isChapterTranslatedForPair(translatedChapters, chapter?.chapter_guid))
  );
};
const simpleTargetLanguageOptions = simpleCandidateTargetLanguageOptions;
const simpleCompletedTargetLanguageOptions = simpleCandidateTargetLanguageOptions.filter(
  language => isSimpleTargetLanguageCompleted(language)
);
const simpleCompletedTargetLanguageSet = new Set(simpleCompletedTargetLanguageOptions);
const expressTargetCandidate =
  expressTargetLanguage ||
  targetLanguage ||
  resolvedTranslationTargetLanguages[0] ||
  '';
const expressResolvedTargetLanguage = simpleTargetLanguageOptions.includes(expressTargetCandidate)
  ? expressTargetCandidate
  : '';
const simpleSelectedTargetCompleted = Boolean(
  expressResolvedTargetLanguage && isSimpleTargetLanguageCompleted(expressResolvedTargetLanguage)
);
const expressGlossaryContext = JSON.stringify([
  bookId, resolvedOwnerUserId || auth.currentUser?.uid, bookLanguage || book.language,
  expressResolvedTargetLanguage,
  (book.chapters || []).map((chapter: any) => [chapter.chapter_guid, chapter.content_version, chapter.word_count, chapter.wordCount]),
]);
const expressGlossaryEstimate = expressGlossaryQuote?.context === expressGlossaryContext
  ? expressGlossaryQuote.estimate : null;
useEffect(() => {
  setExpressForceGlossaryRescan(false);
}, [expressGlossaryContext]);
useEffect(() => {
  if (!bookId || !expressGlossaryEnabled || !expressResolvedTargetLanguage || !auth.currentUser || isSandboxFixtureMode) return;
  let cancelled = false;
  setExpressGlossaryQuote(null);
  setExpressGlossaryQuoteError('');
  void bookDetailApiGet(`${API_BASE}/books/${bookId}/term-scan/estimate`, {
    params: { userId: resolvedOwnerUserId || auth.currentUser.uid,
      sourceLang: bookLanguage || book.language, targetLang: expressResolvedTargetLanguage },
  }).then(response => {
    if (cancelled) return;
    if (glossaryScanCost(response.data) === null) {
      setExpressGlossaryQuoteError('Unable to verify the glossary scan estimate. Retry before starting.');
    } else {
      setExpressGlossaryQuote({ context: expressGlossaryContext, estimate: response.data });
    }
  }).catch(() => {
    if (!cancelled) setExpressGlossaryQuoteError('Unable to verify the glossary scan estimate. Retry before starting.');
  });
  return () => { cancelled = true; };
}, [API_BASE, expressGlossaryEnabled, expressGlossaryContext, expressGlossaryQuoteRetry,
  expressResolvedTargetLanguage, bookId, bookLanguage, book.language, resolvedOwnerUserId,
  auth.currentUser?.uid, isSandboxFixtureMode]);

  if (!bookId) {
    return <div>No book ID provided</div>;
  }

const simpleSelectedTargetLatestExport = (() => {
  if (!expressResolvedTargetLanguage) {
    return null;
  }
  const selectedFamilyKey = getLanguageFamilyKey(expressResolvedTargetLanguage);
  const matchesSelectedLanguage = (rawValue: unknown): boolean => {
    const rawText = String(rawValue || '').trim();
    if (!rawText) {
      return false;
    }
    const cleanText = stripProofreadLabel(rawText);
    const { targetLang: exportTargetLang } = splitLanguagePair(cleanText);
    const candidate = exportTargetLang || cleanText;
    return Boolean(candidate && getLanguageFamilyKey(candidate) === selectedFamilyKey);
  };
  const matchingExports = (recentExports || []).filter(entry => (
    matchesSelectedLanguage(entry.languageKey) || matchesSelectedLanguage(entry.version)
  ));
  if (matchingExports.length === 0) {
    return null;
  }
  return [...matchingExports].sort((a, b) => {
    const aTime = a.completedAt ? new Date(a.completedAt).getTime() : 0;
    const bTime = b.completedAt ? new Date(b.completedAt).getTime() : 0;
    return (Number.isFinite(bTime) ? bTime : 0) - (Number.isFinite(aTime) ? aTime : 0);
  })[0];
})();
const openSimpleExportFormatModal = () => {
  setSimpleExportFormat(isEpubUpload ? expressOutputFormat : 'Word');
  setSimpleExportFormatModalVisible(true);
};
const createSimpleExport = async () => {
  if (!expressResolvedTargetLanguage) {
    message.error('Choose a target language before creating an export.');
    return;
  }
  const requestedFormat: SimpleExportFormat = isEpubUpload ? simpleExportFormat : 'Word';
  setExpressOutputFormat(requestedFormat);
  setSimpleExportSubmitting(true);
  try {
    await handleExport({
      version: `${String(bookLanguage || book.language || 'English')}-${expressResolvedTargetLanguage}`,
      format: requestedFormat,
      includeImages: uploadType === 'Word' ? includeWordImages : true,
      writingOrientation: requestedFormat === 'ePub' ? 'horizontal' : exportOrientation,
    });
    setSimpleExportFormatModalVisible(false);
  } finally {
    setSimpleExportSubmitting(false);
  }
};
const simpleSelectedLanguagePair = (() => {
  if (!expressResolvedTargetLanguage) {
    return '';
  }
  const selectedSourceFamilyKey = getLanguageFamilyKey(bookLanguage || book.language || '');
  const selectedTargetFamilyKey = getLanguageFamilyKey(expressResolvedTargetLanguage);
  return (book.translation_pairs || []).find((pair: string) => {
    const { sourceLang, targetLang } = splitLanguagePair(pair);
    return (
      getLanguageFamilyKey(sourceLang) === selectedSourceFamilyKey
      && getLanguageFamilyKey(targetLang) === selectedTargetFamilyKey
    );
  }) || '';
})();
const simpleSelectedPairParts = simpleSelectedLanguagePair
  ? getEncodedLanguagePairParts(simpleSelectedLanguagePair)
  : {
      sourceLang: String(bookLanguage || book.language || '').replace(/ /g, '0'),
      targetLang: String(expressResolvedTargetLanguage || '').replace(/ /g, '0'),
    };
const getSimpleOutputText = (
  translations: Record<string, any>,
  type: 'blurb' | 'title' | 'series',
): string => {
  if (!expressResolvedTargetLanguage) {
    return '';
  }
  const candidates = [
    simpleSelectedLanguagePair,
    simpleSelectedLanguagePair.replace(/_/g, '-'),
    `${bookLanguage}-${expressResolvedTargetLanguage}`,
  ].filter(Boolean);
  for (const candidate of candidates) {
    const value = translations[candidate];
    if (typeof value === 'string' && value.trim() && value.trim() !== '<Not Translated>') {
      return value;
    }
  }
  const directKey = `${type}_${simpleSelectedPairParts.sourceLang}_${simpleSelectedPairParts.targetLang}`;
  const directValue = (book as any)[directKey];
  return typeof directValue === 'string' && directValue.trim() && directValue.trim() !== '<Not Translated>'
    ? directValue
    : '';
};
const simpleTranslatedBlurb = getSimpleOutputText(blurbTranslations, 'blurb');
const simpleLocalizedTitleText = getSimpleOutputText(titleTranslations, 'title');
const simpleLocalizedSeriesText = getSimpleOutputText(seriesTranslations, 'series');
const simpleHasCompletedLocalizedMetadata = Boolean(simpleLocalizedTitleText || simpleLocalizedSeriesText);
const simpleTranslatedBlurbFilename = `${sanitizeExportFilenamePart(book.title || 'book')}_blurb_${sanitizeExportFilenamePart(expressResolvedTargetLanguage || 'translation')}.txt`;
const expressTranslatedChaptersKey = `translated_chapters_${bookLanguage}_${expressResolvedTargetLanguage}`;
const expressTranslatedChapters: TranslatedChapter[] = (book as any)[expressTranslatedChaptersKey] || [];
const expressEstimatedTranslationCredits = (book.chapters || []).reduce(
  (total, chapter: any) => (
    isChapterTranslatedForPair(expressTranslatedChapters, chapter?.chapter_guid)
      ? total
      : total + Number(chapter?.word_count || chapter?.wordCount || 0)
  ),
  0,
);
const expressGlossaryCost = glossaryScanCost(expressGlossaryEstimate, expressForceGlossaryRescan);
const expressEstimatedGlossaryCredits = expressGlossaryEnabled ? expressGlossaryCost ?? 0 : 0;
const expressGlossaryBlocker = expressGlossaryEnabled && !isSandboxFixtureMode
  ? expressGlossaryQuoteError || (expressGlossaryEstimate?.activeConflict
    ? 'Wait for the existing glossary scan to finish before starting this workflow.'
    : expressGlossaryCost === null
    ? 'Checking glossary scan estimate…'
    : expressGlossaryEstimate?.requiresRescanConfirmation && !expressForceGlossaryRescan
      ? 'Confirm a new paid glossary scan, or turn off AI Glossary Scan.' : '')
  : '';
const expressBlurbWordCount = countWordsIncludingMarkup(expressBlurbSourceText || '');
const expressBlurbFreeRunAvailable = Boolean(expressResolvedTargetLanguage)
  && !metadataFreeRunConsumed((book as any).blurbTranslationFreeRuns, expressResolvedTargetLanguage);
const expressEstimatedBlurbCredits = expressBlurbEnabled && !expressBlurbFreeRunAvailable ? expressBlurbWordCount : 0;
const expressEstimatedTotalCredits = Math.max(
  0,
  Math.ceil(expressEstimatedTranslationCredits + expressEstimatedGlossaryCredits + expressEstimatedBlurbCredits),
);
const simpleCreditBalanceLoading = Boolean(resolvedOwnerUserId) && availableCredits == null;
const simpleCreditShortfall =
  typeof availableCredits === 'number'
    ? Math.max(expressEstimatedTotalCredits - Math.floor(availableCredits), 0)
    : null;
const simpleHasEnoughCredits =
  expressEstimatedTotalCredits <= 0
    ? true
    : typeof simpleCreditShortfall === 'number'
      ? simpleCreditShortfall <= 0
      : null;
const simpleTranslationTopUpContext: CreditTopUpContext | null =
  typeof availableCredits === 'number'
    ? {
        source: 'simple_translation',
        requiredCredits: expressEstimatedTotalCredits,
        availableCredits: Math.max(0, Math.floor(availableCredits)),
        shortfallCredits: Math.max(expressEstimatedTotalCredits - Math.floor(availableCredits), 0),
      }
    : null;
const expressStatusText = (() => {
  const rawStatusText = String(expressJob?.message || '')
    .replace(/\bExpress translation\b/gi, 'Translation')
    .replace(/\bSimple translation\b/gi, 'Translation')
    .trim();
  if (/^translation is running\.?$/i.test(rawStatusText)) {
    return expressResolvedTargetLanguage
      ? `${expressResolvedTargetLanguage} translation is in progress.`
      : 'Translation is in progress.';
  }
  return rawStatusText;
})();
const expressTerminalStatuses = new Set(EXPRESS_TERMINAL_STATUSES);
const expressJobStatus = String(expressJob?.status || '').toLowerCase();
const expressJobInProgress = Boolean(expressJob?.id && !expressTerminalStatuses.has(expressJobStatus));
const expressJobTargetLanguage = String(expressJob?.targetLanguage || '').trim();
const expressJobMatchesResolvedTarget = Boolean(
  expressJob?.id
  && expressResolvedTargetLanguage
  && (
    expressJobTargetLanguage
      ? getLanguageFamilyKey(expressJobTargetLanguage) === getLanguageFamilyKey(expressResolvedTargetLanguage)
    : true
  )
);
const expressGlossaryCharged = !simpleSelectedTargetCompleted && (expressGlossaryEnabled
  || (expressJobMatchesResolvedTarget && Number(expressJob?.glossaryScanCredits || 0) > 0)
);
const activeSimpleStatusForTarget = expressResolvedTargetLanguage
  ? (contextTranslations || []).find((entry: TranslationStatus) => {
      const entryBookId = String((entry as any).bookId || '').trim();
      if (entryBookId !== bookId) return false;
      const entryTargetLanguage = String((entry as any).targetLang || (entry as any).targetLanguage || '').trim();
      if (!entryTargetLanguage) return false;
      if (getLanguageFamilyKey(entryTargetLanguage) !== getLanguageFamilyKey(expressResolvedTargetLanguage)) {
        return false;
      }
      const statusKey = resolveTranslationStatusState(entry);
      if (!simpleActiveStatusStates.has(statusKey)) return false;
      const typeKey = String((entry as any).type || '')
        .replace(/[\s_-]+/g, '')
        .toLowerCase();
      return simpleBlockingStatusTypes.has(typeKey);
    }) || null
  : null;
const simpleGlossaryStatusForTarget = expressResolvedTargetLanguage
  ? (contextTranslations || []).find((entry: TranslationStatus) => {
      const entryBookId = String((entry as any).bookId || '').trim();
      if (entryBookId !== bookId) return false;
      const entryTargetLanguage = String((entry as any).targetLang || (entry as any).targetLanguage || '').trim();
      if (!entryTargetLanguage) return false;
      if (getLanguageFamilyKey(entryTargetLanguage) !== getLanguageFamilyKey(expressResolvedTargetLanguage)) {
        return false;
      }
      const typeKey = String((entry as any).type || '')
        .replace(/[\s_-]+/g, '')
        .toLowerCase();
      if (typeKey !== 'glossaryscan') return false;
      const statusKey = resolveTranslationStatusState(entry);
      return !['failed', 'cancelled', 'canceled'].includes(statusKey);
    }) || null
  : null;
const simpleTranslationInProgress = Boolean(
  (expressJobInProgress && expressJobMatchesResolvedTarget) || activeSimpleStatusForTarget
);
const simpleSelectedTargetReadOnly = simpleTranslationInProgress || simpleSelectedTargetCompleted;
const simpleTranslationInProgressMessage = expressResolvedTargetLanguage
  ? `${expressResolvedTargetLanguage} translation is already in progress.`
  : 'Translation is already running.';
const simpleJobLocalizedMetadataOptionIds = expressJobMatchesResolvedTarget && expressJob?.localizedMetadataOptionIds
  ? expressJob.localizedMetadataOptionIds
  : {};
const simpleInProgressLocalizedTitleText = simpleTranslationInProgress
  ? (
      String(expressJobMatchesResolvedTarget ? expressJob?.localizedTitle || '' : '').trim()
      || (simpleJobLocalizedMetadataOptionIds?.title === '__original__' ? String(book.title || '').trim() : '')
    )
  : '';
const simpleInProgressLocalizedSeriesText = simpleTranslationInProgress
  ? (
      String(expressJobMatchesResolvedTarget ? expressJob?.localizedSeries || '' : '').trim()
      || (simpleJobLocalizedMetadataOptionIds?.series === '__original__' ? String(book.series || '').trim() : '')
    )
  : '';
const simpleHasInProgressLocalizedMetadata = Boolean(simpleInProgressLocalizedTitleText || simpleInProgressLocalizedSeriesText);
const expressSupportCodes = Array.isArray(expressJob?.chapterStatuses)
  && expressJobMatchesResolvedTarget
  ? expressJob.chapterStatuses
      .filter((chapter: any) => chapter?.supportCode)
      .map((chapter: any) => {
        const chapterLabel = chapter?.chapterTitle || chapter?.chapterName || chapter?.chapterGuid || 'Chapter';
        return `${chapterLabel}: ${chapter.supportCode}`;
      })
  : [];
const expressFailedChapterRows = Array.isArray(expressJob?.chapterStatuses)
  && expressJobMatchesResolvedTarget
  ? expressJob.chapterStatuses.filter((chapter: any) => {
      const status = String(chapter?.status || '').toLowerCase();
      return ['failed', 'error', 'expired', 'interrupted', 'released', 'cancelled', 'canceled', 'aborted'].includes(status);
    })
  : [];
const expressFailedChapterCount = expressFailedChapterRows.length || Number(expressJob?.failedChapters || 0) || 0;
const expressActiveChildCount = Number(expressJob?.activeChildCount || 0) || 0;
const expressUntrackedChildCount = Number(expressJob?.untrackedChildCount || 0) || 0;
const expressRetryIsSettled = expressActiveChildCount === 0 && expressUntrackedChildCount === 0;
const showExpressFailedRetryAlert = Boolean(
  expressJobMatchesResolvedTarget
  && expressJobStatus === 'failed'
  && expressRetryIsSettled,
);
const showExpressProvisionalFailures = Boolean(
  expressJobMatchesResolvedTarget
  && expressFailedChapterCount > 0
  && !showExpressFailedRetryAlert
  && (expressActiveChildCount > 0 || expressUntrackedChildCount > 0 || expressJobInProgress),
);
const simpleTitleOptions = simpleMetadataOptions.titleOptions || [];
const simpleSeriesOptions = simpleMetadataOptions.seriesOptions || [];
const selectedSimpleTitleOption = simpleTitleOptions.find(option => option.id === simpleSelectedTitleOptionId);
const selectedSimpleSeriesOption = simpleSeriesOptions.find(option => option.id === simpleSelectedSeriesOptionId);
const simpleNeedsTitleOptions = Boolean(String(book.title || '').trim());
const simpleNeedsSeriesOptions = Boolean(String(book.series || '').trim());
const simpleMetadataOptionsReady = !expressResolvedTargetLanguage || (
  simpleSelectedTargetCompleted ||
  (
    (!simpleNeedsTitleOptions || simpleTitleOptions.length > 0) &&
    (!simpleNeedsSeriesOptions || simpleSeriesOptions.length > 0)
  )
);
const simpleMetadataStatus = !expressResolvedTargetLanguage
  ? { label: 'Recommended', color: 'warning' }
  : simpleSelectedTargetCompleted
    ? { label: 'Complete', color: 'success' }
    : simpleTranslationInProgress
      ? { label: 'Locked', color: 'default' }
    : simpleMetadataLoading
    ? { label: 'Preparing', color: 'processing' }
    : simpleMetadataOptionsReady
      ? { label: 'Ready', color: 'success' }
      : { label: 'Recommended', color: 'warning' };
const simpleShowLocalizedMetadataSetup = Boolean(
  expressResolvedTargetLanguage
  && !simpleSelectedTargetCompleted
  && !simpleTranslationInProgress
);
const simpleShowLocalizedMetadataSection = Boolean(
  simpleShowLocalizedMetadataSetup
  || (simpleTranslationInProgress && simpleHasInProgressLocalizedMetadata)
  || (simpleSelectedTargetCompleted && simpleHasCompletedLocalizedMetadata)
);
const simpleBlurbReady = !expressBlurbEnabled || Boolean(expressBlurbSourceText.trim());
const simpleBlurbTooLong = expressBlurbEnabled && expressBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS;
const metadataAutofillStatus = String(book.metadataAutofill?.status || '').toLowerCase();
const metadataAutofillAppliedFields = book.metadataAutofill?.appliedFields;
const metadataAutofillAppliedCount = Array.isArray(metadataAutofillAppliedFields)
  ? metadataAutofillAppliedFields.length
  : 0;
const simpleBookContextStatus = metadataAutofillStatus === 'running'
  ? { tone: 'processing' as const, text: 'Preparing book metadata' }
  : metadataAutofillStatus === 'completed'
    ? {
        tone: (metadataAutofillAppliedCount > 0 ? 'success' : 'warning') as 'success' | 'warning',
        text: metadataAutofillAppliedCount > 0
          ? 'AI metadata filled'
          : 'AI metadata suggestions ready',
      }
    : metadataAutofillStatus === 'failed'
      ? { tone: 'warning' as const, text: 'Metadata scan failed' }
      : null;
const expressStartDisabledReason =
  !expressResolvedTargetLanguage
    ? 'Choose a target language.'
    : expressGlossaryBlocker
      ? expressGlossaryBlocker
    : simpleSelectedTargetCompleted
      ? `${expressResolvedTargetLanguage} translation is complete.`
    : simpleTranslationInProgress
      ? simpleTranslationInProgressMessage
      : simpleMetadataLoading
        ? 'Localized title options are still being generated.'
        : !simpleMetadataOptionsReady
          ? 'Localized title options must be generated before translation starts.'
          : !simpleBlurbReady
            ? 'Enter the blurb text to translate.'
            : simpleBlurbTooLong
              ? `Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer.`
            : simpleCreditBalanceLoading
              ? 'Credit balance is still loading.'
              : simpleHasEnoughCredits === false && !expressRequestIdentity.current
                ? `You need ${Math.ceil(simpleCreditShortfall || 0).toLocaleString()} more credits.`
                : '';
const getMetadataOptionRecommendationLabel = (option: TranslationOption) => {
  if (option.recommendationLabel) return option.recommendationLabel;
  const score = typeof option.score === 'number' ? Math.round(option.score) : null;
  if (score === null) return '';
  if (score >= 85) return 'Strong fit';
  if (score >= 75) return 'Good alternative';
  if (score >= 65) return 'Viable with caveats';
  return 'Weak fit';
};
const getMetadataOptionRecommendationMeta = (option: TranslationOption) => {
  const label = getMetadataOptionRecommendationLabel(option);
  const rankLabel = option.rankLabel || (option.recommended ? 'Best match' : '');
  return [label, rankLabel].filter(Boolean).join(' · ');
};
const renderSimpleMetadataOption = (option: TranslationOption) => {
  return <span>{option.translatedText}</span>;
};
const renderSimpleSelectedMetadataLabel = (text: string) => (
  <span className="simple-translation-selected-option" title={text}>
    {text}
  </span>
);
const renderSimpleSectionHeader = (
  title: string,
  description: string,
  status?: { label: string; color?: string },
) => {
  const statusTone = status?.color || 'default';
  return (
    <div className="simple-translation-section-header">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {status && (
        <span className={`simple-translation-section-status simple-translation-section-status--${statusTone}`}>
          {statusTone === 'processing' && <LoadingOutlined spin className="simple-translation-section-status-icon" />}
          {status.label}
        </span>
      )}
    </div>
  );
};
const renderSimpleMetadataDetailsContent = (label: string, option?: TranslationOption) => {
  if (!option) return null;
  const originalLanguageMeaning = option.backTranslation || option.sourceTrans || '';
  const description = option.description || option.desc || '';
  return (
    <div className="simple-translation-details-content">
      <Text strong>{label}</Text>
      <Text>{option.translatedText}</Text>
      {originalLanguageMeaning && (
        <Text type="secondary"><strong>Meaning:</strong> {originalLanguageMeaning}</Text>
      )}
      {getMetadataOptionRecommendationMeta(option) && (
        <Text type="secondary"><strong>Recommendation:</strong> {getMetadataOptionRecommendationMeta(option)}</Text>
      )}
      {option.style && <Text type="secondary"><strong>Style:</strong> {option.style}</Text>}
      {(option.confidenceLabel || option.riskLabel) && (
        <Text type="secondary">{[option.confidenceLabel, option.riskLabel].filter(Boolean).join(' · ')}</Text>
      )}
      {option.scoreReason && (
        <Text type="secondary"><strong>Reason:</strong> {option.scoreReason}</Text>
      )}
      {description && (
        <Text type="secondary"><strong>Description:</strong> {description}</Text>
      )}
    </div>
  );
};
const renderSimpleMetadataDetailsButton = (label: string, option?: TranslationOption) => {
  if (!option) return null;
  const reviewMeta = getMetadataOptionRecommendationMeta(option) || 'Review match';
  const button = (
    <Button
      type="default"
      size="small"
      className="simple-translation-metadata-review-action"
      icon={<QuestionCircleOutlined />}
      aria-label={`Review ${label.toLowerCase()} details: ${reviewMeta}`}
      title={reviewMeta}
      onClick={() => {
        if (isMobile) {
          setSimpleMetadataDetail({ label, option });
        }
      }}
    >
      Review match
    </Button>
  );
  if (isMobile) {
    return button;
  }
  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      overlayClassName="simple-translation-details-popover"
      content={renderSimpleMetadataDetailsContent(label, option)}
    >
      {button}
    </Popover>
  );
};
const renderSimpleMetadataSummary = (label: string, option?: TranslationOption) => {
  if (!option) return null;
  const originalLanguageMeaning = option.backTranslation || option.sourceTrans || '';
  const showSelectedText = option.translatedText.length > 40;
  return (
    <div className="simple-translation-metadata-summary">
      <div className="simple-translation-summary-copy">
        {showSelectedText && (
          <Text type="secondary" className="simple-translation-summary-line" title={`Selected: ${option.translatedText}`}>
            <strong>Selected:</strong> {option.translatedText}
          </Text>
        )}
        {originalLanguageMeaning && (
          <Tooltip
            placement="bottomLeft"
            title={<span><strong>Meaning:</strong> {originalLanguageMeaning}</span>}
          >
            <Text
              type="secondary"
              className="simple-translation-summary-line simple-translation-summary-line--meaning"
              tabIndex={0}
              aria-label={`Meaning: ${originalLanguageMeaning}`}
            >
              <strong>Meaning:</strong> {originalLanguageMeaning}
            </Text>
          </Tooltip>
        )}
      </div>
      <div className="simple-translation-summary-actions" aria-label={`${label} review signals`}>
        {renderSimpleMetadataDetailsButton(label, option)}
      </div>
    </div>
  );
};
const renderSimpleAddonRow = ({
  id,
  title,
  description,
  checked,
  disabled,
  onToggle,
  action,
  info,
}: {
  id: string;
  title: string;
  description: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onToggle: (checked: boolean) => void;
  action?: React.ReactNode;
  info?: React.ReactNode;
}) => {
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;
  const toggleAddon = () => {
    if (!disabled) {
      onToggle(!checked);
    }
  };
  return (
    <div
      className={`simple-translation-addon-row${disabled ? ' simple-translation-addon-row--disabled' : ''}`}
      data-testid={`tutorial:simple-workflow:${id}`}
      aria-disabled={disabled || undefined}
      onClick={toggleAddon}
    >
      <div className="simple-translation-addon-copy">
        <div className="simple-translation-addon-title-row">
          <span id={titleId} className="simple-translation-addon-label">
            <Text strong>{title}</Text>
          </span>
          {info && (
            <Tooltip title={info}>
              <Button
                type="text"
                size="small"
                className="simple-translation-info-button"
                aria-label={`${title} details`}
                icon={<QuestionCircleOutlined />}
                onClick={(event) => event.stopPropagation()}
              />
            </Tooltip>
          )}
        </div>
        <span id={descriptionId} className="simple-translation-addon-label simple-translation-addon-description">
          <span className="simple-translation-addon-title">
            <Text type="secondary">{description}</Text>
          </span>
        </span>
      </div>
      <div className="simple-translation-addon-control" onClick={(event) => event.stopPropagation()}>
        {action}
        <Checkbox
          id={id}
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          checked={checked}
          disabled={disabled}
          onChange={(event) => onToggle(event.target.checked)}
        />
      </div>
    </div>
  );
};
const saveSimpleBlurbIfChanged = async (): Promise<boolean> => {
  const nextBlurb = expressBlurbSourceText;
  if (nextBlurb === String(book.blurb || '')) {
    return true;
  }
  const nextBlurbWordCount = getBlurbWordCount(nextBlurb);
  if (nextBlurbWordCount > MAX_BLURB_TRANSLATION_WORDS) {
    message.error(formatBlurbWordLimitMessage(nextBlurbWordCount), 8);
    return false;
  }
  if (!auth.currentUser) {
    message.error('Please sign in to save the blurb.');
    return false;
  }
  setSimpleBlurbSaving(true);
  try {
    await bookDetailApiPost(`${API_BASE}/update-book-blurb/`, {
      bookID: bookId,
      userID: auth.currentUser.uid,
      blurb: nextBlurb,
    });
    setBook(prev => ({ ...prev, blurb: nextBlurb }));
    setBlurb(nextBlurb);
    message.success('Blurb saved.');
    return true;
  } catch (error) {
    console.error('Error saving Simple mode blurb:', error);
    message.error('Unable to save the blurb.');
    return false;
  } finally {
    setSimpleBlurbSaving(false);
  }
};
const closeSimpleBlurbDrawer = async () => {
  const saved = await saveSimpleBlurbIfChanged();
  if (saved) {
    setSimpleBlurbDrawerOpen(false);
  }
};
const handleBookDetailExperienceChange = (mode: BookDetailExperienceMode) => {
  applyBookDetailExperienceMode(mode, { promptSeen: true });
};
const openProcessingStatusPanel = () => {
  setProcessingStatusOpenSignal(value => value + 1);
};
const copyTextToClipboard = async (
  text: string,
  successMessage = 'Copied.',
  errorMessage = 'Unable to copy.',
) => {
  if (!text) {
    return;
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    message.success(successMessage);
  } catch (error) {
    console.error('Error copying text:', error);
    message.error(errorMessage);
  }
};
const copySimpleTranslatedBlurb = async () => copyTextToClipboard(
  simpleTranslatedBlurb,
  'Blurb copied.',
  'Unable to copy blurb.',
);
const renderSimpleBookDetailMode = () => {
  const expressJobForSelectedTarget = expressJobMatchesResolvedTarget ? expressJob : null;
  const totalCredits = simpleSelectedTargetCompleted
    ? 0
    : expressJobForSelectedTarget?.totalCredits ?? expressEstimatedTotalCredits;
  const translationCredits = simpleSelectedTargetCompleted
    ? 0
    : expressJobForSelectedTarget?.translationCredits ?? expressEstimatedTranslationCredits;
  const glossaryCredits = simpleSelectedTargetCompleted
    ? 0
    : expressJobForSelectedTarget?.glossaryScanCredits ?? expressEstimatedGlossaryCredits;
  const blurbCredits = simpleSelectedTargetCompleted
    ? 0
    : expressJobForSelectedTarget?.blurbCredits ?? expressEstimatedBlurbCredits;
  const formatSimpleCredits = (value: number) => Math.max(0, Math.round(value)).toLocaleString();
  const simpleBlurbCostSelected = Boolean(
    expressBlurbEnabled
    || expressJobForSelectedTarget?.translateBlurb
    || Number(expressJobForSelectedTarget?.blurbCredits || 0) > 0
  );
  const simpleBlurbNoCharge = Boolean(
    expressResolvedTargetLanguage
    && (
      (simpleBlurbCostSelected && expressBlurbFreeRunAvailable)
      || Boolean(expressJobForSelectedTarget?.blurbFreeRunUsed)
    )
    && Number(blurbCredits || 0) === 0
  );
  const simpleShowBlurbCostRow = Boolean(
    !simpleSelectedTargetCompleted
    && expressResolvedTargetLanguage
    && (
      simpleBlurbCostSelected
      || expressBlurbFreeRunAvailable
      || Number(blurbCredits || 0) > 0
    )
  );
  const simpleCostBreakdownRows: ModalCostBreakdownRow[] = [
    {
      key: 'translation',
      label: 'Translation',
      amountCredits: translationCredits,
      displayValue: `${formatSimpleCredits(translationCredits)} credits`,
      explanation: expressResolvedTargetLanguage
        ? `Book translation to ${expressResolvedTargetLanguage}. Credits are based on untranslated chapter word count.`
        : 'Book translation.',
      tone: 'neutral',
    },
  ];
  if (expressGlossaryCharged) {
    simpleCostBreakdownRows.push({
      key: 'glossary-scan',
      label: 'AI Glossary Scan',
      amountCredits: glossaryCredits,
      displayValue: `+${formatSimpleCredits(glossaryCredits)} credits`,
      explanation: glossaryCredits === 0
        ? 'Reuses the existing glossary scan for no additional credits.'
        : 'Scans all source chapters before translation so glossary terms can be detected.',
      tone: 'warning',
    });
  } else if (!simpleSelectedTargetCompleted && expressResolvedTargetLanguage) {
    simpleCostBreakdownRows.push({
      key: 'glossary-scan',
      label: 'AI Glossary Scan',
      displayValue: 'Not selected',
      explanation: 'No glossary scan charge unless you turn this on.',
      tone: 'neutral',
    });
  }
  if (simpleShowBlurbCostRow) {
    simpleCostBreakdownRows.push({
      key: 'blurb-translation',
      label: 'Blurb translation',
      amountCredits: blurbCredits,
      displayValue: simpleBlurbCostSelected
        ? simpleBlurbNoCharge
          ? 'No charge'
          : `+${formatSimpleCredits(blurbCredits)} credits`
        : 'Not selected',
      explanation: simpleBlurbNoCharge
        ? 'The first blurb translation for each target language is free.'
        : expressBlurbFreeRunAvailable
          ? 'The first blurb translation for this language will be no charge if selected.'
          : 'Additional blurb translations for this language use credits based on blurb word count.',
      tone: simpleBlurbNoCharge || expressBlurbFreeRunAvailable ? 'success' : 'warning',
    });
  }
  if (!simpleSelectedTargetCompleted && expressResolvedTargetLanguage) {
    simpleCostBreakdownRows.push({
      key: 'email-download-link',
      label: 'Email download link',
      displayValue: expressEmailEnabled ? 'No charge' : 'Not selected',
      explanation: 'Email delivery never costs credits.',
      tone: 'success',
    });
  }
  const readinessText = expressStartDisabledReason || 'Ready to translate and export';
  const expressExportFailedForSelectedTarget = Boolean(
    expressJobForSelectedTarget && expressJobStatus === 'partial_success'
  );
  const simpleRetryExportPayload: ExportBookRequestPayload | null = expressResolvedTargetLanguage
    ? {
        version: `${String(expressJobForSelectedTarget?.sourceLanguage || bookLanguage || book.language || 'English')}-${expressResolvedTargetLanguage}`,
        format: isEpubUpload
          ? String(expressJobForSelectedTarget?.outputFormat || expressOutputFormat || 'ePub')
          : 'Word',
        includeImages: uploadType === 'Word' ? includeWordImages : true,
        writingOrientation: expressJobForSelectedTarget?.writingOrientation === 'vertical' ? 'vertical' : 'horizontal',
      }
    : null;
  const simpleCompletedStatusText = simpleSelectedTargetCompleted && expressResolvedTargetLanguage
    ? expressExportFailedForSelectedTarget && !simpleSelectedTargetLatestExport
      ? (expressStatusText || `${expressResolvedTargetLanguage} translation is complete, but export failed.`)
      : simpleSelectedTargetLatestExport
      ? simpleSelectedTargetLatestExport.isStale
        ? `${expressResolvedTargetLanguage} translation is complete, but the previous export is out of date.`
        : `${expressResolvedTargetLanguage} translation is complete. Latest export is ready to download.`
      : `${expressResolvedTargetLanguage} translation is complete. Choose a format to create an export.`
    : '';
  const simpleStatusText = simpleCompletedStatusText || (expressJobForSelectedTarget ? expressStatusText : '') || (
    (activeSimpleStatusForTarget || simpleTranslationInProgress)
      ? simpleTranslationInProgressMessage
      : ''
  );
  const showSimpleTopUpOffer =
    simpleHasEnoughCredits === false && simpleTranslationTopUpContext && simpleCreditShortfall != null;
  const simpleAddonsDisabled = simpleSelectedTargetReadOnly || !expressResolvedTargetLanguage;
  const showSimpleStatusActions = Boolean(simpleSelectedTargetLatestExport || simpleTranslationInProgress || simpleSelectedTargetCompleted);
  const statusType =
    expressExportFailedForSelectedTarget && !simpleSelectedTargetLatestExport
      ? 'warning'
      : simpleSelectedTargetCompleted
      ? 'success'
      : expressJobForSelectedTarget && expressJobStatus === 'failed'
        ? 'error'
        : expressJobForSelectedTarget && expressJobStatus === 'partial_success'
          ? 'warning'
          : 'info';
  const completedGlossaryIncluded = Boolean(
    expressJobForSelectedTarget?.glossaryScanEnabled
    || Number(expressJobForSelectedTarget?.glossaryScanCredits || 0) > 0
    || simpleGlossaryStatusForTarget
  );
  const completedGlossaryStatus = simpleGlossaryStatusForTarget
    ? resolveTranslationStatusState(simpleGlossaryStatusForTarget)
    : '';
  const getSimpleWorkLabel = (status: string, requested: boolean): string => {
    if (status === 'completed') return 'Completed';
    if (['running', 'retrying'].includes(status)) return 'In progress';
    if (['initializing', 'pending', 'queued'].includes(status)) return 'Queued';
    return requested ? 'Requested' : 'Not selected';
  };
  const completedGlossaryLabel = (() => {
    if (!completedGlossaryIncluded) return 'Not selected';
    if (simpleSelectedTargetCompleted || completedGlossaryStatus === 'completed') {
      return 'Automatic choices applied';
    }
    return getSimpleWorkLabel(completedGlossaryStatus, true);
  })();
  const simpleBlurbRequested = Boolean(
    simpleTranslatedBlurb
    || expressJobForSelectedTarget?.translateBlurb
    || Number(expressJobForSelectedTarget?.blurbCredits || 0) > 0
  );
  const simpleSourceBlurbAvailable = getBlurbWordCount(book.blurb || '') > 0;
  const simpleBlurbOutputLabel = (() => {
    if (simpleTranslatedBlurb) return 'Available';
    if (simpleSelectedTargetCompleted) {
      return simpleSourceBlurbAvailable ? 'Not translated' : 'No source blurb';
    }
    if (simpleBlurbRequested) return 'Requested';
    return simpleSourceBlurbAvailable ? 'Not selected' : 'No source blurb';
  })();
  const simpleBlurbTranslationCredits = (
    simpleSourceBlurbAvailable
    && expressResolvedTargetLanguage
    && metadataBlurbFreeRunConsumed(expressResolvedTargetLanguage)
  )
    ? blurbWordCount
    : 0;
  const simpleBlurbTranslationIncluded = Boolean(
    simpleSourceBlurbAvailable
    && expressResolvedTargetLanguage
    && !metadataBlurbFreeRunConsumed(expressResolvedTargetLanguage)
  );
  const simpleBlurbTranslationTooLong = simpleSourceBlurbAvailable && blurbWordCount > MAX_BLURB_TRANSLATION_WORDS;
  const simpleBlurbTranslationShortfall =
    typeof availableCredits === 'number'
      ? Math.max(simpleBlurbTranslationCredits - Math.floor(availableCredits), 0)
      : null;
  const simpleBlurbTranslationHasEnoughCredits =
    simpleBlurbTranslationCredits <= 0
      ? true
      : typeof simpleBlurbTranslationShortfall === 'number'
        ? simpleBlurbTranslationShortfall <= 0
        : null;
  const simpleBlurbTranslationTopUpContext: CreditTopUpContext | null =
    typeof availableCredits === 'number'
      ? {
          source: 'metadata_modal',
          requiredCredits: simpleBlurbTranslationCredits,
          availableCredits: Math.max(0, Math.floor(availableCredits)),
          shortfallCredits: Math.max(simpleBlurbTranslationCredits - Math.floor(availableCredits), 0),
        }
      : null;
  const simpleBlurbTranslationWillOverwrite = Boolean(
    expressResolvedTargetLanguage
    && metadataHasTranslation('blurb', bookLanguage, expressResolvedTargetLanguage)
  );
  const openSimpleBlurbTranslation = () => {
    if (!expressResolvedTargetLanguage) {
      message.error('Select a target language before translating the blurb.');
      return;
    }
    setSimpleBlurbTranslateModalOpen(true);
  };
  const closeSimpleBlurbTranslation = () => {
    if (simpleBlurbTranslating) {
      return;
    }
    setSimpleBlurbTranslateModalOpen(false);
  };
  const submitSimpleBlurbTranslation = async (forceOverwrite: boolean = false) => {
    const resolvedTargetLanguage = (expressResolvedTargetLanguage || '').trim();
    if (!resolvedTargetLanguage) {
      message.error('Select a target language before translating the blurb.', 8);
      return;
    }
    if (!simpleSourceBlurbAvailable) {
      message.error('Add and save a source blurb before translating it.', 8);
      return;
    }
    if (simpleBlurbTranslationTooLong) {
      message.error(formatBlurbWordLimitMessage(blurbWordCount), 8);
      return;
    }
    const userId = auth.currentUser?.uid;
    if (!userId) {
      message.error('Please sign in to translate the blurb.', 8);
      return;
    }
    setSimpleBlurbTranslating(true);
    try {
      if (simpleBlurbTranslationCredits > 0) {
        const refreshedBalance = await fetchCreditBalanceCached(userId, {
          force: true,
          silent: true,
        });
        const refreshedAvailableRaw =
          refreshedBalance?.available_balance ?? refreshedBalance?.credit_balance ?? availableCredits;
        const refreshedAvailable =
          typeof refreshedAvailableRaw === 'number' && Number.isFinite(refreshedAvailableRaw)
            ? Math.max(0, Math.floor(refreshedAvailableRaw))
            : null;
        if (refreshedAvailable == null) {
          message.error('Unable to verify your credit balance. Please try again.', 8);
          return;
        }
        if (refreshedAvailable < simpleBlurbTranslationCredits) {
          openTopUpFromBook({
            source: 'metadata_modal',
            requiredCredits: simpleBlurbTranslationCredits,
            availableCredits: refreshedAvailable,
            shortfallCredits: Math.max(simpleBlurbTranslationCredits - refreshedAvailable, 0),
          });
          return;
        }
      }
      const response = await bookDetailApiPost(`${API_BASE}/translate-blurb/`, {
        userId,
        bookId,
        sourceLang: bookLanguage,
        targetLang: resolvedTargetLanguage,
        model: selectedMetadataModel,
        genre: genres.join(', '),
        intendedAudience,
        quotationStyle,
        tone: resolvedTargetLanguage === 'French' ? frenchTone : '',
        overwriteFlag: forceOverwrite || simpleBlurbTranslationWillOverwrite,
      });
      if (response.data?.status === 'success') {
        const languagePairKey = `${bookLanguage}-${resolvedTargetLanguage}`;
        setBlurbTranslations(prevTranslations => ({
          ...prevTranslations,
          [languagePairKey]: response.data.translated_blurb,
        }));
        message.success('Blurb translated successfully');
        setSimpleBlurbTranslateModalOpen(false);
        fetchBookDetails({ silent: true });
        return;
      }
      message.error(`Blurb translation failed: ${response.data?.message || 'Unknown error'}`, 10);
    } catch (error) {
      const detail = getMetadataOverwriteDetail(error);
      const detailMessage =
        typeof detail === 'string'
          ? detail
          : detail?.message || (error instanceof Error ? error.message : '');
      if (!forceOverwrite && isMetadataOverwriteRequiredError(error)) {
        Modal.confirm({
          rootClassName: 'theme-aware-confirm',
          title: `Overwrite ${resolvedTargetLanguage} blurb?`,
          content: detailMessage || `A ${resolvedTargetLanguage} blurb already exists and will be replaced.`,
          okText: 'Overwrite and Translate',
          cancelText: 'Cancel',
          onOk: () => {
            void submitSimpleBlurbTranslation(true);
          },
        });
        return;
      }
      console.error('Error translating blurb from Simple mode:', error);
      message.error(detailMessage || 'Blurb translation failed. Please try again.', 10);
    } finally {
      setSimpleBlurbTranslating(false);
    }
  };
  const simpleBlurbOutputAction = simpleTranslatedBlurb ? (
    <Button
      type="link"
      size="small"
      className="simple-translation-inline-action"
      onClick={() => setSimpleTranslatedBlurbVisible(true)}
      data-testid="tutorial:simple-workflow:translated-blurb-open"
    >
      View
    </Button>
  ) : simpleSelectedTargetCompleted && simpleSourceBlurbAvailable ? (
    <Button
      type="link"
      size="small"
      className="simple-translation-inline-action"
      onClick={openSimpleBlurbTranslation}
    >
      Translate blurb
    </Button>
  ) : null;
  const simpleBlurbTranslationCostLabel = simpleBlurbTranslationIncluded
    ? 'Included: first blurb translation for this language.'
    : `${simpleBlurbTranslationCredits.toLocaleString()} credits`;
  const simpleBlurbTranslateDisabled = Boolean(
    !expressResolvedTargetLanguage
    || !simpleSourceBlurbAvailable
    || simpleBlurbTranslationTooLong
    || simpleBlurbTranslationHasEnoughCredits === false
    || simpleBlurbTranslating
  );
  const simpleTutorialEmail = isSandboxFixtureMode
    ? String(sandboxFixturePayload?.simpleWorkflow?.email || sandboxFixturePayload?.expressJob?.email || '').trim()
    : '';
  const completedEmailStatus = String(expressJobForSelectedTarget?.emailStatus || '').toLowerCase();
  const completedEmailRequested = Boolean(expressJobForSelectedTarget?.emailDownloadLink || completedEmailStatus);
  const completedEmailLabel = (() => {
    if (['sent', 'delivered', 'completed', 'success'].includes(completedEmailStatus)) return 'Sent';
    if (['failed', 'error'].includes(completedEmailStatus)) return 'Failed';
    if (completedEmailStatus === 'sending') return 'Sending';
    if (completedEmailRequested && completedEmailStatus !== 'skipped') return 'Requested';
    return 'No email requested';
  })();
  const canResendSimpleExportEmail = Boolean(
    simpleSelectedTargetCompleted
    && expressJobForSelectedTarget?.id
    && !expressExportFailedForSelectedTarget
    && (auth.currentUser?.email || expressJobForSelectedTarget?.email)
    && (simpleSelectedTargetLatestExport || expressJobForSelectedTarget?.downloadUrl || expressJobForSelectedTarget?.exportManifestId)
  );
  const simpleExportEmailActionLabel = ['sent', 'delivered', 'completed', 'success'].includes(completedEmailStatus)
    ? 'Resend email'
    : 'Send email';
  const simpleReadOnlyLocalizedTitleText = simpleSelectedTargetCompleted
    ? simpleLocalizedTitleText
    : simpleInProgressLocalizedTitleText;
  const simpleReadOnlyLocalizedSeriesText = simpleSelectedTargetCompleted
    ? simpleLocalizedSeriesText
    : simpleInProgressLocalizedSeriesText;
  const simpleShowReadOnlyLocalizedMetadata = Boolean(
    (simpleSelectedTargetCompleted || simpleTranslationInProgress)
    && (simpleReadOnlyLocalizedTitleText || simpleReadOnlyLocalizedSeriesText)
  );
  const renderSimpleCompletedOutputRow = (
    title: string,
    status: string,
    action?: React.ReactNode,
  ) => (
    <div className="simple-translation-output-row">
      <div>
        <Text strong>{title}</Text>
        <Text type="secondary">{status}</Text>
      </div>
      {action && <div className="simple-translation-output-action">{action}</div>}
    </div>
  );
  return (
    <div className="simple-translation-workspace" data-testid="tutorial:simple-workflow:workspace">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card
          className="simple-translation-panel"
          data-tour-id="simple-translation-panel"
          data-testid="tutorial:simple-workflow:panel"
          title="Translate and export"
          extra={simpleTranslationInProgress ? <Tag color="processing">Running</Tag> : null}
          bodyStyle={{ padding: 0 }}
        >
          <div className="simple-translation-panel-body">
            <div className="simple-translation-book-summary simple-translation-book-summary--inline" data-testid="tutorial:simple-workflow:book-summary">
              {book.coverUrl ? (
                <img
                  src={book.coverUrl}
                  alt=""
                  className="simple-translation-cover-thumb"
                  style={{
                    objectFit: 'cover',
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                />
              ) : (
                <div
                  className="simple-translation-cover-thumb simple-translation-cover-thumb--placeholder"
                  style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <span className="simple-translation-cover-placeholder-mark">{simpleCoverInitials || 'BK'}</span>
                  <span className="simple-translation-cover-placeholder-label">
                    <BookOutlined />
                    No cover
                  </span>
                </div>
              )}
              <div className="simple-translation-book-copy">
                <div className="simple-translation-book-heading-row">
                  <Typography.Title level={3} style={{ margin: 0 }}>{book.title || 'Untitled book'}</Typography.Title>
                  <Button
                    type="link"
                    size="small"
                    icon={<FormOutlined />}
                    className="simple-translation-edit-details-action"
                    onClick={openSimpleEditDetails}
                  >
                    Edit details
                  </Button>
                </div>
                <Text type="secondary">
                  {[book.author, bookLanguage, `${book.chapters?.length || 0} chapters`].filter(Boolean).join(' · ')}
                </Text>
                {simpleBookContextStatus && (
                  <div className="simple-translation-book-context-row">
                    <Tag color={simpleBookContextStatus.tone}>{simpleBookContextStatus.text}</Tag>
                    {metadataAutofillStatus === 'failed' ? (
                      <Button
                        type="link"
                        size="small"
                        className="simple-translation-inline-action"
                        loading={metadataAutofillInFlightRef.current}
                        onClick={() => void runMetadataAutofill({
                          mode: 'new_upload',
                          applyMode: 'empty_fields',
                          forceRegenerate: true,
                        })}
                      >
                        Retry
                      </Button>
                    ) : metadataAutofillStatus === 'completed' ? (
                      <Button
                        type="link"
                        size="small"
                        className="simple-translation-inline-action"
                        data-testid="tutorial:simple-workflow:ai-metadata-review"
                        onClick={() => setSimpleMetadataAutofillReviewVisible(true)}
                      >
                        Review
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
            {simpleStatusText && (
              <Alert
                data-testid="tutorial:simple-workflow:status-alert"
                type={statusType}
                showIcon
                message={simpleStatusText}
                action={showSimpleStatusActions ? (
                  <Space size="small" wrap className={(simpleSelectedTargetCompleted || simpleTranslationInProgress) ? 'simple-translation-status-actions' : undefined}>
                    {simpleSelectedTargetLatestExport && !simpleSelectedTargetLatestExport.isStale && (
                      <Button
                        type={simpleSelectedTargetCompleted ? 'primary' : 'link'}
                        size="small"
                        className={simpleSelectedTargetCompleted ? undefined : 'simple-translation-inline-action'}
                        icon={simpleSelectedTargetCompleted ? <DownloadOutlined /> : undefined}
                        data-testid="tutorial:simple-workflow:download-export"
                        onClick={() => void downloadRecentExport(simpleSelectedTargetLatestExport)}
                      >
                        Download export
                      </Button>
                    )}
                    {expressExportFailedForSelectedTarget && !simpleSelectedTargetLatestExport && simpleRetryExportPayload && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={isLoading}
                        onClick={() => void handleExport(simpleRetryExportPayload)}
                      >
                        Retry export
                      </Button>
                    )}
                    {simpleSelectedTargetCompleted
                      && !expressExportFailedForSelectedTarget
                      && !simpleTranslationInProgress && (
                      <Button
                        type={simpleSelectedTargetLatestExport && !simpleSelectedTargetLatestExport.isStale ? 'default' : 'primary'}
                        size="small"
                        icon={<ExportOutlined />}
                        disabled={simpleExportSubmitting}
                        onClick={openSimpleExportFormatModal}
                      >
                        Create export
                      </Button>
                    )}
                    {simpleTranslationInProgress && (
                      <Button
                        type="primary"
                        size="small"
                        data-testid="tutorial:simple-workflow:view-processing-status"
                        onClick={openProcessingStatusPanel}
                      >
                        View in Processing Status
                      </Button>
                    )}
                  </Space>
                ) : null}
              />
            )}
            <div className="simple-translation-workflow-grid">
              <div className="simple-translation-main-stack">
                <section className="simple-translation-section" data-tour-id="simple-translation-basics" data-testid="tutorial:simple-workflow:basics">
                  {renderSimpleSectionHeader('Basics', 'Choose where the book is going and the file format.', {
                    label: simpleSelectedTargetCompleted ? 'Complete' : expressResolvedTargetLanguage ? 'Ready' : 'Choose target',
                    color: expressResolvedTargetLanguage ? 'success' : 'warning',
                  })}
                  <div className="simple-translation-grid">
                    <div className="simple-translation-field">
                      <label>Target language</label>
                      <Select
                        data-testid="tutorial:simple-workflow:target-language"
                        value={expressResolvedTargetLanguage || undefined}
                        onChange={setExpressTargetLanguage}
                        style={{ width: '100%' }}
                        placeholder="Choose language"
                        showSearch
                        optionFilterProp="children"
                        disabled={expressSubmitting}
                      >
                        {simpleCandidateTargetLanguageOptions.map(language => {
                          const completed = simpleCompletedTargetLanguageSet.has(language);
                          return (
                            <Select.Option key={language} value={language}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span>{language}</span>
                                {completed && (
                                  <Tooltip title="All chapters are already translated in this book.">
                                    <Tag
                                      color="green"
                                      style={{
                                        marginInlineStart: 0,
                                        marginInlineEnd: 0,
                                        paddingInline: 4,
                                        height: 16,
                                        lineHeight: '14px',
                                        fontSize: 10,
                                        borderRadius: 4,
                                      }}
                                    >
                                      Completed
                                    </Tag>
                                  </Tooltip>
                                )}
                              </span>
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </div>
                    <div className="simple-translation-field">
                      <label>Output format</label>
                      <Select
                        data-testid="tutorial:simple-workflow:output-format"
                        value={expressOutputFormat}
                        onChange={(value) => setExpressOutputFormat(value)}
                        style={{ width: '100%' }}
                        disabled={simpleTranslationInProgress}
                      >
                        <Select.Option value="Word">Word</Select.Option>
                        {isEpubUpload && <Select.Option value="ePub">ePub</Select.Option>}
                      </Select>
                    </div>
                  </div>
                </section>
                {simpleShowLocalizedMetadataSection && (
                  <section className="simple-translation-section" data-tour-id="simple-translation-metadata" data-testid="tutorial:simple-workflow:metadata">
                    {renderSimpleSectionHeader(
                      'Localized metadata',
                      'Review the title and series names that will be used in the export.',
                      simpleShowReadOnlyLocalizedMetadata && simpleTranslationInProgress
                        ? { label: 'Selected', color: 'processing' }
                        : simpleMetadataStatus,
                    )}
                    {simpleShowReadOnlyLocalizedMetadata ? (
                      <div className="simple-translation-output-list">
                        {simpleReadOnlyLocalizedTitleText && renderSimpleCompletedOutputRow(
                          'Title for export',
                          simpleReadOnlyLocalizedTitleText,
                          <Tooltip title="Copy localized title">
                            <Button
                              type="text"
                              size="small"
                              aria-label="Copy localized title"
                              icon={<CopyOutlined />}
                              onClick={() => void copyTextToClipboard(simpleReadOnlyLocalizedTitleText, 'Localized title copied.')}
                            />
                          </Tooltip>,
                        )}
                        {simpleReadOnlyLocalizedSeriesText && renderSimpleCompletedOutputRow(
                          'Series name for export',
                          simpleReadOnlyLocalizedSeriesText,
                          <Tooltip title="Copy localized series name">
                            <Button
                              type="text"
                              size="small"
                              aria-label="Copy localized series name"
                              icon={<CopyOutlined />}
                              onClick={() => void copyTextToClipboard(simpleReadOnlyLocalizedSeriesText, 'Localized series name copied.')}
                            />
                          </Tooltip>,
                        )}
                      </div>
                    ) : simpleMetadataLoading && (
                      <Alert
                        type="info"
                        showIcon
                        icon={<LoadingOutlined spin />}
                        className="simple-translation-loading-alert"
                        message="Preparing localized title options…"
                      />
                    )}
                    {!simpleShowReadOnlyLocalizedMetadata && !simpleSelectedTargetCompleted && simpleMetadataError && !simpleMetadataLoading && (
                      <Alert
                        type="warning"
                        showIcon
                        message={simpleMetadataError}
                        action={
                          <Button size="small" onClick={() => setSimpleMetadataRetryKey(value => value + 1)}>
                            Retry
                          </Button>
                        }
                      />
                    )}
                    {!simpleShowReadOnlyLocalizedMetadata && !simpleSelectedTargetCompleted && (simpleTitleOptions.length > 0 || simpleSeriesOptions.length > 0) && (
                      <div className="simple-translation-grid">
                        {simpleTitleOptions.length > 0 && (
                          <div className="simple-translation-field">
                            <label>Localized title</label>
                            <Select
                              data-testid="tutorial:simple-workflow:localized-title"
                              value={simpleSelectedTitleOptionId}
                              onChange={setSimpleSelectedTitleOptionId}
                              style={{ width: '100%' }}
                              disabled={simpleSelectedTargetReadOnly || simpleMetadataLoading}
                              optionLabelProp="label"
                              aria-label={`Localized title: ${selectedSimpleTitleOption?.translatedText || book.title || 'Untitled book'}`}
                            >
                              <Select.Option
                                value="__original__"
                                label={renderSimpleSelectedMetadataLabel(book.title || 'Untitled book')}
                              >
                                Keep original title: {book.title || 'Untitled book'}
                              </Select.Option>
                              {simpleTitleOptions.map(option => (
                                <Select.Option
                                  key={option.id || option.translatedText}
                                  value={option.id || option.translatedText}
                                  label={renderSimpleSelectedMetadataLabel(option.translatedText)}
                                >
                                  {renderSimpleMetadataOption(option)}
                                </Select.Option>
                              ))}
                            </Select>
                            {renderSimpleMetadataSummary('Localized title', selectedSimpleTitleOption)}
                          </div>
                        )}
                        {simpleSeriesOptions.length > 0 && (
                          <div className="simple-translation-field">
                            <label>Localized series name</label>
                            <Select
                              data-testid="tutorial:simple-workflow:localized-series"
                              value={simpleSelectedSeriesOptionId}
                              onChange={setSimpleSelectedSeriesOptionId}
                              style={{ width: '100%' }}
                              disabled={simpleSelectedTargetReadOnly || simpleMetadataLoading}
                              optionLabelProp="label"
                              aria-label={`Localized series name: ${selectedSimpleSeriesOption?.translatedText || book.series || ''}`}
                            >
                              <Select.Option
                                value="__original__"
                                label={renderSimpleSelectedMetadataLabel(book.series || '')}
                              >
                                Keep original series name: {book.series}
                              </Select.Option>
                              {simpleSeriesOptions.map(option => (
                                <Select.Option
                                  key={option.id || option.translatedText}
                                  value={option.id || option.translatedText}
                                  label={renderSimpleSelectedMetadataLabel(option.translatedText)}
                                >
                                  {renderSimpleMetadataOption(option)}
                                </Select.Option>
                              ))}
                            </Select>
                            {renderSimpleMetadataSummary('Localized series name', selectedSimpleSeriesOption)}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}
              </div>
              <section className="simple-translation-section simple-translation-section--addons" data-tour-id="simple-translation-addons" data-testid="tutorial:simple-workflow:add-ons">
                {renderSimpleSectionHeader(
                  'Add-ons',
                  simpleSelectedTargetCompleted
                    ? 'Review what was included with this translation.'
                    : simpleTranslationInProgress
                      ? 'Track what is running for this translation.'
                      : 'Choose optional work that should run before the export.',
                )}
                {(simpleSelectedTargetCompleted || simpleTranslationInProgress) ? (
                  <div className="simple-translation-output-list" data-testid="tutorial:simple-workflow:completed-output">
                    {renderSimpleCompletedOutputRow(
                      'AI Glossary Scan',
                      completedGlossaryLabel,
                    )}
                    {renderSimpleCompletedOutputRow(
                      'Translated blurb',
                      simpleBlurbOutputLabel,
                      simpleBlurbOutputAction,
                    )}
                    {renderSimpleCompletedOutputRow(
                      'Email download link',
                      completedEmailLabel,
                      canResendSimpleExportEmail ? (
                        <Button
                          type="link"
                          size="small"
                          className="simple-translation-inline-action"
                          loading={expressEmailResending}
                          data-testid="tutorial:simple-workflow:resend-email"
                          onClick={() => void resendExpressExportEmail()}
                        >
                          {simpleExportEmailActionLabel}
                        </Button>
                      ) : null,
                    )}
                  </div>
                ) : (
                  <div className="simple-translation-addon-list">
                    {renderSimpleAddonRow({
                      id: 'simple-addon-glossary-scan',
                      title: 'AI Glossary Scan',
                      description: expressGlossaryEnabled
                        ? expressGlossaryBlocker || (expressGlossaryEstimate?.reuseStatus === 'completed' && !expressForceGlossaryRescan
                          ? 'Reuse the completed scan for 0 additional credits; automatic choices will be used.'
                          : expressGlossaryEstimate?.reuseStatus === 'active'
                            ? 'Continue the active scan for 0 additional credits.'
                            : `${expressEstimatedGlossaryCredits.toLocaleString()} credits added for a scan of all source chapters.`)
                        : 'Optional terminology pass with automatic choices for Simple mode.',
                      checked: expressGlossaryEnabled,
                      disabled: simpleAddonsDisabled,
                      onToggle: setExpressGlossaryEnabled,
                      action: expressGlossaryEnabled && expressGlossaryQuoteError ? (
                        <Button size="small" onClick={() => setExpressGlossaryQuoteRetry(value => value + 1)}>Retry estimate</Button>
                      ) : null,
                      info: 'Runs before chapter translation and automatically applies high-confidence terminology choices for this Simple translation. Use Advanced before translating if you want to review or edit glossary choices manually.',
                    })}
                    {expressGlossaryEnabled && expressGlossaryEstimate
                      && expressGlossaryEstimate.reuseStatus !== 'active'
                      && (expressGlossaryEstimate.reuseStatus === 'completed' || expressGlossaryEstimate.requiresRescanConfirmation) && (
                      <Checkbox checked={expressForceGlossaryRescan} disabled={simpleAddonsDisabled || expressSubmitting}
                        onChange={event => setExpressForceGlossaryRescan(event.target.checked)}>
                        I confirm a new glossary scan of all chapters for {expressGlossaryEstimate.freshCreditCost?.toLocaleString() ?? 'unknown'} credits.
                      </Checkbox>
                    )}
                    {renderSimpleAddonRow({
                      id: 'simple-addon-translate-blurb',
                      title: 'Translate blurb',
                      description: expressBlurbEnabled
                        ? expressBlurbFreeRunAvailable
                          ? 'Included: first blurb translation for this language.'
                          : `${expressBlurbWordCount.toLocaleString()} credits for blurb translation.`
                        : 'Optional storefront blurb translation.',
                      checked: expressBlurbEnabled,
                      disabled: simpleAddonsDisabled,
                      onToggle: setExpressBlurbEnabled,
                      action: expressBlurbEnabled ? (
                        <Button
                          size="small"
                          data-testid="tutorial:simple-workflow:edit-blurb-open"
                          onClick={() => setSimpleBlurbDrawerOpen(true)}
                        >
                          Edit blurb
                        </Button>
                      ) : null,
                      info: `Translates the storefront blurb using the editable source text saved on this book. Blurbs are capped at ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words.`,
                    })}
                    {renderSimpleAddonRow({
                      id: 'simple-addon-email-download-link',
                      title: 'Email download link',
                      description: auth.currentUser?.email || simpleTutorialEmail
                        ? `Send the export link to ${auth.currentUser?.email || simpleTutorialEmail}.`
                        : 'Sign in with an email to enable this.',
                      checked: expressEmailEnabled,
                      disabled: (!auth.currentUser?.email && !simpleTutorialEmail) || simpleAddonsDisabled,
                      onToggle: setExpressEmailEnabled,
                      info: 'Sends a download link after export completes. Email delivery failure will not fail the translation or export.',
                    })}
                  </div>
                )}
              </section>
            </div>
            {showSimpleTopUpOffer && (
              <InsufficientCreditsAlert
                message="You do not have enough credits for this Simple translation."
                shortfallCredits={simpleCreditShortfall}
                onTopUpCredits={() => openTopUpFromBook(simpleTranslationTopUpContext)}
                showIcon={false}
                className="simple-translation-credit-alert"
              />
            )}
          </div>
          {(simpleSelectedTargetCompleted || simpleTranslationInProgress) ? (
            <div className="simple-translation-footer simple-translation-footer--complete" data-tour-id="simple-translation-footer" data-testid="tutorial:simple-workflow:footer">
              <div className="simple-translation-readiness">
                <Text type={simpleSelectedTargetCompleted ? 'success' : 'warning'}>{readinessText}</Text>
              </div>
            </div>
          ) : (
            <div className="simple-translation-footer" data-tour-id="simple-translation-footer" data-testid="tutorial:simple-workflow:footer">
              <div className="simple-translation-readiness">
                <Text type={expressStartDisabledReason ? 'warning' : 'success'}>{readinessText}</Text>
                <Button
                  type="link"
                  size="small"
                  className="simple-translation-inline-action"
                  aria-label="View cost breakdown"
                  data-testid="tutorial:simple-workflow:cost-breakdown-open"
                  onClick={() => setSimpleCostBreakdownOpen(true)}
                >
                  View cost breakdown
                </Button>
              </div>
              <div className="simple-translation-footer-actions">
                <div className="simple-translation-total">
                  <span>Total</span>
                  <strong>{totalCredits.toLocaleString()} credits</strong>
                  <small>
                    {typeof availableCredits === 'number'
                      ? `${Math.floor(availableCredits).toLocaleString()} available`
                      : 'Checking credits...'}
                  </small>
                </div>
                <Button
                  type="primary"
                  className="app-primary-cta"
                  icon={<TranslationOutlined />}
                  onClick={startExpressTranslation}
                  loading={expressSubmitting}
                  disabled={Boolean(expressStartDisabledReason)}
                  title={expressStartDisabledReason}
                  data-tour-id="simple-translation-primary-action"
                  data-testid="tutorial:simple-workflow:start"
                >
                  {expressJobForSelectedTarget?.id ? 'Start another translation' : 'Translate and export'}
                </Button>
                {(simpleSelectedTargetLatestExport || expressJobForSelectedTarget?.downloadUrl) && (
                  <Button
                    icon={<DownloadOutlined />}
                    data-testid="tutorial:simple-workflow:download-export"
                    onClick={() => {
                      if (simpleSelectedTargetLatestExport) {
                        void downloadRecentExport(simpleSelectedTargetLatestExport);
                        return;
                      }
                      if (expressJobForSelectedTarget?.downloadUrl) {
                        const fallbackEntry: RecentExportEntry = {
                          exportId: String(expressJobForSelectedTarget.exportManifestId || ''),
                          downloadUrl: expressJobForSelectedTarget.downloadUrl,
                          downloadFilename: expressJobForSelectedTarget.downloadFilename,
                        };
                        if (fallbackEntry.exportId) {
                          void downloadRecentExport(fallbackEntry);
                        } else {
                          triggerDownloadUrl(expressJobForSelectedTarget.downloadUrl);
                        }
                      }
                    }}
                  >
                    Download export
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>

        <Modal
          title="Cost breakdown"
          visible={simpleCostBreakdownOpen}
          onCancel={() => setSimpleCostBreakdownOpen(false)}
          width={560}
          footer={[
            <Button
              key="close-simple-cost-breakdown"
              data-testid="tutorial:simple-workflow:cost-breakdown-close"
              onClick={() => setSimpleCostBreakdownOpen(false)}
            >
              Close
            </Button>,
          ]}
        >
          <div data-testid="tutorial:simple-workflow:cost-breakdown">
            <ModalCostBreakdown
              rows={simpleCostBreakdownRows}
              totalCredits={totalCredits}
              availableCredits={availableCredits}
              emptyMessage="Choose a target language to view cost details."
              formatCredits={formatSimpleCredits}
            />
          </div>
        </Modal>

        <Modal
          title={`Translate blurb${expressResolvedTargetLanguage ? ` to ${expressResolvedTargetLanguage}` : ''}`}
          visible={simpleBlurbTranslateModalOpen}
          onCancel={closeSimpleBlurbTranslation}
          width={560}
          footer={[
            <Button
              key="cancel-simple-blurb-translation"
              onClick={closeSimpleBlurbTranslation}
              disabled={simpleBlurbTranslating}
            >
              Cancel
            </Button>,
            <Button
              key="submit-simple-blurb-translation"
              type="primary"
              className="app-primary-cta"
              loading={simpleBlurbTranslating}
              disabled={simpleBlurbTranslateDisabled}
              onClick={() => void submitSimpleBlurbTranslation()}
            >
              Translate blurb
            </Button>,
          ]}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="info"
              showIcon
              message="This translates only the book blurb."
              description="Title and series name will not be changed."
            />
            <div className="translation-confirm-cost-panel" aria-label="Simple blurb translation details">
              <div>
                <span>Target language</span>
                <strong>{expressResolvedTargetLanguage || 'Choose a target language'}</strong>
              </div>
              <div>
                <span>Source blurb length</span>
                <strong>
                  {blurbWordCount.toLocaleString()} / {MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words
                </strong>
              </div>
              <div>
                <span>Credit cost</span>
                <strong>{simpleBlurbTranslationCostLabel}</strong>
              </div>
              <div>
                <span>Current credit balance</span>
                <strong>
                  {typeof availableCredits === 'number'
                    ? `${Math.floor(availableCredits).toLocaleString()} credits`
                    : 'Checking...'}
                </strong>
              </div>
            </div>
            {!simpleSourceBlurbAvailable && (
              <Alert
                type="error"
                showIcon
                message="Add and save a source blurb before translating it."
              />
            )}
            {simpleBlurbTranslationTooLong && (
              <Alert
                type="error"
                showIcon
                message={`Blurb must be ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words or fewer.`}
                description={`Current length: ${blurbWordCount.toLocaleString()} words.`}
              />
            )}
            {simpleBlurbTranslationWillOverwrite && (
              <Alert
                type="warning"
                showIcon
                message={`A ${expressResolvedTargetLanguage} blurb already exists and will be replaced.`}
              />
            )}
            {simpleBlurbTranslationHasEnoughCredits === false && simpleBlurbTranslationTopUpContext && (
              <InsufficientCreditsAlert
                message="You do not have enough credits to translate this blurb."
                shortfallCredits={simpleBlurbTranslationTopUpContext.shortfallCredits}
                onTopUpCredits={() => openTopUpFromBook(simpleBlurbTranslationTopUpContext)}
                showIcon={false}
              />
            )}
          </Space>
        </Modal>

        {showExpressFailedRetryAlert && (
          <Alert
            type="warning"
            showIcon
            message="One or more chapters need attention."
            description={
              <Space direction="vertical" size="small">
                {expressFailedChapterCount > 0 && (
                  <span>
                    {expressFailedChapterCount === 1
                      ? '1 chapter failed during translation.'
                      : `${expressFailedChapterCount} chapters failed during translation.`}
                  </span>
                )}
                {expressSupportCodes.length > 0 && <span>{expressSupportCodes.join(', ')}</span>}
                <Space wrap>
                  <Button
                    size="small"
                    onClick={retryFailedExpressTranslation}
                    disabled={expressRetrying}
                    loading={expressRetrying}
                  >
                    Retry failed chapters
                  </Button>
                  <Button size="small" onClick={() => handleBookDetailExperienceChange('advanced')}>
                    Open All Tools
                  </Button>
                </Space>
              </Space>
            }
          />
        )}
        {showExpressProvisionalFailures && (
          <Alert
            type="info"
            showIcon
            message="Some chapters need attention while translation continues."
            description={`${expressFailedChapterCount} ${expressFailedChapterCount === 1 ? 'chapter has' : 'chapters have'} failed so far. Retry will be available after all expected chapters finish processing.`}
          />
        )}
        <Drawer
          title={simpleMetadataDetail?.label || 'Metadata details'}
          placement="bottom"
          open={Boolean(simpleMetadataDetail && isMobile)}
          onClose={() => setSimpleMetadataDetail(null)}
          height="auto"
          contentWrapperStyle={{ maxHeight: '75vh' }}
          className="simple-translation-drawer"
        >
          {renderSimpleMetadataDetailsContent(
            simpleMetadataDetail?.label || 'Metadata details',
            simpleMetadataDetail?.option,
          )}
        </Drawer>
        <MetadataAutofillReviewModal
          open={simpleMetadataAutofillReviewVisible}
          loading={metadataAutofillInFlightRef.current}
          run={book.metadataAutofill}
          onStartTour={metadataAutofillReviewTour.start}
          onClose={() => {
            setSimpleMetadataAutofillReviewVisible(false);
            if (metadataAutofillReviewTour.open) {
              void metadataAutofillReviewTour.close();
            }
          }}
          onApply={(fields: MetadataAutofillFieldKey[], values: Record<string, string[]>) =>
            runMetadataAutofill({
              mode: 'existing_suggestions',
              applyMode: 'suggestions_only',
              applyFields: fields,
              applyValues: values,
            })
          }
        />
        <AppTour
          open={simpleMetadataAutofillReviewVisible && metadataAutofillReviewTour.open}
          steps={metadataAutofillReviewTourSteps}
          current={metadataAutofillReviewTour.current}
          onChange={metadataAutofillReviewTour.setCurrent}
          onClose={metadataAutofillReviewTour.close}
          onFinish={metadataAutofillReviewTour.close}
          zIndex={2600}
        />
        <Modal
          title="Edit book details"
          open={simpleEditDetailsVisible}
          onCancel={() => setSimpleEditDetailsVisible(false)}
          width={isMobile ? 'calc(100vw - 24px)' : 680}
          className="simple-edit-details-modal"
          closable={!simpleEditDetailsSaving}
          maskClosable={!simpleEditDetailsSaving}
          footer={[
            <Button
              key="cancel"
              disabled={simpleEditDetailsSaving}
              onClick={() => setSimpleEditDetailsVisible(false)}
            >
              Cancel
            </Button>,
            <Button
              key="save"
              type="primary"
              icon={<SaveOutlined />}
              loading={simpleEditDetailsSaving}
              onClick={() => void saveSimpleEditDetails()}
            >
              Save details
            </Button>,
          ]}
        >
          <div className="simple-edit-details-layout">
            <section className="simple-edit-details-fields" aria-label="Basic book information">
              <label>
                <span>Title</span>
                <Input
                  value={simpleEditDetailsDraft.title}
                  onChange={(event) => setSimpleEditDetailsDraft(previous => ({ ...previous, title: event.target.value }))}
                  maxLength={300}
                  disabled={simpleEditDetailsSaving}
                />
              </label>
              <label>
                <span>Author</span>
                <Input
                  value={simpleEditDetailsDraft.author}
                  onChange={(event) => setSimpleEditDetailsDraft(previous => ({ ...previous, author: event.target.value }))}
                  maxLength={200}
                  disabled={simpleEditDetailsSaving}
                />
              </label>
              <div className="simple-edit-details-series-row">
                <label>
                  <span>Series</span>
                  <Input
                    value={simpleEditDetailsDraft.series}
                    onChange={(event) => setSimpleEditDetailsDraft(previous => ({ ...previous, series: event.target.value }))}
                    maxLength={300}
                    disabled={simpleEditDetailsSaving}
                  />
                </label>
                <label>
                  <span>Book number</span>
                  <Input
                    type="number"
                    min={0}
                    value={simpleEditDetailsDraft.booknumber}
                    onChange={(event) => setSimpleEditDetailsDraft(previous => ({ ...previous, booknumber: event.target.value }))}
                    disabled={simpleEditDetailsSaving}
                  />
                </label>
              </div>
            </section>
            <section className="simple-edit-details-cover" aria-label="Source book cover">
              <div className="simple-edit-details-cover-preview">
                {book.coverUrl ? (
                  <img src={book.coverUrl} alt={`Cover of ${book.title || 'book'}`} />
                ) : (
                  <div className="simple-edit-details-cover-placeholder">
                    <BookOutlined />
                    <span>No cover</span>
                  </div>
                )}
              </div>
              <div className="simple-edit-details-cover-actions">
                <input
                  ref={simpleCoverInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => void handleCoverUpload(event, { forceOriginal: true })}
                />
                <Button
                  icon={<FileImageOutlined />}
                  disabled={simpleEditDetailsSaving}
                  onClick={() => simpleCoverInputRef.current?.click()}
                >
                  {book.coverUrl ? 'Replace cover' : 'Upload cover'}
                </Button>
                {book.coverUrl ? (
                  <Button danger disabled={simpleEditDetailsSaving} onClick={confirmSimpleCoverRemoval}>
                    Remove cover
                  </Button>
                ) : null}
              </div>
              <Text type="secondary">JPG, PNG, or WebP up to 5 MB.</Text>
            </section>
          </div>
        </Modal>
        <Modal
          title="Edit blurb"
          open={simpleBlurbDrawerOpen}
          onCancel={() => void closeSimpleBlurbDrawer()}
          width={isMobile ? 'calc(100vw - 24px)' : 780}
          className="simple-translation-blurb-modal"
          closable={!simpleBlurbSaving}
          maskClosable={!simpleBlurbSaving}
          footer={[
            <Button
              key="done"
              data-testid="tutorial:simple-workflow:edit-blurb-done"
              loading={simpleBlurbSaving}
              onClick={() => void closeSimpleBlurbDrawer()}
            >
              Done
            </Button>,
          ]}
        >
          <div className="simple-translation-blurb-editor" data-testid="tutorial:simple-workflow:edit-blurb-modal">
            <div className="simple-translation-blurb-editor-meta">
              <Tag color={expressBlurbSourceText.trim() ? 'blue' : 'warning'}>Storefront blurb</Tag>
              <Text type={!expressBlurbSourceText.trim() || simpleBlurbTooLong ? 'danger' : 'secondary'}>
                {!expressBlurbSourceText.trim()
                  ? 'Enter a blurb before starting translation.'
                  : simpleBlurbTooLong
                    ? `${expressBlurbWordCount.toLocaleString()} / ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words. Shorten the blurb to translate it.`
                    : expressBlurbFreeRunAvailable
                      ? `${expressBlurbWordCount.toLocaleString()} / ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words. Included for this language.`
                      : `${expressBlurbWordCount.toLocaleString()} / ${MAX_BLURB_TRANSLATION_WORDS.toLocaleString()} words. ${expressBlurbWordCount.toLocaleString()} credits for blurb translation.`}
              </Text>
            </div>
            <ReactQuill
              value={sanitizeRichTextHtml(expressBlurbSourceText)}
              onChange={setExpressBlurbSourceText}
              readOnly={simpleSelectedTargetReadOnly || simpleBlurbSaving}
              modules={SIMPLE_BLURB_QUILL_MODULES}
              formats={SIMPLE_BLURB_QUILL_FORMATS}
              placeholder="Enter the source-language blurb to translate."
            />
            <BlurbWordCounter value={expressBlurbSourceText} />
          </div>
        </Modal>
        <Modal
          title={`${expressResolvedTargetLanguage || 'Translated'} blurb`}
          open={simpleTranslatedBlurbVisible}
          onCancel={() => setSimpleTranslatedBlurbVisible(false)}
          width={isMobile ? 'calc(100vw - 24px)' : 760}
          className="simple-translation-blurb-modal simple-translation-blurb-view-modal"
          footer={[
            <Space
              key="translated-blurb-actions"
              wrap
              data-testid="tutorial:simple-workflow:translated-blurb-actions"
            >
              <Button
                key="copy"
                icon={<CopyOutlined />}
                disabled={!simpleTranslatedBlurb}
                data-testid="tutorial:simple-workflow:translated-blurb-copy"
                onClick={() => void copySimpleTranslatedBlurb()}
              >
                Copy blurb
              </Button>
              <Button
                key="download"
                icon={<DownloadOutlined />}
                disabled={!simpleTranslatedBlurb}
                data-testid="tutorial:simple-workflow:translated-blurb-download"
                onClick={() => downloadTextFile(simpleTranslatedBlurb, simpleTranslatedBlurbFilename)}
              >
                Download .txt
              </Button>
              <Button
                key="close"
                type="primary"
                data-testid="tutorial:simple-workflow:translated-blurb-close"
                onClick={() => setSimpleTranslatedBlurbVisible(false)}
              >
                Close
              </Button>
            </Space>,
          ]}
        >
          <div className="simple-translation-blurb-view" data-testid="tutorial:simple-workflow:translated-blurb-modal">
            <div className="simple-translation-blurb-view-meta">
              <Tag color="blue">Storefront blurb</Tag>
              <Text type="secondary">
                {expressResolvedTargetLanguage ? `${bookLanguage || book.language || 'Source'} to ${expressResolvedTargetLanguage}` : 'Translated blurb'}
              </Text>
            </div>
            <div
              className="simple-translation-translated-blurb"
              dangerouslySetInnerHTML={{
                __html: sanitizeRichTextHtml(simpleTranslatedBlurb || 'No translated blurb is available.'),
              }}
            />
          </div>
        </Modal>
      </Space>
    </div>
  );
};

const showBookDetailBlockingOverlay = Boolean(
  isLoading
  || isGeneratingCover
  || isCreditCheckPending
  || isFrenchDialogueModalOpening
  || isFrenchDialogueRunStarting
  || isChapterUploadProcessing
  || pendingProofreadOverlayMessage,
);

return (
<>
    {showBookDetailBlockingOverlay && (
      <div className="app-loading-overlay book-detail-loading-overlay">
        {(() => {
          if (isChapterUploadProcessing) {
            return (
              <div
                role="status"
                aria-live="polite"
                className="process-loading-card"
              >
                <FeatherSpinner style={{ width: 64, height: 64 }} />
                <div className="process-loading-copy">
                  <div className="process-loading-title">Saving chapter updates…</div>
                </div>
              </div>
            );
          }

          let sagaOn = true;
          try {
            const v = localStorage.getItem('showSagaRaven');
            if (v === 'false') sagaOn = false;
          } catch {}

          if (isGeneratingCover) {
            return (
              <div
                role="status"
                aria-live="polite"
                className="process-loading-card process-loading-card--cover"
              >
                <div className="process-loading-graphic process-loading-graphic--cover">
                  {sagaOn ? (
                    coverSpinnerKind === 'coloring' ? (
                      <SagaRaven size="xl" coloringMixer priority={SAGA_SINGLETON_PRIORITIES.loading} style={{ width: 256, height: 256 }} />
                    ) : (
                      <SagaRaven size="xl" drawingMixer priority={SAGA_SINGLETON_PRIORITIES.loading} style={{ width: 256, height: 256 }} />
                    )
                  ) : (
                    <FeatherSpinner style={{ width: 64, height: 64 }} />
                  )}
                </div>
                <div className="process-loading-copy">
                  <div className="process-loading-title">Generating cover images…</div>
                  <div className="process-loading-subtitle">This may take a few minutes.</div>
                </div>
              </div>
            );
          }

          // Generic loading overlay (non-cover)
          const genericLabel = pendingProofreadOverlayMessage
            ? pendingProofreadOverlayMessage
            : isFrenchDialogueRunStarting
            ? 'Starting French dialogue run…'
            : isFrenchDialogueModalOpening
              ? 'Preparing French dialogue options…'
            : isCreditCheckPending
              ? 'Checking available credits…'
              : firstEnter
                ? 'Loading book details…'
                : 'Working on your request…';

          return (
            <div
              role="status"
              aria-live="polite"
              className="process-loading-card"
            >
              <Spin size="large" />
              <div className="process-loading-copy">
                <div className="process-loading-title">{genericLabel}</div>
              </div>
            </div>
          );
        })()}
      </div>
    )}
    <div
      className={`book-detail-page-shell ${
        isMobile ? 'book-detail-page-shell--mobile' : 'book-detail-page-shell--desktop'
      } ${bookDetailExperienceLoaded && isSimpleBookDetailMode ? 'book-detail-page-shell--simple' : ''}`}
    >
  <div
    data-tour-id="book-header"
    data-testid="tutorial:book-detail:header"
    className="book-detail-header-region"
    style={{ marginBottom: (translationTrackerData || shouldDisplayLanguageFlags) ? '4px' : '10px' }}
  >
    <div className="book-detail-header">
      <div className="book-detail-header-primary-actions">
        {!isSimpleBookDetailMode && (
          <Button
            type="default"
            icon={<FormOutlined />}
            onClick={handleEditMetadataClick}
            disabled={!bookDetailExperienceLoaded || (isEditing && activeMetadataTab === 'metadata')}
            size={isMobile ? 'small' : 'middle'}
          >
            {headerEditLabel}
          </Button>
        )}
        {bookDetailExperienceLoaded && (
          <div className={`book-detail-experience-switcher${!isSimpleBookDetailMode ? ' book-detail-experience-switcher--with-edit-action' : ''}`}>
            <Radio.Group
              data-testid="tutorial:book-detail:mode-toggle"
              aria-label="Book workspace mode"
              aria-describedby={!isMobile ? 'book-workspace-mode-hint' : undefined}
              size={isMobile ? 'small' : 'middle'}
              value={bookDetailExperienceMode}
              onChange={(event) => handleBookDetailExperienceChange(event.target.value as BookDetailExperienceMode)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="simple">
                <span data-testid="tutorial:book-detail:simple-mode">Guided Workflow</span>
              </Radio.Button>
              <Radio.Button value="advanced">
                <span data-testid="tutorial:book-detail:advanced-mode">All Tools</span>
              </Radio.Button>
            </Radio.Group>
            {!isMobile && (
              <span id="book-workspace-mode-hint" className="book-detail-experience-switcher__hint">
                {isSimpleBookDetailMode ? 'Step-by-step translate and export' : 'Full book workspace'}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="book-detail-title-group">
        {isSimpleBookDetailMode ? (
          <div className="book-detail-title-text">{book.title}</div>
        ) : (
          <Tooltip title={headerEditLabel}>
          <button
            type="button"
            onClick={handleEditMetadataClick}
            disabled={!bookDetailExperienceLoaded}
            onMouseEnter={() => setIsMetadataTitleHovered(true)}
            onMouseLeave={() => setIsMetadataTitleHovered(false)}
            className="book-detail-title-button"
            aria-label={headerEditLabel}
            style={{ textDecoration: isMetadataTitleHovered ? 'underline' : 'none' }}
          >
            {book.title}
          </button>
          </Tooltip>
        )}
        <p>Review the source book, choose a language workspace, and continue the next publishing action.</p>
      </div>
      <div className="book-detail-header-utility-actions">
        <TranslationStatusPanel
          bookId={bookId}
          refreshDisabled={isCachePrewarming}
          triggerMode="inline"
          compactTrigger
          minimalTrigger
          openSignal={processingStatusOpenSignal}
          tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
        />
        {auth.currentUser?.uid && (
          <SeriesNavigator
            seriesName={book.series}
            seriesId={book.seriesId}
            currentBookId={bookId}
            currentBookNumber={book.booknumber}
            userId={auth.currentUser.uid}
          />
        )}
        <Button
          type="text"
          icon={<QuestionCircleOutlined />}
          onClick={handleStartMainTour}
          data-tour-id="book-tour-trigger"
          className="book-detail-tour-link app-tour-trigger"
          size={isMobile ? 'small' : 'middle'}
        >
          Take a tour
        </Button>
      </div>
    </div>
  </div>
      {/* Main Content Area */}
      <div
        className="book-detail-body"
      >

        {/* Left Panel */}
{bookDetailExperienceLoaded && !isSimpleBookDetailMode && (
<BookDetailLeftRail
  isCollapsed={isSidebarCollapsed}
>
  <ChapterList
    chapters={book.chapters.filter(isContentBearingChapterRow)}
    isSidebarCollapsed={isSidebarCollapsed}
    toggleSidebar={toggleSidebar}
    selectedChapter={selectedChapter}
    onSelectChapter={selectChapter}
    editingChapterGuid={editingChapterGuid}
    editedChapterName={editedChapterName}
    onEditChapterName={(e) => setEditedChapterName(e.target.value)}
    onSaveChapterName={saveChapterName}
    onCancelEdit={cancelEdit}
    onStartEditingChapter={startEditingChapter}
    onDeleteChapter={deleteChapter}
    allowSourceChapterDeletion={selectedTab === 'Original'}
    isSelectedChapter={isSelectedChapter}
    onReorderChapters={handleChapterReorder}
    isReordering={isSavingChapterOrder}
    disableReorder={isEpubUpload}
    pendingProofreadReviewChapterGuids={pendingProofreadReviewChapterGuidsForSelectedTab}
    pendingProofreadReviewLabel={
      chapterListPendingReviewVersion === 'Original'
        ? originalLanguageLabel
        : formatLangDisplayText(chapterListPendingReviewVersion)
    }
  />
</BookDetailLeftRail>
)}

      {/* Right Panel */}
<div
  data-tour-id="book-main-column"
  className={`book-detail-main-column ${bookDetailExperienceLoaded && isSimpleBookDetailMode ? 'book-detail-main-column--simple' : ''}`}
>
  <div
    data-tour-id="book-main-card"
    className={`book-detail-main-card ${bookDetailExperienceLoaded && isSimpleBookDetailMode ? 'book-detail-main-card--simple' : ''}`}
  >
    {/* Content with single scroll container */}
    <div
      data-tour-id="workspace-container"
      data-testid="tutorial:book-detail:workspace"
      className={`book-detail-workspace-scroll ${
        !bookDetailExperienceLoaded || isSimpleBookDetailMode || isEditing ? 'book-detail-workspace-scroll--editing' : 'book-detail-workspace-scroll--chapter'
      } ${bookDetailExperienceLoaded && isSimpleBookDetailMode ? 'book-detail-workspace-scroll--simple' : ''}`}
    >
      <div className={`book-detail-workspace-frame ${bookDetailExperienceLoaded && isSimpleBookDetailMode ? 'book-detail-workspace-frame--simple' : ''}`}>
      {!bookDetailExperienceLoaded ? (
        showBookDetailBlockingOverlay ? (
          <div className="book-detail-empty-workspace" aria-hidden="true" />
        ) : (
        <div className="book-detail-empty-workspace book-detail-experience-loading" role="status" aria-live="polite">
          <Spin size="small" />
          <span>Loading book view...</span>
        </div>
        )
      ) : isSimpleBookDetailMode ? (
        renderSimpleBookDetailMode()
      ) : isEditing ? (
  <BookEditPanel
    book={book}
    editBook={editBook}
    handleInputChange={handleInputChange}
    handleSave={handleSave}
    blurb={blurb}
    setBlurb={setBlurb}
    shouldSuppressBlurbDirty={shouldSuppressBlurbDirty}
    languages={languages}
    bookLanguage={bookLanguage}
    setBookLanguage={setBookLanguage}
    genres={genres}
    setGenres={setGenres}
    tropes={tropes}
    setTropes={setTropes}
    keywords={keywords}
    intendedAudience={intendedAudience}
    setIntendedAudience={setIntendedAudience}
    setKeywords={setKeywords}
    toggleMetadataTranslateModal={toggleMetadataTranslateModal}
    toggleKeywordSuggestionModal={toggleKeywordSuggestionModal}
    exportBlurbToTxt={exportBlurbToTxt}
    handleGenerateBlurb={handleGenerateBlurb}
    isTitleValid={isTitleValid}
    hasUnsavedChanges={hasUnsavedChanges}
    setHasUnsavedChanges={setHasUnsavedChanges}
    blurbActiveTabKey={blurbActiveTabKey}
    setBlurbActiveTabKey={setBlurbActiveTabKey}
    setCurrentLanguagePair={setCurrentLanguagePair}
    setIsTranslatedTabActive={setIsTranslatedTabActive}
    isTranslatedTabActive={isTranslatedTabActive}
    blurbTranslations={blurbTranslations}
    titleTranslations={titleTranslations}
    seriesTranslations={seriesTranslations}
    seriesDescriptionTranslations={seriesDescriptionTranslations}
    keywordTranslations={keywordTranslations}
    neverTranslateWords={neverTranslateWords}
    alwaysTranslateWords={alwaysTranslateWords}
    accountNeverTranslateWords={accountNeverTranslateWords}
    accountAlwaysTranslateWords={accountAlwaysTranslateWords}
    setNeverTranslateWords={setNeverTranslateWords}
    setAlwaysTranslateWords={setAlwaysTranslateWords}
    setAccountNeverTranslateWords={setAccountNeverTranslateWords}
    setAccountAlwaysTranslateWords={setAccountAlwaysTranslateWords}
    seriesNeverTranslateWords={seriesTranslateRules.neverTranslate}
    seriesAlwaysTranslateWords={seriesTranslateRules.alwaysTranslate}
    setSeriesTranslateRules={setSeriesTranslateRules}
    bookId={bookId}
    auth={auth}
    seriesOptions={filteredSeriesOptions}
    selectedSeriesId={selectedSeriesId}
    onSeriesChange={handleSeriesSelection}
    seriesDescription={seriesDescription}
    onSeriesDescriptionChange={handleSeriesDescriptionChange}
    seriesInheritance={seriesInheritance}
    onSeriesInheritanceChange={handleSeriesInheritanceToggle}
    onCreateSeries={handleCreateSeries}
    onCopyMetadata={handleCopyMetadata}
    isSeriesBusy={isSeriesBusy}
    creditBalance={creditBalance}
    fetchCreditBalance={fetchCreditBalance}
    handleDeleteTranslatedChapters={handleDeleteTranslatedChapters}
    handleDeleteProofreadChapters={handleDeleteProofreadChapters}
    setIsNeverTranslateModalVisible={setIsNeverTranslateModalVisible}
    setIsAlwaysTranslateModalVisible={setIsAlwaysTranslateModalVisible}
    setTitleTranslations={setTitleTranslations}
    setSeriesTranslations={setSeriesTranslations}
    setBlurbTranslations={setBlurbTranslations}
    onFrenchNarrativeToneSaved={handleFrenchNarrativeToneSaved}
    selectedChapter={selectedChapter}
    handleAddLanguage={handleAddLanguage}
    commonGenres={commonGenres}
    handleTranslateBookClick={handleTranslateBookClick}
    handleProofreadBookClick={handleProofreadBookClick}
    handleExportBookClick={handleExportBookClick}
    recentExports={recentExports}
    onDownloadRecentExport={downloadRecentExport}
    onRefreshProgress={refreshTranslationProgress}
    isRefreshingProgress={isRefreshingProgress}
    toggleAudiobookModal={toggleAudiobookModal}
	navigateToAudiobookEdit={navigateToAudiobookEdit}
    handleCoverUpload={handleCoverUpload}
    handleRemoveCover={handleRemoveCover}
    setHumanReviewModalVisible={setHumanReviewModalVisible}
    showRemoveCover={showRemoveCover}
    setShowRemoveCover={setShowRemoveCover}
    audiobookVersionOptions={audiobookVersionOptions}
    onTranslateText={handleTranslateAudiobookCreditsText}
	isLangTesterUser={isLangTesterUser}
	isBetaUser={isBetaUser}
	chapterWordCounts={getChapterWordCounts()}
    languageCovers={languageCovers}
    selectedTab={selectedTab}
    onTabChange={handleTabChange}
    onActiveTabChange={handleMetadataTabChange}
    onMetadataLanguageChange={handleMetadataLanguageChange}
    startAudiobookTourSignal={audiobookTourSignal}
    onAudiobookTopUpCredits={openTopUpFromBook}
    startCharactersTourSignal={charactersTourSignal}
    onUpdateLanguageCovers={handleUpdateLanguageCovers}
  onCoverGenerationStart={handleCoverGenerationStart}
  onCoverGenerationComplete={handleCoverGenerationComplete}
  openCoverGenerationSignal={openCoverGenerationSignal || undefined}
  openCoverHistorySignal={openCoverHistorySignal || undefined}
  onOpenCoverTranslationForLanguage={handleOpenCoverTranslationForLanguage}
  onOpenCoverHistoryForLanguage={(language: string) => setOpenCoverHistorySignal({ language, ts: Date.now() })}
    onOpenChapterUploadModal={() => setIsChapterUploadModalVisible(true)}
    isChapterUploadDisabled={isChapterUploadBlockedByJobs}
    chapterUploadDisabledReason={chapterUploadDisabledReason}
    relationshipSummaryCard={
      RELATIONSHIP_UI_ENABLED
        ? renderRelationshipSummaryCard({
            style: {
              marginBottom: 0,
              width: '100%',
              background: 'var(--surface-muted)',
              borderColor: 'var(--border-subtle)',
              boxShadow: 'none',
            },
            hideTitle: true,
          })
        : undefined
    }
    relationshipFreeRunAvailable={relationshipSummary?.freeRunAvailable}
  relationshipAnalysisLoading={relationshipAnalysisLoading}
    registryRefreshSignal={registryRefreshSignal}
    registryClearSignal={registryClearSignal}
    resolvedUserId={resolvedOwnerUserId}
    glossaryScanTrigger={glossaryScanTrigger}
    termScanReviewTrigger={termScanReviewTrigger}
    onOpenRelationshipEditor={handleOpenRelationshipEditor}
    onOpenRelationshipAnalysis={() => handleRunRelationshipAnalysis()}
    onCloseRelationshipEditor={handleCloseRelationshipEditor}
    onCloseRelationshipAnalysis={() => setIsRelationshipModalVisible(false)}
    metadataTabSignal={metadataTabSignal}
    metadataLanguageSignal={metadataLanguageSignal}
    translationTrackerData={translationTrackerData}
    translationProgress={filteredTranslationProgress}
    translationLanguages={flagLanguages}
    showTranslationProgress={shouldDisplayLanguageFlags}
    onSelectTranslationLanguage={handleOpenTranslateForLanguage}
    ensureMetadataLanguage={ensureMetadataLanguage}
    onPrepareMetadataTranslateLanguage={handlePrepareMetadataTranslateLanguage}
    onPrepareKeywordSuggestionLanguage={handlePrepareKeywordSuggestionLanguage}
    onOpenProofreadForLanguage={handleOpenProofreadForLanguage}
    onApplyPendingProofreadReviewsForLanguage={handleApplyPendingProofreadReviewsForLanguage}
    applyingPendingProofreadLanguage={applyingPendingProofreadLanguage}
    frenchDialogueLegacyEligibilityByPair={frenchDialogueLegacyEligibilityByPair}
    onFetchFrenchDialogueLegacyEligibility={fetchFrenchDialogueLegacyEligibility}
    onRunFrenchDialogueLegacyCorrection={handleRunFrenchDialogueLegacyCorrection}
    isAdmin={showMetadataAdminTab && isAdmin}
  onAskAI={handleOpenAskAI}
  onTermScanTopUpCredits={openTopUpFromBook}
  onRunMetadataAutofill={runMetadataAutofill}
/>
) : selectedChapter ? (
  <ChapterContentPanel
    selectedChapter={selectedChapter}
    book={book}
    selectedTab={selectedTab}
    handleTabChange={handleTabChange}
    formatLangDisplayText={formatLangDisplayText}
    rejectedChapters={rejectedChapters}
    handleEditToggle={handleEditMetadataClick}
    toggleTranslateModal={toggleTranslateModal}
    toggleProofreadModal={toggleProofreadModal}
    handleExportBookClick={handleExportBookClick}
    renderProofreadTabContent={renderProofreadTabContent}
    renderTranslatedTabContent={renderTranslatedTabContent}
    handleDeleteSingleTranslatedChapter={handleDeleteSingleTranslatedChapter}
    isEditing={isEditing}
    handleTranslateBookClick={handleTranslateBookClick}
    handleProofreadBookClick={handleProofreadBookClick}
    onSaveChapterContent={handleChapterContentSave}
    onRevertChapterContent={handleRevertChapterContent}
    editLocked={editLocked}
    proofreadProcessing={hasActiveProofreadForVersion(activeEditLocks, bookId, selectedTab)}
    editLockReason={editLockReason}
    isAdmin={isAdmin}
    onAskAI={handleOpenAskAI}
    bookId={bookId}
    userId={auth.currentUser?.uid ?? sharedUserId ?? ''}
    apiBase={API_BASE}
    bookLanguage={bookLanguage}
    targetLanguage={targetLanguage}
    selectedAIModel={selectedAIModel}
    onChapterRelationshipsChange={handleChapterRelationshipsChange}
    onRunFrenchDialogueLegacyCorrection={handleRunFrenchDialogueLegacyCorrection}
  />
) : (
  <div className="book-detail-empty-workspace">No chapter selected</div>
)}
      </div>
      </div>


    </div>
		</div>
      </div>
    </div>
	{isGenerateBlurbModalVisible && (
  <GenerateBlurbModal
    isVisible={isGenerateBlurbModalVisible}
    onClose={() => setIsGenerateBlurbModalVisible(false)}
    bookId={bookId}
    auth={auth}
    genres={genres}
    selectedAIModel={selectedAIModel}
	setSelectedAIModel={setSelectedAIModel}
    fetchCreditBalance={fetchCreditBalance}
    creditBalance={creditBalance}
    setBlurb={setBlurb}
    onBlurbSaved={handleGeneratedBlurbSaved}
	isLoading={isLoading}
    setIsLoading={setIsLoading}
	book={book}
  />
)}
{RELATIONSHIP_UI_ENABLED && (
  <>
    <RelationshipMatrixEditorModal
      visible={isRelationshipEditorVisible}
      onClose={handleCloseRelationshipEditor}
      onOpenAnalysis={() => handleRunRelationshipAnalysis()}
      analyzeDisabled={!hasRelationshipChapters || isRelationshipSummaryLoading || isRelationshipAnalysisRunning}
      initialChapterGuid={relationshipEditorChapterGuid}
      bookId={bookId}
      userId={auth.currentUser?.uid ?? sharedUserId ?? ''}
      apiBase={API_BASE}
      chapters={relationshipChapterOptions}
      onSaved={() => {
        fetchRelationshipSummary({ silent: true });
        bumpRegistryRefreshSignal();
      }}
    />
    <RelationshipAnalysisModal
      visible={isRelationshipModalVisible}
      onCancel={() => setIsRelationshipModalVisible(false)}
      onConfirm={handleConfirmRelationshipAnalysis}
      confirmLoading={isRelationshipAnalysisRunning}
      chaptersToAnalyze={
        activeRelationshipSummary?.chaptersToAnalyze ??
        activeRelationshipSummary?.missingChapters ??
        null
      }
      creditCost={activeRelationshipCreditCost ?? null}
      chapterDetails={activeRelationshipSummary?.chapters || []}
      freeRunAvailable={activeRelationshipSummary?.freeRunAvailable}
      creditBalance={availableCredits}
      disableConfirm={activeRelationshipCreditInsufficient || activeRelationshipSummaryLoading}
      insufficientCredits={activeRelationshipCreditInsufficient}
      includeAnalyzed={relationshipAnalysisMode === 'rerun'}
      onIncludeAnalyzedChange={handleRelationshipAnalysisModeChange}
      includeAnalyzedDisabled={isRelationshipAnalysisRunning}
      selectedChapterGuids={relationshipSelectedChapterGuids}
      onSelectedChapterGuidsChange={handleRelationshipSelectionChange}
    />
  </>
)}
<AppTour
  open={mainTourOpen}
  steps={mainTourSteps}
  current={mainTourStep}
  onChange={handleMainTourChange}
  onClose={() => closeTour('main')}
  onFinish={() => closeTour('main')}
  zIndex={2200}
/>
<AppTour
  open={simpleMainTourOpen}
  steps={simpleMainTourSteps}
  current={simpleMainTourStep}
  onChange={setSimpleMainTourStep}
  onClose={() => closeTour('simpleMain')}
  onFinish={() => closeTour('simpleMain')}
  zIndex={2200}
/>
<AppTour
  open={metadataLanguageTourOpen}
  steps={metadataLanguageTourSteps}
  current={metadataLanguageTourStep}
  onChange={handleMetadataLanguageTourChange}
  onClose={() => closeTour('metadataLanguage')}
  onFinish={() => closeTour('metadataLanguage')}
  zIndex={2200}
/>
<AppTour
  open={chapterTourOpen}
  steps={chapterTourSteps}
  current={chapterTourStep}
  onChange={setChapterTourStep}
  onClose={() => closeTour('chapter')}
  onFinish={() => closeTour('chapter')}
  zIndex={2200}
/>
<AppTour
  open={!tutorialUiSuppressionRequested && dashboardTourOpen}
  steps={dashboardTourSteps}
  current={dashboardTourStep}
  onChange={setDashboardTourStep}
  onClose={handleDashboardTourClose}
  onFinish={handleDashboardTourClose}
  zIndex={2200}
/>

<Modal
  title="Try Simple Mode"
  visible={bookDetailExperiencePromptVisible}
  onCancel={() => handleBookDetailExperienceChange('advanced')}
  footer={[
    <Button key="advanced" onClick={() => handleBookDetailExperienceChange('advanced')}>
      Keep Current UI
    </Button>,
    <Button key="simple" type="primary" onClick={() => handleBookDetailExperienceChange('simple')}>
      Try Simple Mode
    </Button>,
  ]}
  width={480}
  centered
>
  <Typography.Paragraph>
    Simple Mode keeps this page focused on one workflow: choose a language, choose an output format,
    translate, export, and download the finished file.
  </Typography.Paragraph>
  <Typography.Paragraph style={{ marginBottom: 0 }}>
    You can switch between Simple and Advanced from the book header at any time.
  </Typography.Paragraph>
</Modal>

<SimpleExportFormatModal
  visible={simpleExportFormatModalVisible}
  format={simpleExportFormat}
  allowEpub={isEpubUpload}
  loading={simpleExportSubmitting}
  includeImages={includeWordImages}
  onIncludeImagesChange={uploadType === 'Word' ? setIncludeWordImages : undefined}
  onChange={setSimpleExportFormat}
  onCancel={() => setSimpleExportFormatModalVisible(false)}
  onConfirm={createSimpleExport}
/>

<ExportBookModal
  isVisible={isExportModalVisible}
  onCancel={toggleExportModal}
  onExport={handleExport}
  exportVersion={exportVersion}
  setExportVersion={setExportVersion}
  exportFormat={exportFormat}
  setExportFormat={setExportFormat}
  uploadType={uploadType}
  exportOrientation={exportOrientation}
  setExportOrientation={setExportOrientation}
  proofreadOptionProps={exportProofreadOptionProps}
  book={book}
  originalLanguageLabel={originalLanguageLabel}
  formatLangDisplayText={formatLangDisplayText}
  proofreadOptions={proofreadOptions}
  showWarning={showWarning}
  untranslatedChapters={untranslatedChapters}
  unproofreadChapters={unproofreadChapters}
  includeImages={uploadType === 'Word' ? includeWordImages : includeImages}
  setIncludeImages={uploadType === 'Word' ? setIncludeWordImages : setIncludeImages}
  recentExports={recentExports}
  onDownloadRecentExport={downloadRecentExport}
  tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
/>

<ChapterUploadModal
  visible={isChapterUploadModalVisible}
  onClose={() => setIsChapterUploadModalVisible(false)}
  bookId={bookId}
  userId={auth.currentUser?.uid || undefined}
  authUser={auth.currentUser}
  existingChapters={book.chapters || []}
  isEpubUpload={isEpubUpload}
  onUploadProcessingChange={setIsChapterUploadProcessing}
  onSuccess={() => {
    fetchBookDetails();
    setIsChapterUploadModalVisible(false);
  }}
  hasSeenUploadTour={!!tourFlags.chapterUploadTourSeen}
  tourFlagsInitialized={tourFlagsInitialized}
  onMarkUploadTourSeen={() => {
    void markTourSeen('chapterUploadTourSeen');
  }}
/>


<TranslationModal
  isVisible={isTranslateModalVisible}
  onCancel={toggleTranslateModal}
  onTranslate={() => {
    setIsConfirmationModalVisible(true);
  }}
  selectedChapter={selectedChapter}
  translateVersion={translateVersion}
  setTranslateVersion={setTranslateVersion}
  bookLanguage={bookLanguage}
  targetLanguage={targetLanguage}
  setTargetLanguage={setTargetLanguage}
  targetLanguages={resolvedTranslationTargetLanguages}
  setTargetLanguages={setTranslationTargetLanguages}
  handleLanguageChange={handleLanguageChange}
  handleAIModelChange={handleAIModelChange}
  workflowMode={translationWorkflowMode}
  setWorkflowMode={handleWorkflowModeChange}
  preferredModelId={preferredTranslationModelId}
  recommendedWorkflowPolicy={recommendedWorkflowPolicy}
  onAdvancedModeChange={setIsTranslationAdvancedOpen}
  languages={languages}
  betaLanguages={betaLanguages}
  betaLanguageWarning={betaLanguageWarning}
  setBetaLanguageWarning={setBetaLanguageWarning}
  quotationStyle={quotationStyle}
  setQuotationStyle={setQuotationStyle}
  frenchDialogueRulesetVersion={frenchDialogueRulesetVersion}
  setFrenchDialogueRulesetVersion={setFrenchDialogueRulesetVersion}
  selectedAIModel={selectedAIModel}
  creditBalance={creditBalance}
  getCreditCost={getCreditCost}
  getCreditCostByLanguage={getCreditCostByLanguage}
  isLanguageTranslationComplete={isLanguageTranslationComplete}
  autoProofreadEnabled={autoProofreadEnabled}
  setAutoProofreadEnabled={setAutoProofreadEnabled}
  requireCharacterAnalysis={effectiveRequireCharacterAnalysis}
  characterAnalysisAlreadyAvailable={characterAnalysisAlreadyAvailable}
  onRequireCharacterAnalysisChange={handleRequireCharacterAnalysisChange}
  isChapterTranslated={isChapterTranslated}
  hasUntranslatedChapters={hasUntranslatedChapters}
  userHasEnoughCredits={userHasEnoughCredits}
  hasUnproofreadChapters={!hasOriginalProofreadComplete}
  proofreadOptionProps={proofreadOptionProps}
  enforceFrenchEmDash={enforceFrenchEmDash}
  setEnforceFrenchEmDash={setEnforceFrenchEmDash}
  isLangTesterUser={isLangTesterUser}
  onDetectFrenchTone={async () => {
    const { data } = await bookDetailApiPost(`${API_BASE}/books/french-tone/detect`, {
      bookId, userId: auth.currentUser?.uid, preview: true,
    });
    if (data?.status !== 'success' || !['modern', 'classical', 'present'].includes(data?.tone)) {
      throw new Error('French narrative detection failed');
    }
    return data.tone;
  }}
  frenchTone={frenchTone}
  setFrenchTone={setFrenchTone}
  useBatch={useBatch}
  setUseBatch={setUseBatch}
  modelOptions={translationOrderedModels}
  modelsLoading={translationModelsLoading}
  bookFrenchTone={book.frenchNarrativeTone}
  termScanWarningState={termScanWarningState}
  termScanPendingReviewCount={termScanPendingReviewCount}
  termScanWarningDetail={termScanWarningDetail}
  onDismissTermScanWarning={handleDismissTermScanWarning}
  onOpenGlossaryScanModal={handleOpenGlossaryScanFromTranslateModal}
  onReviewGlossaryScanModal={handleReviewGlossaryScanFromTranslateModal}
  isOpeningGlossaryScan={isOpeningGlossaryScan}
  isCheckingGlossaryScanStatus={isCheckingTermScanWarningStatus}
  termScanPreflightExtraCreditCost={termScanPreflightExtraCreditCostTotal}
  termScanPreflightExtraCreditCostByLanguage={termScanPreflightExtraCreditCostByLanguage}
  showTermScanAutoRunSetting={!termScanRunCompletedForPair}
  translationAutoRunTermScanBeforeBulk={
    bulkTermScanPreflightEnabled ? translationAutoRunTermScanBeforeBulk : false
  }
  onTranslationAutoRunTermScanBeforeBulkChange={
    bulkTermScanPreflightEnabled ? handleTranslationAutoRunTermScanBeforeBulkChange : undefined
  }
  onTopUpCredits={openTopUpFromBook}
  onSubmitBetaFeedback={submitTranslationBetaFeedback}
  betaFeedbackEmail={auth.currentUser?.email || ''}
  simpleMode={isSimpleBookDetailMode}
  tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
/>

<Modal
  title={
    <div className="translation-confirm-title">
      <span>Confirm Translation Settings</span>
    </div>
  }
  visible={isConfirmationModalVisible}
  onCancel={() => setIsConfirmationModalVisible(false)}
  zIndex={1200}
  centered
  width={520}
  className="translation-confirm-modal"
  footer={[
    <Button key="back" onClick={() => setIsConfirmationModalVisible(false)}>
      Back
    </Button>,
    <Button key="cancel" onClick={() => {
      setIsConfirmationModalVisible(false);
      toggleTranslateModal();
    }}>
      Cancel
    </Button>,
    <Button key="submit" type="primary" className="app-primary-cta" onClick={handleTranslation}>
      Confirm and Start Translation
    </Button>,
  ]}
>
  <div className="translation-confirm-body">
    <h3 className="translation-confirm-headline">
      Translate {selectedChapter ? 'the selected chapter' : book.title || 'this book'} from {originalLanguageLabel || bookLanguage || 'the source language'} to{' '}
      {resolvedTranslationTargetLanguages.length > 0
        ? resolvedTranslationTargetLanguages.join(', ')
        : targetLanguage || 'the selected language'}?
    </h3>
    <p className="translation-confirm-intro">
      {selectedChapter ? 'Only this chapter will be submitted.' : 'The full manuscript will be submitted.'}
      {' '}Existing chapter structure will be preserved.
    </p>
    <div className="translation-confirm-summary-list translation-confirm-summary-table" role="table" aria-label="Translation settings summary">
      {resolvedTranslationTargetLanguages.length === 1 && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">Dialogue style</span>
          <strong role="cell">
            {(() => {
              const quotationConfig = getQuotationStyle(targetLanguage);
              if (quotationConfig?.options?.length && quotationStyle) {
                const selectedOption = quotationConfig.options.find(opt => opt.value === quotationStyle);
                return selectedOption ? `${selectedOption.label} (${selectedOption.marks})` : 'Default';
              }
              if (quotationConfig?.fixed) {
                return `${quotationConfig.fixed.name} (${quotationConfig.fixed.marks})`;
              }
              return 'Default';
            })()}
          </strong>
        </div>
      )}
      {resolvedTranslationTargetLanguages.length === 1 &&
      targetLanguage === 'French' &&
      normalizeQuotationStyleValue(quotationStyle) === 'french em dash' && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">French dialogue rules</span>
          <strong role="cell">
            {(() => {
              const resolvedVersion = resolveFrenchDialogueRulesetForRequest({
                workflowMode: translationWorkflowMode,
                targetLang: targetLanguage,
                quotationStyle,
                selectedRulesetVersion: frenchDialogueRulesetVersion,
              });
              const selectedOption = FALLBACK_FRENCH_DIALOGUE_RULESET_OPTIONS.find(
                (option) => option.version === resolvedVersion,
              );
              return selectedOption?.label || 'Updated French Dialog Rules';
            })()}
          </strong>
        </div>
      )}
      {isTranslationAdvancedOpen && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">AI model</span>
          <strong role="cell">
            {selectedModelData?.displayName || selectedAIModel || 'Recommended setup'}
          </strong>
        </div>
      )}
      {bulkTermScanPreflightEnabled && translationAutoRunTermScanBeforeBulk && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">Glossary scan</span>
          <strong role="cell">
            {termScanPreflightExtraCreditCostTotal > 0
              ? `Auto-run (+${termScanPreflightExtraCreditCostTotal} credits)`
              : 'Auto-run'}
          </strong>
        </div>
      )}
      {effectiveRequireCharacterAnalysis && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">Character analysis</span>
          <strong role="cell">
            {characterAnalysisAlreadyAvailable ? 'Already completed' : 'Run before translation'}
          </strong>
        </div>
      )}
      {autoProofreadEnabled && (
        <div className="translation-confirm-summary-row" role="row">
          <span role="rowheader">Additional Quality Pass</span>
          <strong role="cell">Enabled (+15%)</strong>
        </div>
      )}
    </div>
    <div className="translation-confirm-cost-panel" aria-label="Translation cost and readiness">
      <div>
        <span>Estimated credit cost</span>
        <strong>{getCreditCost().toLocaleString()} credits</strong>
      </div>
      <div>
        <span>Current credit balance</span>
        <strong>
          {creditBalance
            ? `${(creditBalance.available_balance ?? creditBalance.credit_balance ?? 0).toLocaleString()} credits`
            : 'Loading...'}
        </strong>
      </div>
      <div>
        <span>Readiness</span>
        <strong>{userHasEnoughCredits ? 'Sufficient credits' : 'Insufficient credits'}</strong>
      </div>
    </div>
  </div>
</Modal>
<MetadataTranslationModal
  isVisible={isMetadataTranslateModalVisible}
  onCancel={toggleMetadataTranslateModal}
  onTranslate={handleMetadataTranslation}
  translateMetadata={translateMetadata}
  setTranslateMetadata={setTranslateMetadata}
  bookLanguage={bookLanguage}
  targetLanguage={targetLanguage}
  setTargetLanguage={setTargetLanguage}
  quotationStyle={quotationStyle}
  setQuotationStyle={setQuotationStyle}
  selectedAIModel={selectedMetadataModel}
  setSelectedAIModel={setSelectedMetadataModel}
  aiInstructions={metadataAiInstructions}
  setAiInstructions={setMetadataAiInstructions}
  languages={languages}
  canTranslateTitle={canTranslateTitle}
  canTranslateSeries={canTranslateSeries}
  canTranslateBlurb={canTranslateBlurb}
  metadataHasTranslation={metadataHasTranslation}
  calculateMetadataTranslationCreditCost={calculateMetadataTranslationCreditCost}
  metadataOptionFreeRunConsumed={metadataOptionFreeRunConsumed}
  metadataBlurbFreeRunConsumed={metadataBlurbFreeRunConsumed}
  metadataBlurbCreditCost={blurbWordCount}
  userHasEnoughCreditsMetadata={userHasEnoughCreditsMetadata}
  creditBalance={creditBalance}
  isAnyMetadataSelectedForTranslation={isAnyMetadataSelectedForTranslation}
  frenchTone={frenchTone}
  setFrenchTone={setFrenchTone}
  bookFrenchTone={book?.frenchNarrativeTone}
  onTopUpCredits={openTopUpFromBook}
  onSubmitBetaFeedback={submitMetadataTranslationBetaFeedback}
  betaFeedbackEmail={auth.currentUser?.email || ''}
  simpleMode={isSimpleBookDetailMode}
  tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
/>

<ProofreadModal
  isVisible={isProofreadModalVisible}
  onCancel={toggleProofreadModal}
  onProofread={() => {
    if (!selectedChapter) {
      proofreadPassRequestIdRef.current ||= `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setIsProofreadConfirmationModalVisible(true);
    } else {
      handleProofread();
    }
  }}
  selectedChapter={selectedChapter}
  proofreadVersion={proofreadVersion}
  setProofreadVersion={setProofreadVersionSafe}
  isProofreadChecked={isProofreadChecked}
  setIsProofreadChecked={setIsProofreadChecked}
  autoAcceptAllChanges={autoAcceptAllProofreadChanges}
  setAutoAcceptAllChanges={handleAutoAcceptAllProofreadChangesChange}
  isSavingAutoAcceptPreference={isSavingAutoAcceptProofreadPreference}
  explanationLanguage={proofreadExplanationLanguage}
  setExplanationLanguage={handleProofreadExplanationLanguageChange}
  isSavingExplanationLanguagePreference={isSavingProofreadExplanationLanguagePreference}
  isEditingNotesChecked={isEditingNotesChecked}
  setIsEditingNotesChecked={setIsEditingNotesChecked}
  quotationStyle={quotationStyle}
  setQuotationStyle={setQuotationStyle}
  proofreadAdditionalInstructions={proofreadAdditionalInstructions}
  setProofreadAdditionalInstructions={setProofreadAdditionalInstructions}
  selectedAIModel={selectedProofreadModel}
  setSelectedAIModel={setSelectedProofreadModel}
  creditBalance={creditBalance}
  calculateAdjustedCost={calculateAdjustedCost}
  hasEligibleProofreadChapters={bulkProofreadEligibility.hasEligibleProofreadChapters}
  hasUnproofreadNotesChapters={hasUnproofreadNotesChapters}
  pendingProofreadReviewCount={bulkProofreadEligibility.pendingProofreadReviewCount}
  proofreadingPassSummary={proofreadingPassSummary}
  bulkProofreadMode={bulkProofreadMode}
  setBulkProofreadMode={setBulkProofreadMode}
  isLoadingProofreadingPassSummary={isLoadingProofreadingPassSummary}
  canChapterBeProofread={canChapterBeProofread}
  hasChapterBeenProofread={hasChapterBeenProofread}
  hasChapterBeenEditNotes={hasChapterBeenEditNotes}
  hasUntranslatedChaptersForProofread={hasUntranslatedChaptersForProofread}
  isLoadingTranslationAvailability={isHydratingProofreadTranslations}
  book={book}
  formatLangDisplayText={formatLangDisplayText}
  bookLanguage={bookLanguage}
  frenchTone={frenchTone}
  setFrenchTone={setFrenchTone}
  useBatch={useBatch}
  setUseBatch={setUseBatch}
  isLangTesterUser={isLangTesterUser}
  onTopUpCredits={openTopUpFromBook}
  tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
/>

<Modal
  title={
    <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 18 }}>
      Credit Top Up
    </div>
  }
  open={isInlineTopUpModalVisible}
  onCancel={() => {
    if (!isInlineRenewing) {
      setIsInlineTopUpModalVisible(false);
    }
  }}
  footer={null}
  width={isMobile ? '95vw' : 820}
  style={{ maxWidth: 920 }}
  centered
  destroyOnClose={false}
  bodyStyle={{ padding: isMobile ? 12 : 16 }}
>
  {isInlineTopUpLoading ? (
    <div style={{ textAlign: 'center', padding: '24px 0' }}>
      <Spin />
    </div>
  ) : resolvedOwnerUserId ? (
    <CustomPricing
      userID={resolvedOwnerUserId}
      email={auth.currentUser?.email || undefined}
      isForeverCredits={true}
      currentPlanPriceId={inlineTopUpPlanDetails.stripePriceID || inlineTopUpPlanDetails.priceId}
      currentPlanIsLegacy={!!inlineTopUpPlanDetails.isLegacy}
      currentPlanForeverDiscountPercent={inlineTopUpPlanDetails.foreverCreditDiscountPercent}
      shortfallContext={
        inlineTopUpContext
          ? {
              source: inlineTopUpContext.source,
              requiredCredits: inlineTopUpContext.requiredCredits,
              availableCredits: inlineTopUpContext.availableCredits,
              shortfallCredits: inlineTopUpContext.shortfallCredits,
              returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            }
          : undefined
      }
      compactForeverGrid={true}
      renderMode="foreverWithSide"
      purchaseMode="direct"
      onSuccess={async () => {
        const userId = auth.currentUser?.uid ?? sharedUserId ?? '';
        if (userId) {
          await Promise.allSettled([
            fetchCreditBalanceCached(userId, { force: true }),
            fetchInlineTopUpBillingContext(userId),
          ]);
        }
        setIsInlineTopUpModalVisible(false);
      }}
      sidePanel={{
        type: inlineHasCurrentPlan ? 'currentPlan' : 'subscribe',
        planName: inlineTopUpPlanDetails.planName,
        monthlyCreditsAllocated: inlinePlanMonthlyAllocation,
        monthlyPrice:
          typeof inlineTopUpPlanDetails.price === 'number' ? inlineTopUpPlanDetails.price : 0,
        billingInterval: inlineTopUpPlanDetails.billingInterval,
        intervalMonths: inlineTopUpPlanDetails.intervalMonths,
        upfrontCredits: inlinePlanUpfrontCredits,
        nextRenewalDate: inlineTopUpSubscriptionSummary
          ? (
              inlineTopUpSubscriptionSummary.trialEnd ??
              inlineTopUpSubscriptionSummary.cancelAt ??
              (inlineTopUpSubscriptionSummary.nextPaymentDate ||
                inlineTopUpSubscriptionSummary.currentPeriodEnd)
            ) || undefined
          : undefined,
        pendingChange:
          inlineTopUpSubscriptionSummary?.scheduledTargetProductId
            ? {
                targetPriceId:
                  inlineTopUpSubscriptionSummary.scheduledTargetPriceId || undefined,
                targetProductId:
                  inlineTopUpSubscriptionSummary.scheduledTargetProductId || undefined,
                effectiveAt:
                  inlineTopUpSubscriptionSummary.scheduledEffectiveDate || undefined,
                action: inlineTopUpSubscriptionSummary.scheduledAction || undefined,
              }
            : undefined,
        renewDisabled: inlineRenewDisabled,
        renewDisabledTooltip: inlineRenewDisabledMessage,
        onRenewNow: handleInlineRenewNow,
        onSubscribe: () => {
          window.open('/account/subscription', '_blank', 'noopener,noreferrer');
        },
      }}
    />
  ) : (
    <div style={{ textAlign: 'center', padding: '24px 0' }}>
      Please sign in to top up credits.
    </div>
  )}
</Modal>

      <Modal
        title={bulkProofreadMode === 'next_pass' ? 'Confirm New Paid Proofreading Pass' : 'Confirm Proofreading Settings'}
        className="book-detail-confirm-modal"
        visible={isProofreadConfirmationModalVisible}
        onCancel={() => setIsProofreadConfirmationModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsProofreadConfirmationModalVisible(false)}>
            Back
          </Button>,
          <Button key="submit" type="primary" className="app-primary-cta" onClick={handleProofread}>
            {bulkProofreadMode === 'next_pass' ? 'Start New Paid Pass' : 'Confirm and Proofread'}
          </Button>,
        ]}
      >
        <div className="book-detail-confirm-block">
        <p>Please confirm your proofreading settings:</p>
        <ul>
          <li><strong>Version to proofread:</strong> {proofreadVersion.replace('-', ' to ')}</li>
          <li><strong>Proofread entire book:</strong> {isProofreadChecked ? 'Yes' : 'No'}</li>
          {isProofreadChecked && (
            <li>
              <strong>Chapters submitted:</strong>{' '}
              {proofreadingPassSummary?.targetChapterGuidsByMode?.[bulkProofreadMode]?.length || 0}
            </li>
          )}
          {isProofreadChecked && (
            <li><strong>Estimated credit cost:</strong> {calculateAdjustedCost().toLocaleString()} credits</li>
          )}
          <li><strong>Get editing notes:</strong> {isEditingNotesChecked ? 'Yes' : 'No'}</li>
          {proofreadVersion.includes('-German') && (
            <li>
              <strong>Quotation Style:</strong> {
                quotationStyle === 'german quotation marks'
                  ? 'German Quotation Marks („Dialog Text")'
                  : 'German Guillemets (»Dialog Text«)'
              }
            </li>
          )}
          <li><strong>AI model:</strong> {selectedProofreadModel || 'Not selected'}</li>
        </ul>
        </div>
        <div className="book-detail-confirm-notice">
          {bulkProofreadMode === 'next_pass'
            ? `This starts proofreading pass ${proofreadingPassSummary?.nextPassNumber || 2}, creates a new credit charge, and replaces existing proofread output after review. Your previous pass is already complete.`
            : 'This will submit eligible content chapters using these settings. Structural EPUB sections are preserved and are not charged.'}
        </div>
      </Modal>

{isTranslationOptionsModalVisible && (
  <TranslationOptionsModal
    isVisible={isTranslationOptionsModalVisible}
    onCancel={() => {
      setMetadataTranslationOverwriteApproved(false);
      setIsTranslationOptionsModalVisible(false);
    }}
    translationOptions={translationOptions}
    translateMetadata={translateMetadata}
    bookId={bookId}
    bookLanguage={bookLanguage}
    targetLanguage={targetMetadataLanguage}
    fetchBookDetails={fetchBookDetails}
    overwriteFlag={metadataTranslationOverwriteApproved}
    tutorialBypassSave={tutorialUiSuppressionRequested}
    tourUserId={auth.currentUser?.uid || sharedUserId || undefined}
    onTutorialSaveSelections={({ titleOption, seriesOption, sourceLanguage, targetLanguage: savedTargetLanguage }) => {
      const pairKey = `${sourceLanguage}-${savedTargetLanguage}`;
      const sourceKey = sourceLanguage.replace(/ /g, '0');
      const targetKey = savedTargetLanguage.replace(/ /g, '0');
      if (titleOption?.translatedText) {
        setTitleTranslations(prev => ({
          ...prev,
          [pairKey]: titleOption.translatedText,
        }));
      }
      if (seriesOption?.translatedText) {
        setSeriesTranslations(prev => ({
          ...prev,
          [pairKey]: seriesOption.translatedText,
        }));
      }
      if (titleOption?.translatedText || seriesOption?.translatedText) {
        setBook(prevBook => ({
          ...prevBook,
          ...(titleOption?.translatedText
            ? { [`title_${sourceKey}_${targetKey}`]: titleOption.translatedText }
            : {}),
          ...(seriesOption?.translatedText
            ? { [`series_${sourceKey}_${targetKey}`]: seriesOption.translatedText }
            : {}),
        }));
      }
    }}
  />
)}

{isKeywordSuggestionModalVisible && (
  <KeywordSuggestionModal
    isVisible={isKeywordSuggestionModalVisible}
    toggleModal={toggleKeywordSuggestionModal}
    bookId={bookId}
    bookLanguage={bookLanguage}
    genres={genres.join(', ') || ''}
    tropes={book.tropes || []}
    keywords={book.keywords || []}
	blurb={book.blurb || ''}
    selectedAIModel={selectedAIModel}
    fetchCreditBalance={fetchCreditBalance}
    creditBalance={creditBalance}
    auth={auth}
    languages={languages}
    betaLanguages={betaLanguages}
	fetchBookDetails={fetchBookDetails}
	isLoading={isLoading}
    setIsLoading={setIsLoading}
	targetLanguage={targetLanguage}
    keywordSuggestionFreeRuns={(book as any).keywordSuggestionFreeRuns}
    tutorialBypassGenerate={tutorialUiSuppressionRequested}
    onTutorialGenerateKeywords={handleTutorialKeywordSuggestions}
  />
)}

    <Modal
      title="Delete Translated Chapters"
      className="book-detail-confirm-modal"
      visible={showDeleteTranslatedChaptersModal}
	  onCancel={handleDeleteTranslatedChaptersCancel}
      footer={[
        <Button key="back" onClick={handleDeleteTranslatedChaptersCancel}>
          Cancel
        </Button>,
        <Button key="submit" danger type="primary" onClick={handleDeleteTranslatedChaptersOK}>
          Delete translated chapters
        </Button>,
      ]}
    >
      <div className="book-detail-confirm-danger">This will delete all <strong>{deleteChaptersTargetLang}</strong> translated chapters. This action cannot be undone.</div>
    </Modal>

    <Modal
      title="Delete Translated Chapter"
      className="book-detail-confirm-modal"
      visible={showDeleteSingleTranslatedChapterModal}
	  onCancel={handleDeleteSingleTranslatedChapterCancel}
      footer={[
        <Button key="back" onClick={handleDeleteSingleTranslatedChapterCancel}>
          Cancel
        </Button>,
        <Button key="submit" danger type="primary" onClick={handleDeleteSingleTranslatedChapterOK}>
          Delete translation
        </Button>,
      ]}
    >
      <div className="book-detail-confirm-danger">This will delete the <strong>{deleteSingleChapterTargetLang}</strong> translation for this chapter. This action cannot be undone.</div>
    </Modal>

    <Modal
      title="Delete Proofread Chapters"
      className="book-detail-confirm-modal"
      visible={showDeleteProofreadChaptersModal}
	  onCancel={handleDeleteProofreadChaptersCancel}
      footer={[
        <Button key="back" onClick={handleDeleteProofreadChaptersCancel}>
          Cancel
        </Button>,
        <Button key="submit" danger type="primary" onClick={handleDeleteProofreadChaptersOK}>
          Delete proofread chapters
        </Button>,
      ]}
    >
      <div className="book-detail-confirm-danger">This will delete all <strong>{deleteChaptersTargetLang}</strong> proofread chapters. This action cannot be undone.</div>
    </Modal>

    <Modal
      title="Delete Proofread Chapter"
      className="book-detail-confirm-modal"
      visible={showDeleteSingleProofreadChapterModal}
	  onCancel={handleDeleteSingleProofreadChapterCancel}
      footer={[
        <Button key="back" onClick={handleDeleteSingleProofreadChapterCancel}>
          Cancel
        </Button>,
        <Button key="submit" danger type="primary" onClick={handleDeleteSingleProofreadChapterOK}>
          Delete proofreading
        </Button>,
      ]}
    >
      <div className="book-detail-confirm-danger">This will delete this chapter's proofreading. This action cannot be undone.</div>
    </Modal>


<Modal
  title={
    <div className="book-detail-human-review-title">Need a human review of your translation?</div>
  }
  className="book-detail-human-review-modal"
  visible={humanReviewModalVisible}
  onCancel={() => setHumanReviewModalVisible(false)}
	  footer={[
	    <Button
	      key="providers"
	      type="primary"
	      onClick={() => {
	        setHumanReviewModalVisible(false);
	        navigate(HUMAN_REVIEW_PROVIDERS_ROUTE);
	      }}
	    >
	      View Human Review Providers
	    </Button>,
	    <Button key="close" onClick={() => setHumanReviewModalVisible(false)}>
	      Close
	    </Button>,
	  ]}
	  width={800}
	>
	  <div className="book-detail-human-review-body">
	    <div className="book-detail-confirm-block">
	  <p>
	    Some authors choose to work with an independent human reviewer before publication.
	  </p>
	  <p>
	    View third-party providers familiar with AI-assisted translation workflows.
	  </p>
	    </div>
	  </div>
</Modal>

<AddLanguageModal
  isVisible={isAddLanguageModalVisible}
  onClose={() => setIsAddLanguageModalVisible(false)}
  onLanguageAdded={handleLanguageAdded}
  bookId={bookId}
  existingLanguages={book.translation_pairs?.map((pair) => splitLanguagePair(pair).targetLang).filter(Boolean) || []}
  allLanguages={languages}
  sourceLanguage={bookLanguage}
  betaLanguages={betaLanguages} // Add this line
/>

<NeverTranslateModal
  seriesGlossaryStatus={seriesGlossaryStatus}
  onRefreshSeriesGlossary={refreshSeriesGlossary}
  isVisible={isNeverTranslateModalVisible}
  onClose={() => setIsNeverTranslateModalVisible(false)}
  currentLanguagePair={currentLanguagePair}
  neverTranslateWords={neverTranslateWords}
  seriesNeverTranslateWords={seriesTranslateRules.neverTranslate}
  alwaysTranslateWords={alwaysTranslateWords}
  seriesAlwaysTranslateWords={seriesTranslateRules.alwaysTranslate}
  setNeverTranslateWords={setNeverTranslateWords}
  setSeriesTranslateRules={setSeriesTranslateRules}
  selectedSeriesId={selectedSeriesId}
  resolvedUserId={resolvedOwnerUserId}
  apiBase={API_BASE}
  bookGuid={bookId}
  onSwitchRuleType={handleGlossaryRuleSwitch}
/>

<AlwaysTranslateModal
  seriesGlossaryStatus={seriesGlossaryStatus}
  onRefreshSeriesGlossary={refreshSeriesGlossary}
  isVisible={isAlwaysTranslateModalVisible}
  onClose={() => setIsAlwaysTranslateModalVisible(false)}
  currentLanguagePair={currentLanguagePair}
  alwaysTranslateWords={alwaysTranslateWords}
  seriesAlwaysTranslateWords={seriesTranslateRules.alwaysTranslate}
  neverTranslateWords={neverTranslateWords}
  seriesNeverTranslateWords={seriesTranslateRules.neverTranslate}
  setAlwaysTranslateWords={setAlwaysTranslateWords}
  setSeriesTranslateRules={setSeriesTranslateRules}
  selectedSeriesId={selectedSeriesId}
  resolvedUserId={resolvedOwnerUserId}
  apiBase={API_BASE}
  bookGuid={bookId}
  onSwitchRuleType={handleGlossaryRuleSwitch}
/>

<CoverSelectionModal
  visible={coverSelectionModalVisible}
  onClose={() => {
    setCoverSelectionModalVisible(false);
    setGeneratedCoverImages([]);
    setCoverGenerationOptions(undefined);
  }}
  generatedImages={generatedCoverImages}
  targetLanguage={coverGenerationLanguage}
  bookLanguageName={bookLanguage}
  onSaveCovers={handleUpdateLanguageCovers}
  bookId={bookId}
  userId={auth.currentUser?.uid || ''}
  bookTitle={book.title}
  translatedTitle={getTranslatedTitle()}
  seriesName={book.series}
  translatedSeriesName={getTranslatedSeriesName()}
  authorName={book.author}
  currentCoverUrl={languageCovers?.['original'] === '' ? '' : (languageCovers?.['original'] || book.coverUrl)}
  generationOptions={coverGenerationOptions} // Add this prop
  expectedImageCount={coverGenerationOptions?.requestedCount}
  failureMessages={coverGenerationOptions?.failureMessages}
  creditBalance={creditBalance}
  getCoverCreditCost={getCoverCreditCost}
  onRequestRegenerate={(language) => {
    setCoverSelectionModalVisible(false);
    setGeneratedCoverImages([]);
    setOpenCoverGenerationSignal({ language, ts: Date.now() });
    setIsEditing(true);
  }}
  onVariationsStart={handleCoverVariationsStart}
  onVariationsComplete={handleCoverVariationsComplete}
  variationSeedImage={variationSeedImage || undefined}
/>

<AskAIModal
  visible={askAIModalVisible}
  onClose={() => setAskAIModalVisible(false)}
  bookId={bookId}
  chapterGuid={askAIPayload?.chapterGuid || ''}
  variant={askAIPayload?.variant || 'original'}
  variantLabel={askAIPayload?.variantLabel || (selectedTab === 'Original' ? originalLanguageLabel : selectedTab)}
  chapterHtml={askAIPayload?.chapterHtml || ''}
  originalChapterHtml={askAIPayload?.originalChapterHtml}
  auth={auth}
  defaultModelId={selectedAIModel || undefined}
  initialReviewContext={askAIPayload?.initialReviewContext || null}
  autoFetchReviewContext={askAIPayload?.autoFetchReviewContext}
/>

</>
);
};

const BookDetailPageContainer: React.FC<BookDetailPageContentProps> = ({ bookId }) => (
  <BookDetailPageContent key={bookId} bookId={bookId} />
);

export default BookDetailPageContainer;
